import {
  ActivitiesWithDeals,
  CustomRecordsWithTables,
  DealLineItemsWithJoins,
  DataIssuesWithChildrenInsert,
  DBFunctionReturns,
  DBFunctions,
  InvoicesWithDeals,
  LineItemsWithJoins,
  NotificationsQuery,
  ProfileWithJoins
} from '@dolfin/business/custom_types'
import { buildAdminAnalyticsCapture } from '@dolfin/utils/analytics/admin-events'
import { safeCapturePosthogServerEvents } from '@dolfin/utils/analytics/posthog-node'
import { isInRange, parseDateRange } from '@dolfin/utils/dates'
import { batched } from '@dolfin/utils/iterators'
import { getSourceColumns } from '@dolfin/utils/columns/sourceColumns.server'
import { isUuid } from '@dolfin/utils/strings'
import { isSplitAwareOwnerField } from '../domains/deals/splits'
import { MetricPreviewError } from '../domains/metrics/preview-contract'
import { getMetricPreviewDatabasePredicates } from '../domains/metrics/preview-filter'
import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { addDays, endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns'
import CompensationTierSnapshots from '../domains/compensation_tier_snapshots'
import type {
  DisputeAnalyticsIssueRow,
  DisputeAnalyticsStatus,
  DisputeAnalyticsTeamLookup,
  DisputeAnalyticsTeamReference,
  DisputeTag
} from '../domains/disputes'
import type { MetricSourceRecord } from '../domains/metrics'
import { ADMIN_ORGANIZATION_MRR_CURRENCY } from '../domains/organizations'
import { DolfinSupabaseClient, withDolfinRpcTypes } from './supabase_types'

import {
  buildStatementMonthStarts,
  buildGeneratedPayoutsSummary,
  buildGeneratedPayoutsSummaryFromStatusTotals,
  buildManualCompensationSummary,
  buildPayoutAdjustmentBreakdowns,
  buildPayoutAdjustmentsSummary,
  buildPayoutsToBePaidSummary,
  buildPayoutsToBePaidSummaryFromMonthTotals,
  type GeneratedPayoutsAnalytics,
  type ManualPayoutsAnalytics,
  type PayoutAdjustmentsAnalytics,
  type PayoutAnalytics,
  type PayoutAnalyticsAdjustment,
  type PayoutAnalyticsAdjustmentAudit,
  type PayoutAnalyticsParams,
  type PayoutAnalyticsPayout,
  type PayablePayoutsAnalytics
} from '../domains/payouts/analytics'
import type {
  CompensationRuleDraftHistoryEvent,
  CompensationRuleDraftHistoryRecord,
  CompensationRuleDraftHistoryRow,
  CompensationRuleAuthoringCommitRpcParams,
  CompensationRuleAuthoringReferenceSearchResult,
  CompensationRuleAuthoringReferences,
  CompensationRuleAuthoringSessionRpcParams,
  CompensationRuleAuthoringSessionUpdateRpcParams
} from '.'
import {
  BusinessUnitRemovalResult,
  BusinessUnitListItem,
  DataIssueProcessingClaim,
  DataIssueProcessingClaimArgs,
  DataIssueProcessingFinalizeArgs,
  DataIssueProcessingHeartbeatArgs,
  DataIssueProcessingRun,
  DataIssueProcessingStream,
  DataIssueProcessingStreamSelector,
  DataIngestionBatchContext,
  DataIngestionBatchRpcResult,
  DataIngestionResource,
  DataIngestionRpcResult,
  CompleteDomainEventDeliveryParams,
  CompensationTierEvent,
  CompensationTierSnapshotWrite,
  PendingToReviewItemsOptions,
  PendingToReviewItemsCursor,
  CurrentOrganizationProfileData,
  CreditSetupRuleForMetric,
  CreditSetupRuleMetricValueReconciliation,
  CreditSetupRuleMetricValueBootstrapStatus,
  CreditSetupRuleMetricValueScopeRow,
  CreditedMetricValueTelemetry,
  CreditedMetricValueRequest,
  CreditedMetricValueRow,
  CreditedTargetAchievementSeriesParams,
  CreditedTargetMetricValueAggregate,
  GetSubscriptionCompensationSourceRowsParams,
  HasRecentSubscriptionDataParams,
  BusinessUnitTimelinePreviewResult,
  BusinessUnitTimelineHistoryRow,
  CurrentTeamMembershipSetupRow,
  CurrentPlanTeamLink,
  CustomFieldFilterCandidateIdsParams,
  GetDealsGridParams,
  GetStatementMonthRowsParams,
  ChangeStatementPaymentStatusParams,
  StatementMonthRow,
  StatementReadRelations,
  StatementReadChanges,
  RelationalCustomFieldQueryIdsParams,
  RelationalCustomFieldQueryIdsResult,
  DolfinRpc,
  MetricPreviewRecipient,
  LinkTeamToPlanArgs,
  ReplacePlanTeamHistoryArgs,
  UpsertPlanTeamHistoryArgs,
  ManualPayoutUpsertPayload,
  MeaningfulAuditChange,
  MeaningfulAuditChangeTableName,
  MetricValueCurrencyBackfillPage,
  MetricValueCurrencyBackfillProgress,
  MetricValueCurrencyReconciliationChunk,
  MetricValueCurrencyProgressUpdate,
  MetricValueCurrencySnapshot,
  NotificationPayoutReference,
  PayoutManualAdjustmentBreakdown,
  CompensationRecordSearchMatch,
  CompensationRuleForExplanation,
  PayoutCalculationContextResponse,
  CompensationRecordSearchParams,
  CompensationRecordType,
  OrganizationReference,
  OrganizationProfileSearchResult,
  CreditSetupRuleAssignmentSubjectPage,
  OrganizationPlanSearchResult,
  OrganizationSearchResult,
  OrganizationTargetSearchResult,
  OrganizationTeamSearchResult,
  AdminOrganizationMrrOverviewRow,
  AdminOrganizationMrrSetting,
  AdminOrganizationMrrSettingInput,
  PayoutRuleMetricFieldReference,
  PayoutRuleScopedMetricContribution,
  NotificationStatementReference,
  OrganizationSetupFlags,
  OrganizationInvitationSetupRow,
  OrganizationMemberSetupRow,
  PlanDuplicationRecord,
  PayoutDiscrepancyRecalculationReason,
  UpdateOnHoldBlocker,
  UpdateOnHoldReviewRow,
  UpdateOnHoldScaffoldRow,
  RecordInvestigationSource,
  RecordRecalculationHistoryEntry,
  PeriodRulesAffectedScopeOrganizationStats,
  PeriodRulesAffectedScopePage,
  PeriodRulesAffectedScopePaginatedRow,
  PeriodRulesAffectedScopeCursor,
  GetOrganizationMetricsValuesParams,
  PeriodRulesAffectedScopeRow,
  PeriodRulesRunWatermark,
  ProfilesResult,
  ProviderProfileIdentity,
  SubscriptionCompensationSourceRow,
  StatementDiscrepancyRevertResult,
  TeamSetupMemberRow,
  TeamSetupPlanSummaryRow,
  TeamSetupSummaryRow,
  TeamSetupTargetSummaryRow,
  UnlinkTeamFromPlanArgs,
  TeamMembershipHistorySetupRow,
  TeamMembershipEvent,
  TeamMembershipInPeriod,
  TeamProfileRow,
  TargetContributingRecordsAccess,
  TargetContributingRecordsAccessParams,
  TargetCumulativeAchievementRow,
  TargetCreditSetupSelectionParams,
  TargetUpsertWithCreditSetupSelectionParams,
  TargetWithCreditSetup,
  StatementPeriodReference,
  SaveUserEditTeamMembershipRangeSetupParams,
  CreateKpiTableWithTeamsParams,
  CreateCustomRecordTableWithTeamsParams,
  UpdateDataTableTeamsParams,
  DataTableCreationResult,
  DataView,
  DataViewCreateParams,
  DataViewListParams,
  DataViewFilterFieldsParams,
  DataViewFilterField,
  DataViewUpdateParams,
  StatementResetEligibility,
  StatementReviewFlowRow,
  StartStatementsReviewResult,
  UpdateOnHoldCommittedResult,
  UpdateOnHoldDecisionInput,
  UpdateOnHoldPreview,
  ValueDiscrepancyAdjustmentInput,
  GetValidationProcessPayoutParams,
  GetValidationProcessStatementParams,
  ValidationProcessOrganizationConfig,
  ValidationProcessRawPayoutSample,
  ValidationProcessRawStatementSample,
  ValidationProcessReviewerRow,
  ValidationProcessStepEntity
} from '.'
import type { ActivityGridRequest } from '../domains/activities'
import type { InvoiceGridRequest } from '../domains/invoices'
import type { KpiGridRequest } from '../domains/kpis'
import type { CustomRecordGridRequest } from '../domains/custom_records'
import {
  CompositeTypes,
  Database,
  Enums,
  Json,
  Tables,
  TablesInsert,
  TablesUpdate
} from '../db_types'
import { type GetDisputeThreadParams } from '../domains/disputes'
import {
  CloseImpersonationParams,
  CreateImpersonationAttemptParams,
  CreateImpersonationAttemptResult,
  ImpersonationAuditLog,
  ImpersonationCandidateProfile,
  ImpersonationProfile,
  ListImpersonationCandidateProfilesParams
} from '../domains/impersonation/types'
import { MetricUsage } from '../domains/metrics'
import { getMetricReferenceIds } from '../domains/metrics/references'
import type { GetProfilesParams } from '../domains/profiles'
import DolfinLoggerFactory from '../logger/factory'
import DolfinRecord, {
  getRecordQueryFieldPath,
  resolveRecordFieldReference,
  getSourceCustomFields,
  canonicalizeRecordFilterField,
  collectRecordAuditReferenceIds,
  extractFormulaSourceFieldKeys,
  getRecordForeignKeyField,
  findMeaningfulRecordAuditChanges,
  getMetricValueSourceOrganizationPath,
  getMetricValueSourceSelectQuery,
  loadRecordAuditReferenceLookup,
  sanitizeMetricValueSourceRow
} from '../record'
import {
  CalculationContextSchema,
  MetricValueToDelete,
  ParsedExecutionResult,
  PayoutRelease,
  PayoutReleaseContext,
  PayoutWithSource,
  RecordData,
  CreditedTargetEvidence
} from '../types'

const Logger = DolfinLoggerFactory.create()
const PERIOD_RULES_AFFECTED_SCOPE_PAGE_SIZE = 1000
const PAYOUTS_FOR_SIMULATION_PAGE_SIZE = 1000
const DASHBOARD_KPI_PAGE_SIZE = 1000
const SUBSCRIPTION_COMPENSATION_PAYOUTS_PAGE_SIZE = 1000
const SUBSCRIPTION_COMPENSATION_LOOKUP_BATCH_SIZE = 50
const KPI_PREVIEW_MONTHS_PAGE_SIZE = 1000
const PAYOUT_DISCREPANCY_INDICATORS_PAGE_SIZE = 1000
export const DEFAULT_WITHHELD_PAYOUT_MIN_CHECK_AGE_MS = 60 * 60 * 1000
const PROFILE_IDS_IN_FILTER_BATCH_SIZE = 100
const ORGANIZATION_IDS_IN_FILTER_BATCH_SIZE = 100
const ORGANIZATION_ENTITY_SEARCH_PREFETCH_MULTIPLIER = 5
const ADMIN_ORGANIZATION_MRR_PAGE_SIZE = 1000
const ACTIVE_ORGANIZATION_USER_COUNT_CACHE_TTL_MS = 60 * 1000
const ACTIVE_ORGANIZATION_USER_COUNT_CACHE_MAX_ENTRIES = 500
type ActiveOrganizationUserCountCacheEntry = {
  count: number
  expiresAt: number
  referenceDate: string
}

function getCachedActiveOrganizationUserCount(
  cache: Map<number, ActiveOrganizationUserCountCacheEntry>,
  organizationId: number,
  referenceDate: string
) {
  const cached = cache.get(organizationId)
  if (cached && cached.referenceDate === referenceDate && cached.expiresAt > Date.now()) {
    return cached.count
  }

  if (cached) {
    cache.delete(organizationId)
  }

  return null
}

function cacheActiveOrganizationUserCount(
  cache: Map<number, ActiveOrganizationUserCountCacheEntry>,
  organizationId: number,
  count: number,
  referenceDate: string
) {
  if (
    !cache.has(organizationId) &&
    cache.size >= ACTIVE_ORGANIZATION_USER_COUNT_CACHE_MAX_ENTRIES
  ) {
    const oldestOrganizationId = cache.keys().next().value
    if (oldestOrganizationId != null) {
      cache.delete(oldestOrganizationId)
    }
  }

  cache.delete(organizationId)
  cache.set(organizationId, {
    count,
    expiresAt: Date.now() + ACTIVE_ORGANIZATION_USER_COUNT_CACHE_TTL_MS,
    referenceDate
  })
}

type PaginatedMrrQuery<Row> = {
  range: (
    from: number,
    to: number
  ) => PromiseLike<{
    data: Row[] | null
    error: { message: string } | null
  }>
}

async function fetchAllMrrRows<Row>(query: PaginatedMrrQuery<Row>): Promise<Row[]> {
  const rows: Row[] = []

  for (let offset = 0; ; offset += ADMIN_ORGANIZATION_MRR_PAGE_SIZE) {
    const { data, error } = await query.range(offset, offset + ADMIN_ORGANIZATION_MRR_PAGE_SIZE - 1)

    if (error) throw error
    if (!data || data.length === 0) break

    rows.push(...data)
  }

  return rows
}
const MAX_COMPENSATION_RECORD_SEARCH_TOKENS = 5
const ORGANIZATION_ENTITY_SEARCH_PREFETCH_MIN = 50
const DASHBOARD_PAYOUT_SUMMARY_SELECT = `
  id,
  profile_id,
  value,
  effective_date,
  description,
  statement_id,
  status,
  is_withheld,
  compensation_rule_id,
  target_id,
  payout_discrepancy_id,
  profiles!payouts_profile_id_fkey(id, name, email, img_url),
  statements!payouts_statement_id_fkey(period_start, status),
  payout_installments(
    id,
    amount,
    statement_id,
    status,
    statements!payout_installments_statement_id_fkey(period_start, status)
  )
`
const DASHBOARD_PAYABLE_PAYOUT_SELECT = `
  id,
  value,
  effective_date,
  statement_id,
  profiles!payouts_profile_id_fkey!inner(
    id,
    organizations_profiles!inner(organization_id, archived, get_profile_status_field),
    teams_profiles!inner(role, teams!inner(organization_id))
  ),
  statements!payouts_statement_id_fkey!inner(period_start, status),
  payout_installments(
    id,
    amount,
    statement_id,
    statements!payout_installments_statement_id_fkey(period_start, status)
  )
`

const DASHBOARD_PAYABLE_INSTALLMENT_PAYOUT_SELECT = `
  id,
  value,
  effective_date,
  statement_id,
  profiles!payouts_profile_id_fkey!inner(
    id,
    organizations_profiles!inner(organization_id, archived, get_profile_status_field),
    teams_profiles!inner(role, teams!inner(organization_id))
  ),
  payout_installments!inner(
    id,
    amount,
    statement_id,
    statements!payout_installments_statement_id_fkey!inner(period_start, status)
  )
`

const DASHBOARD_MANUAL_PAYOUT_SELECT = `
  id,
  profile_id,
  value,
  effective_date,
  description,
  statement_id,
  compensation_rule_id,
  target_id,
  payout_discrepancy_id,
  profiles!payouts_profile_id_fkey(id, name, email, img_url),
  statements!payouts_statement_id_fkey(period_start, status)
`

const COMPENSATION_RULE_VERSION_ANALYSIS_SELECT = `id, active_period, diagram, is_tvp_based, compensation_rule:compensation_rules!inner(id, label, description, source, type, outcome_period, user_field, accrual_date_field, kpis_table_id, archived, plans!inner(organization_id))`

type CompensationRuleVersionAnalysisQueryRow = {
  id: number
  active_period: Tables<'compensation_rule_versions'>['active_period']
  diagram: Tables<'compensation_rule_versions'>['diagram']
  is_tvp_based: Tables<'compensation_rule_versions'>['is_tvp_based']
  compensation_rule: {
    id: number
    label: string
    description: string
    source: Tables<'compensation_rules'>['source']
    type: Tables<'compensation_rules'>['type']
    outcome_period: Tables<'compensation_rules'>['outcome_period']
    user_field: Tables<'compensation_rules'>['user_field']
    accrual_date_field: Tables<'compensation_rules'>['accrual_date_field']
    kpis_table_id: Tables<'compensation_rules'>['kpis_table_id']
  }
}

const mapCompensationRuleVersionForAnalysis = (
  version: CompensationRuleVersionAnalysisQueryRow
) => ({
  version_id: version.id,
  active_period: version.active_period,
  diagram: version.diagram,
  is_tvp_based: version.is_tvp_based,
  description: version.compensation_rule.description,
  id: version.compensation_rule.id,
  label: version.compensation_rule.label,
  source: version.compensation_rule.source,
  type: version.compensation_rule.type,
  outcome_period: version.compensation_rule.outcome_period,
  user_field: version.compensation_rule.user_field,
  accrual_date_field: version.compensation_rule.accrual_date_field,
  kpis_table_id: version.compensation_rule.kpis_table_id
})

type InvoiceSubscriptionOverviewRow = {
  id: number
  effective_at: string | null
  subscription_id: string | null
  subscription_fk: number | null
  payroll_provider: string | null
  deals?: { id: number; name: string | null } | null
}

const normalizeInvoiceSubscriptionOverviewRow = (
  value: InvoiceSubscriptionOverviewRow | InvoiceSubscriptionOverviewRow[] | null | undefined
) => {
  if (Array.isArray(value)) {
    return value[0] ?? null
  }

  return value ?? null
}

const hasCanonicalSubscriptionReference = (invoice: InvoiceSubscriptionOverviewRow | null) =>
  !!invoice?.payroll_provider?.trim() && !!invoice?.subscription_id?.trim()

const resolveSubscriptionOverviewInvoice = (row: {
  invoices?: InvoiceSubscriptionOverviewRow | InvoiceSubscriptionOverviewRow[] | null
  line_items?: {
    id: number
    invoices?: InvoiceSubscriptionOverviewRow | InvoiceSubscriptionOverviewRow[] | null
  } | null
}) => {
  const directInvoice = normalizeInvoiceSubscriptionOverviewRow(row.invoices)
  const lineItemInvoice = normalizeInvoiceSubscriptionOverviewRow(row.line_items?.invoices)

  if (hasCanonicalSubscriptionReference(directInvoice)) {
    return directInvoice
  }

  if (hasCanonicalSubscriptionReference(lineItemInvoice)) {
    return lineItemInvoice
  }

  return directInvoice ?? lineItemInvoice ?? null
}

type CanonicalSubscriptionOverviewRow = {
  id: number
  provider: string
  subscription_id: string
  provider_customer_id: string | null
  customer_id: number | null
  status: string | null
  start_date: string | null
  current_period_end: string | null
  cancel_at: string | null
  cancelled_at: string | null
  ended_at: string | null
  customers?: {
    id: number
    provider_customer_id: string
    name: string | null
    company: string | null
    email: string | null
    meta?: {
      local_backfill?: {
        reason?: string | null
      } | null
    } | null
  } | null
}

const isBackfilledPlaceholderCustomer = (customer: CanonicalSubscriptionOverviewRow['customers']) =>
  !!customer?.meta?.local_backfill

const getCanonicalSubscriptionAccountName = (
  subscription: CanonicalSubscriptionOverviewRow | null
) => {
  const customer = subscription?.customers ?? null

  if (!customer) {
    return null
  }

  if (isBackfilledPlaceholderCustomer(customer)) {
    return customer.provider_customer_id?.trim() || null
  }

  return customer.company?.trim() || customer.name?.trim() || null
}

// PostgREST expects a range literal when filtering daterange columns with `cs`.
export const buildSingleDateRangeLiteral = (referenceDate: string) =>
  `[${referenceDate},${referenceDate}]`

export const SOURCE_TO_TABLE: Record<
  Enums<'plan_source'>,
  'deals' | 'activities' | 'invoices' | 'kpis' | 'line_items' | 'deal_line_items' | 'custom_records'
> = {
  deal: 'deals',
  activity: 'activities',
  invoice: 'invoices',
  kpi: 'kpis',
  line_item: 'line_items',
  deal_line_item: 'deal_line_items',
  custom_record: 'custom_records'
}
export const getCompensationRuleAuthoringMetricSources = (
  source: Enums<'plan_source'>
): Enums<'plan_source'>[] => {
  switch (source) {
    case 'activity':
      return ['activity', 'deal']
    case 'invoice':
      return ['invoice', 'deal']
    case 'line_item':
      return ['line_item', 'invoice', 'deal']
    case 'deal_line_item':
      return ['deal_line_item', 'deal']
    default:
      return [source]
  }
}

type DiscrepancyStatementSummary = {
  period_start: string
  period_end: string
  cut_period: string | null
  status: string
}

type DiscrepancyProfileSummary = Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>

type ProfileForEditRow = Pick<
  Tables<'profiles'>,
  | 'created_at'
  | 'email'
  | 'id'
  | 'img_url'
  | 'name'
  | 'name_lower_case'
  | 'name_normalized'
  | 'onboarded'
  | 'tax_id_number'
  | 'updated_at'
> & {
  organizations_profiles: ProfileWithJoins['organizations_profiles'] | null
  profile_org_start_date: NonNullable<ProfileWithJoins['profile_org_start_date']> | null
  profile_org_variable_salary: NonNullable<ProfileWithJoins['profile_org_variable_salary']> | null
  teams_profiles:
    | (Tables<'teams_profiles'> & {
        teams: Pick<Tables<'teams'>, 'id' | 'name' | 'organization_id'> | null
      })[]
    | null
}

type DiscrepancyDealSummary = Pick<Tables<'deals'>, 'id' | 'deal_id' | 'name'>

type DiscrepancyInvoiceSummary = Pick<Tables<'invoices'>, 'id' | 'invoice_number' | 'deal_id'> & {
  deals?: DiscrepancyDealSummary | null
}

type DiscrepancyActivitySummary = Pick<
  Tables<'activities'>,
  'id' | 'activity_id' | 'type' | 'deal_id'
> & {
  deals?: DiscrepancyDealSummary | null
}

type DiscrepancyKpiSummary = Pick<Tables<'kpis'>, 'id' | 'kpis_table_id'> & {
  kpis_tables?: { label: string } | null
}

type DiscrepancyLineItemSummary = Pick<Tables<'line_items'>, 'id'> & {
  invoices?: DiscrepancyInvoiceSummary | null
}

type DiscrepancyInstallmentSummary = Pick<
  Tables<'payout_installments'>,
  'id' | 'installment_number' | 'phase_id' | 'statement_id' | 'amount'
> & {
  statements?: DiscrepancyStatementSummary | null
}

type DiscrepancyPayoutSummary = Tables<'payouts'> & {
  payout_categories?: { label: string } | null
  compensation_rules?: {
    id: number
    source: Enums<'plan_source'>
  } | null
  profiles?: DiscrepancyProfileSummary | null
  deals?: DiscrepancyDealSummary | null
  invoices?: DiscrepancyInvoiceSummary | null
  line_items?: DiscrepancyLineItemSummary | null
  activities?: DiscrepancyActivitySummary | null
  kpis?: DiscrepancyKpiSummary | null
  statements?: DiscrepancyStatementSummary | null
  payout_installments?: DiscrepancyInstallmentSummary[] | null
}

type PayoutDiscrepancyWithPayout = Tables<'payout_discrepancies'> & {
  payouts?: DiscrepancyPayoutSummary | null
  reviewer?: DiscrepancyProfileSummary | null
  existing_statement?: DiscrepancyStatementSummary | null
  calculated_statement?: DiscrepancyStatementSummary | null
}

type PayoutDiscrepancyWithStatements = Tables<'payout_discrepancies'> & {
  existing_statement?: DiscrepancyStatementSummary | null
  calculated_statement?: DiscrepancyStatementSummary | null
  adjustment_payouts?: Array<
    Pick<Tables<'payouts'>, 'currency' | 'statement_id' | 'value'> & {
      statements?: DiscrepancyStatementSummary | null
    }
  >
}

type ValidationProcessPendingReviewerRow = {
  id: number
  next_validators?: string | null
  next_approvers?: string | null
}

type ValidationProcessPendingPayoutRow = {
  id: number
  profile_id: string
  value: number | null
  currency: string | null
  effective_date: string | null
  period_start: string | null
  period_end: string | null
  next_validators: string | null
  status: Tables<'payouts'>['status']
  profiles?: Pick<Tables<'profiles'>, 'id' | 'name' | 'email'> | null
}

type ValidationProcessPendingStatementRow = {
  id: number
  profile_id: string
  period_start: string
  period_end: string
  status: Tables<'statements'>['status']
  next_approvers: string | null
  profiles?: Pick<Tables<'profiles'>, 'id' | 'name' | 'email'> | null
}

function parseCommaSeparatedProfileIds(value: string | null | undefined) {
  if (!value) return []

  // `next_validators` / `next_approvers` are stored as compact comma-separated
  // profile ID lists on each pending row. The domain treats the parsed result as
  // "who can act on this item right now" and handles dedup/counting across items.
  return value
    .split(',')
    .map(profileId => profileId.trim())
    .filter(Boolean)
}

const STATEMENT_READ_COLUMNS =
  '*, has_pending_payouts, pending_approval, next_approvers, issue_count:issues(count)'

type StatementReadRow = Omit<StatementMonthRow, 'issueCount' | 'nextApproverIds'> & {
  issue_count: { count: number }[]
}

function mapStatementReadRow({ issue_count, ...row }: StatementReadRow): StatementMonthRow {
  return {
    ...row,
    issueCount: issue_count[0]?.count ?? 0,
    nextApproverIds: parseCommaSeparatedProfileIds(row.next_approvers)
  }
}

type PayoutManualAdjustmentRow = Pick<
  Tables<'payout_adjustments'>,
  'id' | 'payout_id' | 'amount' | 'created_at'
> & {
  profiles?: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'> | null
}

function escapeLikePattern(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}

function escapeOrLikePattern(value: string) {
  return escapeLikePattern(value).replace(/"/g, '\\"')
}

function subtractIsoDateDay(value: string) {
  const parsed = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`invalid ISO date: ${value}`)
  }
  parsed.setUTCDate(parsed.getUTCDate() - 1)
  return parsed.toISOString().slice(0, 10)
}

function addIsoDateDay(value: string) {
  const parsed = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`invalid ISO date: ${value}`)
  }
  parsed.setUTCDate(parsed.getUTCDate() + 1)
  return parsed.toISOString().slice(0, 10)
}

function normalizeImpersonationCandidateQuery(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function normalizeOrganizationLookupValue(value: string) {
  return value
    .replace(/🇪🇸/g, ' spain ')
    .replace(/🇬🇧/g, ' uk ')
    .replace(/🇲🇽/g, ' mexico ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\bgb\b/g, ' uk ')
    .replace(/\bunited kingdom\b/g, ' uk ')
    .replace(/\bespana\b/g, ' spain ')
    .replace(/\bmexico\b/g, ' mexico ')
    .replace(/\s+/g, ' ')
    .trim()
}

function scoreNormalizedLookupMatch(
  normalizedCandidate: string,
  normalizedQuery: string,
  normalizedQueryTokens: string[]
) {
  if (normalizedCandidate === normalizedQuery) {
    return 0
  }

  if (normalizedCandidate.startsWith(normalizedQuery)) {
    return 1
  }

  if (
    normalizedQueryTokens.length > 0 &&
    normalizedQueryTokens.every(token => normalizedCandidate.includes(token))
  ) {
    return 2
  }

  if (normalizedCandidate.includes(normalizedQuery)) {
    return 3
  }

  return Number.POSITIVE_INFINITY
}

function searchOrganizationEntities<TEntity extends { id: number }>(args: {
  rows: TEntity[]
  query: string
  limit: number
  getSearchText: (row: TEntity) => string
}) {
  const normalizedQuery = normalizeOrganizationLookupValue(args.query)
  if (!normalizedQuery) {
    return []
  }

  const normalizedQueryTokens = normalizedQuery.split(' ').filter(Boolean)

  return args.rows
    .map(row => {
      const normalizedCandidate = normalizeOrganizationLookupValue(args.getSearchText(row))
      const score = scoreNormalizedLookupMatch(
        normalizedCandidate,
        normalizedQuery,
        normalizedQueryTokens
      )

      return {
        row,
        score,
        normalizedCandidate
      }
    })
    .filter(entry => entry.score !== Number.POSITIVE_INFINITY)
    .sort((left, right) => {
      if (left.score !== right.score) {
        return left.score - right.score
      }

      if (left.normalizedCandidate.length !== right.normalizedCandidate.length) {
        return left.normalizedCandidate.length - right.normalizedCandidate.length
      }

      return left.row.id - right.row.id
    })
    .slice(0, args.limit)
    .map(entry => entry.row)
}
function buildCompensationRecordSearchOrFilter(columns: string[], query: string) {
  const rawTokens = query.trim().split(/\s+/).filter(Boolean)
  const normalizedTokens = normalizeOrganizationLookupValue(query).split(' ').filter(Boolean)
  const tokens = Array.from(new Set([...rawTokens, ...normalizedTokens])).slice(
    0,
    MAX_COMPENSATION_RECORD_SEARCH_TOKENS
  )
  if (tokens.length === 0) {
    return null
  }

  return columns
    .flatMap(column => tokens.map(token => `${column}.ilike."%${escapeOrLikePattern(token)}%"`))
    .join(',')
}

function parseNumericRecordSearchQuery(query: string) {
  if (!/^\d+$/.test(query.trim())) {
    return null
  }

  const value = Number(query)
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

function buildOrganizationEntitySearchPrefetchLimit(limit: number) {
  return Math.max(
    limit * ORGANIZATION_ENTITY_SEARCH_PREFETCH_MULTIPLIER,
    ORGANIZATION_ENTITY_SEARCH_PREFETCH_MIN
  )
}

function buildOrganizationEntitySearchOrFilter(column: string, query: string) {
  const rawTokens = query.trim().split(/\s+/).filter(Boolean)
  const normalizedTokens = normalizeOrganizationLookupValue(query).split(' ').filter(Boolean)
  const tokens = Array.from(new Set([...rawTokens, ...normalizedTokens]))

  if (tokens.length === 0) {
    return null
  }

  return tokens.map(token => `${column}.ilike."%${escapeOrLikePattern(token)}%"`).join(',')
}

export function buildBusinessUnitCostCenterName({
  departmentName,
  legalEntityName,
  locationCountry,
  locationCity
}: {
  departmentName: string
  legalEntityName?: string | null
  locationCountry?: string | null
  locationCity?: string | null
}) {
  return [
    departmentName.trim(),
    legalEntityName?.trim(),
    locationCountry?.trim(),
    locationCity?.trim()
  ]
    .filter(Boolean)
    .join(' - ')
}

async function loadPayoutRuleMetricsContext(supabase: SupabaseClient<Database>, payoutId: number) {
  const { data: payout, error: payoutError } = await supabase
    .from('payouts')
    .select('id, period_start, period_end, compensation_rule_id, compensation_rules(source)')
    .eq('id', payoutId)
    .maybeSingle()

  if (payoutError) throw payoutError

  const payoutSource = payout?.compensation_rules?.source

  if (
    !payout ||
    !payout.compensation_rule_id ||
    !payout.period_start ||
    !payout.period_end ||
    !payoutSource
  ) {
    return null
  }

  const activePeriodFilter = buildSingleDateRangeLiteral(payout.period_end)
  const { data: version, error: versionError } = await supabase
    .from('compensation_rule_versions')
    .select('id')
    .eq('compensation_rule_id', payout.compensation_rule_id)
    .contains('active_period', activePeriodFilter)
    .maybeSingle()

  if (versionError) throw versionError
  if (!version?.id) return null

  const [
    { data: versionMetrics, error: versionMetricsError },
    { data: versionTargets, error: versionTargetsError }
  ] = await Promise.all([
    supabase
      .from('compensation_rule_version_metrics')
      .select('metric_id')
      .eq('compensation_rule_version_id', version.id),
    supabase
      .from('compensation_rule_version_targets')
      .select('target_id, targets(metric_id)')
      .eq('compensation_rule_version_id', version.id)
  ])

  if (versionMetricsError) throw versionMetricsError
  if (versionTargetsError) throw versionTargetsError

  const seedMetricIds = Array.from(
    new Set([
      ...(versionMetrics ?? []).map(row => row.metric_id),
      ...(
        (versionTargets ?? []) as Array<{
          targets?: { metric_id?: number | null } | null
        }>
      ).flatMap(row => (row.targets?.metric_id ? [row.targets.metric_id] : []))
    ])
  )

  if (seedMetricIds.length === 0) return null

  const metricsById = new Map<
    number,
    Pick<Tables<'metrics'>, 'id' | 'label' | 'source' | 'formula'>
  >()
  const pendingMetricIds = [...seedMetricIds]

  while (pendingMetricIds.length > 0) {
    const unresolvedMetricIds = Array.from(
      new Set(pendingMetricIds.splice(0).filter(metricId => !metricsById.has(metricId)))
    )

    if (unresolvedMetricIds.length === 0) {
      continue
    }

    const { data: metrics, error: metricsError } = await supabase
      .from('metrics')
      .select('id, label, source, formula')
      .in('id', unresolvedMetricIds)

    if (metricsError) throw metricsError

    for (const metric of metrics ?? []) {
      metricsById.set(metric.id, metric)
    }

    const referencedMetricIds = Array.from(
      new Set((metrics ?? []).flatMap(metric => getMetricReferenceIds(metric.formula)))
    )

    pendingMetricIds.push(...referencedMetricIds)
  }

  const metricReferenceGraph = new Map(
    Array.from(metricsById.entries()).map(([metricId, metric]) => [
      metricId,
      getMetricReferenceIds(metric.formula)
    ])
  )
  const reachableMetricIds = new Set<number>()
  const metricIdsToVisit = [...seedMetricIds]

  while (metricIdsToVisit.length > 0) {
    const metricId = metricIdsToVisit.pop()

    if (metricId == null || reachableMetricIds.has(metricId)) {
      continue
    }

    reachableMetricIds.add(metricId)

    for (const referencedMetricId of metricReferenceGraph.get(metricId) ?? []) {
      if (!reachableMetricIds.has(referencedMetricId)) {
        metricIdsToVisit.push(referencedMetricId)
      }
    }
  }

  const ruleMetricIds = Array.from(reachableMetricIds).filter(
    metricId => metricsById.get(metricId)?.source === payoutSource
  )

  return {
    payoutSource,
    periodStart: payout.period_start,
    periodEnd: payout.period_end,
    metricsById,
    ruleMetricIds
  }
}

type DisputeAnalyticsTagQueryRow = {
  id: number
  tag: string
}

type DisputeAnalyticsQueryRow = {
  id: number
  created_at: string
  created_by: string
  status: DisputeAnalyticsStatus
  issues_tags: Array<{
    tags: DisputeAnalyticsTagQueryRow | DisputeAnalyticsTagQueryRow[] | null
  }> | null
  profiles:
    | { id: string; name: string | null; email: string | null }
    | { id: string; name: string | null; email: string | null }[]
    | null
  statements:
    | { period_start: string | null; period_end: string | null }
    | { period_start: string | null; period_end: string | null }[]
    | null
  payouts:
    | {
        statements:
          | { period_start: string | null; period_end: string | null }
          | { period_start: string | null; period_end: string | null }[]
          | null
      }
    | Array<{
        statements:
          | { period_start: string | null; period_end: string | null }
          | { period_start: string | null; period_end: string | null }[]
          | null
      }>
    | null
}

function normalizeUpdatesOnHoldBlockers(value: unknown): UpdateOnHoldBlocker[] {
  if (!Array.isArray(value)) return []

  return value.flatMap(entry => {
    if (!entry || typeof entry !== 'object') return []

    const rawEntry = entry as Record<string, unknown>
    const updateId = Number(rawEntry.update_id ?? rawEntry.updateId)
    if (!Number.isSafeInteger(updateId) || updateId <= 0) return []

    const rawBlockers = Array.isArray(rawEntry.blockers) ? rawEntry.blockers : [rawEntry]
    return rawBlockers.flatMap(blocker => {
      if (!blocker || typeof blocker !== 'object') return []

      const rawBlocker = blocker as Record<string, unknown>
      if (typeof rawBlocker.code !== 'string' || !rawBlocker.code) return []

      return [
        {
          updateId,
          code: rawBlocker.code,
          ...(typeof rawBlocker.message === 'string' && rawBlocker.message
            ? { message: rawBlocker.message }
            : {})
        }
      ]
    })
  })
}

export class DolfinSupabaseDbRpc extends DolfinRpc {
  protected supabase: DolfinSupabaseClient
  private targetCache = new Map<
    number,
    | (Tables<'targets'> & {
        metrics: Tables<'metrics'> | null
        teams_targets: Tables<'teams_targets'>[] | null
      })
    | null
  >()
  private activeOrganizationUserCountCache = new Map<
    number,
    ActiveOrganizationUserCountCacheEntry
  >()

  constructor(client?: SupabaseClient<Database>) {
    super()

    this.supabase = withDolfinRpcTypes(
      client ??
        createClient<Database>(
          process.env.NEXT_PUBLIC_SUPABASE_URL!,
          process.env.SUPABASE_SECRET_KEY!,
          {
            auth: {
              persistSession: false
            }
          }
        )
    )
  }

  async getManualCompensationImportContext(organizationId: number, actorId: string) {
    const { data, error } = await this.supabase.rpc('manual_compensation_import_context', {
      _organization_id: organizationId,
      _actor_id: actorId
    })
    if (error) throw error
    return data as unknown as import('../domains/payouts/compensation-import').CompensationImportContext
  }

  async getManualCompensationImport(organizationId: number, actorId: string, hash: string) {
    const { data, error } = await this.supabase.rpc('get_manual_compensation_import', {
      _organization_id: organizationId,
      _actor_id: actorId,
      _file_sha256: hash
    })
    if (error) throw error
    return data
  }

  async commitManualCompensationImport(
    organizationId: number,
    actorId: string,
    hash: string,
    rows: Json
  ) {
    const { data, error } = await this.supabase.rpc('commit_manual_compensation_import', {
      _organization_id: organizationId,
      _actor_id: actorId,
      _file_sha256: hash,
      _rows: rows
    })
    if (error) throw error
    return data
  }

  async isOrganizationMember(profileId: string, organizationId: number) {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .select('role')
      .eq('profile_id', profileId)
      .eq('organization_id', organizationId)
      .eq('archived', false)
      .maybeSingle()

    if (error) throw error
    return !!data
  }

  async isOrganizationActiveMember(profileId: string, organizationId: number) {
    if (!(await this.isOrganizationMember(profileId, organizationId))) {
      return false
    }

    const { data: profileStatus, error } = await this.supabase.rpc('get_profile_status', {
      p_organization_id: organizationId,
      p_profile_id: profileId
    })

    if (error) throw error

    return profileStatus !== 'terminated' && profileStatus !== 'inactive'
  }

  async isActiveOrganizationAdmin(profileId: string, organizationId: number) {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .select('role')
      .eq('profile_id', profileId)
      .eq('organization_id', organizationId)
      .eq('archived', false)
      .maybeSingle()

    if (error) throw error
    if (data?.role !== 'admin') return false
    const { data: status, error: statusError } = await this.supabase.rpc('get_profile_status', {
      p_profile_id: profileId,
      p_organization_id: organizationId,
      p_date: new Date().toISOString()
    })
    if (statusError) throw statusError
    return status !== 'inactive' && status !== 'terminated'
  }

  async isActiveOrganizationMember(profileId: string, organizationId: number) {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .select('profile_id')
      .eq('profile_id', profileId)
      .eq('organization_id', organizationId)
      .eq('archived', false)
      .maybeSingle()

    if (error) throw error
    if (!data) return false
    const { data: status, error: statusError } = await this.supabase.rpc('get_profile_status', {
      p_profile_id: profileId,
      p_organization_id: organizationId,
      p_date: new Date().toISOString()
    })
    if (statusError) throw statusError
    return status !== 'inactive' && status !== 'terminated'
  }

  async getOrganizationProfileIdByEmail(organizationId: number, email: string) {
    const { data, error } = await this.supabase
      .from('profiles')
      .select('id, organizations_profiles!inner(organization_id, archived)')
      .eq('email', email.trim().toLowerCase())
      .eq('organizations_profiles.organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return data?.id ?? null
  }

  async getOrganizationProfileEmailById(organizationId: number, profileId: string) {
    if (!isUuid(profileId)) return null

    const { data, error } = await this.supabase
      .from('profiles')
      .select('email, organizations_profiles!inner(organization_id, archived)')
      .eq('id', profileId)
      .eq('organizations_profiles.organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return data?.email ?? null
  }

  async getDealIdByExternalId(organizationId: number, externalId: string) {
    const { data, error } = await this.supabase
      .from('deals')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('deal_id', externalId)
      .maybeSingle()

    if (error) throw error
    return data?.id ?? null
  }

  async getLineItemByExternalId(organizationId: number, externalId: string) {
    const { data, error } = await this.supabase
      .from('line_items')
      .select('id, invoice_id')
      .eq('organization_id', organizationId)
      .eq('line_item_id', externalId)
      .maybeSingle()

    if (error) throw error
    return data ?? null
  }

  async getDealLineItemByExternalId(organizationId: number, externalId: string) {
    const { data, error } = await this.supabase
      .from('deal_line_items')
      .select('id, deal_id')
      .eq('organization_id', organizationId)
      .eq('line_item_id', externalId)
      .maybeSingle()

    if (error) throw error
    return data ?? null
  }

  async deleteDealLineItemsByProviderIds({
    organizationId,
    provider,
    providerIds
  }: {
    organizationId: number
    provider: string
    providerIds: string[]
  }) {
    const { data, error } = await this.supabase.rpc('delete_deal_line_items_by_provider_ids', {
      _organization_id: organizationId,
      _provider: provider,
      _provider_ids: providerIds
    })
    if (error) throw error
    return data
  }

  async dataRecordExists(
    resource: DataIngestionResource,
    organizationId: number,
    externalId: string
  ) {
    const table = {
      deals: 'deals',
      invoices: 'invoices',
      activities: 'activities'
    }[resource] as 'deals' | 'invoices' | 'activities'
    const idColumn = {
      deals: 'deal_id',
      invoices: 'invoice_id',
      activities: 'activity_id'
    }[resource]
    const { data, error } = await this.supabase
      .from(table)
      .select('id')
      .eq('organization_id', organizationId)
      .eq(idColumn, externalId)
      .maybeSingle()

    if (error) throw error
    return data !== null
  }

  async ingestDataRecord(
    resource: DataIngestionResource,
    organizationId: number,
    record: Json
  ): Promise<DataIngestionRpcResult> {
    const functionName = {
      deals: 'ingest_deal_record',
      invoices: 'ingest_invoice_record',
      activities: 'ingest_activity_record'
    }[resource]
    const { data, error } = await this.supabase.rpc(
      functionName as never,
      {
        _organization_id: organizationId,
        _record: record
      } as never
    )

    if (error) throw error
    return data as DataIngestionRpcResult
  }

  async getManagedIngestionBatchContext({
    organizationId,
    resource,
    externalIds,
    ownerEmails,
    profileIds,
    dealExternalIds,
    lineItemExternalIds
  }: {
    organizationId: number
    resource: DataIngestionResource
    externalIds: string[]
    ownerEmails: string[]
    profileIds: string[]
    dealExternalIds: string[]
    lineItemExternalIds: string[]
  }): Promise<DataIngestionBatchContext> {
    const { data, error } = await this.supabase.rpc(
      'get_managed_ingestion_batch_context' as never,
      {
        _organization_id: organizationId,
        _resource: resource,
        _external_ids: externalIds,
        _owner_emails: ownerEmails,
        _profile_ids: profileIds,
        _deal_external_ids: dealExternalIds,
        _line_item_external_ids: lineItemExternalIds
      } as never
    )

    if (error) throw error

    const context = (data ?? {}) as Record<string, unknown>
    const readObject = (key: string) =>
      context[key] && typeof context[key] === 'object' && !Array.isArray(context[key])
        ? (context[key] as Record<string, unknown>)
        : {}

    const existing = Object.fromEntries(
      Object.entries(readObject('existing')).map(([key, value]) => [key, value === true])
    )
    const profilesByEmail = Object.fromEntries(
      Object.entries(readObject('profiles_by_email')).flatMap(([key, value]) =>
        typeof value === 'string' ? [[key, value]] : []
      )
    )
    const profilesById = Object.fromEntries(
      Object.entries(readObject('profiles_by_id')).flatMap(([key, value]) =>
        typeof value === 'string' ? [[key, value]] : []
      )
    )
    const dealsByExternalId = Object.fromEntries(
      Object.entries(readObject('deals_by_external_id')).flatMap(([key, value]) => {
        const parsed = typeof value === 'number' ? value : Number(value)
        return Number.isInteger(parsed) ? [[key, parsed]] : []
      })
    )
    const lineItemsByExternalId = Object.fromEntries(
      Object.entries(readObject('line_items_by_external_id')).flatMap(([key, value]) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return []
        const row = value as { id?: unknown; invoice_id?: unknown }
        const id = Number(row.id)
        const invoiceId = row.invoice_id == null ? null : Number(row.invoice_id)
        return Number.isInteger(id) && (invoiceId === null || Number.isInteger(invoiceId))
          ? [[key, { id, invoice_id: invoiceId }]]
          : []
      })
    )
    const dealLineItemsByExternalId = Object.fromEntries(
      Object.entries(readObject('deal_line_items_by_external_id')).flatMap(([key, value]) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return []
        const row = value as { id?: unknown; deal_id?: unknown }
        const id = Number(row.id)
        const dealId = Number(row.deal_id)
        return Number.isInteger(id) && Number.isInteger(dealId)
          ? [[key, { id, deal_id: dealId }]]
          : []
      })
    )

    return {
      existing,
      profilesByEmail,
      profilesById,
      dealsByExternalId,
      lineItemsByExternalId,
      dealLineItemsByExternalId
    }
  }

  async ingestManagedDataRecords(
    resource: DataIngestionResource,
    organizationId: number,
    records: Json[]
  ): Promise<DataIngestionBatchRpcResult[]> {
    const { data, error } = await this.supabase.rpc(
      'ingest_managed_data_records' as never,
      {
        _organization_id: organizationId,
        _resource: resource,
        _records: records
      } as never
    )

    if (error) throw error
    return (data ?? []) as DataIngestionBatchRpcResult[]
  }

  async getDisputeThread(params: GetDisputeThreadParams): Promise<NotificationsQuery | null> {
    const { organizationId, issueId, notificationType } = params

    const { data, error } = await this.supabase
      .from('notifications')
      .select(
        '*, notifications_status(*), profiles!notifications_profile_id_fkey(*), comments(*, profiles!comments_profile_id_fkey(*), comments_mentions(*, profiles(*)))'
      )
      .eq('organization_id', organizationId)
      .eq('entity_type', 'issue')
      .eq('entity_id', issueId)
      .eq('type', notificationType)
      .limit(1)
      .maybeSingle()

    if (error) throw error
    if (!data) return null

    const comments = [...(data.comments ?? [])].sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
    )

    return {
      ...(data as NotificationsQuery),
      comments
    }
  }
  async createDisputeComment(params: {
    notificationId: number
    currentUserId: string
    content: string
    imagePath?: string | null
  }): Promise<NotificationsQuery['comments'][number]> {
    const { data, error } = await this.supabase
      .from('comments')
      .insert({
        notification_id: params.notificationId,
        profile_id: params.currentUserId,
        content: params.content,
        image_path: params.imagePath ?? null
      })
      .select('*, profiles!comments_profile_id_fkey(*), comments_mentions(*, profiles(*))')
      .single()

    if (error) throw error
    return data as NotificationsQuery['comments'][number]
  }

  async getDisputeImageComment({
    organizationId,
    issueId,
    commentId
  }: {
    organizationId: number
    issueId: number
    commentId: number
  }): Promise<{ image_path: string | null; profile_id: string } | null> {
    const { data, error } = await this.supabase
      .from('comments')
      .select(
        'image_path, profile_id, notifications!inner(organization_id, entity_type, entity_id)'
      )
      .eq('id', commentId)
      .eq('notifications.organization_id', organizationId)
      .eq('notifications.entity_type', 'issue')
      .eq('notifications.entity_id', issueId)
      .maybeSingle()

    if (error) throw error
    return data ? { image_path: data.image_path, profile_id: data.profile_id } : null
  }

  async validateDisputeImage(params: {
    organizationId: number
    issueId: number
    imagePath: string
  }): Promise<boolean> {
    // Storage reads stay on the authenticated server; object metadata is not validation.
    const response = await fetch(`/api/v1/disputes/${params.issueId}/images`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organizationId: params.organizationId,
        path: params.imagePath,
        validateAttachment: true
      })
    })
    if (response.status === 400) return false
    if (!response.ok) throw new Error('Could not validate dispute image')
    const result = await response.json()
    return result.path === params.imagePath
  }

  async updateDisputeStatus(params: {
    organizationId: number
    issueId: number
    status: DisputeAnalyticsStatus
    actorId: string
  }): Promise<void> {
    const isResolved = params.status === 'resolved'
    const { data, error } = await this.supabase
      .from('issues')
      .update({
        status: params.status,
        resolved_at: isResolved ? new Date().toISOString() : null,
        resolved_by: isResolved ? params.actorId : null
      })
      .eq('organization_id', params.organizationId)
      .eq('id', params.issueId)
      .select('id')
      .maybeSingle()

    if (error) throw error
    if (!data) throw new Error('Dispute was not found')
  }
  async getDisputeTags(organizationId: number): Promise<DisputeTag[]> {
    const { data, error } = await this.supabase.rpc('get_dispute_tags', {
      _organization_id: organizationId
    })

    if (error) throw error

    return (data ?? []).map(row => ({ id: row.id, tag: row.tag }))
  }

  async createDisputeTag(
    organizationId: number,
    issueId: number,
    tag: string
  ): Promise<DisputeTag> {
    const { data, error } = await this.supabase.rpc('create_dispute_tag', {
      _organization_id: organizationId,
      _issue_id: issueId,
      _tag: tag
    })

    if (error) throw error

    const createdTag = data?.[0]
    if (!createdTag) throw new Error('Dispute tag was not created')

    return { id: createdTag.id, tag: createdTag.tag }
  }

  async getDisputeIssueTags(organizationId: number, issueId: number): Promise<DisputeTag[]> {
    const { data, error } = await this.supabase.rpc('get_dispute_issue_tags', {
      _organization_id: organizationId,
      _issue_id: issueId
    })

    if (error) throw error

    return (data ?? []).map(row => ({ id: row.id, tag: row.tag }))
  }

  async setDisputeIssueTags(
    organizationId: number,
    issueId: number,
    tagIds: number[]
  ): Promise<void> {
    const { error } = await this.supabase.rpc('set_dispute_issue_tags', {
      _organization_id: organizationId,
      _issue_id: issueId,
      _tag_ids: tagIds
    })

    if (error) throw error
  }

  async getDisputeAnalyticsIssues(params: {
    organizationId: number
    start: string
    end: string
    statuses?: DisputeAnalyticsStatus[]
  }): Promise<DisputeAnalyticsIssueRow[]> {
    const { organizationId, start, end, statuses } = params
    let query = this.supabase
      .from('issues')
      .select(
        [
          'id',
          'created_at',
          'created_by',
          'status',
          'issues_tags(tags(id, tag))',
          'profiles!public_issues_profile_id_fkey(id, name, email)',
          'statements!public_issues_statement_id_fkey(period_start, period_end)',
          'payouts(statements!payouts_statement_id_fkey(period_start, period_end))'
        ].join(', ')
      )
      .eq('organization_id', organizationId)
      .gte('created_at', start)
      .lt('created_at', end)

    if (statuses?.length) {
      query = query.in('status', statuses)
    }

    const { data, error } = await query.order('created_at', {
      ascending: false
    })

    if (error) throw error

    const rows = (data ?? []) as unknown as DisputeAnalyticsQueryRow[]

    return rows.map(row => {
      const profile = Array.isArray(row.profiles) ? row.profiles[0] : row.profiles
      const statement = Array.isArray(row.statements) ? row.statements[0] : row.statements
      const payout = Array.isArray(row.payouts) ? row.payouts[0] : row.payouts
      const payoutStatement = Array.isArray(payout?.statements)
        ? payout?.statements[0]
        : payout?.statements
      const payoutCycle = statement ?? payoutStatement ?? null

      return {
        id: row.id,
        created_at: row.created_at,
        created_by: row.created_by,
        status: row.status,
        tags: (row.issues_tags ?? []).flatMap(relation => {
          const tag = Array.isArray(relation.tags) ? relation.tags[0] : relation.tags
          return tag ? [{ id: tag.id, tag: tag.tag }] : []
        }),
        profile: profile
          ? {
              id: profile.id,
              name: profile.name,
              email: profile.email
            }
          : null,
        payoutCycle: payoutCycle
          ? {
              periodStart: payoutCycle.period_start,
              periodEnd: payoutCycle.period_end
            }
          : null
      }
    })
  }

  async getDisputeAnalyticsTeams(params: {
    organizationId: number
    lookups: DisputeAnalyticsTeamLookup[]
  }): Promise<Map<number, DisputeAnalyticsTeamReference>> {
    const { organizationId, lookups } = params
    const teamsByIssueId = new Map<number, DisputeAnalyticsTeamReference>()

    if (!lookups.length) return teamsByIssueId

    const { data, error } = await this.supabase.rpc('get_dispute_teams', {
      _organization_id: organizationId,
      _lookups: lookups.map(lookup => ({
        issue_id: lookup.issueId,
        profile_id: lookup.profileId,
        event_date: lookup.createdAt
      }))
    })

    if (error) throw error

    for (const row of data ?? []) {
      teamsByIssueId.set(row.issue_id, {
        teamId: row.team_id,
        name: row.team_name ?? 'No team'
      })
    }

    for (const lookup of lookups) {
      if (!teamsByIssueId.has(lookup.issueId)) {
        teamsByIssueId.set(lookup.issueId, { teamId: null, name: 'No team' })
      }
    }

    return teamsByIssueId
  }

  async getTeamMembershipsInPeriod(params: {
    organizationId: number
    start: string
    end: string
    teamIds?: number[]
    profileId?: string
  }): Promise<TeamMembershipInPeriod[]> {
    const args = {
      _organization_id: params.organizationId,
      _start_date: params.start.slice(0, 10),
      _end_date: params.end.slice(0, 10),
      ...(params.teamIds?.length ? { _team_ids: params.teamIds } : {}),
      // PostgREST selects the profile-scoped overload by argument names, so its
      // required team argument must be present even when it intentionally means all teams.
      ...(params.profileId
        ? {
            _team_ids: params.teamIds?.length ? params.teamIds : null,
            _profile_id: params.profileId
          }
        : {})
    }
    const rows: TeamMembershipInPeriod[] = []
    for (let from = 0; ; from += 1000) {
      const { data, error } = await this.supabase
        .rpc('get_team_memberships_in_period', args)
        .order('profile_id')
        .order('team_id')
        .order('active_from')
        .range(from, from + 999)
      if (error) throw error
      rows.push(
        ...(data ?? []).map(row => ({
          profileId: row.profile_id,
          teamId: row.team_id,
          activeFrom: row.active_from,
          activeUntil: row.active_until
        }))
      )
      if ((data ?? []).length < 1000) return rows
    }
  }

  async getProfileOrganizationActivePeriods(params: {
    organizationId: number
    profileId: string
    start: string
    end: string
  }) {
    const { data, error } = await this.supabase.rpc('get_profile_organization_active_periods', {
      _organization_id: params.organizationId,
      _profile_id: params.profileId,
      _start_date: params.start.slice(0, 10),
      _end_date: params.end.slice(0, 10)
    })

    if (error) throw error

    return (data ?? []).map(period => ({
      activeFrom: period.active_from,
      activeUntil: period.active_until
    }))
  }

  async getManagerId(profileId: string, organizationId: number) {
    const { data, error } = await this.supabase.rpc('get_manager_id', {
      team_organization_id: organizationId,
      owner_profile_id: profileId
    })

    if (error) {
      console.error(error)
    }

    return data ?? undefined
  }

  async getTeam(
    profileId: string,
    organizationId: number,
    date: string,
    options?: { throwOnError?: boolean }
  ) {
    const { data, error } = await this.supabase.rpc('get_active_team', {
      _profile_id: profileId,
      _organization_id: organizationId,
      _event_date: date
    })

    if (error) {
      if (options?.throwOnError) throw error
      console.error(error)
    }

    return data?.[0] as unknown as Tables<'teams'>
  }

  async getPeriodTargetAchievement(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    effectiveDate?: string,
    currentEntityId?: number
  ) {
    const { data, error } = await this.supabase.rpc('get_period_target_achievement', {
      _target_id: targetId,
      _start: start,
      _end: end,
      _profile_id: profileId,
      _effective_date: effectiveDate,
      _source_id_limit_on_last_day: currentEntityId
    })

    if (error) {
      console.error(error)
    }

    return Array.isArray(data) && data.length > 0 ? data[0] : null
  }

  async getPeriodTargetAchievementWithEvidence(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    effectiveDate?: string,
    currentEntityId?: number
  ): Promise<
    | ({
        achievement: number
        period: string | null
        target_amount: number
        period_start: string
        period_end: string
        metric_source: Enums<'plan_source'> | null
        metric_formula: string
        global_target: boolean
        team_target: boolean
        team_ids: number[]
      } & { creditedEvidence: CreditedTargetEvidence[] })
    | null
  > {
    const { data, error } = await this.supabase.rpc('get_period_target_achievement_with_evidence', {
      _target_id: targetId,
      _start: start,
      _end: end,
      _profile_id: profileId,
      _effective_date: effectiveDate,
      _source_id_limit_on_last_day: currentEntityId
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    const result = Array.isArray(data) && data.length > 0 ? data[0] : null
    if (!result) return null

    return {
      ...result,
      creditedEvidence: Array.isArray(result.credited_evidence)
        ? (result.credited_evidence as CreditedTargetEvidence[])
        : []
    }
  }

  async getUserTargetsAchievementInPeriod(
    profileId: string,
    start: string,
    end: string,
    organizationId: number,
    targetId?: number,
    onlyUserAchievementCounts?: boolean
  ) {
    if (targetId != null) {
      const { data, error } = await this.supabase.rpc(
        'get_credited_user_targets_achievement_scaffold',
        {
          _profile_id: profileId,
          _organization_id: organizationId,
          _start_date: start,
          _end_date: end,
          _target_id: targetId
        }
      )

      if (error) {
        const rpcError = new Error(error.message)
        Object.assign(rpcError, error)
        throw rpcError
      }

      if (Array.isArray(data) && data.length > 0) {
        return await this.appendTargetCreditSetupMetadata(
          data as DBFunctionReturns<'get_user_targets_achievement'>
        )
      }
    }

    const { data, error } = await this.supabase.rpc('get_user_targets_achievement', {
      _profile_id: profileId,
      _organization_id: organizationId,
      _start_date: start,
      _end_date: end,
      _target_id: targetId,
      _only_user_achievement_counts: onlyUserAchievementCounts
    })

    if (error) {
      console.error(error)
      const rpcError = new Error(error.message)
      Object.assign(rpcError, error)
      throw rpcError
    }

    return await this.appendTargetCreditSetupMetadata(
      data as DBFunctionReturns<'get_user_targets_achievement'> | null
    )
  }

  async getUserViewTargetsSummary(
    profileId: string,
    start: string,
    end: string,
    organizationId: number
  ) {
    const { data, error } = await this.supabase.rpc('get_user_view_targets_summary', {
      _profile_id: profileId,
      _organization_id: organizationId,
      _start_date: start,
      _end_date: end
    })

    if (error) {
      throw new Error(error.message)
    }

    return await this.appendTargetCreditSetupMetadata(
      data as DBFunctionReturns<'get_user_view_targets_summary'> | null
    )
  }

  async getOrganizationCompletedTargetAttainment(
    organizationId: number,
    startDate: string,
    endDate: string,
    targetId?: number
  ) {
    const { data, error } = await this.supabase.rpc(
      'get_organization_completed_target_attainment',
      {
        _organization_id: organizationId,
        _start_date: startDate,
        _end_date: endDate,
        _target_id: targetId
      }
    )

    if (error) {
      throw new Error(error.message)
    }

    return data ?? []
  }

  private async appendCompanyTargetFlags<TRow extends { target_id: number }>(
    rows: TRow[] | null
  ): Promise<(TRow & { company_target: boolean })[] | null> {
    if (!rows || rows.length === 0) {
      return rows as (TRow & { company_target: boolean })[] | null
    }

    const targetIds = Array.from(new Set(rows.map(row => row.target_id)))
    const { data, error } = await this.supabase
      .from('targets')
      .select('id, company_target')
      .in('id', targetIds)

    if (error) {
      throw error
    }

    const companyTargetById = new Map(
      (data ?? []).map(target => [target.id, Boolean(target.company_target)])
    )

    return rows.map(row => ({
      ...row,
      company_target: companyTargetById.get(row.target_id) ?? false
    }))
  }

  private async appendTargetCreditSetupMetadata<TRow extends { target_id: number }>(
    rows: TRow[] | null
  ) {
    if (!rows || rows.length === 0) return rows

    const targetIds = Array.from(new Set(rows.map(row => row.target_id)))
    const { data, error } = await this.supabase
      .from('targets')
      .select(
        'id, company_target, credit_setup_id, teams_targets(team_id), metrics(aggregate_function)'
      )
      .in('id', targetIds)

    if (error) throw error

    const metadataByTargetId = new Map(
      (data ?? []).map(target => {
        const metadata = target as unknown as {
          id: number
          company_target: boolean | null
          credit_setup_id: number | null
          teams_targets: Array<{ team_id: number }> | null
          metrics: { aggregate_function: Tables<'metrics'>['aggregate_function'] } | null
        }

        return [metadata.id, metadata] as const
      })
    )

    return rows.map(row => {
      const metadata = metadataByTargetId.get(row.target_id)

      return {
        ...row,
        company_target: metadata?.company_target ?? false,
        credit_setup_id: metadata?.credit_setup_id ?? null,
        aggregate_function: metadata?.metrics?.aggregate_function ?? null,
        target_team_ids: (metadata?.teams_targets ?? []).map(team => team.team_id)
      }
    })
  }

  async getCumulativePeriodTargetAchievement(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    groupBy: string
  ) {
    const { data, error } = await this.supabase.rpc('get_period_target_achievement', {
      _target_id: targetId,
      _start: start,
      _end: end,
      _profile_id: profileId,
      _group_by: groupBy,
      _cumulative: true
    })

    if (error) throw error

    return data ?? []
  }

  async getCreditedTargetAchievementSeries({
    targetId,
    profileId,
    startDate,
    endDate,
    groupBy,
    ruleSegments
  }: CreditedTargetAchievementSeriesParams) {
    const { data, error } = await this.supabase.rpc('get_credited_target_achievement_series', {
      _target_id: targetId,
      _profile_id: profileId,
      _start_date: startDate,
      _end_date: endDate,
      _group_by: groupBy,
      _rule_segments: ruleSegments.map(segment => ({
        credit_setup_rule_id: segment.creditSetupRuleId,
        effective_from: segment.effectiveFrom,
        effective_to: segment.effectiveTo
      }))
    })

    if (error) throw new Error(error.message)

    return (data as TargetCumulativeAchievementRow[]) ?? []
  }

  async getRecordHistory(
    recordId: number,
    source: Enums<'plan_source'>,
    startDate: string,
    endDate: string
  ) {
    const { data, error } =
      source === 'deal_line_item'
        ? await this.supabase.rpc('get_deal_line_item_record_history', {
            _record_id: recordId,
            _start_date: startDate,
            _end_date: endDate
          })
        : await this.supabase.rpc('get_record_history', {
            _record_id: recordId,
            _source: source,
            _start_date: startDate,
            _end_date: endDate
          })

    if (error) {
      console.error(error)
    }

    return data as { changed_at: string; record: RecordData }[]
  }

  async getPeriodTargetOutcomes(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    planId?: number
  ) {
    const { data, error } = await this.supabase.rpc('get_period_target_outcomes', {
      _target_id: targetId,
      _start: start,
      _end: end,
      _profile_id: profileId,
      _plan_id: planId
    })

    if (error) {
      console.error(error)
    }

    return data!
  }

  async getPeriodTargetAmount(targetId: number, start: string, end: string, profileId: string) {
    const { data, error } = await this.supabase.rpc('get_period_target_amount', {
      _target_id: targetId,
      _start: start,
      _end: end,
      _profile_id: profileId
    })

    if (error) {
      console.error('Error fetching period target amount:', error)
      throw error
    }

    const result = Array.isArray(data) ? data[0] : data

    if (!result) {
      return {
        total_amount: 0,
        periods: [],
        target_period: ''
      }
    }

    return {
      total_amount: result.total_amount ?? 0,
      periods: result.periods ?? [],
      target_period: result.target_period
    }
  }

  async getTargetPeriodDates(referenceDate: string, targetPeriod: Enums<'period'>) {
    const { data, error } = await this.supabase.rpc('get_target_period_dates', {
      _reference_date: referenceDate,
      _target_period: targetPeriod
    })

    if (error) {
      console.error('Error fetching target period dates:', error)
      throw error
    }

    return data ?? []
  }

  async getUsersRankingForTarget({
    endDate,
    order,
    organizationId,
    startDate,
    tags,
    targetId,
    teamId
  }: {
    endDate: string
    order?: string | null
    organizationId: number
    startDate: string
    tags?: number[] | null
    targetId: number
    teamId?: number | null
  }) {
    const { data, error } = await this.supabase.rpc('get_users_ranking_for_target', {
      _end_date: endDate,
      _order: order ?? undefined,
      _organization_id: organizationId,
      _start_date: startDate,
      _tags: tags ?? undefined,
      _target_id: targetId,
      _team_id: teamId ?? undefined
    })

    if (error) {
      console.error('Error fetching users ranking for target:', error)
      throw error
    }

    return data ?? []
  }

  async getTargetCreditSetupSegments({
    targetId,
    profileIds,
    startDate,
    endDate
  }: {
    targetId: number
    profileIds: string[]
    startDate: string
    endDate: string
  }) {
    if (profileIds.length === 0) return []

    const { data, error } = await this.supabase.rpc('get_target_credit_setup_segments', {
      _end_date: endDate,
      _profile_ids: profileIds,
      _start_date: startDate,
      _target_id: targetId
    })

    if (error) throw new Error(error.message)

    return (data ?? []).map(row => ({
      targetId: row.target_id,
      metricId: row.metric_id,
      consumerKind: 'target' as const,
      creditSetupId: row.credit_setup_id,
      creditSetupRuleId: row.credit_setup_rule_id,
      assignmentId: row.assignment_id,
      profileId: row.profile_id,
      teamId: row.team_id,
      effectiveFrom: row.effective_from,
      effectiveTo: row.effective_to,
      noRuleApplied: row.no_rule_applied
    }))
  }

  async saveCurrencyRate(from: string, to: string, rate: number, daterange: string) {
    const { error } = await this.supabase.from('exchanges').upsert(
      [
        {
          from,
          to,
          rate,
          period: daterange
        }
      ],
      { onConflict: 'from,to,period' }
    )

    if (error) {
      console.error(error)
      return new Error(error.message)
    }
  }

  async getFixedCurrencyRate(from: string, to: string, date: string, organizationId: number) {
    const { data, error } = await this.supabase.rpc('get_exchange_rate', {
      p_date: date,
      p_from_currency: from,
      p_to_currency: to,
      p_organization_id: organizationId
    })

    if (error) throw error

    return data as number | null
  }

  async getMarketCurrencyRate(from: string, to: string, date: string) {
    const dateRange = `[${date},${format(addDays(parseISO(date), 1), 'yyyy-MM-dd')})`
    const baseQuery = () =>
      this.supabase.from('exchanges').select('rate, period').eq('from', from).eq('to', to)

    const { data: datedRate, error: datedRateError } = await baseQuery()
      .contains('period', dateRange)
      .order('period', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (datedRateError) throw datedRateError
    if (datedRate?.rate != null) return datedRate.rate

    const { data: previousRate, error: previousRateError } = await baseQuery()
      .rangeLt('period', dateRange)
      .order('period', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (previousRateError) throw previousRateError

    return previousRate?.rate ?? null
  }

  async getMetricsValues(
    metricIds: number[],
    periodStart: string,
    endDate: string,
    organizationId: number,
    profileIds?: string[],
    teamIds?: number[],
    sourceId?: number,
    metricsToUpsert?: TablesInsert<'metric_values'>[],
    metricsToExclude?: Pick<
      TablesInsert<'metric_values'>,
      | 'metric_id'
      | 'deal_id'
      | 'activity_id'
      | 'invoice_id'
      | 'kpi_id'
      | 'line_item_id'
      | 'deal_line_item_id'
      | 'custom_record_id'
    >[],
    groupBy?: string,
    cumulative?: boolean,
    targetCurrency?: string
  ) {
    const { data, error } = await this.supabase.rpc('get_metrics_values', {
      _metric_ids: metricIds,
      _start_date: periodStart,
      _end_date: endDate,
      _profile_ids: profileIds,
      _team_ids: teamIds,
      _organization_id: organizationId,
      _source_id_limit_on_last_day: sourceId,
      _metrics_to_upsert: metricsToUpsert,
      _metrics_to_exclude: metricsToExclude,
      _group_by: groupBy,
      _cumulative: cumulative,
      _target_currency: targetCurrency
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getTargetContributingRecordsAccess({
    requesterId,
    displayedProfileId,
    organizationId
  }: TargetContributingRecordsAccessParams): Promise<TargetContributingRecordsAccess> {
    const { data: requesterMembership, error: requesterMembershipError } = await this.supabase
      .from('organizations_profiles')
      .select('role')
      .eq('profile_id', requesterId)
      .eq('organization_id', organizationId)
      .eq('archived', false)
      .maybeSingle()

    if (requesterMembershipError) throw requesterMembershipError
    if (!requesterMembership) return { canAccess: false, isAdmin: false }

    const { data: displayedMembership, error: displayedMembershipError } = await this.supabase
      .from('organizations_profiles')
      .select('profile_id')
      .eq('profile_id', displayedProfileId)
      .eq('organization_id', organizationId)
      .eq('archived', false)
      .maybeSingle()

    if (displayedMembershipError) throw displayedMembershipError
    if (!displayedMembership) return { canAccess: false, isAdmin: false }

    const [requesterStatusResult, displayedStatusResult] = await Promise.all([
      this.supabase.rpc('get_profile_status', {
        p_profile_id: requesterId,
        p_organization_id: organizationId,
        p_date: new Date().toISOString()
      }),
      this.supabase.rpc('get_profile_status', {
        p_profile_id: displayedProfileId,
        p_organization_id: organizationId,
        p_date: new Date().toISOString()
      })
    ])

    if (requesterStatusResult.error) throw requesterStatusResult.error
    if (displayedStatusResult.error) throw displayedStatusResult.error
    if (
      requesterStatusResult.data === 'inactive' ||
      requesterStatusResult.data === 'terminated' ||
      displayedStatusResult.data === 'inactive' ||
      displayedStatusResult.data === 'terminated'
    ) {
      return { canAccess: false, isAdmin: false }
    }

    const isAdmin = requesterMembership.role === 'admin'
    if (displayedProfileId === requesterId) return { canAccess: true, isAdmin }

    if (isAdmin || requesterMembership.role === 'supervisor') {
      return { canAccess: true, isAdmin }
    }

    const { data: requesterTeams, error: requesterTeamsError } = await this.supabase
      .from('teams_profiles')
      .select('team_id')
      .eq('profile_id', requesterId)
      .eq('organization_id', organizationId)
      .or('full_view_access.eq.true,role.eq.manager')

    if (requesterTeamsError) throw requesterTeamsError
    if (!requesterTeams?.length) return { canAccess: false, isAdmin: false }

    const { data: displayedProfileTeams, error: displayedProfileTeamsError } = await this.supabase
      .from('teams_profiles')
      .select('team_id')
      .eq('profile_id', displayedProfileId)
      .eq('organization_id', organizationId)
      .eq('role', 'member')

    if (displayedProfileTeamsError) throw displayedProfileTeamsError

    const managedTeamIds = new Set(requesterTeams.map(team => team.team_id))
    return {
      canAccess: displayedProfileTeams?.some(team => managedTeamIds.has(team.team_id)) ?? false,
      isAdmin: false
    }
  }

  async getTargetContributingRecords({
    metricId,
    organizationId,
    periodStart,
    periodEnd,
    source,
    profileId,
    excludedProfileId,
    teamIds,
    targetCurrency,
    offset = 0,
    limit = 100
  }: import('./index').TargetContributingRecordsParams): Promise<
    import('./index').TargetContributingRecordsPage
  > {
    let query = this.supabase
      .from('metric_values')
      .select(
        `*, profiles(id, name, email, img_url), metric_value_currencies(value, currency), ${getMetricValueSourceSelectQuery(source)}`,
        { count: 'exact' }
      )
      .eq('metric_id', metricId)
      .eq(getMetricValueSourceOrganizationPath(source), organizationId)
      .gte('accrual_date', periodStart)
      .lte('accrual_date', periodEnd)
      .order('accrual_date', { ascending: false })
      .order('id', { ascending: false })
      .range(offset, offset + limit - 1)

    if (profileId) {
      query = query.eq('profile_id', profileId)
    }

    if (excludedProfileId) {
      query = query.neq('profile_id', excludedProfileId)
    }

    if (teamIds) {
      query = query.in('team_id', teamIds)
    }

    if (targetCurrency) {
      query = query.eq('metric_value_currencies.currency', targetCurrency)
    }

    const { data, error, count } = await query

    if (error) {
      throw new Error(error.message)
    }

    return {
      records: (data ?? []).map(row =>
        sanitizeMetricValueSourceRow(source, row, organizationId)
      ) as import('./index').TargetContributingRecordsPage['records'],
      count: count ?? 0
    }
  }

  async getTargetTeamIds(targetId: number, organizationId: number) {
    const cachedTarget = this.targetCache.get(targetId)
    if (cachedTarget && cachedTarget.organization_id === organizationId) {
      return Array.from(new Set((cachedTarget.teams_targets ?? []).map(team => team.team_id)))
    }

    const target = await this.getTarget(targetId)
    if (target?.organization_id === organizationId) {
      return Array.from(new Set((target.teams_targets ?? []).map(team => team.team_id)))
    }

    const teamIds: number[] = []
    const pageSize = 1000

    for (let offset = 0; ; offset += pageSize) {
      const { data, error } = await this.supabase
        .from('teams_targets')
        .select('team_id, teams!inner(organization_id), targets!inner(organization_id)')
        .eq('target_id', targetId)
        .eq('teams.organization_id', organizationId)
        .eq('targets.organization_id', organizationId)
        .range(offset, offset + pageSize - 1)

      if (error) throw new Error(error.message)
      teamIds.push(...(data ?? []).map(row => row.team_id))

      if (!data || data.length < pageSize) break
    }

    return Array.from(new Set(teamIds))
  }

  async getMetricsByIdsForPeriod(metricIds: number[], organizationId: number, periodStart: string) {
    const { data, error } = await this.supabase
      .from('metrics')
      .select('*')
      .in('id', metricIds)
      .eq('organization_id', organizationId)
      .or(`archived_at.is.null,archived_at.gte.${periodStart}`)

    if (error) throw new Error(error.message)
    return data ?? []
  }

  async getOrganizationMetricsValues({
    metricIds,
    startDate,
    endDate,
    organizationId,
    sourceId,
    metricsToUpsert,
    metricsToExclude,
    groupBy,
    cumulative,
    targetCurrency
  }: GetOrganizationMetricsValuesParams) {
    const { data, error } = await this.supabase.rpc('get_organization_metrics_values', {
      _metric_ids: metricIds,
      _start_date: startDate,
      _end_date: endDate,
      _organization_id: organizationId,
      _source_id_limit_on_last_day: sourceId,
      _metrics_to_upsert: metricsToUpsert,
      _metrics_to_exclude: metricsToExclude,
      _group_by: groupBy,
      _cumulative: cumulative,
      _target_currency: targetCurrency
    })

    if (error) throw new Error(error.message)
    return data ?? []
  }

  async getOrganizationProfileIds(organizationId: number) {
    const profileIds: string[] = []
    const pageSize = 1000

    for (let offset = 0; ; offset += pageSize) {
      const { data, error } = await this.supabase
        .from('organizations_profiles')
        .select('profile_id')
        .eq('organization_id', organizationId)
        .range(offset, offset + pageSize - 1)

      if (error) throw new Error(error.message)
      profileIds.push(...(data ?? []).map(row => row.profile_id))

      if (!data || data.length < pageSize) break
    }

    return Array.from(new Set(profileIds))
  }

  async getMetricPreviewRecipientsPage({
    organizationId,
    profileIds,
    search,
    offset,
    limit,
    sortDescending
  }: Parameters<DolfinRpc['getMetricPreviewRecipientsPage']>[0]) {
    if (profileIds?.length === 0) return { recipients: [], total: 0 }
    // PostgREST puts .in() values in the URL. A large team scope can exceed
    // the gateway URL limit before the preview calculation even begins.
    if (profileIds && profileIds.length > 100) {
      const recipients: MetricPreviewRecipient[] = []
      const groups = [...batched(new Set(profileIds), 100)]
      for (const groupBatch of batched(groups, 5)) {
        const pages = await Promise.all(
          groupBatch.map(group =>
            this.getMetricPreviewRecipientsPage({
              organizationId,
              profileIds: group,
              search,
              offset: 0,
              limit: group.length,
              sortDescending
            })
          )
        )
        for (const page of pages) recipients.push(...page.recipients)
      }
      recipients.sort((left, right) => {
        if (left.name == null || right.name == null) {
          if (left.name == null && right.name != null) return 1
          if (right.name == null && left.name != null) return -1
        }
        const nameOrder = (left.name ?? '').localeCompare(right.name ?? '', 'en-US')
        return (sortDescending ? -nameOrder : nameOrder) || left.id.localeCompare(right.id)
      })
      return { recipients: recipients.slice(offset, offset + limit), total: recipients.length }
    }

    let query = this.supabase
      .from('profiles')
      .select('id, name, email, img_url, organizations_profiles!inner(organization_id)', {
        count: 'exact'
      })
      .eq('organizations_profiles.organization_id', organizationId)
      .order('name', { ascending: !sortDescending, nullsFirst: false })
      .order('id', { ascending: true })

    if (profileIds) query = query.in('id', profileIds)
    if (search?.trim()) {
      const trimmedSearch = search.trim()
      const pattern = `"%${escapeOrLikePattern(trimmedSearch)}%"`
      const tokenPattern = `"%${trimmedSearch
        .split(/\s+/)
        .filter(Boolean)
        .map(escapeOrLikePattern)
        .join('%')}%"`
      query = query.or(
        `name.ilike.${pattern},name.ilike.${tokenPattern},email.ilike.${pattern},email.ilike.${tokenPattern}`
      )
    }

    const { data, count, error } = await query.range(offset, offset + limit - 1)
    if (error) throw error
    return {
      recipients: (data ?? []).map(
        ({ organizations_profiles: _membership, ...profile }) => profile
      ),
      total: count ?? 0
    }
  }

  async getMetricPreviewDealCustomDateCandidateIds({
    organizationId,
    customFieldId,
    profileIds,
    dateStart,
    dateEnd,
    limit,
    ownerCustomFieldId
  }: Parameters<DolfinRpc['getMetricPreviewDealCustomDateCandidateIds']>[0]) {
    const ids: number[] = []
    const pageSize = 1000
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.supabase
        .rpc('get_metric_preview_deal_custom_date_candidate_ids', {
          _organization_id: organizationId,
          _custom_field_id: customFieldId,
          _profile_ids: profileIds,
          _date_start: dateStart,
          _date_end: dateEnd,
          _limit: Math.min(limit, 2147483647),
          _owner_custom_field_id: ownerCustomFieldId ?? null
        })
        .range(from, from + pageSize - 1)
      if (error) throw error
      ids.push(...(data ?? []).map(row => row.record_id))
      if ((data ?? []).length < pageSize) return ids
    }
  }

  async getMetricPreviewSourceCandidates({
    metric,
    profileIds,
    periodStart,
    periodEnd,
    limit,
    customFields: suppliedCustomFields,
    includedOnly,
    onPage
  }: Parameters<DolfinRpc['getMetricPreviewSourceCandidates']>[0]): Promise<RecordData[]> {
    if (!metric.source || profileIds.length === 0) return []

    const userPath = getRecordQueryFieldPath(metric.source, metric.user_field)
    const datePath = getRecordQueryFieldPath(metric.source, metric.accrual_date_field)
    if (!userPath || !datePath) {
      throw new Error('Metric preview requires queryable user and accrual date fields')
    }

    const splitAware = isSplitAwareOwnerField(metric.source, metric.user_field)
    const virtualTableId =
      metric.source === 'kpi'
        ? (metric.kpis_table_id ?? undefined)
        : metric.source === 'custom_record'
          ? (metric.custom_record_table_id ?? undefined)
          : undefined
    const virtualTableScope =
      metric.source === 'kpi' && virtualTableId != null
        ? { kind: 'kpi' as const, tableId: virtualTableId }
        : metric.source === 'custom_record' && virtualTableId != null
          ? { kind: 'custom_record' as const, tableId: virtualTableId }
          : null
    const customFields =
      suppliedCustomFields ??
      (await getSourceCustomFields(this, metric.source, metric.organization_id, virtualTableId))
    const canNarrowWithViewFilters =
      includedOnly &&
      ['deal', 'activity', 'invoice', 'kpi', 'custom_record'].includes(metric.source) &&
      !isSplitAwareOwnerField(metric.source, metric.user_field) &&
      metric.filters != null &&
      typeof metric.filters === 'object' &&
      !Array.isArray(metric.filters) &&
      Object.keys(metric.filters).length > 0
    const databasePredicates = canNarrowWithViewFilters
      ? getMetricPreviewDatabasePredicates(metric, {
          customFields: Object.values(customFields),
          fields: await this.getDataViewFilterFields({
            organizationId: metric.organization_id,
            source: metric.source
          }),
          virtualTableScope
        })
      : []
    const userDescriptor = DolfinRecord.getRelationalCustomFieldDescriptorFromMap(
      metric.source,
      metric.user_field,
      customFields,
      virtualTableScope
    )
    const dateDescriptor =
      splitAware && datePath.includes('deals.')
        ? null
        : DolfinRecord.getRelationalCustomFieldDescriptorFromMap(
            metric.source,
            metric.accrual_date_field,
            customFields,
            virtualTableScope
          )
    const userNeedsLocalEvaluation = userPath.includes('meta->>') && !userDescriptor
    const dateNeedsLocalEvaluation = datePath.includes('meta->>') && !dateDescriptor
    const useOwnerScopedDealDate =
      metric.source === 'deal' &&
      (metric.user_field === 'owner_id' || userDescriptor?.sourceRole === 'local') &&
      dateDescriptor?.sourceRole === 'local'
    const userCandidatePages =
      userDescriptor && !useOwnerScopedDealDate
        ? await Promise.all(
            profileIds.map(valueUserId =>
              this.getCustomFieldFilterCandidateIds({
                organizationId: metric.organization_id,
                source: metric.source!,
                customFieldId: userDescriptor.customFieldId,
                sourceRole: userDescriptor.sourceRole,
                virtualTableId,
                valueUserId,
                maxResults: limit
              })
            )
          )
        : null
    const userCandidateIds = userCandidatePages ? [...new Set(userCandidatePages.flat())] : null
    if (userCandidateIds && userCandidateIds.length >= limit) {
      throw new MetricPreviewError('too_broad', 'Narrow the preview month, teams, or users')
    }
    const ownerScopedDealDateIds =
      useOwnerScopedDealDate && dateDescriptor
        ? await this.getMetricPreviewDealCustomDateCandidateIds({
            organizationId: metric.organization_id,
            customFieldId: dateDescriptor.customFieldId,
            profileIds,
            dateStart: periodStart,
            dateEnd: subtractIsoDateDay(periodEnd),
            limit,
            ...(userDescriptor ? { ownerCustomFieldId: userDescriptor.customFieldId } : {})
          })
        : null
    const dateCandidateIds =
      dateDescriptor && ownerScopedDealDateIds == null
        ? await this.getCustomFieldFilterCandidateIds({
            organizationId: metric.organization_id,
            source: metric.source!,
            customFieldId: dateDescriptor.customFieldId,
            sourceRole: dateDescriptor.sourceRole,
            virtualTableId,
            valueDateStart: periodStart,
            valueDateEnd: subtractIsoDateDay(periodEnd),
            maxResults: limit
          })
        : null
    const dateCandidateIdSet = dateCandidateIds ? new Set(dateCandidateIds) : null
    const selectedCandidateIds =
      ownerScopedDealDateIds ??
      (userCandidateIds && dateCandidateIdSet
        ? userCandidateIds.filter(id => dateCandidateIdSet.has(id))
        : (userCandidateIds ?? dateCandidateIds))
    if (selectedCandidateIds?.length === 0) return []
    if (selectedCandidateIds && selectedCandidateIds.length >= limit) {
      throw new MetricPreviewError('too_broad', 'Narrow the preview month, teams, or users')
    }

    let splitAnchorIds: number[] = []
    if (splitAware && metric.source !== 'deal') {
      const childDatePath =
        metric.accrual_date_field &&
        resolveRecordFieldReference(metric.accrual_date_field, metric.source).prefix === 'deal'
          ? getRecordQueryFieldPath('deal', metric.accrual_date_field)
          : null
      const splitRows: { id: number; split_anchor_id: number | null }[] = []
      let lastSplitId: number | null = null
      for (;;) {
        let query = this.supabase
          .from('deals')
          .select('id, split_anchor_id')
          .eq('organization_id', metric.organization_id)
          .eq('split_record_type', 'split_child')
          .in('owner_id', profileIds)
        if (childDatePath) {
          query = query.gte(childDatePath, periodStart).lt(childDatePath, periodEnd)
        }
        if (lastSplitId != null) query = query.gt('id', lastSplitId)
        const { data, error } = await query.order('id').limit(1000)
        if (error) throw error
        splitRows.push(...(data ?? []))
        if ((data ?? []).length < 1000) break
        lastSplitId = data![data!.length - 1].id
      }
      splitAnchorIds = [
        ...new Set(
          splitRows.map(row => row.split_anchor_id).filter((id): id is number => id != null)
        )
      ]
    }

    const selectBySource: Record<Enums<'plan_source'>, string> = {
      deal: '*, split_children:deals!split_anchor_id(*)',
      activity: '*, deals!inner(*, split_children:deals!split_anchor_id(*))',
      invoice: '*, deals!inner(*, split_children:deals!split_anchor_id(*))',
      line_item: '*, invoices!inner(*, deals!inner(*, split_children:deals!split_anchor_id(*)))',
      deal_line_item: '*, deals!inner(*, split_children:deals!split_anchor_id(*))',
      kpi: '*, kpis_tables(*)',
      custom_record: '*, custom_record_tables(*)'
    }
    const tableName = SOURCE_TO_TABLE[metric.source]
    const emittedIds = new Set<number>()
    const fetch = async (
      fieldPath: string,
      values: Array<string | number>,
      candidateIds?: number[]
    ) => {
      const rows: RecordData[] = []
      let lastId: number | null = null
      for (;;) {
        // Build a fresh query so each page has only one cursor predicate.
        let query = this.supabase
          .from(tableName)
          .select(selectBySource[metric.source!])
          .eq('organization_id', metric.organization_id)
        if (fieldPath !== userPath || (!userDescriptor && !userNeedsLocalEvaluation)) {
          query = query.in(fieldPath, values)
        }
        if (candidateIds) query = query.in('id', candidateIds)
        for (const predicate of databasePredicates) {
          query = query.eq(predicate.field, predicate.value)
        }
        // Deal split attribution can replace the parent deal date. A date
        // predicate on that parent would omit a child in the requested month.
        if (
          !dateDescriptor &&
          !dateNeedsLocalEvaluation &&
          (metric.source === 'deal' || !splitAware || !datePath.includes('deals.'))
        ) {
          query = query.gte(datePath, periodStart).lt(datePath, periodEnd)
        }
        if (metric.source === 'kpi' && metric.kpis_table_id) {
          query = query.eq('kpis_table_id', metric.kpis_table_id)
        }
        if (metric.source === 'custom_record' && metric.custom_record_table_id) {
          query = query.eq('custom_record_table_id', metric.custom_record_table_id)
        }
        if (lastId != null) query = query.gt('id', lastId)
        const { data, error } = await query.order('id', { ascending: true }).limit(1000)
        if (error) throw error
        const page = (data ?? []) as unknown as RecordData[]
        if (onPage) {
          const unique = page.filter(row => {
            if (emittedIds.has(row.id)) return false
            emittedIds.add(row.id)
            return true
          })
          if (unique.length) await onPage(unique)
        } else {
          rows.push(...page)
        }
        if (page.length < 1000) return rows
        lastId = page[page.length - 1].id
      }
    }

    const fetchAll = async (fieldPath: string, values: Array<string | number>) => {
      if (!selectedCandidateIds) return fetch(fieldPath, values)
      const rows: RecordData[] = []
      for (let i = 0; i < selectedCandidateIds.length; i += 500) {
        rows.push(...(await fetch(fieldPath, values, selectedCandidateIds.slice(i, i + 500))))
      }
      return rows
    }
    const direct = await fetchAll(userPath, profileIds)
    if (!splitAnchorIds.length) return direct
    const dealIdPath = getRecordQueryFieldPath(metric.source, 'deal.id')
    if (!dealIdPath) throw new Error('Split attribution requires a queryable parent deal')
    const split = await fetchAll(dealIdPath, splitAnchorIds)
    const unique = new Map([...direct, ...split].map(row => [row.id, row]))
    if (unique.size >= limit) {
      throw new MetricPreviewError('too_broad', 'Narrow the preview month, teams, or users')
    }
    return [...unique.values()].sort((a, b) => a.id - b.id)
  }

  async getMetricsByPeriod({
    aggregateFunction,
    endDate,
    metricIds,
    organizationId,
    period = 'day',
    startDate,
    tagIds,
    teamIds,
    userIds
  }: {
    aggregateFunction?: Enums<'aggregate_function'>
    endDate: string
    metricIds?: number[]
    organizationId: number
    period?: 'day' | 'week' | 'month'
    startDate: string
    tagIds?: number[]
    teamIds?: number[]
    userIds?: string[]
  }) {
    const { data, error } = await this.supabase.rpc('get_metrics_by_period', {
      p_aggregate_function: aggregateFunction,
      p_end_date: endDate,
      p_metric_ids: metricIds,
      p_organization_id: organizationId,
      p_period: period,
      p_start_date: startDate,
      p_tag_ids: tagIds,
      p_team_ids: teamIds,
      p_user_ids: userIds
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getCompensationHistoryReport({
    compensationRuleIds,
    groupBy,
    groupTagIds,
    organizationId,
    planId,
    tags,
    teamId,
    year
  }: {
    compensationRuleIds?: number[]
    groupBy:
      | 'business_unit'
      | 'compensation_rule'
      | 'compensation_status'
      | 'plan'
      | 'team'
      | 'team_tag'
      | 'compensation_type'
    groupTagIds?: number[]
    organizationId: number
    planId?: number
    tags?: number[]
    teamId?: number
    year: number
  }) {
    // Keep the JavaScript contract aligned with the SQL function so the domain
    // can treat the RPC as the single source of truth for grouping behavior.
    const { data, error } = await this.supabase.rpc('get_compensation_history_report', {
      _compensation_rule_ids: compensationRuleIds,
      _group_by: groupBy,
      _group_tag_ids: groupTagIds,
      _organization_id: organizationId,
      _plan_id: planId,
      _tags: tags,
      _team_id: teamId,
      _year: year
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getCompensationHistoryProfileReport({
    compensationRuleIds,
    organizationId,
    planId,
    tags,
    teamId,
    year
  }: {
    compensationRuleIds?: number[]
    organizationId: number
    planId?: number
    tags?: number[]
    teamId: number
    year: number
  }) {
    const { data, error } = await this.supabase.rpc('get_compensation_history_profile_report', {
      _compensation_rule_ids: compensationRuleIds,
      _organization_id: organizationId,
      _plan_id: planId,
      _tags: tags,
      _team_id: teamId,
      _year: year
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getCompensationHistoryCompensationRules({
    organizationId,
    planId,
    tags,
    teamId,
    year
  }: {
    organizationId: number
    planId?: number
    tags?: number[]
    teamId?: number
    year: number
  }) {
    const { data, error } = await this.supabase.rpc('get_compensation_history_compensation_rules', {
      _organization_id: organizationId,
      _plan_id: planId,
      _tags: tags,
      _team_id: teamId,
      _year: year
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getCompensationHistoryMetricBuckets({
    groupBy,
    metricId,
    organizationId,
    tags,
    teamId,
    year
  }: {
    groupBy: 'profile' | 'team'
    metricId: number
    organizationId: number
    tags?: number[]
    teamId?: number
    year: number
  }) {
    const { data, error } = await this.supabase.rpc('get_compensation_history_metric_buckets', {
      _group_by: groupBy,
      _metric_id: metricId,
      _organization_id: organizationId,
      _tags: tags,
      _team_id: teamId,
      _year: year
    })

    if (error) {
      console.error(error)
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getOrganizationProfilesData(organizationId: number, profileIds: string[]) {
    const uniqueProfileIds = Array.from(new Set(profileIds)).sort((a, b) => a.localeCompare(b))
    if (uniqueProfileIds.length === 0) {
      return {}
    }

    const rows: Tables<'organizations_profiles_data'>[] = []
    for (const profileIdsChunk of batched(uniqueProfileIds, PROFILE_IDS_IN_FILTER_BATCH_SIZE)) {
      const { data, error } = await this.supabase
        .from('organizations_profiles_data')
        .select('*')
        .eq('organization_id', organizationId)
        .in('profile_id', profileIdsChunk)
        .order('profile_id', { ascending: true })
        .order('from_date', { ascending: true })

      if (error) {
        throw new Error(error.message)
      }

      if (data?.length) {
        rows.push(...data)
      }
    }

    return (
      rows.reduce(
        (acc, curr) => {
          if (!acc[curr.profile_id]) {
            acc[curr.profile_id] = []
          }

          acc[curr.profile_id].push(curr)

          return acc
        },
        {} as Record<string, Tables<'organizations_profiles_data'>[]>
      ) ?? {}
    )
  }

  async upsertOrganizationProfileDataSnapshot(params: {
    organizationId: number
    profileId: string
    fromDate: string
    type?: string | null
    totalVariablePay?: number | null
  }): Promise<Tables<'organizations_profiles_data'>> {
    const hasType = params.type !== undefined
    const hasTotalVariablePay = params.totalVariablePay !== undefined
    const rpcArgs: Database['public']['Functions']['upsert_organization_profile_data_snapshot']['Args'] =
      {
        _organization_id: params.organizationId,
        _profile_id: params.profileId,
        _from_date: params.fromDate,
        _set_type: hasType,
        _set_total_variable_pay: hasTotalVariablePay,
        ...(hasType ? { _type: params.type ?? undefined } : {}),
        ...(hasTotalVariablePay
          ? { _total_variable_pay: params.totalVariablePay ?? undefined }
          : {})
      }

    const { data, error } = await this.supabase.rpc(
      'upsert_organization_profile_data_snapshot',
      rpcArgs
    )

    if (error) {
      throw error
    }

    return data
  }

  async getActiveOrganizationProfilesData(
    organizationId: number,
    referenceDate: string,
    profileIds?: string[]
  ) {
    const { data, error } = await this.supabase.rpc('get_active_organizations_profiles_data', {
      _organization_id: organizationId,
      _as_of_date: referenceDate,
      _profile_ids: profileIds?.length ? Array.from(new Set(profileIds)) : undefined
    })

    if (error) {
      console.error('Error fetching active organization profile data:', error)
      throw error
    }

    return (data ?? []) as CurrentOrganizationProfileData[]
  }

  async getBusinessUnits(
    organizationId: number,
    includeArchived = false
  ): Promise<BusinessUnitListItem[]> {
    // Fetch the hierarchy in layers so the UI receives a denormalized,
    // story-friendly shape without issuing its own cross-table queries.
    let query = this.supabase
      .from('business_units')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name', { ascending: true })

    if (!includeArchived) {
      query = query.is('archived_at', null)
    }

    const { data: business_units, error } = await query

    if (error) {
      console.error('Error fetching business_units:', error)
      throw error
    }

    const businessUnitIds = (business_units ?? []).map(businessUnit => businessUnit.id)
    const costCentersByBusinessUnitId = new Map<number, BusinessUnitListItem['cost_centers']>()

    if (businessUnitIds.length > 0) {
      const { data: costCenters, error: costCentersError } = await this.supabase
        .from('business_unit_cost_centers')
        .select('*')
        .in('business_unit_id', businessUnitIds)
        .is('archived_at', null)
        .order('name', { ascending: true })

      if (costCentersError) {
        console.error('Error fetching business unit cost centers:', costCentersError)
        throw costCentersError
      }

      const departmentIds = Array.from(
        new Set((costCenters ?? []).map(costCenter => costCenter.department_id))
      )
      const legalEntityIds = Array.from(
        new Set(
          (costCenters ?? [])
            .map(costCenter => costCenter.legal_entity_id)
            .filter((value): value is number => value != null)
        )
      )
      const locationIds = Array.from(
        new Set(
          (costCenters ?? [])
            .map(costCenter => costCenter.location_id)
            .filter((value): value is number => value != null)
        )
      )

      const [
        { data: departments, error: departmentsError },
        { data: legalEntities, error: legalEntitiesError },
        { data: locations, error: locationsError }
      ] = await Promise.all([
        departmentIds.length > 0
          ? this.supabase.from('departments').select('*').in('id', departmentIds)
          : Promise.resolve<{ data: Tables<'departments'>[]; error: null }>({
              data: [],
              error: null
            }),
        legalEntityIds.length > 0
          ? this.supabase.from('legal_entities').select('*').in('id', legalEntityIds)
          : Promise.resolve<{ data: Tables<'legal_entities'>[]; error: null }>({
              data: [],
              error: null
            }),
        locationIds.length > 0
          ? this.supabase.from('locations').select('*').in('id', locationIds)
          : Promise.resolve<{ data: Tables<'locations'>[]; error: null }>({
              data: [],
              error: null
            })
      ])

      if (departmentsError) throw departmentsError
      if (legalEntitiesError) throw legalEntitiesError
      if (locationsError) throw locationsError

      const departmentsById = new Map<number, Tables<'departments'>>(
        (departments ?? []).map(row => [row.id, row] as const)
      )
      const legalEntitiesById = new Map<number, Tables<'legal_entities'>>(
        (legalEntities ?? []).map(row => [row.id, row] as const)
      )
      const locationsById = new Map<number, Tables<'locations'>>(
        (locations ?? []).map(row => [row.id, row] as const)
      )

      for (const costCenter of costCenters ?? []) {
        const groupedCostCenters =
          costCentersByBusinessUnitId.get(costCenter.business_unit_id) ?? []
        const location =
          costCenter.location_id != null
            ? (locationsById.get(costCenter.location_id) ?? null)
            : null
        const legalEntity =
          costCenter.legal_entity_id != null
            ? (legalEntitiesById.get(costCenter.legal_entity_id) ?? null)
            : location != null
              ? (legalEntitiesById.get(location.legal_entity_id) ?? null)
              : null

        groupedCostCenters.push({
          ...costCenter,
          assigned_users_count: 0,
          department: departmentsById.get(costCenter.department_id)!,
          legal_entity: legalEntity,
          location
        })
        costCentersByBusinessUnitId.set(costCenter.business_unit_id, groupedCostCenters)
      }
    }

    const assignments = await this.getActiveOrganizationProfilesData(
      organizationId,
      new Date().toISOString().slice(0, 10)
    )

    const assignedUsersCountByBusinessUnitId = assignments.reduce((acc, assignment) => {
      const businessUnitId = assignment.business_unit_id
      if (businessUnitId == null) {
        return acc
      }

      acc.set(businessUnitId, (acc.get(businessUnitId) ?? 0) + 1)
      return acc
    }, new Map<number, number>())

    const assignedUsersCountByCostCenterId = assignments.reduce((acc, assignment) => {
      const costCenterId = assignment.business_unit_cost_center_id

      if (costCenterId == null) {
        return acc
      }

      acc.set(costCenterId, (acc.get(costCenterId) ?? 0) + 1)
      return acc
    }, new Map<number, number>())

    return (business_units ?? []).map(business_unit => ({
      ...business_unit,
      assigned_users_count: assignedUsersCountByBusinessUnitId.get(business_unit.id) ?? 0,
      cost_centers:
        costCentersByBusinessUnitId.get(business_unit.id)?.map(costCenter => ({
          ...costCenter,
          assigned_users_count: assignedUsersCountByCostCenterId.get(costCenter.id) ?? 0
        })) ?? []
    }))
  }

  async getProviderProfileIdentity(
    providerProfileId: string,
    providerSpaceId: string
  ): Promise<ProviderProfileIdentity | null> {
    const { data, error } = await this.supabase
      .from('provider_profile_info')
      .select(
        'organization_id, profiles!inner(created_at, email, id, img_url, name, name_lower_case, name_normalized, onboarded, tax_id_number, updated_at)'
      )
      .eq('provider_profile_id', providerProfileId)
      .eq('provider_space_id', providerSpaceId)
      .limit(1)
      .maybeSingle()

    if (error || !data?.profiles) {
      return null
    }

    return {
      organizationId: data.organization_id,
      profile: data.profiles
    }
  }

  async getAdminProviderProfileIdentity(
    providerProfileId: string,
    providerSpaceId: string
  ): Promise<ProviderProfileIdentity | null> {
    const identity = await this.getProviderProfileIdentity(providerProfileId, providerSpaceId)
    if (!identity) return null

    const isAdmin = await this.isActiveOrganizationAdmin(
      identity.profile.id,
      identity.organizationId
    )
    return isAdmin ? identity : null
  }

  async getOrganizationProfilesTeamEvents(
    organizationId: number,
    profileIds: string[]
  ): Promise<Record<string, TeamMembershipEvent[]> | null> {
    const uniqueProfileIds = Array.from(new Set(profileIds))
    if (uniqueProfileIds.length === 0) {
      return {}
    }

    const events: TeamMembershipEvent[] = []

    for (const profileIdsChunk of batched(uniqueProfileIds, PROFILE_IDS_IN_FILTER_BATCH_SIZE)) {
      const { data, error } = await this.supabase
        .from('teams_profiles_events')
        .select('profile_id, team_id, event_date, status')
        .eq('organization_id', organizationId)
        .in('profile_id', profileIdsChunk)
        .order('profile_id', { ascending: true })
        .order('event_date', { ascending: true })

      if (error) {
        console.error(error)
        return null
      }

      events.push(...((data ?? []) as TeamMembershipEvent[]))
    }

    return events.reduce(
      (acc, curr) => {
        if (!acc[curr.profile_id]) {
          acc[curr.profile_id] = []
        }

        acc[curr.profile_id].push(curr)

        return acc
      },
      {} as Record<string, TeamMembershipEvent[]>
    )
  }

  async listImpersonationCandidateProfiles(
    args: ListImpersonationCandidateProfilesParams
  ): Promise<ImpersonationCandidateProfile[]> {
    const likePattern = `"%${escapeOrLikePattern(args.query)}%"`
    const normalizedLikePattern = `"%${escapeOrLikePattern(
      normalizeImpersonationCandidateQuery(args.query)
    )}%"`
    const limit = args.limit ?? 100
    const { data, error } = await this.supabase
      .from('profiles')
      .select(
        'id, name, email, organizations_profiles!inner(organization_id, archived, get_profile_status_field, organizations(name))'
      )
      .or(`name_normalized.ilike.${normalizedLikePattern},email.ilike.${likePattern}`)
      .order('name')
      .limit(limit)

    if (error) {
      throw error
    }

    return (data ?? []) as unknown as ImpersonationCandidateProfile[]
  }

  async getImpersonationProfile(profileId: string): Promise<ImpersonationProfile | null> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select('id, email, organizations_profiles(*, get_profile_status_field)')
      .eq('id', profileId)
      .maybeSingle()

    if (error) {
      throw error
    }

    return data as unknown as ImpersonationProfile | null
  }

  async createImpersonationAttempt(
    args: CreateImpersonationAttemptParams
  ): Promise<CreateImpersonationAttemptResult> {
    const { data, error } = await this.supabase.rpc('create_impersonation_attempt', {
      _organization_id: args.organizationId,
      _admin_id: args.adminId,
      _user_id: args.userId,
      _ip_address: args.ipAddress,
      _window_start: args.windowStart,
      _max_attempts: args.maxAttempts
    })

    if (error) {
      throw error
    }

    return data?.[0] ?? null
  }

  async markImpersonationLinkGenerationFailed(
    auditLogId: string,
    errorMessage: string
  ): Promise<void> {
    const { error } = await this.supabase
      .from('impersonation_logs')
      .update({
        status: 'failed_link_generation',
        error_message: errorMessage
      })
      .eq('id', auditLogId)

    if (error) {
      throw error
    }
  }

  async markImpersonationLinkGenerated(auditLogId: string): Promise<void> {
    const { error } = await this.supabase
      .from('impersonation_logs')
      .update({
        status: 'link_generated',
        error_message: null
      })
      .eq('id', auditLogId)
      .eq('status', 'initiated')

    if (error) {
      throw error
    }
  }

  async getImpersonationAuditLog(auditLogId: string): Promise<ImpersonationAuditLog | null> {
    const { data, error } = await this.supabase
      .from('impersonation_logs')
      .select(
        'id, organization_id, admin_id, user_id, status, started_at, ended_at, error_message, ip_address'
      )
      .eq('id', auditLogId)
      .maybeSingle()

    if (error) {
      throw error
    }

    return data as ImpersonationAuditLog | null
  }

  async closeImpersonation(
    auditLogId: CloseImpersonationParams['auditLogId'],
    endedAt: CloseImpersonationParams['endedAt'],
    errorMessage: CloseImpersonationParams['errorMessage'] = null,
    options?: {
      expectedCurrentStatus?: Tables<'impersonation_logs'>['status']
      requireUnended?: boolean
      values?: Omit<TablesUpdate<'impersonation_logs'>, 'ended_at' | 'status' | 'error_message'>
    }
  ): Promise<void> {
    let query = this.supabase
      .from('impersonation_logs')
      .update({
        ...(options?.values ?? {}),
        ended_at: endedAt,
        status: 'ended',
        error_message: errorMessage
      })
      .eq('id', auditLogId)

    if (options?.expectedCurrentStatus) {
      query = query.eq('status', options.expectedCurrentStatus)
    }

    if (options?.requireUnended !== false) {
      query = query.is('ended_at', null)
    }

    const { error } = await query

    if (error) {
      throw error
    }
  }

  async getUserCompensationRules(
    profileId: string,
    organizationId: number,
    startDate: string,
    endDate: string
  ) {
    const { data, error } = await this.supabase.rpc('get_user_compensation_rules', {
      _profile_id: profileId,
      _organization_id: organizationId,
      _start_date: startDate,
      _end_date: endDate
    })

    if (error) {
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getUserMetricsFromCompensationRule(
    ruleId: number,
    periodStart: string,
    periodEnd: string,
    profileId: string,
    organizationId: number
  ) {
    const { data, error } = await this.supabase.rpc('get_user_metrics_from_compensation_rules', {
      _rule_id: ruleId,
      _period_start: periodStart,
      _period_end: periodEnd,
      _profile_id: profileId,
      _organization_id: organizationId
    })

    if (error) {
      console.error(error)
      return []
    }

    return data ?? []
  }

  async getCompensationRuleVersionMetricIds(ruleVersionIds: number[]) {
    if (ruleVersionIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('compensation_rule_version_metrics')
      .select('metric_id')
      .in('compensation_rule_version_id', ruleVersionIds)

    if (error) {
      throw new Error(error.message)
    }

    return Array.from(new Set((data ?? []).map(row => row.metric_id))).sort((a, b) => a - b)
  }

  async getPendingProcessingItems(
    source: Enums<'plan_source'>,
    organizationId?: number
  ): Promise<RecordData[]> {
    if (source === 'deal') {
      return await this.getPendingProcessingDeals(organizationId)
    } else if (source === 'invoice') {
      return await this.getPendingProcessingInvoices(organizationId)
    } else if (source === 'kpi') {
      return await this.getPendingProcessingKpis(organizationId)
    } else if (source === 'line_item') {
      return await this.getPendingProcessingLineItems(organizationId)
    } else if (source === 'deal_line_item') {
      return await this.getPendingProcessingDealLineItems(organizationId)
    } else if (source === 'custom_record') {
      return await this.getPendingProcessingCustomRecords(organizationId)
    } else {
      return await this.getPendingProcessingActivities(organizationId)
    }
  }

  async upsertPeriodRulesAffectedScope(rows: PeriodRulesAffectedScopeRow[]): Promise<number> {
    if (rows.length === 0) {
      return 0
    }

    const { data, error } = await this.supabase.rpc('upsert_period_rules_affected_scope', {
      _rows: rows
    })

    if (error) {
      throw new Error(error.message)
    }

    return Number(data ?? 0)
  }

  async getPeriodRulesAffectedScopeSnapshot(): Promise<string> {
    const { data, error } = await this.supabase.rpc('get_period_rules_affected_scope_snapshot')

    if (error) {
      throw new Error(error.message)
    }

    if (typeof data !== 'string') {
      throw new Error('Failed to load period rules affected scope snapshot timestamp')
    }

    return data
  }

  async listPeriodRulesAffectedScopeOrganizations(
    snapshotBefore: string
  ): Promise<PeriodRulesAffectedScopeOrganizationStats[]> {
    const { data, error } = await this.supabase.rpc(
      'list_period_rules_affected_scope_organizations',
      {
        _snapshot_before: snapshotBefore
      }
    )

    if (error) {
      throw new Error(error.message)
    }

    return (data as PeriodRulesAffectedScopeOrganizationStats[]) ?? []
  }

  async getPeriodRulesAffectedScopePage(
    organizationId: number,
    snapshotBefore: string,
    afterCursor: PeriodRulesAffectedScopeCursor | null = null
  ): Promise<PeriodRulesAffectedScopePage> {
    const args: DBFunctions['get_period_rules_affected_scope']['Args'] = {
      _organization_id: organizationId,
      _snapshot_before: snapshotBefore,
      _limit: PERIOD_RULES_AFFECTED_SCOPE_PAGE_SIZE
    }

    if (afterCursor) {
      args._after_updated_at = afterCursor.updated_at
      args._after_id = afterCursor.id
    }

    const { data, error } = await this.supabase.rpc('get_period_rules_affected_scope', args)

    if (error) {
      throw new Error(error.message)
    }

    const rows = (data as PeriodRulesAffectedScopePaginatedRow[]) ?? []
    if (rows.length === 0) {
      return {
        rows,
        throughCursor: null,
        hasMore: false
      }
    }

    const lastRow = rows[rows.length - 1]
    const throughCursor: PeriodRulesAffectedScopeCursor = {
      id: lastRow.id,
      updated_at: lastRow.updated_at
    }

    if (rows.length < PERIOD_RULES_AFFECTED_SCOPE_PAGE_SIZE) {
      return {
        rows,
        throughCursor,
        hasMore: false
      }
    }

    const { data: nextPageData, error: nextPageError } = await this.supabase.rpc(
      'get_period_rules_affected_scope',
      {
        _organization_id: organizationId,
        _snapshot_before: snapshotBefore,
        _limit: 1,
        _after_updated_at: throughCursor.updated_at,
        _after_id: throughCursor.id
      }
    )

    if (nextPageError) {
      throw new Error(nextPageError.message)
    }

    return {
      rows,
      throughCursor,
      hasMore: ((nextPageData as PeriodRulesAffectedScopePaginatedRow[]) ?? []).length > 0
    }
  }

  async getPeriodRulesAffectedScope(
    organizationId: number,
    snapshotBefore: string
  ): Promise<PeriodRulesAffectedScopePaginatedRow[]> {
    const rows: PeriodRulesAffectedScopePaginatedRow[] = []
    let afterCursor: PeriodRulesAffectedScopeCursor | null = null

    while (true) {
      const {
        rows: pageRows,
        throughCursor,
        hasMore
      } = await this.getPeriodRulesAffectedScopePage(organizationId, snapshotBefore, afterCursor)
      if (pageRows.length === 0) {
        break
      }

      rows.push(...pageRows)
      if (!hasMore || !throughCursor) {
        break
      }

      afterCursor = throughCursor
    }

    return rows
  }

  async ackPeriodRulesAffectedScopeChunk(
    organizationId: number,
    snapshotBefore: string,
    rowIds: number[]
  ): Promise<number> {
    const { data, error } = await this.supabase.rpc('ack_period_rules_affected_scope_chunk', {
      _organization_id: organizationId,
      _snapshot_before: snapshotBefore,
      _row_ids: rowIds
    })

    if (error) {
      throw new Error(error.message)
    }

    return Number(data ?? 0)
  }

  async listPeriodRulesRunWatermarks(
    organizationIds: number[]
  ): Promise<PeriodRulesRunWatermark[]> {
    if (organizationIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase.rpc('list_period_rules_run_watermarks', {
      _organization_ids: organizationIds
    })

    if (error) {
      throw new Error(error.message)
    }

    return (data as PeriodRulesRunWatermark[]) ?? []
  }

  async upsertPeriodRulesRunWatermark(
    organizationId: number,
    ranAt?: string
  ): Promise<string | null> {
    const args: DBFunctions['upsert_period_rules_run_watermark']['Args'] = {
      _organization_id: organizationId
    }

    if (ranAt) {
      args._ran_at = ranAt
    }

    const { data, error } = await this.supabase.rpc('upsert_period_rules_run_watermark', args)

    if (error) {
      throw new Error(error.message)
    }

    return data ?? null
  }

  async ensureDataIssueProcessingStreams(
    organizationId: number
  ): Promise<DataIssueProcessingStream[]> {
    const { data, error } = await this.supabase.rpc('ensure_data_issue_processing_streams', {
      _organization_id: organizationId
    })

    if (error) {
      throw new Error(error.message)
    }

    return data ?? []
  }

  async getDataIssueProcessingStream({
    organizationId,
    source,
    isCustom
  }: DataIssueProcessingStreamSelector): Promise<DataIssueProcessingStream | null> {
    const { data, error } = await this.supabase
      .from('data_issue_processing_streams')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('source', source)
      .eq('is_custom', isCustom)
      .maybeSingle()

    if (error) {
      throw new Error(error.message)
    }

    return data ?? null
  }

  async claimDataIssueProcessingStream(
    args: DataIssueProcessingClaimArgs
  ): Promise<DataIssueProcessingClaim | null> {
    const { data, error } = await this.supabase.rpc('claim_data_issue_processing_stream', {
      _organization_id: args.organizationId,
      _source: args.source,
      _is_custom: args.isCustom,
      _lease_token: args.leaseToken,
      _lease_duration_seconds: args.leaseDurationSeconds
    })

    if (error) {
      throw new Error(error.message)
    }

    return (data as DataIssueProcessingClaim | null) ?? null
  }

  async heartbeatDataIssueProcessingRun(args: DataIssueProcessingHeartbeatArgs): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('heartbeat_data_issue_processing_run', {
      _stream_id: args.streamId,
      _run_id: args.runId,
      _lease_token: args.leaseToken,
      _lease_duration_seconds: args.leaseDurationSeconds,
      _records_seen: args.recordsSeen,
      _records_processed: args.recordsProcessed,
      _issues_saved: args.issuesSaved,
      _issues_removed: args.issuesRemoved,
      _last_processed_updated_at: args.lastProcessedUpdatedAt,
      _last_processed_id: args.lastProcessedId
    })

    if (error) {
      throw new Error(error.message)
    }

    return data === true
  }

  async finalizeDataIssueProcessingRun(
    args: DataIssueProcessingFinalizeArgs
  ): Promise<DataIssueProcessingRun | null> {
    const { data, error } = await this.supabase.rpc('finalize_data_issue_processing_run', {
      _stream_id: args.streamId,
      _run_id: args.runId,
      _lease_token: args.leaseToken,
      _status: args.status,
      _failure_reason: args.failureReason,
      _cursor_updated_at: args.cursorUpdatedAt,
      _cursor_id: args.cursorId,
      _snapshot_upper_updated_at: args.snapshotUpperUpdatedAt,
      _snapshot_upper_id: args.snapshotUpperId,
      _records_seen: args.recordsSeen,
      _records_processed: args.recordsProcessed,
      _issues_saved: args.issuesSaved,
      _issues_removed: args.issuesRemoved
    })

    if (error) {
      throw new Error(error.message)
    }

    return data ?? null
  }

  async saveExecution(execution: TablesInsert<'executions'>) {
    const { error } = await this.supabase.from('executions').insert(execution)

    if (error) {
      throw error
    }
  }

  async getInvoicesDealLinkField(organizationId: number) {
    const query = this.supabase
      .from('invoices')
      .select('deal_link_field')
      .eq('organization_id', organizationId)
      .neq('deal_link_id', null)
      .order('effective_at', { ascending: false })
      .limit(1)

    const { data } = await query

    return data?.[0]?.deal_link_field
  }

  async getPendingToReviewItems(
    source: 'deal',
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<(Tables<'deals'> & { invoices: Tables<'invoices'>[] | null })[]>
  async getPendingToReviewItems(
    source: 'activity',
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<ActivitiesWithDeals[]>

  async getPendingToReviewItems(
    source: 'invoice',
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<InvoicesWithDeals[]>
  async getPendingToReviewItems(
    source: Extract<Enums<'plan_source'>, 'deal' | 'activity' | 'invoice'>,
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<
    | (Tables<'deals'> & { invoices: Tables<'invoices'>[] | null })[]
    | ActivitiesWithDeals[]
    | InvoicesWithDeals[]
  > {
    const tableName = SOURCE_TO_TABLE[source]
    const selectQuery = tableName === 'deals' ? '*, invoices(*)' : '*, deals(*)'
    const query = this.supabase
      .from(tableName)
      .select(selectQuery)
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })

    if (options?.after) {
      query.or(
        `updated_at.gt.${options.after.updatedAt},and(updated_at.eq.${options.after.updatedAt},id.gt.${options.after.id})`
      )
    } else if (!options?.until) {
      const startOfCurrentYear = new Date(new Date().getFullYear(), 0, 1).toISOString()
      const { data: latestExecution } = await this.supabase
        .from('executions')
        .select('reference_date')
        .eq('task_id', 'DISCREPANCIES')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(1)

      const referenceDate = latestExecution?.[0]?.reference_date ?? startOfCurrentYear
      query.gte('updated_at', referenceDate)
    }

    if (options?.until) {
      query.or(
        `updated_at.lt.${options.until.updatedAt},and(updated_at.eq.${options.until.updatedAt},id.lte.${options.until.id})`
      )
    }

    const pageLimit = options?.limit ?? (options ? 1000 : 5000)
    const { data: items, error } = await query.limit(pageLimit)

    if (error) {
      throw error
    }

    Logger.info(
      `🔍 Found ${items.length} records to be reviewed for source ${source} and organization ${organizationId}`
    )

    return items as
      | (Tables<'deals'> & { invoices: Tables<'invoices'>[] | null })[]
      | ActivitiesWithDeals[]
      | InvoicesWithDeals[]
  }

  protected async getPendingProcessingDeals(organizationId?: number): Promise<Tables<'deals'>[]> {
    const query = this.supabase.from('deals').select('*').is('pending_processing', true)

    if (organizationId) {
      query.eq('organization_id', organizationId)
      // Keep the constant org filter in the sort so Postgres can use the
      // pending-processing partial index instead of falling back to close_date.
      query.order('organization_id', { ascending: true })
    }

    query
      .order('close_date', { ascending: true })
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(100)

    const { data: deals, error: getDealsError } = await query

    if (getDealsError) {
      console.error(getDealsError)
      throw getDealsError
    }

    return (deals ?? []) as Tables<'deals'>[]
  }

  async loadChecksConfig(organizationId: number) {
    const { error, data } = await this.supabase
      .from('organizations')
      .select('data_issues_checks_config')
      .eq('id', organizationId)

    if (error) {
      throw error
    }

    return data
  }

  async getPendingToReviewItemsUpperBound(
    source: Extract<Enums<'plan_source'>, 'deal' | 'activity' | 'invoice'>,
    organizationId: number
  ): Promise<PendingToReviewItemsCursor | null> {
    const tableName = SOURCE_TO_TABLE[source]
    const { data, error } = await this.supabase
      .from(tableName)
      .select('id, updated_at')
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(1)

    if (error) {
      throw error
    }

    const latestItem = data?.[0]

    if (!latestItem?.updated_at) {
      return null
    }

    return {
      id: latestItem.id,
      updatedAt: latestItem.updated_at
    }
  }

  async saveDiscrepancies(
    data_issues: DataIssuesWithChildrenInsert[],
    source: Enums<'plan_source'>
  ) {
    const { error } = await this.supabase.rpc('upsert_data_issues', {
      _source: source,
      _rows: data_issues
    })

    if (error) {
      throw error
    }
  }

  async getOrgCurrencyExchangeConfig(organizationId: number) {
    const { error, data } = await this.supabase
      .from('organizations')
      .select('use_fixed_exchange_rates, use_invoices_exchange_rates')
      .eq('id', organizationId)
      .maybeSingle()

    if (error) throw error

    return data
  }

  async getExchangeConfigs(
    organizationId: number,
    tableName: Enums<'customizable_tables'>
  ): Promise<Tables<'exchange_config'>[]> {
    const { error, data } = await this.supabase
      .from('exchange_config')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('table_name', tableName)

    if (error) {
      throw error
    }

    return data ?? []
  }

  async removeDiscrepancies(
    ids: (
      | {
          deal_id: number
          error_code: string
          column_key: string
        }
      | {
          invoice_id: number
          error_code: string
          column_key: string
        }
    )[],
    source: Enums<'plan_source'>,
    organizationId: number
  ) {
    const { error } = await this.supabase.rpc(`delete_data_issues_batch`, {
      _source: SOURCE_TO_TABLE[source],
      _items: ids,
      _organization_id: organizationId
    })

    if (error) {
      throw error
    }
  }

  protected async getPendingProcessingActivities(
    organizationId?: number
  ): Promise<ActivitiesWithDeals[]> {
    const query = this.supabase
      .from('activities')
      .select('*,deals!inner(*,split_children:deals!split_anchor_id(*))')
      .is('pending_processing', true)

    if (organizationId) {
      query.eq('organization_id', organizationId)
    }

    query
      .order('activity_date', { ascending: true })
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(100)

    const { data: activities, error: getActivitiesError } = await query

    if (getActivitiesError) {
      console.error(getActivitiesError)
      throw getActivitiesError
    }

    return (activities ?? []) as ActivitiesWithDeals[]
  }

  protected async getPendingProcessingInvoices(
    organizationId?: number
  ): Promise<InvoicesWithDeals[]> {
    const query = this.supabase
      .from('invoices')
      .select('*,deals!inner(*,split_children:deals!split_anchor_id(*))')
      .is('pending_processing', true)

    if (organizationId) {
      query.eq('organization_id', organizationId)
    }

    query
      .order('effective_at', { ascending: true })
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(100)

    const { data: invoices, error: getInvoicesError } = await query

    if (getInvoicesError) {
      console.error(getInvoicesError)
      throw getInvoicesError
    }

    return (invoices ?? []) as InvoicesWithDeals[]
  }

  protected async getPendingProcessingLineItems(
    organizationId?: number
  ): Promise<LineItemsWithJoins[]> {
    const query = this.supabase
      .from('line_items')
      .select('*, invoices!inner(*, deals!inner(*,split_children:deals!split_anchor_id(*)))')
      .is('pending_processing', true)

    if (organizationId) {
      query.eq('organization_id', organizationId)
    }

    query
      .order('invoices(effective_at)', { ascending: true })
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(100)

    const { data: lineItems, error: getLineItemsError } = await query

    if (getLineItemsError) {
      console.error(getLineItemsError)
      throw getLineItemsError
    }

    return (lineItems ?? []) as unknown as LineItemsWithJoins[]
  }

  protected async getPendingProcessingDealLineItems(
    organizationId?: number
  ): Promise<DealLineItemsWithJoins[]> {
    const query = this.supabase
      .from('deal_line_items')
      .select('*, deals!inner(*, split_children:deals!split_anchor_id(*))')
      .is('pending_processing', true)

    if (organizationId) query.eq('organization_id', organizationId)

    query.order('updated_at', { ascending: true }).order('id', { ascending: true }).limit(100)

    const { data, error } = await query
    if (error) throw error
    return (data ?? []) as unknown as DealLineItemsWithJoins[]
  }

  protected async getPendingProcessingKpis(organizationId?: number): Promise<Tables<'kpis'>[]> {
    const query = this.supabase
      .from('kpis')
      .select('*, kpis_tables(*)')
      .is('pending_processing', true)

    if (organizationId) {
      query.eq('organization_id', organizationId)
    }

    query
      .order('period_start', { ascending: true })
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(100)

    const { data: kpis, error: getKpisError } = await query

    if (getKpisError) {
      console.error(getKpisError)
      throw getKpisError
    }

    return (kpis ?? []) as unknown as Tables<'kpis'>[]
  }

  protected async getPendingProcessingCustomRecords(
    organizationId?: number
  ): Promise<CustomRecordsWithTables[]> {
    const query = this.supabase
      .from('custom_records')
      .select('*, custom_record_tables(*)')
      .is('pending_processing', true)

    if (organizationId) {
      query.eq('organization_id', organizationId)
    }

    query
      .order('created_at', { ascending: true })
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(100)

    const { data: customRecords, error } = await query

    if (error) {
      console.error(error)
      throw error
    }

    return (customRecords ?? []) as CustomRecordsWithTables[]
  }

  async getRecordCompensationRules(
    organizationId: number,
    source: Enums<'plan_source'>,
    sourceTableId?: number
  ) {
    const query = this.supabase
      .from('compensation_rules')
      .select(
        '*, plans!inner(label, teams_plans_history!inner(id, plan_id, team_id, effective_from, effective_to)), compensation_rule_versions(*)'
      )
      .eq('plans.organization_id', organizationId)
      .eq('source', source)
      .is('outcome_period', null)
      .order('active_period', {
        referencedTable: 'compensation_rule_versions',
        ascending: true
      })

    if (source === 'kpi') {
      query.eq('kpis_table_id', sourceTableId!)
    } else if (source === 'custom_record') {
      query.eq('custom_record_table_id', sourceTableId!)
    }

    const { data: compRules, error } = await query

    if (error) {
      console.error(error)
      throw error
    }

    return compRules
  }

  async getPeriodCompensationRules(organizationId: number) {
    const query = this.supabase
      .from('compensation_rules')
      .select(
        '*, plans!inner(teams_plans_history!inner(id, plan_id, team_id, effective_from, effective_to)), compensation_rule_versions(*, compensation_rule_version_metrics(*))'
      )
      .eq('plans.organization_id', organizationId)
      .not('outcome_period', 'is', null)
      .order('active_period', {
        referencedTable: 'compensation_rule_versions',
        ascending: true
      })

    const { data: compRules, error } = await query

    if (error) {
      console.error(error)
      throw error
    }

    return compRules
  }

  async getCompensationRulesForExplanation(
    organizationId: number
  ): Promise<CompensationRuleForExplanation[]> {
    const { data, error } = await this.supabase
      .from('compensation_rules')
      .select(
        '*, plans!inner(id, label, organization_id, teams_plans_history(id, plan_id, team_id, effective_from, effective_to)), compensation_rule_versions(*)'
      )
      .eq('plans.organization_id', organizationId)
      .order('active_period', {
        referencedTable: 'compensation_rule_versions',
        ascending: true
      })

    if (error) {
      throw error
    }

    return (data ?? []) as unknown as CompensationRuleForExplanation[]
  }

  async findCompensationRuleByIdForOrg(
    compensationRuleId: number,
    organizationId?: number
  ): Promise<
    | (Tables<'compensation_rules'> & {
        plans?: { organization_id: number } | null
      })
    | null
  > {
    const query = this.supabase
      .from('compensation_rules')
      .select('*, plans!inner(organization_id)')
      .eq('id', compensationRuleId)
    if (organizationId != null) {
      query.eq('plans.organization_id', organizationId)
    }

    const { data, error } = await query.maybeSingle()

    if (error) {
      console.error('Error fetching compensation rule for organization', error)
      throw error
    }

    return (data ?? null) as
      | (Tables<'compensation_rules'> & {
          plans?: { organization_id: number } | null
        })
      | null
  }

  async transitionCompensationRuleVersion(
    params: import('./index').CompensationRuleVersionTransition
  ) {
    const { data, error } = await this.supabase.rpc('transition_compensation_rule_version', {
      _action: params.action,
      _version_id: 'versionId' in params ? params.versionId : undefined,
      _compensation_rule_id: 'compensationRuleId' in params ? params.compensationRuleId : undefined,
      _start_date: 'start' in params ? params.start : undefined,
      _end_date: 'end' in params ? params.end : undefined,
      _diagram: 'diagram' in params ? params.diagram : undefined,
      _is_tvp_based: 'isTvpBased' in params ? params.isTvpBased : undefined,
      _visible_if_scheduled: 'visibleIfScheduled' in params ? params.visibleIfScheduled : undefined,
      _confirmed_version_id: 'confirmedVersionId' in params ? params.confirmedVersionId : undefined,
      _confirmed_deactivation_date:
        'confirmedDeactivationDate' in params ? params.confirmedDeactivationDate : undefined
    })
    if (error) throw error
    return data
  }

  async overrideCompensationRuleVersion({
    compensationRuleId,
    versionId,
    diagram,
    isTvpBased
  }: {
    compensationRuleId: number
    versionId: number
    diagram: Json
    isTvpBased: boolean
  }): Promise<Pick<Tables<'compensation_rule_versions'>, 'id'> | null> {
    const { data, error } = await this.supabase
      .from('compensation_rule_versions')
      .update({
        diagram,
        is_tvp_based: isTvpBased
      })
      .eq('id', versionId)
      .eq('compensation_rule_id', compensationRuleId)
      .select('id')
      .maybeSingle()

    if (error) throw error

    return data
  }

  async getProfilesForTeamIds(teamIds: number[]) {
    if (teamIds.length === 0) {
      return []
    }

    // Simulations should only include real team members, and a profile can
    // belong to more than one selected team. We normalize that here so every
    // downstream caller gets the same team-scope semantics.
    const { data, error } = await this.supabase
      .from('teams_profiles')
      .select('profile_id, profiles!inner(img_url, name, email)')
      .in('team_id', teamIds)
      .eq('role', 'member')

    if (error) {
      throw new Error(`Error fetching profiles for teams: ${error.message}`)
    }

    const rows = (data ?? []) as TeamProfileRow[]
    const dedupedProfiles = new Map<string, TeamProfileRow>()

    for (const row of rows) {
      if (!dedupedProfiles.has(row.profile_id)) {
        dedupedProfiles.set(row.profile_id, row)
      }
    }

    return [...dedupedProfiles.values()]
  }

  async getOrganizationTeams(organizationId: number): Promise<Tables<'teams'>[]> {
    const { data, error } = await this.supabase
      .from('teams')
      .select('*')
      .eq('organization_id', organizationId)

    if (error) {
      throw new Error(`Error fetching teams for organization ${organizationId}: ${error.message}`)
    }

    return (data ?? []).map(team => ({
      ...team,
      tags_array: (team as { tags_array?: number[] | null }).tags_array ?? null,
      teams_search_columns:
        (team as { teams_search_columns?: string | null }).teams_search_columns ?? null
    })) as Tables<'teams'>[]
  }

  async getTeamsByIds(
    organizationId: number,
    teamIds: number[]
  ): Promise<Pick<Tables<'teams'>, 'id' | 'name' | 'currency'>[]> {
    if (teamIds.length === 0) return []

    const { data, error } = await this.supabase
      .from('teams')
      .select('id, name, currency')
      .eq('organization_id', organizationId)
      .in('id', teamIds)

    if (error) throw error
    return data ?? []
  }

  async createKpiTableWithTeams({
    label,
    period,
    organizationId,
    teamIds
  }: CreateKpiTableWithTeamsParams): Promise<DataTableCreationResult> {
    const { data: tableId, error } = await this.supabase.rpc('create_kpi_table_with_teams', {
      _label: label,
      _organization_id: organizationId,
      _period: period,
      _team_ids: teamIds
    })
    if (error) throw new Error(`Error creating KPI table: ${error.message}`)
    return { tableId: Number(tableId) }
  }

  async createCustomRecordTableWithTeams({
    label,
    organizationId,
    teamIds
  }: CreateCustomRecordTableWithTeamsParams): Promise<DataTableCreationResult> {
    const { data: tableId, error } = await this.supabase.rpc(
      'create_custom_record_table_with_teams',
      {
        _label: label,
        _organization_id: organizationId,
        _team_ids: teamIds
      }
    )
    if (error) throw new Error(`Error creating record table: ${error.message}`)
    return { tableId: Number(tableId) }
  }
  async updateKpiTableTeams({
    tableId,
    organizationId,
    teamIds
  }: UpdateDataTableTeamsParams): Promise<void> {
    const { error } = await this.supabase.rpc('update_kpi_table_teams', {
      _table_id: tableId,
      _organization_id: organizationId,
      _team_ids: teamIds
    })
    if (error) throw new Error(`Error updating KPI table teams: ${error.message}`)
  }

  async updateCustomRecordTableTeams({
    tableId,
    organizationId,
    teamIds
  }: UpdateDataTableTeamsParams): Promise<void> {
    const { error } = await this.supabase.rpc('update_custom_record_table_teams', {
      _table_id: tableId,
      _organization_id: organizationId,
      _team_ids: teamIds
    })
    if (error) throw new Error(`Error updating record table teams: ${error.message}`)
  }

  async getOrganizationTeamsForCompRule(
    organizationId: number,
    compensationRuleId: number,
    includeHistoricalPlanLinks = false
  ): Promise<Array<Pick<Tables<'teams'>, 'id' | 'name'>>> {
    const planLinkRelation = includeHistoricalPlanLinks ? 'teams_plans_history' : 'teams_plans'
    const { data, error } = await this.supabase
      .from('teams')
      .select(`id, name, ${planLinkRelation}!inner(plans!inner(compensation_rules!inner(id)))`)
      .order('name', { ascending: true })
      .eq('organization_id', organizationId)
      .eq(`${planLinkRelation}.plans.compensation_rules.id`, compensationRuleId)

    if (error) {
      throw new Error(`Error fetching teams for comp rule ${compensationRuleId}: ${error.message}`)
    }

    const teams = new Map<number, Pick<Tables<'teams'>, 'id' | 'name'>>()
    for (const team of data ?? []) {
      teams.set(team.id, { id: team.id, name: team.name })
    }

    return [...teams.values()]
  }

  async listDataViews({
    organizationId,
    includeArchived = false
  }: DataViewListParams): Promise<DataView[]> {
    let query = this.supabase
      .from('data_views')
      .select('*')
      .eq('organization_id', organizationId)
      .order('label', { ascending: true })

    if (!includeArchived) {
      query = query.is('archived_at', null)
    }

    const { data, error } = await query
    if (error) throw new Error(`Error fetching data views: ${error.message}`)
    return (data ?? []) as DataView[]
  }

  async getDataViewFilterFields({
    source
  }: DataViewFilterFieldsParams): Promise<DataViewFilterField[]> {
    return Object.entries(getSourceColumns(source)).map(([name, definition]) => ({
      name,
      type: definition.type
    }))
  }

  async createDataView(params: DataViewCreateParams): Promise<DataView> {
    const { organizationId, createdBy, ...view } = params
    const { data, error } = await this.supabase
      .from('data_views')
      .insert({
        ...view,
        organization_id: organizationId,
        created_by: createdBy
      })
      .select('*')
      .single()

    if (error) throw new Error(`Error creating data view: ${error.message}`)
    return data as DataView
  }

  async updateDataView({ id, organizationId, updates }: DataViewUpdateParams): Promise<DataView> {
    const { data, error } = await this.supabase
      .from('data_views')
      .update(updates)
      .eq('id', id)
      .eq('organization_id', organizationId)
      .select('*')
      .single()

    if (error) throw new Error(`Error updating data view: ${error.message}`)
    return data as DataView
  }

  async archiveDataView({
    id,
    organizationId
  }: {
    id: number
    organizationId: number
  }): Promise<void> {
    const { data, error } = await this.supabase
      .from('data_views')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id)
      .eq('organization_id', organizationId)
      .select('id')
      .maybeSingle()

    if (error) throw new Error(`Error archiving data view: ${error.message}`)
    if (!data) throw new Error('Data view not found or could not be archived')
  }

  async claimDomainEventDeliveries(params: {
    consumer: string
    eventType: string
    limit?: number
  }) {
    const { data, error } = await this.supabase.rpc('claim_domain_event_deliveries', {
      _consumer: params.consumer,
      _event_type: params.eventType,
      _limit: params.limit
    })

    if (error) {
      throw error
    }

    return data ?? []
  }

  async completeDomainEventDelivery(params: CompleteDomainEventDeliveryParams): Promise<void> {
    const { error } = await this.supabase.rpc('complete_domain_event_delivery', {
      _delivery_id: params.deliveryId,
      _status: params.status,
      _error: params.error ?? undefined,
      _skipped_reason: params.skippedReason ?? undefined,
      _retry_after_seconds: params.retryAfterSeconds ?? 60
    })

    if (error) {
      throw error
    }
  }

  async recordCalculationFailure(
    source: Enums<'plan_source'>,
    organizationId: number,
    recordIds: number[],
    reason: string
  ): Promise<void> {
    const { error } = await this.supabase.rpc('record_calculation_failure', {
      _source: source,
      _organization_id: organizationId,
      _record_ids: recordIds,
      _reason: reason
    })
    if (error) throw error
  }

  async saveResults(
    results: ParsedExecutionResult[],
    source?:
      | 'deals'
      | 'activities'
      | 'invoices'
      | 'kpis'
      | 'line_items'
      | 'deal_line_items'
      | 'custom_records'
  ): Promise<Error | void> {
    results.forEach(result => {
      if (result.calculationContext) {
        CalculationContextSchema.parse(result.calculationContext)
      }
    })
    const { error } = await this.supabase.rpc('save_execution_results', {
      _source_type: source ?? '',
      _execution_results: results as unknown as Json
    })

    if (error) {
      console.error(error)
      return new Error(error.message)
    }

    try {
      await this.captureGeneratedPayouts(results, source)
    } catch (analyticsError) {
      console.warn('[PostHog] failed to capture payouts_generated', analyticsError)
    }
    // Keep tier snapshot persistence at the production RPC boundary so every runner shares it.
    await new CompensationTierSnapshots(this).saveForResults(results)
  }
  async saveCompensationTierSnapshots(
    snapshots: CompensationTierSnapshotWrite[]
  ): Promise<CompensationTierEvent[]> {
    const { data, error } = await this.supabase.rpc('save_compensation_tier_snapshots', {
      _snapshots: snapshots as unknown as Json
    })

    if (error) {
      throw error
    }

    return (data ?? []).map(event => ({
      eventId: event.event_id,
      eventType: event.event_type as CompensationTierEvent['eventType'],
      organizationId: event.event_organization_id,
      profileId: event.event_profile_id,
      payload: event.event_payload,
      occurredAt: event.event_occurred_at
    }))
  }

  async getPayoutCalculationContext(payoutId: number) {
    const { data, error } = await this.supabase.rpc('get_payout_calculation_context', {
      _payout_id: payoutId
    })

    if (error) throw error
    const response = data as PayoutCalculationContextResponse | null
    if (response?.status === 'available') {
      CalculationContextSchema.parse(response.context)
    }
    return response
  }

  private async captureGeneratedPayouts(
    results: ParsedExecutionResult[],
    source?:
      | 'deals'
      | 'activities'
      | 'invoices'
      | 'kpis'
      | 'line_items'
      | 'deal_line_items'
      | 'custom_records'
  ) {
    const groupedByOrganization = new Map<
      number,
      {
        payoutCount: number
        profileIds: Set<string>
        ruleIds: Set<number>
      }
    >()

    for (const result of results) {
      const payoutCount = result.payouts.reduce((count, payout) => {
        return (
          count +
          payout.node_output.filter(nodeOutput => Number(nodeOutput.value ?? 0) !== 0).length
        )
      }, 0)

      if (payoutCount === 0) {
        continue
      }

      const current = groupedByOrganization.get(result.organizationId) ?? {
        payoutCount: 0,
        profileIds: new Set<string>(),
        ruleIds: new Set<number>()
      }

      current.payoutCount += payoutCount

      if (result.profileId) {
        current.profileIds.add(result.profileId)
      }

      if (result.ruleId) {
        current.ruleIds.add(result.ruleId)
      }

      groupedByOrganization.set(result.organizationId, current)
    }

    if (groupedByOrganization.size === 0) {
      return
    }

    const organizationIds = [...groupedByOrganization.keys()]
    const organizations: Pick<Tables<'organizations'>, 'id' | 'name'>[] = []

    for (const organizationIdsChunk of batched(
      organizationIds,
      ORGANIZATION_IDS_IN_FILTER_BATCH_SIZE
    )) {
      const { data, error } = await this.supabase
        .from('organizations')
        .select('id, name')
        .in('id', organizationIdsChunk)

      if (error) {
        console.warn('[PostHog] failed to load organizations for payouts_generated', error)
        return
      }

      organizations.push(...(data ?? []))
    }

    const organizationNameById = new Map<number, string | null>(
      organizations.map(organization => [Number(organization.id), organization.name ?? null])
    )

    const environment = process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV ?? null

    const payloads = [...groupedByOrganization].map(([organizationId, summary]) =>
      buildAdminAnalyticsCapture({
        distinctId: `org_${organizationId}`,
        event: 'payouts_generated',
        organizationId,
        organizationName: organizationNameById.get(organizationId) ?? null,
        environment,
        userId: null,
        app: 'web',
        surface: 'system',
        properties: {
          payout_count: summary.payoutCount,
          rule_count: summary.ruleIds.size,
          profile_count: summary.profileIds.size,
          source_type: source ?? 'period'
        }
      })
    )

    await safeCapturePosthogServerEvents(payloads)
  }

  async movePayoutInstallmentToStatement(args: {
    new_statement_date: string
    payout_installment_id: number
  }): Promise<void> {
    const { error } = await this.supabase.rpc('move_payout_installment_to_statement', args)

    if (error) {
      Logger.error(error)
      throw error
    }
  }

  async getCustomFields(
    source: Enums<'plan_source'>,
    organizationId: number,
    sourceTableId?: number
  ) {
    const query = this.supabase
      .from('custom_fields')
      .select('*')
      .eq('table_name', SOURCE_TO_TABLE[source])
      .eq('organization_id', organizationId)

    if (source === 'kpi') {
      query.eq('kpis_table_id', sourceTableId!)
    } else if (source === 'custom_record') {
      query.eq('custom_record_table_id', sourceTableId!)
    }

    const { data, error } = await query

    if (error) {
      throw error
    }

    return data ?? []
  }

  async backfillConnectedAppUserMappings({
    organizationId,
    provider,
    mappingDelta
  }: {
    organizationId: number
    provider: 'hubspot' | 'salesforce'
    mappingDelta: Record<string, string>
  }) {
    if (Object.keys(mappingDelta).length === 0) {
      return {
        deal_owner_updates: 0,
        activity_owner_updates: 0,
        deal_custom_user_field_updates: 0,
        activity_custom_user_field_updates: 0,
        custom_record_owner_updates: 0,
        custom_record_custom_user_field_updates: 0
      }
    }

    const args: Database['public']['Functions']['backfill_connected_app_user_mappings']['Args'] = {
      _organization_id: organizationId,
      _provider: provider,
      _mapping_delta: mappingDelta
    }
    const { data, error } = await this.supabase.rpc('backfill_connected_app_user_mappings', args)

    if (error) {
      throw error
    }

    const { data: customRecordUpdates, error: customRecordError } = await this.supabase.rpc(
      'backfill_connected_app_custom_record_user_mappings',
      args
    )

    if (customRecordError) {
      throw customRecordError
    }

    return {
      ...(data?.[0] ?? {
        deal_owner_updates: 0,
        activity_owner_updates: 0,
        deal_custom_user_field_updates: 0,
        activity_custom_user_field_updates: 0
      }),
      ...(customRecordUpdates?.[0] ?? {
        custom_record_owner_updates: 0,
        custom_record_custom_user_field_updates: 0
      })
    }
  }

  async getCustomFieldFilterCandidateIds({
    organizationId,
    source,
    customFieldId,
    sourceRole,
    virtualTableId,
    maxResults,
    ...value
  }: CustomFieldFilterCandidateIdsParams): Promise<number[]> {
    const args: Database['public']['Functions']['get_custom_field_filter_candidate_ids']['Args'] = {
      _custom_field_id: customFieldId,
      _organization_id: organizationId,
      _source: source,
      _source_role: sourceRole
    }

    if (virtualTableId != null) {
      args._virtual_table_id = virtualTableId
    }

    if ('valueUserId' in value) {
      args._value_user_id = value.valueUserId
    } else {
      args._value_date_end = value.valueDateEnd
      args._value_date_start = value.valueDateStart
    }

    const candidateIds: number[] = []
    const pageSize = 1000

    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.supabase
        .rpc('get_custom_field_filter_candidate_ids', args)
        .order('record_id', { ascending: true })
        .range(from, from + pageSize - 1)

      if (error) throw error

      const page = data ?? []
      candidateIds.push(...page.map(row => row.record_id))

      if (maxResults != null && candidateIds.length > maxResults) {
        throw new MetricPreviewError('too_broad', 'Narrow the preview month, teams, or users')
      }

      if (page.length < pageSize) {
        return candidateIds
      }
    }
  }

  async getRelationalCustomFieldQueryIds({
    organizationId,
    source,
    filters,
    sort,
    offset,
    limit,
    virtualTableId
  }: RelationalCustomFieldQueryIdsParams): Promise<RelationalCustomFieldQueryIdsResult> {
    const args: Database['public']['Functions']['get_relational_custom_field_query_ids']['Args'] = {
      _filters: filters.map(filter =>
        filter.operator === 'user_eq'
          ? {
              custom_field_id: filter.customFieldId,
              operator: filter.operator,
              source_role: filter.descriptor.sourceRole,
              value_user_id: filter.value
            }
          : {
              custom_field_id: filter.customFieldId,
              operator: filter.operator,
              source_role: filter.descriptor.sourceRole,
              value_date_end: filter.value.end,
              value_date_start: filter.value.start
            }
      ) as Json,
      _limit: limit,
      _offset: offset,
      _organization_id: organizationId,
      _source: source,
      _sort: sort
        ? ({
            custom_field_id: sort.customFieldId,
            direction: sort.direction,
            source_role: sort.descriptor.sourceRole
          } as Json)
        : null
    }

    if (virtualTableId != null) {
      args._virtual_table_id = virtualTableId
    }

    const { data, error } = await this.supabase.rpc('get_relational_custom_field_query_ids', args)
    if (error) throw error

    const rows = data ?? []
    return {
      recordIds: rows.flatMap(row => (row.record_id == null ? [] : [row.record_id])),
      totalCount: rows[0]?.total_count ?? 0
    }
  }

  async getDealsGrid(params: GetDealsGridParams) {
    const { data, error } = await this.supabase.rpc('get_deals_grid', {
      _filters: params.filters as Json,
      _limit: params.limit ?? 100,
      _offset: params.offset ?? 0,
      _organization_id: params.organizationId,
      _sort: (params.sort ?? null) as Json | null
    })

    if (error) throw error

    const rows = data ?? []
    return {
      items: rows.flatMap(row => (row.deal == null ? [] : [row.deal as Record<string, unknown>])),
      totalCount: rows[0]?.total_count ?? 0
    }
  }

  async getActivitiesGrid(params: ActivityGridRequest) {
    const { data, error } = await this.supabase.rpc('get_activities_grid', {
      _filters: params.filters as Json,
      _limit: params.limit ?? 100,
      _offset: params.offset ?? 0,
      _organization_id: params.organizationId,
      _sort: (params.sort ?? null) as Json | null
    })

    if (error) throw error

    const rows = data ?? []
    return {
      items: rows.flatMap(row =>
        row.activity == null ? [] : [row.activity as Record<string, unknown>]
      ),
      totalCount: rows[0]?.total_count ?? 0
    }
  }

  async getInvoicesGrid(params: InvoiceGridRequest) {
    const { data, error } = await this.supabase.rpc('get_invoices_grid', {
      _filters: params.filters as Json,
      _limit: params.limit ?? 100,
      _offset: params.offset ?? 0,
      _organization_id: params.organizationId,
      _sort: (params.sort ?? null) as Json | null
    })

    if (error) throw error

    const rows = data ?? []
    return {
      items: rows.flatMap(row =>
        row.invoice == null ? [] : [row.invoice as Record<string, unknown>]
      ),
      totalCount: rows[0]?.total_count ?? 0
    }
  }

  async getKpisGrid(params: KpiGridRequest) {
    const { data, error } = await this.supabase.rpc('get_kpis_grid', {
      _filters: params.filters as Json,
      _limit: params.limit ?? 100,
      _offset: params.offset ?? 0,
      _organization_id: params.organizationId,
      _sort: (params.sort ?? null) as Json | null,
      _virtual_table_id: params.virtualTableId
    })
    if (error) throw error

    const rows = data ?? []
    return {
      items: rows.flatMap(row => (row.kpi == null ? [] : [row.kpi as Record<string, unknown>])),
      totalCount: rows[0]?.total_count ?? 0
    }
  }

  async getCustomRecordsGrid(params: CustomRecordGridRequest) {
    const { data, error } = await this.supabase.rpc('get_custom_records_grid', {
      _filters: params.filters as Json,
      _limit: params.limit ?? 100,
      _offset: params.offset ?? 0,
      _organization_id: params.organizationId,
      _sort: (params.sort ?? null) as Json | null,
      _virtual_table_id: params.virtualTableId
    })
    if (error) throw error

    const rows = data ?? []
    return {
      items: rows.flatMap(row =>
        row.custom_record == null ? [] : [row.custom_record as Record<string, unknown>]
      ),
      totalCount: rows[0]?.total_count ?? 0
    }
  }

  async getKpiCustomFields(tableId: number) {
    const { data, error } = await this.supabase
      .from('custom_fields')
      .select('*')
      .eq('table_name', 'kpis')
      .eq('kpis_table_id', tableId)

    if (error) {
      throw error
    }

    return data ?? []
  }

  async getKpiTable(tableId: number) {
    const { data, error } = await this.supabase
      .from('kpis_tables')
      .select('id, organization_id, label, period')
      .eq('id', tableId)
      .maybeSingle()

    if (error) {
      throw error
    }

    if (!data) {
      throw new Error(`KPI table ${tableId} was not found or is not accessible`)
    }

    return data
  }

  async getKpiPreviewMonths(organizationId: number, profileIds: string[], kpisTableId: number) {
    const uniqueProfileIds = [...new Set(profileIds)]
    if (uniqueProfileIds.length === 0) return {}

    const rows: { profile_id: string; period_start: string }[] = []
    for (const profileIdsChunk of batched(uniqueProfileIds, PROFILE_IDS_IN_FILTER_BATCH_SIZE)) {
      for (let offset = 0; ; offset += KPI_PREVIEW_MONTHS_PAGE_SIZE) {
        const { data, error } = await this.supabase
          .from('kpis')
          .select('profile_id, period_start')
          .eq('organization_id', organizationId)
          .eq('kpis_table_id', kpisTableId)
          .in('profile_id', profileIdsChunk)
          .order('profile_id', { ascending: true })
          .order('period_start', { ascending: false })
          .range(offset, offset + KPI_PREVIEW_MONTHS_PAGE_SIZE - 1)

        if (error) throw error

        rows.push(...(data ?? []))
        if ((data?.length ?? 0) < KPI_PREVIEW_MONTHS_PAGE_SIZE) break
      }
    }

    return rows.reduce<Record<string, string[]>>((months, row) => {
      const month = row.period_start.slice(0, 7)
      const profileMonths = months[row.profile_id] ?? []

      if (!profileMonths.includes(month)) {
        profileMonths.push(month)
        months[row.profile_id] = profileMonths
      }

      return months
    }, {})
  }

  async getOwnerEmail(ownerId: string) {
    const { data, error } = await this.supabase.from('profiles').select('email').eq('id', ownerId)

    if (error) {
      console.error('Error getting owner email', error)
    }

    return data?.[0]?.email
  }

  async getProfiles(range?: GetProfilesParams): Promise<ProfilesResult> {
    let query = this.supabase
      .from('profiles')
      .select(
        'created_at, email, id, img_url, name, name_lower_case, name_normalized, onboarded, tax_id_number, updated_at',
        { count: 'exact' }
      )

    if (range?.from != null && range?.to != null) {
      query = query.range(range.from, range.to)
    }

    const { data, error, count } = await query
    if (error) throw error

    return {
      items: (data ?? []) as ProfilesResult['items'],
      count
    }
  }

  async getProfileForEdit(
    organizationId: number,
    profileId: string
  ): Promise<ProfileWithJoins | null> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select(
        `
          id,
          created_at,
          updated_at,
          email,
          img_url,
          name,
          name_lower_case,
          name_normalized,
          onboarded,
          tax_id_number,
          organizations_profiles!inner(*, get_profile_status_field),
          profile_org_start_date(*),
          profile_org_variable_salary(*),
          teams_profiles(profile_id, organization_id, team_id, role, full_view_access, teams(id, name, organization_id))
        `
      )
      .eq('id', profileId)
      .eq('organizations_profiles.organization_id', organizationId)
      .eq('organizations_profiles.archived', false)
      .maybeSingle()

    if (error) {
      throw error
    }

    if (data == null) {
      return null
    }

    const profileForEdit = data as unknown as ProfileForEditRow

    return {
      ...(profileForEdit as unknown as Omit<
        ProfileWithJoins,
        | 'deals'
        | 'organizations_profiles'
        | 'payouts'
        | 'profile_org_start_date'
        | 'profile_org_variable_salary'
        | 'statements'
        | 'teams'
        | 'teams_profiles'
      >),
      organizations_profiles: (profileForEdit.organizations_profiles ?? []).filter(
        organizationProfile => organizationProfile.organization_id === organizationId
      ),
      profile_org_start_date: (profileForEdit.profile_org_start_date ?? []).filter(
        startDate => startDate.organization_id === organizationId
      ),
      profile_org_variable_salary: (profileForEdit.profile_org_variable_salary ?? []).filter(
        variableSalary => variableSalary.organization_id === organizationId
      ),
      teams: null,
      teams_profiles: (
        (profileForEdit.teams_profiles ?? []) as NonNullable<ProfileWithJoins['teams_profiles']>
      ).filter(
        teamProfile =>
          teamProfile.organization_id === organizationId && teamProfile.role === 'member'
      ),
      payouts: null,
      statements: null,
      deals: null
    }
  }

  async hasFrozenPayouts(compRule: Tables<'compensation_rules'>, item: RecordData) {
    const record = new DolfinRecord(compRule.source, item)

    const { data, error } =
      compRule.source === 'deal_line_item'
        ? await this.supabase.rpc('record_has_frozen_deal_line_item_payouts', {
            _compensation_rule_id: compRule.id,
            _deal_line_item_id: item.id
          })
        : await this.supabase.rpc('record_has_frozen_payouts', {
            _compensation_rule_id: compRule.id,
            _deal_id: record.getDealId(),
            _activity_id: record.getActivityId(),
            _invoice_id: record.getInvoiceId(),
            _line_item_id: record.getLineItemId(),
            _kpi_id: record.getKpiId(),
            _custom_record_id: record.getCustomRecordId()
          })

    if (error) {
      console.error('Error getting frozen payouts', error)
      return true
    }

    if (data == null) {
      return true
    }

    return Boolean(data)
  }

  // TODO:
  // For comp rules based on team/global/company targets, sometimes does not return true
  // when there are frozen payouts, if current profile ID has not received a payout
  private getCompRuleTargetScope(compRule: Tables<'compensation_rules'>) {
    const compRuleWithScope = compRule as Tables<'compensation_rules'> & {
      global_target?: boolean | null
      company_target?: boolean | null
      scope?: string | null
      targetScope?: string | null
      target_scope?: string | null
      target_scope_type?: string | null
      team_target?: boolean | null
    }

    const targetScope =
      compRuleWithScope.target_scope ??
      compRuleWithScope.targetScope ??
      compRuleWithScope.target_scope_type ??
      compRuleWithScope.scope

    if (targetScope === 'team' || targetScope === 'global' || targetScope === 'company') {
      return targetScope
    }

    if (compRuleWithScope.team_target === true) {
      return 'team' as const
    }

    if (compRuleWithScope.global_target === true) {
      return 'global' as const
    }

    if (compRuleWithScope.company_target === true) {
      return 'company' as const
    }

    return null
  }

  async hasPeriodFrozenPayouts(
    compRule: Tables<'compensation_rules'>,
    profileId: string,
    period: { start: string; end: string }
  ) {
    const targetScope = this.getCompRuleTargetScope(compRule)
    const isSharedScope =
      targetScope === 'team' || targetScope === 'global' || targetScope === 'company'

    if (!isSharedScope) {
      const { data, error } = await this.supabase.rpc('period_has_frozen_payouts', {
        _compensation_rule_id: compRule.id,
        _profile_id: profileId,
        _period_start: period.start,
        _period_end: period.end
      })

      if (error) {
        console.error('Error getting frozen payouts', error)
        return true
      }

      if (data == null) {
        return true
      }

      return Boolean(data)
    }

    const { data: frozenPayouts, error: frozenPayoutsError } = await this.supabase
      .from('payouts')
      .select('id')
      .eq('compensation_rule_id', compRule.id)
      .eq('period_start', period.start)
      .eq('period_end', period.end)
      .in('status', ['validated', 'paid'])
      .limit(1)

    if (frozenPayoutsError) {
      console.error('Error getting frozen payouts', frozenPayoutsError)
      return true
    }

    if ((frozenPayouts?.length ?? 0) > 0) {
      return true
    }

    const { data: installmentPayouts, error: installmentPayoutsError } = await this.supabase
      .from('payouts')
      .select('id, payout_installments!inner(status)')
      .eq('compensation_rule_id', compRule.id)
      .eq('period_start', period.start)
      .eq('period_end', period.end)
      .eq('payout_installments.status', 'paid')
      .limit(1)

    if (installmentPayoutsError) {
      console.error('Error getting frozen payouts', installmentPayoutsError)
      return true
    }

    return (installmentPayouts?.length ?? 0) > 0
  }

  async getMetrics(
    source: Enums<'plan_source'>,
    sourceTableId: number | undefined,
    organizationId: number
  ) {
    const query = this.supabase
      .from('metrics')
      .select('*')
      .eq('source', source)
      .eq('organization_id', organizationId)

    if (source === 'kpi') {
      query.eq('kpis_table_id', sourceTableId!)
    } else if (source === 'custom_record') {
      query.eq('custom_record_table_id', sourceTableId!)
    }

    const { data, error } = await query

    if (error) throw error

    return data ?? []
  }

  async getMetricById(metricId: number, organizationId?: number) {
    let query = this.supabase.from('metrics').select('*').eq('id', metricId)
    if (organizationId !== undefined) {
      query = query.eq('organization_id', organizationId)
    }

    const { data, error } = await query.maybeSingle()

    if (error) throw error
    return data
  }

  async getNonArchivedMetrics(
    organizationId: number,
    options?: {
      mode?: 'not_archived' | 'not_archived_yet'
      referenceDate?: string
    }
  ) {
    const mode = options?.mode ?? 'not_archived'
    const query = this.supabase.from('metrics').select('*').eq('organization_id', organizationId)

    if (mode === 'not_archived_yet') {
      query.or(
        `archived_at.is.null,archived_at.gte.${
          options?.referenceDate ?? format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx")
        }`
      )
    } else {
      query.is('archived_at', null)
    }

    const { data, error } = await query

    if (error) {
      throw error
    }

    return data ?? []
  }

  async getFormulaMetrics(organizationId: number) {
    const query = this.supabase
      .from('metrics')
      .select('*')
      .is('source', null)
      .eq('organization_id', organizationId)

    const { data, error } = await query

    if (error) throw error

    return data ?? []
  }

  async getActiveCreditSetupRulesForMetric(
    sourceMetricId: number,
    organizationId: number
  ): Promise<CreditSetupRuleForMetric[]> {
    const { data, error } = await this.supabase
      .from('credit_setup_rules')
      .select(
        'id, credit_setup_id, value_filters, archived_at, credit_setups!inner(id, source_metric_id, archived_at, metrics!inner(organization_id))'
      )
      .eq('credit_setups.metrics.organization_id', organizationId)
      .eq('credit_setups.source_metric_id', sourceMetricId)
      .is('archived_at', null)
      .is('credit_setups.archived_at', null)

    if (error) throw error
    return (data ?? []) as unknown as CreditSetupRuleForMetric[]
  }

  async getActiveCreditSetupRulesForMetrics(
    sourceMetricIds: number[],
    organizationId: number
  ): Promise<CreditSetupRuleForMetric[]> {
    if (sourceMetricIds.length === 0) return []

    const { data, error } = await this.supabase
      .from('credit_setup_rules')
      .select(
        'id, credit_setup_id, value_filters, archived_at, credit_setups!inner(id, source_metric_id, archived_at, metrics!inner(organization_id))'
      )
      .eq('credit_setups.metrics.organization_id', organizationId)
      .in('credit_setups.source_metric_id', sourceMetricIds)
      .is('archived_at', null)
      .is('credit_setups.archived_at', null)

    if (error) throw error
    return (data ?? []) as unknown as CreditSetupRuleForMetric[]
  }

  async getCreditSetupSourceMetricIdsWithHistory(
    sourceMetricIds: number[],
    organizationId: number
  ): Promise<number[]> {
    if (sourceMetricIds.length === 0) return []

    const { data, error } = await this.supabase
      .from('credit_setups')
      .select('source_metric_id, metrics!inner(organization_id)')
      .eq('metrics.organization_id', organizationId)
      .in('source_metric_id', sourceMetricIds)

    if (error) throw error

    return [
      ...new Set(
        (data ?? []).map(row => {
          const setup = row as unknown as { source_metric_id: number }
          return setup.source_metric_id
        })
      )
    ]
  }

  async getCreditSetupRuleMetricValueBootstrapStatus(
    organizationId: number,
    sourceMetricIds: number[]
  ): Promise<CreditSetupRuleMetricValueBootstrapStatus[]> {
    if (sourceMetricIds.length === 0) return []

    const { data, error } = await this.supabase.rpc(
      'get_credit_setup_rule_metric_value_bootstrap_status',
      {
        _organization_id: organizationId,
        _source_metric_ids: sourceMetricIds
      }
    )

    if (error) throw error
    return (data as CreditSetupRuleMetricValueBootstrapStatus[]) ?? []
  }

  async enqueueMetricRecordsPendingProcessing(metricId: number): Promise<void> {
    const { error } = await this.supabase.rpc('set_metric_records_pending_processing', {
      _metric_id: metricId
    })

    if (error) throw error
  }

  async getCreditSetupRuleMetricValueScope(
    organizationId: number,
    sourceMetricId: number,
    attributionKeys: string[]
  ): Promise<CreditSetupRuleMetricValueScopeRow[]> {
    if (attributionKeys.length === 0) {
      return []
    }

    const { data, error } = await this.supabase.rpc('get_credit_setup_rule_metric_value_scope', {
      _organization_id: organizationId,
      _source_metric_id: sourceMetricId,
      _attribution_keys: attributionKeys
    })

    if (error) {
      throw new Error(error.message)
    }

    return (data as CreditSetupRuleMetricValueScopeRow[]) ?? []
  }

  async getCreditedMetricValues(
    requests: CreditedMetricValueRequest[]
  ): Promise<CreditedMetricValueRow[]> {
    if (requests.length === 0) return []

    const startedAt = performance.now()
    const callSites = [
      ...new Set(requests.map(request => request.callSite).filter(Boolean))
    ] as string[]
    const uniqueContextCount = new Set(
      requests.map(request =>
        [
          request.organizationId,
          request.metricId,
          request.creditSetupId,
          request.profileId ?? '',
          request.startDate,
          request.endDate
        ].join(':')
      )
    ).size

    const emitTelemetry = (telemetry: CreditedMetricValueTelemetry) => {
      if (process.env.DOLFIN_CREDIT_SETUP_RPC_TELEMETRY !== '1') return
      console.info('[credited-metric-values]', telemetry)
    }

    try {
      const functionName = requests.every(request => request.callSite === 'target_summary')
        ? 'get_credited_target_metric_values'
        : 'get_credited_metric_values'
      const args = {
        _requests: requests.map(request => ({
          organization_id: request.organizationId,
          metric_id: request.metricId,
          credit_setup_id: request.creditSetupId,
          profile_id: request.profileId,
          team_ids: request.teamIds,
          ...(request.valueProfileIds !== undefined
            ? { value_profile_ids: request.valueProfileIds ?? [] }
            : {}),
          ...(request.valueTeamIds ? { value_team_ids: request.valueTeamIds } : {}),
          ...(request.sourceIdLimitOnLastDay != null
            ? { source_id_limit_on_last_day: request.sourceIdLimitOnLastDay }
            : {}),
          ...(request.teamMemberships
            ? {
                team_memberships: request.teamMemberships.map(membership => ({
                  team_id: membership.teamId,
                  effective_from: membership.effectiveFrom,
                  effective_to: membership.effectiveTo
                }))
              }
            : {}),
          ...(request.targetCurrency ? { target_currency: request.targetCurrency } : {}),
          start_date: request.startDate,
          end_date: request.endDate
        }))
      }
      const rows: CreditedMetricValueRow[] = []
      // Request counts do not bound result rows. Keep fetching even after a
      // short page: the server's row cap may be smaller than our page size.
      const rpcQuery = this.supabase.rpc(functionName, args)
      type PaginatedCreditedMetricQuery = {
        order: (column: string, options: { ascending: boolean }) => PaginatedCreditedMetricQuery
        range: (
          from: number,
          to: number
        ) => PromiseLike<{
          data: CreditedMetricValueRow[] | null
          error: { message: string } | null
        }>
      }
      const paginatedQuery = rpcQuery as unknown as Partial<PaginatedCreditedMetricQuery>
      if (typeof paginatedQuery.range !== 'function') {
        const { data, error } = await rpcQuery
        if (error) throw new Error(error.message)
        rows.push(...((data as CreditedMetricValueRow[] | null) ?? []))
      } else {
        const orderedQuery = (paginatedQuery as PaginatedCreditedMetricQuery)
          .order('request_id', { ascending: true })
          .order('accrual_date', { ascending: true })
          .order('metric_value_id', { ascending: true })
          .order('assignment_id', { ascending: true })
        for (;;) {
          const { data, error } = await orderedQuery.range(rows.length, rows.length + 999)
          if (error) throw new Error(error.message)
          if (!data?.length) break
          rows.push(...(data as CreditedMetricValueRow[]))
        }
      }
      emitTelemetry({
        callSites,
        durationMs: performance.now() - startedAt,
        requestCount: requests.length,
        resultRowCount: rows.length,
        uniqueContextCount
      })
      return rows
    } catch (error) {
      emitTelemetry({
        callSites,
        durationMs: performance.now() - startedAt,
        error: error instanceof Error ? error.message : String(error),
        requestCount: requests.length,
        resultRowCount: 0,
        uniqueContextCount
      })
      throw error
    }
  }

  async getCreditedTargetMetricValueAggregates(
    requests: CreditedMetricValueRequest[]
  ): Promise<CreditedTargetMetricValueAggregate[]> {
    if (requests.length === 0) return []
    const { data, error } = await this.supabase.rpc('get_credited_target_metric_value_aggregates', {
      _requests: requests.map(request => ({
        organization_id: request.organizationId,
        metric_id: request.metricId,
        credit_setup_id: request.creditSetupId,
        profile_id: request.profileId,
        team_ids: request.teamIds,
        ...(request.teamMemberships
          ? {
              team_memberships: request.teamMemberships.map(membership => ({
                team_id: membership.teamId,
                effective_from: membership.effectiveFrom,
                effective_to: membership.effectiveTo
              }))
            }
          : {}),
        ...(request.targetCurrency ? { target_currency: request.targetCurrency } : {}),
        aggregate_function: request.aggregateFunction ?? 'sum',
        rule_segments: (request.ruleSegments ?? []).map(segment => ({
          credit_setup_rule_id: segment.creditSetupRuleId,
          effective_from: segment.effectiveFrom,
          effective_to: segment.effectiveTo
        })),
        start_date: request.startDate,
        end_date: request.endDate
      }))
    })
    if (error) throw new Error(error.message)
    return (data as CreditedTargetMetricValueAggregate[]) ?? []
  }

  async saveMetricValues(
    source: Enums<'plan_source'>,
    values: TablesInsert<'metric_values'>[]
  ): Promise<Error | Tables<'metric_values'>[]> {
    void source
    const { data, error } = await this.supabase
      .from('metric_values')
      .upsert(values, { onConflict: 'metric_id, attribution_key' })
      .select()

    if (error) {
      console.error('Error saving metric values', error)
      return new Error(error.message)
    }

    return data ?? []
  }

  async reconcileCreditSetupRuleMetricValues(
    reconciliations: CreditSetupRuleMetricValueReconciliation[],
    affectedScope: PeriodRulesAffectedScopeRow[] = []
  ): Promise<number> {
    const args: DBFunctions['reconcile_credit_setup_rule_metric_values']['Args'] = {
      _reconciliations: reconciliations,
      _affected_scope: affectedScope
    }

    const { data, error } = await this.supabase.rpc(
      'reconcile_credit_setup_rule_metric_values',
      args
    )

    if (error) {
      throw new Error(error.message)
    }

    return Number(data ?? 0)
  }

  async deleteMetricValues(values: MetricValueToDelete[]) {
    const { error } = await this.supabase.rpc('delete_metric_values', {
      _values: values
    })

    if (error) {
      console.error('Error deleting metric values', error)
      return new Error(error.message)
    }
  }

  async deleteCrmRecordsAndReferences(params: {
    organizationId: number
    tableName: string
    recordIds: string[]
  }): Promise<void> {
    const { error } = await this.supabase.rpc('delete_crm_records_and_references', {
      _organization_id: params.organizationId,
      _table_name: params.tableName,
      _record_ids: params.recordIds
    })

    if (error) {
      throw new Error(error.message)
    }
  }

  async getPayouts(
    source: Enums<'plan_source'>,
    recordId: number,
    compRuleId: number,
    attributionKey?: string
  ): Promise<Tables<'payouts'>[]> {
    const recordField = getRecordForeignKeyField(source)

    let query = this.supabase.from('payouts').select('*').eq('compensation_rule_id', compRuleId)

    if (attributionKey) {
      query = query.eq('attribution_key', attributionKey)
    } else {
      query = query.eq(recordField, recordId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error getting payouts', error)
    }

    return (data ?? []) as unknown as Tables<'payouts'>[]
  }

  async getPayoutsForSimulation(
    args: Parameters<DolfinRpc['getPayoutsForSimulation']>[0]
  ): Promise<Tables<'payouts'>[]> {
    let query = this.supabase
      .from('payouts')
      .select(
        '*, executions(path), payout_installments(*), deals(*), activities(*, deals(*)), invoices(*, deals(*)), line_items(*, invoices(*, deals(*))), deal_line_items(*, deals(*)), kpis(*)'
      )

    if (args.organizationId != null) {
      query = query.eq('organization_id', args.organizationId)
    }

    if ((args.compensationRuleIds?.length ?? 0) === 1) {
      query = query.eq('compensation_rule_id', args.compensationRuleIds![0])
    } else if ((args.compensationRuleIds?.length ?? 0) > 1) {
      query = query.in('compensation_rule_id', args.compensationRuleIds!)
    }

    if ((args.profileIds?.length ?? 0) === 1) {
      query = query.eq('profile_id', args.profileIds![0])
    } else if ((args.profileIds?.length ?? 0) > 1) {
      query = query.in('profile_id', args.profileIds!)
    }

    if (args.effectiveDateStart) {
      query = query.gte('effective_date', args.effectiveDateStart)
    }

    if (args.effectiveDateEnd) {
      query = query.lte('effective_date', args.effectiveDateEnd)
    }

    const sourceFilters = Object.entries(args.entityIdsBySource ?? {}).flatMap(([source, ids]) => {
      if (!ids || ids.length === 0) return []

      const field = getRecordForeignKeyField(source as Enums<'plan_source'>)

      return `${field}.in.(${ids.join(',')})`
    })

    if (sourceFilters.length > 0) {
      query = query.or(sourceFilters.join(','))
    }

    query = query
      .order('effective_date', { ascending: true })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })
    const payouts: Tables<'payouts'>[] = []
    let start = 0

    while (true) {
      const end = start + PAYOUTS_FOR_SIMULATION_PAGE_SIZE - 1
      const { data, error } = await query.range(start, end)

      if (error) {
        throw error
      }

      const page = (data ?? []) as unknown as Tables<'payouts'>[]

      if (page.length === 0) {
        break
      }

      payouts.push(...page)
      start += PAYOUTS_FOR_SIMULATION_PAGE_SIZE
    }

    return payouts
  }

  async getCompensationRulesByIds(
    compensationRuleIds: number[]
  ): Promise<
    Pick<
      Tables<'compensation_rules'>,
      'deal_resolution_config' | 'deal_resolution_strategy' | 'id' | 'source'
    >[]
  > {
    if (compensationRuleIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('compensation_rules')
      .select('id, source, deal_resolution_strategy, deal_resolution_config')
      .in('id', compensationRuleIds)

    if (error) {
      throw error
    }

    return (data ?? []) as Pick<
      Tables<'compensation_rules'>,
      'deal_resolution_config' | 'deal_resolution_strategy' | 'id' | 'source'
    >[]
  }

  async getProfilesByIds(
    profileIds: string[]
  ): Promise<Pick<Tables<'profiles'>, 'email' | 'id' | 'img_url' | 'name'>[]> {
    if (profileIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('profiles')
      .select('id, email, img_url, name')
      .in('id', profileIds)

    if (error) {
      throw error
    }

    return (data ?? []) as Pick<Tables<'profiles'>, 'email' | 'id' | 'img_url' | 'name'>[]
  }

  async getPayoutById(payoutId: number, organizationId: number): Promise<Tables<'payouts'> | null> {
    const { data, error } = await this.supabase
      .from('payouts')
      .select('*')
      .eq('id', payoutId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return (data ?? null) as Tables<'payouts'> | null
  }

  async getOrInsertStatement(params: {
    organizationId: number
    profileId: string
    periodStart: string
  }): Promise<number> {
    const { data, error } = await this.supabase.rpc('get_or_insert_statement', {
      _organization_id: params.organizationId,
      _period_start: params.periodStart,
      _profile_id: params.profileId
    })

    if (error) throw error

    return Number(data)
  }

  async getPayoutCategoryById(
    categoryId: number,
    organizationId: number
  ): Promise<Pick<Tables<'payout_categories'>, 'id' | 'organization_id'> | null> {
    const { data, error } = await this.supabase
      .from('payout_categories')
      .select('id, organization_id')
      .eq('id', categoryId)
      .eq('organization_id', organizationId)
      .maybeSingle()
    if (error) throw error
    return data
  }

  async upsertManualPayout(payload: ManualPayoutUpsertPayload): Promise<Tables<'payouts'>> {
    const normalizedPayload = {
      ...payload,
      manual_exchange_rate_from_currency: payload.manual_exchange_rate_from_currency ?? null,
      manual_exchange_rate_method: payload.manual_exchange_rate_method ?? null,
      manual_exchange_rate_source: payload.manual_exchange_rate_source ?? null
    }

    if (payload.id != null) {
      const { data, error } = await this.supabase
        .from('payouts')
        .update(normalizedPayload)
        .eq('id', payload.id)
        .eq('organization_id', payload.organization_id)
        .select('*')
        .single()

      if (error) throw error

      return data as Tables<'payouts'>
    }

    const { data, error } = await this.supabase
      .from('payouts')
      .insert(normalizedPayload)
      .select('*')
      .single()

    if (error) throw error

    return data as Tables<'payouts'>
  }

  async getStatementMonthRows(params: GetStatementMonthRowsParams): Promise<StatementMonthRow[]> {
    if (params.managedTeamIds?.length === 0) return []

    const ownerColumns =
      params.managedTeamIds === null
        ? 'owner:profiles!statements_profile_id_fkey(id, name, email)'
        : 'owner:profiles!statements_profile_id_fkey!inner(id, name, email, teams_profiles!inner())'
    let query = this.supabase
      .from('statements')
      .select(`${STATEMENT_READ_COLUMNS}, ${ownerColumns}`)
      .eq('organization_id', params.organizationId)
      .gte('period_start', params.startDate)
      .lt('period_start', params.endDate)
      .order('id', { ascending: true })
      .limit(params.limit)

    if (params.managedTeamIds !== null) {
      query = query
        .eq('owner.teams_profiles.organization_id', params.organizationId)
        .eq('owner.teams_profiles.role', 'member')
        .in('owner.teams_profiles.team_id', params.managedTeamIds)
    }
    if (params.afterId !== undefined) query = query.gt('id', params.afterId)

    const { data, error } = await query
    if (error) throw error
    return ((data ?? []) as unknown as StatementReadRow[]).map(mapStatementReadRow)
  }

  async getStatementReadRow(
    statementId: number,
    organizationId: number
  ): Promise<StatementMonthRow | null> {
    const { data, error } = await this.supabase
      .from('statements')
      .select(
        `${STATEMENT_READ_COLUMNS}, owner:profiles!statements_profile_id_fkey(id, name, email)`
      )
      .eq('id', statementId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return data ? mapStatementReadRow(data as unknown as StatementReadRow) : null
  }

  async getStatementPayoutTotal(params: {
    organizationId: number
    profileId: string
    periodStart: string
  }): Promise<number> {
    const { data, error } = await this.supabase.rpc('get_user_view_payouts_sum', {
      _organization_id: params.organizationId,
      _profile_id: params.profileId,
      _start_date: params.periodStart,
      _by_statement: true
    })
    if (error) throw error
    const total = (data as { total_in_organization_currency?: number } | null)
      ?.total_in_organization_currency
    if (typeof total !== 'number' || !Number.isFinite(total)) {
      throw new Error('Statement total is unavailable')
    }
    return total
  }

  async getStatementReadRelations(
    statementId: number,
    organizationId: number
  ): Promise<StatementReadRelations> {
    const [payouts, payoutInstallments, approvals, issues] = await Promise.all([
      fetchAllMrrRows(
        this.supabase
          .from('payouts')
          .select('*, execution:executions!payouts_execution_id_fkey(created_at)')
          .eq('statement_id', statementId)
          .eq('organization_id', organizationId)
          .order('id', { ascending: true })
      ),
      fetchAllMrrRows(
        this.supabase
          .from('payout_installments')
          .select('*, payouts!inner(*, execution:executions!payouts_execution_id_fkey(created_at))')
          .eq('statement_id', statementId)
          .eq('payouts.organization_id', organizationId)
          .order('id', { ascending: true })
      ),
      fetchAllMrrRows(
        this.supabase
          .from('statement_approvals')
          .select(
            '*, approver:profiles!public_statement_approvals_profile_id_fkey(id, name, email), statements!inner()'
          )
          .eq('statement_id', statementId)
          .eq('statements.organization_id', organizationId)
          .order('profile_id', { ascending: true })
      ),
      fetchAllMrrRows(
        this.supabase
          .from('issues')
          .select('*')
          .eq('statement_id', statementId)
          .eq('organization_id', organizationId)
          .order('id', { ascending: true })
      )
    ])
    return { payouts, payoutInstallments, approvals, issues } as StatementReadRelations
  }

  async getStatementReadChanges(
    statementId: number,
    organizationId: number,
    payoutIds: number[]
  ): Promise<StatementReadChanges> {
    const updatesQuery = () =>
      this.supabase
        .from('payout_discrepancies')
        .select(this.updatesOnHoldReviewSelect)
        .eq('organization_id', organizationId)
        .eq('reviewed', false)
        .order('id', { ascending: true })
    const linkedUpdates = await fetchAllMrrRows(
      updatesQuery().or(
        `existing_statement_id.eq.${statementId},calculated_statement_id.eq.${statementId}`
      )
    )
    const updates = new Map<number, UpdateOnHoldReviewRow>(
      (linkedUpdates as unknown as UpdateOnHoldReviewRow[]).map(row => [row.id, row])
    )
    const adjustments: PayoutManualAdjustmentRow[] = []
    const audits: PayoutAnalyticsAdjustmentAudit[] = []

    for (const ids of batched(new Set(payoutIds), 100)) {
      const [payoutAdjustments, payoutUpdates] = await Promise.all([
        fetchAllMrrRows(
          this.supabase
            .from('payout_adjustments')
            .select(
              'id, payout_id, amount, created_at, profiles(id, name, email, img_url), payouts!inner()'
            )
            .eq('payouts.organization_id', organizationId)
            .in('payout_id', ids)
            .order('created_at', { ascending: true })
            .order('id', { ascending: true })
        ),
        fetchAllMrrRows(updatesQuery().in('payout_id', ids))
      ])
      adjustments.push(...payoutAdjustments)
      for (const update of payoutUpdates as unknown as UpdateOnHoldReviewRow[]) {
        updates.set(update.id, update)
      }
      if (payoutAdjustments.length === 0) continue

      audits.push(
        ...(await fetchAllMrrRows(
          this.supabase
            .from('payouts_audit_log')
            .select('old_data, new_data, changed_at')
            .eq('organization_id', organizationId)
            .eq('operation_type', 'UPDATE')
            .in('old_data->>id', [...new Set(payoutAdjustments.map(row => String(row.payout_id)))])
            .order('id', { ascending: true })
        ))
      )
    }
    return {
      manualAdjustmentHistory: buildPayoutAdjustmentBreakdowns(adjustments, audits),
      updatesOnHold: [...updates.values()].sort((left, right) => left.id - right.id)
    }
  }

  async getStatementById(
    statementId: number,
    organizationId: number
  ): Promise<Tables<'statements'> | null> {
    const { data, error } = await this.supabase
      .from('statements')
      .select('*')
      .eq('id', statementId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return (data ?? null) as Tables<'statements'> | null
  }

  async getIssueById(issueId: number, organizationId: number): Promise<Tables<'issues'> | null> {
    const { data, error } = await this.supabase
      .from('issues')
      .select('*')
      .eq('id', issueId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return (data ?? null) as Tables<'issues'> | null
  }

  async getPayoutDiscrepancyById(
    discrepancyId: number,
    organizationId: number
  ): Promise<Tables<'payout_discrepancies'> | null> {
    const { data, error } = await this.supabase
      .from('payout_discrepancies')
      .select('*')
      .eq('id', discrepancyId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) throw error
    return (data ?? null) as Tables<'payout_discrepancies'> | null
  }

  async getOrganizationsWithWithheldPayouts(): Promise<number[]> {
    const { data, error } = await this.supabase.rpc(
      'get_organizations_with_pending_withheld_payouts'
    )

    if (error) {
      console.error('Error getting organizations', error)
      return []
    }

    return data ?? []
  }

  async getWithheldPayouts(
    limit: number,
    offset: number,
    organizationId?: number,
    minimumCheckAgeMs = DEFAULT_WITHHELD_PAYOUT_MIN_CHECK_AGE_MS
  ) {
    const sanitizedMinimumCheckAgeMs =
      Number.isFinite(minimumCheckAgeMs) && minimumCheckAgeMs >= 0
        ? minimumCheckAgeMs
        : DEFAULT_WITHHELD_PAYOUT_MIN_CHECK_AGE_MS
    const lastReleaseCheckCutoff = new Date(Date.now() - sanitizedMinimumCheckAgeMs).toISOString()

    let payoutsQuery = this.supabase
      .from('payouts')
      .select(
        '*, payout_installments(*), deals(*), activities(*, deals(*)), invoices(*, deals(*)), line_items(*, invoices(*, deals(*))), deal_line_items(*, deals(*)), kpis(*), executions(*), compensation_rules(deal_resolution_strategy, deal_resolution_config)'
      )
      .is('statement_id', null)
      .neq('status', 'expired')
      .eq('is_withheld', true)
      .is('payout_installments.statement_id', null)
      .neq('payout_installments.status', 'expired')
      .or(`last_release_check_at.is.null,last_release_check_at.lte.${lastReleaseCheckCutoff}`)
      .order('last_release_check_at', { ascending: true, nullsFirst: true })
      .order('effective_date', { ascending: true })
      .range(offset, offset + limit - 1)
    if (organizationId !== undefined) {
      payoutsQuery = payoutsQuery.eq('organization_id', organizationId)
    }

    const { data, error } = await payoutsQuery

    if (error) {
      console.error('Error getting withheld payouts', error)
    }

    return (data ?? []) as PayoutWithSource[]
  }

  async getPayoutReleaseContext(
    payoutId: number,
    organizationId: number
  ): Promise<PayoutReleaseContext | null> {
    const { data, error } = await this.supabase
      .from('payouts')
      .select(
        '*, payout_installments(*), deals(*), activities(*, deals(*)), invoices(*, deals(*)), line_items(*, invoices(*, deals(*))), deal_line_items(*, deals(*)), kpis(*), executions(*), compensation_rules(source, deal_resolution_strategy, deal_resolution_config)'
      )
      .eq('id', payoutId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) {
      console.error('Error getting payout release context', error)
      throw error
    }

    return (data ?? null) as PayoutReleaseContext | null
  }

  async markPayoutsChecked(payoutIds: number[]) {
    if (payoutIds.length === 0) return

    const { error } = await this.supabase
      .from('payouts')
      .update({ last_release_check_at: new Date().toISOString() })
      .in('id', payoutIds)

    if (error) {
      throw error
    }
  }

  async updateReleasedPayouts(payouts: PayoutRelease[]) {
    const { error } = await this.supabase.rpc('update_released_payouts', {
      _payouts: payouts
    })

    if (error) {
      console.error('Error updating released payouts', error)
      return new Error(error.message)
    }
  }

  async getPreviousPayouts(
    profileId: string,
    periodStart: string,
    compRuleId: number,
    ltbPeriodStart: string
  ): Promise<Tables<'payouts'>[]> {
    const { data, error } = await this.supabase
      .from('payouts')
      .select('*')
      .eq('profile_id', profileId)
      .eq('compensation_rule_id', compRuleId)
      .lt('period_start', periodStart)
      .gte('period_start', ltbPeriodStart)

    if (error) {
      console.error(error)
    }

    return (data ?? []) as Tables<'payouts'>[]
  }

  async getOrgCurrency(organizationId: number) {
    const { data, error } = await this.supabase
      .from('organizations')
      .select('currency')
      .eq('id', organizationId)

    if (error) throw error

    return (
      data?.[0] ?? {
        currency: 'EUR'
      }
    )
  }

  async searchOrganizationProfiles(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationProfileSearchResult[]> {
    const normalizedQuery = params.query.trim()
    if (!normalizedQuery) {
      return []
    }

    const normalizedQueryTokens = normalizedQuery.split(/\s+/).filter(Boolean)
    const escapedNormalizedQuery = escapeOrLikePattern(normalizedQuery)
    const escapedNormalizedQueryTokens = normalizedQueryTokens.map(token =>
      escapeOrLikePattern(token)
    )
    const searchPattern = `"%${escapedNormalizedQuery}%"`
    const tokenSearchPattern = `"%${escapedNormalizedQueryTokens.join('%')}%"`
    const { data, error } = await this.supabase
      .from('profiles')
      .select('id, name, email, organizations_profiles!inner(organization_id)')
      .eq('organizations_profiles.organization_id', params.organizationId)
      .or(
        `name.ilike.${searchPattern},name.ilike.${tokenSearchPattern},email.ilike.${searchPattern}`
      )
      .limit(params.limit)

    if (error) {
      throw error
    }

    return (data ?? []).map(profile => ({
      id: profile.id,
      name: profile.name,
      email: profile.email
    }))
  }
  private async getActiveOrganizationUserCounts(
    organizationId?: number
  ): Promise<Map<number, number>> {
    // PostgREST caps responses at 1,000 rows without explicit pagination.
    const now = new Date()
    const referenceDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      .toISOString()
      .slice(0, 10)
    if (organizationId != null) {
      const cachedCount = getCachedActiveOrganizationUserCount(
        this.activeOrganizationUserCountCache,
        organizationId,
        referenceDate
      )
      if (cachedCount != null) {
        return new Map([[organizationId, cachedCount]])
      }
    }

    const membershipsQuery = this.supabase
      .from('organizations_profiles')
      .select('organization_id, profile_id, role')
      .eq('archived', false)
      .order('organization_id', { ascending: true })
      .order('profile_id', { ascending: true })
    const profileStatusEventsQuery = this.supabase
      .from('profile_status_events')
      .select('organization_id, profile_id, event_date, status')
      .lte('event_date', referenceDate)
      .order('organization_id', { ascending: true })
      .order('profile_id', { ascending: true })
      .order('event_date', { ascending: false })
    const teamMembershipsQuery = this.supabase
      .from('teams_profiles')
      .select('organization_id, profile_id, team_id, role')
      .order('organization_id', { ascending: true })
      .order('team_id', { ascending: true })
      .order('profile_id', { ascending: true })
    const teamsQuery = this.supabase
      .from('teams')
      .select('id, organization_id')
      .is('archived_at', null)
      .order('id', { ascending: true })

    if (organizationId != null) {
      membershipsQuery.eq('organization_id', organizationId)
      profileStatusEventsQuery.eq('organization_id', organizationId)
      teamMembershipsQuery.eq('organization_id', organizationId)
      teamsQuery.eq('organization_id', organizationId)
    }

    const [memberships, profileStatusEvents, teamMemberships, teams] = await Promise.all([
      fetchAllMrrRows(membershipsQuery),
      fetchAllMrrRows(profileStatusEventsQuery),
      fetchAllMrrRows(teamMembershipsQuery),
      fetchAllMrrRows(teamsQuery)
    ])

    const activeTeamIds = new Set(teams.map(team => team.id))
    const latestProfileStatusByOrganizationId = new Map<
      number,
      Map<string, { eventDate: string; status: string }>
    >()

    for (const event of profileStatusEvents) {
      const statuses =
        latestProfileStatusByOrganizationId.get(event.organization_id) ??
        new Map<string, { eventDate: string; status: string }>()
      const current = statuses.get(event.profile_id)

      if (!current || event.event_date > current.eventDate) {
        statuses.set(event.profile_id, {
          eventDate: event.event_date,
          status: event.status
        })
      }

      latestProfileStatusByOrganizationId.set(event.organization_id, statuses)
    }

    const eligibleProfileIdsByOrganizationId = new Map<number, Set<string>>()

    for (const membership of memberships) {
      if (membership.role === 'admin') continue
      if (
        latestProfileStatusByOrganizationId
          .get(membership.organization_id)
          ?.get(membership.profile_id)?.status !== 'active'
      ) {
        continue
      }

      const profileIds =
        eligibleProfileIdsByOrganizationId.get(membership.organization_id) ?? new Set<string>()
      profileIds.add(membership.profile_id)
      eligibleProfileIdsByOrganizationId.set(membership.organization_id, profileIds)
    }

    const activeUserIdsByOrganizationId = new Map<number, Set<string>>()

    for (const teamMembership of teamMemberships) {
      if (teamMembership.role !== 'member') continue
      if (!activeTeamIds.has(teamMembership.team_id)) continue

      const eligibleProfileIds = eligibleProfileIdsByOrganizationId.get(
        teamMembership.organization_id
      )
      if (!eligibleProfileIds?.has(teamMembership.profile_id)) continue

      const profileIds =
        activeUserIdsByOrganizationId.get(teamMembership.organization_id) ?? new Set<string>()
      profileIds.add(teamMembership.profile_id)
      activeUserIdsByOrganizationId.set(teamMembership.organization_id, profileIds)
    }

    const counts = new Map(
      Array.from(activeUserIdsByOrganizationId, ([id, profileIds]) => [id, profileIds.size])
    )

    for (const [id, count] of counts) {
      cacheActiveOrganizationUserCount(
        this.activeOrganizationUserCountCache,
        id,
        count,
        referenceDate
      )
    }

    if (organizationId != null && !counts.has(organizationId)) {
      cacheActiveOrganizationUserCount(
        this.activeOrganizationUserCountCache,
        organizationId,
        0,
        referenceDate
      )
    }

    return counts
  }

  async getActiveOrganizationUserCount(organizationId: number): Promise<number> {
    const userCounts = await this.getActiveOrganizationUserCounts(organizationId)
    return userCounts.get(organizationId) ?? 0
  }

  async getAdminOrganizationMrrOverview(): Promise<AdminOrganizationMrrOverviewRow[]> {
    const organizationsQuery = this.supabase
      .from('organizations')
      .select('id, name')
      .order('name', { ascending: true })
      .order('id', { ascending: true })
    const settingsQuery = this.supabase
      .from('organization_mrr_settings')
      .select('*')
      .order('organization_id', { ascending: true })

    const [organizations, activeUserCounts, settings] = await Promise.all([
      fetchAllMrrRows(organizationsQuery),
      this.getActiveOrganizationUserCounts(),
      fetchAllMrrRows(settingsQuery)
    ])

    const settingsByOrganizationId = new Map(
      (settings as Tables<'organization_mrr_settings'>[]).map(setting => [
        setting.organization_id,
        setting
      ])
    )

    return organizations.map(organization => {
      const setting = settingsByOrganizationId.get(organization.id)
      const included = setting?.included ?? false
      const pricePerSeat = Number(setting?.price_per_seat ?? 0)
      const seatCount = activeUserCounts.get(organization.id) ?? 0

      return {
        id: organization.id,
        name: organization.name,
        currency: ADMIN_ORGANIZATION_MRR_CURRENCY,
        seatCount,
        included,
        pricePerSeat,
        mrr: included ? seatCount * pricePerSeat : 0
      }
    })
  }

  async saveAdminOrganizationMrrSetting(
    params: AdminOrganizationMrrSettingInput
  ): Promise<AdminOrganizationMrrSetting> {
    const { data, error } = await this.supabase
      .from('organization_mrr_settings')
      .upsert({
        organization_id: params.organizationId,
        included: params.included,
        price_per_seat: params.pricePerSeat,
        updated_at: new Date().toISOString()
      })
      .select('*')
      .single()

    if (error) throw error
    return data
  }

  async searchOrganizationTeams(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationTeamSearchResult[]> {
    const orFilter = buildOrganizationEntitySearchOrFilter('name', params.query)
    if (!orFilter) {
      return []
    }

    const query = this.supabase
      .from('teams')
      .select('id, name')
      .eq('organization_id', params.organizationId)
      .is('archived_at', null)
    const scopedQuery = query.or(orFilter)
    const { data, error } = await scopedQuery.limit(
      buildOrganizationEntitySearchPrefetchLimit(params.limit)
    )

    if (error) {
      throw error
    }

    return searchOrganizationEntities({
      rows: (data ?? []) as OrganizationTeamSearchResult[],
      query: params.query,
      limit: params.limit,
      getSearchText: row => row.name ?? ''
    })
  }

  async searchCreditSetupRuleAssignmentSubjects(params: {
    organizationId: number
    subjectType: 'profile' | 'team'
    query: string
    offset: number
    limit: number
  }): Promise<CreditSetupRuleAssignmentSubjectPage> {
    const queryText = params.query.trim()
    const rangeEnd = params.offset + params.limit

    if (params.subjectType === 'profile') {
      const escapedQuery = escapeOrLikePattern(queryText)
      const tokenPattern = queryText
        ? `%${queryText.split(/\s+/).filter(Boolean).map(escapeOrLikePattern).join('%')}%`
        : null
      let query = this.supabase
        .from('profiles')
        .select('id, name, email, organizations_profiles!inner(organization_id)')
        .eq('organizations_profiles.organization_id', params.organizationId)
        .order('name_normalized', { ascending: true })
        .order('id', { ascending: true })
        .range(params.offset, rangeEnd)

      if (queryText) {
        query = query.or(
          `name.ilike.%${escapedQuery}%,name.ilike.${tokenPattern},email.ilike.%${escapedQuery}%`
        )
      }

      const { data, error } = await query
      if (error) throw error
      const items = (data ?? []).slice(0, params.limit).map(profile => ({
        type: 'profile' as const,
        id: profile.id,
        name: profile.name,
        email: profile.email
      }))
      return {
        items,
        nextOffset: data?.length === params.limit + 1 ? params.offset + params.limit : null
      }
    }

    const escapedQuery = escapeOrLikePattern(queryText)
    let query = this.supabase
      .from('teams')
      .select('id, name, teams_profiles!inner(role, profiles(id, name, email, img_url))')
      .eq('organization_id', params.organizationId)
      .eq('teams_profiles.role', 'member')
      .is('archived_at', null)
      .order('name_normalized', { ascending: true })
      .order('id', { ascending: true })
      .range(params.offset, rangeEnd)

    if (queryText) query = query.ilike('name', `%${escapedQuery}%`)

    const { data, error } = await query
    if (error) throw error
    const items = (data ?? []).slice(0, params.limit).map(team => ({
      type: 'team' as const,
      id: team.id,
      name: team.name,
      memberCount: team.teams_profiles.length,
      members: team.teams_profiles
        .map(membership => membership.profiles)
        .filter((profile): profile is NonNullable<typeof profile> => profile != null)
        .slice(0, 10)
    }))
    return {
      items,
      nextOffset: data?.length === params.limit + 1 ? params.offset + params.limit : null
    }
  }

  async searchOrganizationPlans(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationPlanSearchResult[]> {
    const orFilter = buildOrganizationEntitySearchOrFilter('label', params.query)
    if (!orFilter) {
      return []
    }

    const query = this.supabase
      .from('plans')
      .select('id, label')
      .eq('organization_id', params.organizationId)
    const scopedQuery = query.or(orFilter)
    const { data, error } = await scopedQuery.limit(
      buildOrganizationEntitySearchPrefetchLimit(params.limit)
    )

    if (error) {
      throw error
    }

    return searchOrganizationEntities({
      rows: (data ?? []) as OrganizationPlanSearchResult[],
      query: params.query,
      limit: params.limit,
      getSearchText: row => row.label ?? ''
    })
  }

  async searchOrganizationTargets(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationTargetSearchResult[]> {
    const orFilter = buildOrganizationEntitySearchOrFilter('label', params.query)
    if (!orFilter) {
      return []
    }

    const query = this.supabase
      .from('targets')
      .select('id, label, plan_id')
      .eq('organization_id', params.organizationId)
      .is('archived_at', null)
    const scopedQuery = query.or(orFilter)
    const { data, error } = await scopedQuery.limit(
      buildOrganizationEntitySearchPrefetchLimit(params.limit)
    )

    if (error) {
      throw error
    }

    return searchOrganizationEntities({
      rows: (data ?? []) as OrganizationTargetSearchResult[],
      query: params.query,
      limit: params.limit,
      getSearchText: row => row.label ?? ''
    })
  }
  async searchCompensationRecords(
    params: CompensationRecordSearchParams
  ): Promise<CompensationRecordSearchMatch[]> {
    const query = params.query.trim()
    if (!query) {
      return []
    }
    const normalizedQuery = query.toLowerCase()

    const numericQuery = parseNumericRecordSearchQuery(query)
    const searchableColumnsByType: Partial<Record<CompensationRecordType, string[]>> = {
      deal: ['name', 'deal_id', 'deals_search_columns'],
      payout: ['description', 'payout_desc_deal_name', 'compensations_search_columns'],
      statement: ['statements_search_columns']
    }
    const textFilter = buildCompensationRecordSearchOrFilter(
      searchableColumnsByType[params.recordType] ?? [],
      query
    )
    const searchFilter = numericQuery
      ? [`id.eq.${numericQuery}`, textFilter].filter(Boolean).join(',')
      : textFilter
    const prefetchLimit = Math.max(params.limit * 5, 50)

    if (params.recordType === 'deal') {
      if (!searchFilter) {
        return []
      }

      let queryBuilder = this.supabase
        .from('deals')
        .select('id, name, deal_id, amount, currency, close_date, owner_id, deals_search_columns')
        .eq('organization_id', params.organizationId)
        .or(searchFilter)

      if (params.profileId) {
        queryBuilder = queryBuilder.eq('owner_id', params.profileId)
      }
      if (params.status) {
        queryBuilder = queryBuilder.eq('stage', params.status)
      }
      if (params.periodStart) {
        queryBuilder = queryBuilder.gte('close_date', params.periodStart)
      }
      if (params.periodEnd) {
        queryBuilder = queryBuilder.lt('close_date', params.periodEnd)
      }

      const { data, error } = await queryBuilder.limit(prefetchLimit)
      if (error) {
        throw error
      }

      const rows = (data ?? []) as Array<{
        id: number
        name: string | null
        deal_id: string
        amount: number
        currency: string | null
        close_date: string | null
        owner_id: string | null
        deals_search_columns: string | null
      }>
      const profiles = await this.getProfilesByIds(
        rows.flatMap(row => (row.owner_id ? [row.owner_id] : []))
      )
      const profileById = new Map(profiles.map(profile => [profile.id, profile]))

      return searchOrganizationEntities({
        rows,
        query,
        limit: params.limit,
        getSearchText: row =>
          [row.id, row.name, row.deal_id, row.deals_search_columns].filter(Boolean).join(' ')
      }).map(row => {
        const profile = row.owner_id ? profileById.get(row.owner_id) : null
        return {
          type: 'deal' as const,
          id: row.id,
          label: row.name ?? row.deal_id,
          status: null,
          profileId: row.owner_id,
          profileName: profile?.name ?? null,
          amount: row.amount,
          currency: row.currency,
          date: row.close_date,
          periodStart: null,
          periodEnd: null,
          related: {
            dealId: row.id,
            payoutId: null,
            statementId: null
          }
        }
      })
    }

    if (params.recordType === 'payout') {
      if (!searchFilter) {
        return []
      }

      let queryBuilder = this.supabase
        .from('payouts')
        .select(
          'id, description, payout_desc_deal_name, value, currency, effective_date, period_start, period_end, status, profile_id, deal_id, statement_id, compensations_search_columns'
        )
        .eq('organization_id', params.organizationId)
        .or(searchFilter)

      if (params.profileId) {
        queryBuilder = queryBuilder.eq('profile_id', params.profileId)
      }
      if (params.status) {
        queryBuilder = queryBuilder.eq('status', params.status as Enums<'payout_status'>)
      }
      if (params.periodStart) {
        queryBuilder = queryBuilder.gte('effective_date', params.periodStart)
      }
      if (params.periodEnd) {
        queryBuilder = queryBuilder.lt('effective_date', params.periodEnd)
      }

      const { data, error } = await queryBuilder.limit(prefetchLimit)
      if (error) {
        throw error
      }

      const rows = (data ?? []) as Array<{
        id: number
        description: string | null
        payout_desc_deal_name: string | null
        value: number
        currency: string | null
        effective_date: string
        period_start: string | null
        period_end: string | null
        status: string
        profile_id: string
        deal_id: number | null
        statement_id: number | null
        compensations_search_columns: string | null
      }>
      const profiles = await this.getProfilesByIds(rows.map(row => row.profile_id))
      const profileById = new Map(profiles.map(profile => [profile.id, profile]))

      return searchOrganizationEntities({
        rows,
        query,
        limit: params.limit,
        getSearchText: row =>
          [row.id, row.description, row.payout_desc_deal_name, row.compensations_search_columns]
            .filter(Boolean)
            .join(' ')
      }).map(row => {
        const profile = profileById.get(row.profile_id)
        return {
          type: 'payout' as const,
          id: row.id,
          label: row.description ?? row.payout_desc_deal_name ?? `Payout ${row.id}`,
          status: row.status,
          profileId: row.profile_id,
          profileName: profile?.name ?? null,
          amount: row.value,
          currency: row.currency,
          date: row.effective_date,
          periodStart: row.period_start,
          periodEnd: row.period_end,
          related: {
            dealId: row.deal_id,
            payoutId: row.id,
            statementId: row.statement_id
          }
        }
      })
    }

    if (params.recordType === 'statement') {
      if (!searchFilter) {
        return []
      }

      let queryBuilder = this.supabase
        .from('statements')
        .select('id, period_start, period_end, status, profile_id, statements_search_columns')
        .eq('organization_id', params.organizationId)
        .or(searchFilter)

      if (params.profileId) {
        queryBuilder = queryBuilder.eq('profile_id', params.profileId)
      }
      if (params.status) {
        queryBuilder = queryBuilder.eq('status', params.status as Enums<'statement_status'>)
      }
      if (params.periodStart) {
        queryBuilder = queryBuilder.gte('period_start', params.periodStart)
      }
      if (params.periodEnd) {
        queryBuilder = queryBuilder.lt('period_start', params.periodEnd)
      }

      const { data, error } = await queryBuilder.limit(prefetchLimit)
      if (error) {
        throw error
      }

      const rows = (data ?? []) as Array<{
        id: number
        period_start: string
        period_end: string
        status: string
        profile_id: string
        statements_search_columns: string | null
      }>
      const profiles = await this.getProfilesByIds(rows.map(row => row.profile_id))
      const profileById = new Map(profiles.map(profile => [profile.id, profile]))

      return searchOrganizationEntities({
        rows,
        query,
        limit: params.limit,
        getSearchText: row =>
          [row.id, row.period_start, row.period_end, row.statements_search_columns]
            .filter(Boolean)
            .join(' ')
      }).map(row => {
        const profile = profileById.get(row.profile_id)
        return {
          type: 'statement' as const,
          id: row.id,
          label: `${profile?.name ?? 'Statement'} (${row.period_start})`,
          status: row.status,
          profileId: row.profile_id,
          profileName: profile?.name ?? null,
          amount: null,
          currency: null,
          date: row.period_start,
          periodStart: row.period_start,
          periodEnd: row.period_end,
          related: {
            dealId: null,
            payoutId: null,
            statementId: row.id
          }
        }
      })
    }
    if (params.recordType === 'dispute') {
      const issueStatus =
        normalizedQuery === 'open' ||
        normalizedQuery === 'in_review' ||
        normalizedQuery === 'resolved'
          ? normalizedQuery
          : null
      if (!numericQuery && !issueStatus) {
        return []
      }

      let queryBuilder = this.supabase
        .from('issues')
        .select('id, status, created_at, resolved_at, created_by, payout_id, statement_id')
        .eq('organization_id', params.organizationId)

      if (numericQuery) {
        queryBuilder = queryBuilder.eq('id', numericQuery)
      } else if (issueStatus) {
        queryBuilder = queryBuilder.eq('status', issueStatus)
      }
      if (params.status) {
        queryBuilder = queryBuilder.eq('status', params.status as Enums<'issue_status'>)
      }
      if (params.profileId) {
        queryBuilder = queryBuilder.eq('created_by', params.profileId)
      }
      if (params.periodStart) {
        queryBuilder = queryBuilder.gte('created_at', params.periodStart)
      }
      if (params.periodEnd) {
        queryBuilder = queryBuilder.lt('created_at', params.periodEnd)
      }

      const { data, error } = await queryBuilder
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(prefetchLimit)
      if (error) {
        throw error
      }

      const rows = (data ?? []) as Array<{
        id: number
        status: string
        created_at: string
        resolved_at: string | null
        created_by: string
        payout_id: number | null
        statement_id: number | null
      }>
      const profiles = await this.getProfilesByIds(rows.map(row => row.created_by))
      const profileById = new Map(profiles.map(profile => [profile.id, profile]))

      return rows.slice(0, params.limit).map(row => {
        const profile = profileById.get(row.created_by)
        return {
          type: 'dispute' as const,
          id: row.id,
          label: `Dispute ${row.id}`,
          status: row.status,
          profileId: row.created_by,
          profileName: profile?.name ?? null,
          amount: null,
          currency: null,
          date: row.created_at,
          periodStart: null,
          periodEnd: null,
          related: {
            dealId: null,
            payoutId: row.payout_id,
            statementId: row.statement_id
          }
        }
      })
    }
    const discrepancyType =
      normalizedQuery === 'value' || normalizedQuery === 'statement' ? normalizedQuery : null
    if (!numericQuery && !discrepancyType) {
      return []
    }

    let queryBuilder = this.supabase
      .from('payout_discrepancies')
      .select(
        'id, payout_id, discrepancy_type, calculated_value, existing_currency, created_at, reviewed'
      )
      .eq('organization_id', params.organizationId)

    if (numericQuery) {
      queryBuilder = queryBuilder.eq('id', numericQuery)
    } else if (discrepancyType) {
      queryBuilder = queryBuilder.eq('discrepancy_type', discrepancyType)
    }
    if (params.status) {
      queryBuilder = queryBuilder.eq('reviewed', params.status === 'reviewed')
    }
    if (params.periodStart) {
      queryBuilder = queryBuilder.gte('created_at', params.periodStart)
    }
    if (params.periodEnd) {
      queryBuilder = queryBuilder.lt('created_at', params.periodEnd)
    }

    const { data, error } = await queryBuilder
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(prefetchLimit)
    if (error) {
      throw error
    }

    const rows = (data ?? []) as Array<{
      id: number
      payout_id: number
      discrepancy_type: string
      calculated_value: number
      existing_currency: string | null
      created_at: string | null
      reviewed: boolean
    }>

    return rows.slice(0, params.limit).map(row => ({
      type: 'discrepancy' as const,
      id: row.id,
      label: `Discrepancy ${row.id}`,
      status: row.reviewed ? 'reviewed' : 'pending',
      profileId: null,
      profileName: null,
      amount: row.calculated_value,
      currency: row.existing_currency,
      date: row.created_at,
      periodStart: null,
      periodEnd: null,
      related: {
        dealId: null,
        payoutId: row.payout_id,
        statementId: null
      }
    }))
  }

  async searchOrganizations(params: {
    organizationIds: number[]
    query: string
    limit: number
  }): Promise<OrganizationSearchResult[]> {
    if (params.organizationIds.length === 0) {
      return []
    }

    const normalizedQuery = params.query.trim()
    if (!normalizedQuery) {
      return []
    }

    const escapedNormalizedQuery = escapeOrLikePattern(normalizedQuery)
    const searchPattern = `%${escapedNormalizedQuery}%`
    const { data, error } = await this.supabase
      .from('organizations')
      .select('id, name')
      .in('id', params.organizationIds)
      .ilike('name', searchPattern)
      .limit(params.limit)

    if (error) {
      throw error
    }

    return (data ?? []) as OrganizationSearchResult[]
  }

  async getOrganizationsByIds(organizationIds: number[]): Promise<OrganizationReference[]> {
    if (organizationIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('organizations')
      .select('id, name')
      .in('id', organizationIds)

    if (error) {
      throw error
    }

    return (data ?? []) as OrganizationReference[]
  }

  async getGlobalTargetsMetricsAndProfiles(affectedMatrics: CompositeTypes<'affected_metrics'>[]) {
    const { data, error } = await this.supabase.rpc('get_global_target_metrics_and_profiles', {
      affected_metrics: affectedMatrics
    })

    if (error) {
      console.error('Error getting global targets metrics and profiles', error)
      throw error
    }

    return data
  }

  async getCompanyTargetAffectedProfiles(
    organizationId: number,
    metricId: number,
    affectedDate: string
  ): Promise<string[]> {
    const { data, error } = await this.supabase.rpc('get_company_target_affected_profiles', {
      _organization_id: organizationId,
      _metric_id: metricId,
      _affected_date: affectedDate
    })

    if (error) throw error

    return (data as { profile_id: string }[] | null)?.map(row => row.profile_id) ?? []
  }

  async getCompanyTargetAffectedProfilesBatch(
    requests: {
      organizationId: number
      metricId: number
      affectedDate: string
    }[]
  ): Promise<DBFunctionReturns<'get_company_target_affected_profiles_batch'>> {
    if (requests.length === 0) return []

    const { data, error } = await this.supabase.rpc('get_company_target_affected_profiles_batch', {
      _requests: requests.map(request => ({
        organization_id: request.organizationId,
        metric_id: request.metricId,
        affected_date: request.affectedDate
      }))
    })

    if (error) throw error

    return data ?? []
  }

  async loadCustomChecks(
    organizationId: number,
    source: Enums<'plan_source'>,
    onlyLoadActiveChecks?: boolean
  ): Promise<Tables<'custom_data_checks'>[]> {
    const { data, error } = await this.supabase
      .from('custom_data_checks')
      .select(`*, data_issues_${source === 'deal' ? 'deals' : 'invoices'}(custom_check_id)`)
      .eq('organization_id', organizationId)
      .eq('source', source)

    if (error) {
      console.error('Error loading custom checks', error)
      throw error
    }

    if (!onlyLoadActiveChecks) {
      return data ?? []
    }

    type CustomCheckWithDataIssues = Tables<'custom_data_checks'> & {
      data_issues_deals?: { custom_check_id: number | null }[]
      data_issues_invoices?: { custom_check_id: number | null }[]
    }
    const typedData = data as CustomCheckWithDataIssues[] | null
    const issuesKey = source === 'deal' ? 'data_issues_deals' : 'data_issues_invoices'
    return typedData?.filter(d => d.status === 'ACTIVE' || (d[issuesKey]?.length ?? 0) > 0) ?? []
  }

  async getNextValidators(payoutId: number) {
    const { data } = await this.supabase.rpc('get_next_validators', {
      _payout_id: payoutId
    })

    return data
  }

  async getCompRuleActiveVersion(
    compRuleId: number,
    referenceDate: string
  ): Promise<Tables<'compensation_rule_versions'> | null> {
    const activePeriodFilter = buildSingleDateRangeLiteral(referenceDate)
    const { data, error } = await this.supabase
      .from('compensation_rule_versions')
      .select('*')
      .eq('compensation_rule_id', compRuleId)
      .contains('active_period', activePeriodFilter)
      .maybeSingle()

    if (error) {
      throw error
    }

    return data
  }

  async getCompensationRuleVersionsForAnalysis(organizationId: number, versionIds: number[]) {
    if (versionIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('compensation_rule_versions')
      .select(COMPENSATION_RULE_VERSION_ANALYSIS_SELECT)
      .eq('compensation_rule.plans.organization_id', organizationId)
      .in('id', versionIds)

    if (error) {
      throw error
    }

    return (
      (data as unknown as CompensationRuleVersionAnalysisQueryRow[] | null)?.map(
        mapCompensationRuleVersionForAnalysis
      ) ?? []
    )
  }

  async getActiveCompensationRuleVersions(organizationId: number, referenceDate: string) {
    const activePeriodFilter = buildSingleDateRangeLiteral(referenceDate)
    const { data, error } = await this.supabase
      .from('compensation_rule_versions')
      .select(COMPENSATION_RULE_VERSION_ANALYSIS_SELECT)
      .eq('compensation_rule.plans.organization_id', organizationId)
      .eq('compensation_rule.archived', false)
      .contains('active_period', activePeriodFilter)

    if (error) {
      throw error
    }

    return (
      (data as unknown as CompensationRuleVersionAnalysisQueryRow[] | null)?.map(
        mapCompensationRuleVersionForAnalysis
      ) ?? []
    )
  }

  async getDealPayoutsForProfile(organizationId: number, dealId: number, profileId: string) {
    const { data, error } = await this.supabase
      .rpc('get_deal_related_payouts', {
        _organization_id: organizationId,
        _deal_id: dealId,
        _profile_id: profileId
      })
      .select<string, Tables<'payouts'> & { statements?: Tables<'statements'> }>('*, statements(*)')

    if (error) {
      throw error
    }

    return data ?? []
  }

  async hasRecentSubscriptionData({
    organizationId,
    profileId
  }: HasRecentSubscriptionDataParams): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('has_recent_subscription_data', {
      _organization_id: organizationId,
      _profile_id: profileId
    })

    if (error) {
      throw error
    }

    return data === true
  }

  async getSubscriptionCompensationSourceRows(
    params: GetSubscriptionCompensationSourceRowsParams
  ): Promise<SubscriptionCompensationSourceRow[]> {
    const statuses = params.filters?.statuses ?? []
    const normalizedSearch = params.filters?.search?.trim().toLowerCase()
    const referenceDate = parseISO(params.referenceDate ?? format(new Date(), 'yyyy-MM-dd'))
    const visibleStart = format(
      startOfMonth(subMonths(referenceDate, params.months - 1)),
      'yyyy-MM-dd'
    )
    const visibleEnd = format(endOfMonth(referenceDate), 'yyyy-MM-dd')

    const payoutSelect = `
          id,
          organization_id,
          profile_id,
          value,
          currency,
          effective_date,
          statements!inner(period_start, period_end),
          compensation_rule_id,
          invoice_id,
          line_item_id,
          compensation_rules(label),
          invoices(
            id,
            effective_at,
            payroll_provider,
            subscription_id,
            subscription_fk,
            deals(id, name)
          ),
          line_items(
            id,
            invoices(
              id,
              effective_at,
              payroll_provider,
              subscription_id,
              subscription_fk,
              deals(id, name)
            )
          )
        `

    const buildPayoutQuery = (bounded: boolean) => {
      let payoutQuery = this.supabase
        .from('payouts')
        .select(payoutSelect)
        .eq('organization_id', params.organizationId)

      if (params.profileId) {
        payoutQuery = payoutQuery.eq('profile_id', params.profileId)
      }

      if ((params.filters?.compensationRuleIds?.length ?? 0) === 1) {
        payoutQuery = payoutQuery.eq(
          'compensation_rule_id',
          params.filters!.compensationRuleIds![0]!
        )
      } else if ((params.filters?.compensationRuleIds?.length ?? 0) > 1) {
        payoutQuery = payoutQuery.in('compensation_rule_id', params.filters!.compensationRuleIds!)
      }

      if (bounded) {
        payoutQuery = payoutQuery.gte('statements.period_start', visibleStart)
        payoutQuery = payoutQuery.lte('statements.period_start', visibleEnd)
      }

      return payoutQuery.order('effective_date', { ascending: true })
    }

    type SubscriptionCompensationRawRow = {
      id: number
      organization_id: number
      profile_id: string
      value: number
      currency: string | null
      effective_date: string
      statements?: { period_start: string | null; period_end: string | null } | null
      compensation_rule_id: number | null
      invoice_id: number | null
      line_item_id: number | null
      compensation_rules?: { label: string | null } | null
      invoices?: InvoiceSubscriptionOverviewRow | InvoiceSubscriptionOverviewRow[] | null
      line_items?: {
        id: number
        invoices?: InvoiceSubscriptionOverviewRow | InvoiceSubscriptionOverviewRow[] | null
      } | null
    }

    const fetchPayoutRows = async (query: ReturnType<typeof buildPayoutQuery>) => {
      const rows: SubscriptionCompensationRawRow[] = []
      let start = 0

      while (true) {
        const end = start + SUBSCRIPTION_COMPENSATION_PAYOUTS_PAGE_SIZE - 1
        const { data, error } = await query.range(start, end)

        if (error) {
          throw error
        }

        const page = (data ?? []) as SubscriptionCompensationRawRow[]
        if (page.length === 0) {
          break
        }

        rows.push(...page)
        start += SUBSCRIPTION_COMPENSATION_PAYOUTS_PAGE_SIZE
      }

      return rows
    }

    const rawRows = await fetchPayoutRows(buildPayoutQuery(true))

    // Rehydrate all source records for subscriptions visible in the bounded
    // query. This keeps whole-subscription totals and continuation flags while
    // avoiding an organization-wide historical payout scan.
    const visibleSubscriptionIds = Array.from(
      new Set(
        rawRows
          .map(row => resolveSubscriptionOverviewInvoice(row)?.subscription_id?.trim())
          .filter((value): value is string => !!value)
      )
    )

    if (visibleSubscriptionIds.length > 0) {
      const relatedInvoices: Array<{ id: number; subscription_id: string | null }> = []

      for (const subscriptionIdsChunk of batched(
        visibleSubscriptionIds,
        SUBSCRIPTION_COMPENSATION_LOOKUP_BATCH_SIZE
      )) {
        const { data, error } = await this.supabase
          .from('invoices')
          .select('id, subscription_id')
          .eq('organization_id', params.organizationId)
          .in('subscription_id', subscriptionIdsChunk)

        if (error) {
          throw error
        }

        relatedInvoices.push(...((data ?? []) as typeof relatedInvoices))
      }

      const invoiceIds = Array.from(new Set(relatedInvoices.map(invoice => invoice.id)))
      const relatedLineItems: Array<{ id: number; invoice_id: number | null }> = []

      for (const invoiceIdsChunk of batched(
        invoiceIds,
        SUBSCRIPTION_COMPENSATION_LOOKUP_BATCH_SIZE
      )) {
        const { data, error } = await this.supabase
          .from('line_items')
          .select('id, invoice_id')
          .eq('organization_id', params.organizationId)
          .in('invoice_id', invoiceIdsChunk)

        if (error) {
          throw error
        }

        relatedLineItems.push(...((data ?? []) as typeof relatedLineItems))
      }

      const lineItemIds = (relatedLineItems ?? []).map(lineItem => lineItem.id)
      const completeRowsById = new Map<number, SubscriptionCompensationRawRow>()
      const fetchCompleteRows = async (column: 'invoice_id' | 'line_item_id', ids: number[]) => {
        for (const idsChunk of batched(ids, SUBSCRIPTION_COMPENSATION_LOOKUP_BATCH_SIZE)) {
          const rows = await fetchPayoutRows(
            buildPayoutQuery(false).or(`${column}.in.(${idsChunk.join(',')})`)
          )

          rows.forEach(row => completeRowsById.set(row.id, row))
        }
      }

      await fetchCompleteRows('invoice_id', invoiceIds)
      await fetchCompleteRows('line_item_id', lineItemIds)

      if (completeRowsById.size > 0) {
        rawRows.splice(
          0,
          rawRows.length,
          ...Array.from(completeRowsById.values()).sort((left, right) =>
            left.effective_date.localeCompare(right.effective_date)
          )
        )
      }
    }

    const canonicalSubscriptionIds = new Set<number>()
    const canonicalSubscriptionKeys = Array.from(
      new Set(
        rawRows
          .map(row => resolveSubscriptionOverviewInvoice(row))
          .flatMap(invoice => {
            if (invoice?.subscription_fk != null) {
              canonicalSubscriptionIds.add(invoice.subscription_fk)
            }

            const provider = invoice?.payroll_provider?.trim()
            const subscriptionId = invoice?.subscription_id?.trim()

            if (!provider || !subscriptionId) {
              return []
            }

            return [`${provider}::${subscriptionId}`]
          })
      )
    )

    const canonicalSubscriptionsByKey = new Map<string, CanonicalSubscriptionOverviewRow>()

    if (canonicalSubscriptionKeys.length > 0) {
      const subscriptionIds = Array.from(
        new Set(canonicalSubscriptionKeys.map(key => key.split('::')[1]!).filter(Boolean))
      )

      for (const subscriptionIdsChunk of batched(
        subscriptionIds,
        SUBSCRIPTION_COMPENSATION_LOOKUP_BATCH_SIZE
      )) {
        const { data: canonicalSubscriptions, error: canonicalSubscriptionsError } =
          await this.supabase
            .from('subscriptions')
            .select(
              `
                id,
                provider,
                subscription_id,
                provider_customer_id,
                customer_id,
                status,
                start_date,
                current_period_end,
                cancel_at,
                cancelled_at,
                ended_at,
                customers:customers!subscriptions_customer_id_organization_id_fkey(
                  id,
                  provider_customer_id,
                  name,
                  company,
                  email,
                  meta
                )
              `
            )
            .eq('organization_id', params.organizationId)
            .in('subscription_id', subscriptionIdsChunk)

        if (canonicalSubscriptionsError) {
          throw canonicalSubscriptionsError
        }

        for (const subscription of (canonicalSubscriptions ??
          []) as CanonicalSubscriptionOverviewRow[]) {
          canonicalSubscriptionsByKey.set(
            `${subscription.provider}::${subscription.subscription_id}`,
            subscription
          )
        }
      }

      if (canonicalSubscriptionIds.size > 0) {
        for (const subscriptionIdsChunk of batched(
          [...canonicalSubscriptionIds],
          SUBSCRIPTION_COMPENSATION_LOOKUP_BATCH_SIZE
        )) {
          const { data: canonicalSubscriptionsById, error: canonicalSubscriptionsByIdError } =
            await this.supabase
              .from('subscriptions')
              .select(
                `
                id,
                provider,
                subscription_id,
                provider_customer_id,
                customer_id,
                status,
                start_date,
                current_period_end,
                cancel_at,
                cancelled_at,
                ended_at,
                customers:customers!subscriptions_customer_id_organization_id_fkey(
                  id,
                  provider_customer_id,
                  name,
                  company,
                  email,
                  meta
                )
              `
              )
              .eq('organization_id', params.organizationId)
              .in('id', subscriptionIdsChunk)

          if (canonicalSubscriptionsByIdError) {
            throw canonicalSubscriptionsByIdError
          }

          for (const subscription of (canonicalSubscriptionsById ??
            []) as CanonicalSubscriptionOverviewRow[]) {
            canonicalSubscriptionsByKey.set(
              `${subscription.provider}::${subscription.subscription_id}`,
              subscription
            )
            canonicalSubscriptionsByKey.set(`id::${subscription.id}`, subscription)
          }
        }
      }
    }

    return rawRows.flatMap(row => {
      // The subscriptions overview is statement-based. Payouts without a
      // statement are still locked and must not be attributed to their
      // effective-date month.
      if (!row.statements?.period_start) {
        return []
      }

      const invoice = resolveSubscriptionOverviewInvoice(row)
      const subscription =
        invoice?.subscription_fk != null
          ? (canonicalSubscriptionsByKey.get(`id::${invoice.subscription_fk}`) ?? null)
          : (() => {
              const provider = invoice?.payroll_provider?.trim()
              const invoiceSubscriptionId = invoice?.subscription_id?.trim()

              return provider && invoiceSubscriptionId
                ? (canonicalSubscriptionsByKey.get(`${provider}::${invoiceSubscriptionId}`) ?? null)
                : null
            })()
      const subscriptionId = subscription?.subscription_id?.trim() || null

      if (!invoice || !subscriptionId || !subscription) {
        return []
      }
      const customer = subscription?.customers ?? null
      const status = subscription?.status?.trim() || null
      const dealId = invoice?.deals?.id ?? null
      const accountName = getCanonicalSubscriptionAccountName(subscription)
      const customerDisplayName =
        customer && !isBackfilledPlaceholderCustomer(customer)
          ? customer.name?.trim() || customer.company?.trim() || null
          : null

      if (statuses.length > 0 && (!status || !statuses.includes(status))) {
        return []
      }

      if (normalizedSearch) {
        const haystack = [
          accountName,
          customerDisplayName,
          subscriptionId,
          subscription?.customer_id != null
            ? String(subscription.customer_id)
            : customer?.id != null
              ? String(customer.id)
              : null,
          subscription?.provider_customer_id,
          customer?.provider_customer_id,
          row.compensation_rules?.label
        ]
          .filter((value): value is string => !!value)
          .join(' ')
          .toLowerCase()

        if (!haystack.includes(normalizedSearch)) {
          return []
        }
      }

      return [
        {
          payoutId: row.id,
          organizationId: row.organization_id,
          profileId: row.profile_id,
          amount: row.value,
          currency: row.currency,
          effectiveDate: row.effective_date,
          statementPeriodStart: row.statements?.period_start ?? null,
          statementPeriodEnd: row.statements?.period_end ?? null,
          compensationRuleId: row.compensation_rule_id,
          compensationRuleLabel: row.compensation_rules?.label ?? null,
          source: row.line_item_id != null ? 'line_item' : 'invoice',
          invoiceId: invoice?.id ?? row.invoice_id,
          lineItemId: row.line_item_id,
          dealId,
          invoiceEffectiveAt: invoice?.effective_at ?? null,
          subscriptionId,
          subscriptionStartDate: subscription?.start_date ?? null,
          subscriptionEndDate:
            subscription?.ended_at ?? subscription?.cancelled_at ?? subscription?.cancel_at ?? null,
          subscriptionStatus: status,
          customerId: subscription?.customer_id ?? customer?.id ?? null,
          providerCustomerId:
            subscription?.provider_customer_id ?? customer?.provider_customer_id ?? null,
          customerDisplayName,
          accountName
        }
      ]
    })
  }

  async getNotificationPayoutReferences(
    payoutIds: number[]
  ): Promise<NotificationPayoutReference[]> {
    if (payoutIds.length === 0) return []

    const { data, error } = await this.supabase
      .from('payouts')
      .select(
        `id, description, effective_date, compensation,
        deals(deal_id, name),
        invoices(invoice_number, deals(deal_id, name)),
        activities(activity_id, type, deals(deal_id, name)),
        kpis(id, kpis_tables(label)),
        line_items(id, invoices(invoice_number, deals(deal_id, name))),
        deal_line_items(id, line_item_id, product_name, description, deals(deal_id, name)),
        profiles!payouts_profile_id_fkey(id, name, email, img_url),
        compensation_rules(label, source)`
      )
      .in('id', payoutIds)

    if (error) throw error

    return (data ?? []) as NotificationPayoutReference[]
  }

  async getPayoutManualAdjustmentBreakdowns(
    organizationId: number,
    payoutIds: number[]
  ): Promise<PayoutManualAdjustmentBreakdown[]> {
    if (payoutIds.length === 0) return []

    const { data: adjustments, error: adjustmentsError } = await this.supabase
      .from('payout_adjustments')
      .select('id, payout_id, amount, created_at, profiles(id, name, email, img_url)')
      .in('payout_id', payoutIds)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })

    if (adjustmentsError) throw adjustmentsError

    const adjustmentsByPayoutId = new Map<number, PayoutManualAdjustmentRow[]>()

    ;(adjustments ?? []).forEach(adjustment => {
      const payoutAdjustments = adjustmentsByPayoutId.get(adjustment.payout_id) ?? []
      payoutAdjustments.push(adjustment)
      adjustmentsByPayoutId.set(adjustment.payout_id, payoutAdjustments)
    })

    const allAdjustments = Array.from(adjustmentsByPayoutId.values()).flat()
    if (allAdjustments.length === 0) return []

    const { data: auditRows, error: auditError } = await this.supabase
      .from('payouts_audit_log')
      .select('old_data, new_data, changed_at')
      .eq('organization_id', organizationId)
      .eq('operation_type', 'UPDATE')
      .in(
        'old_data->>id',
        allAdjustments.map(adjustment => String(adjustment.payout_id))
      )
      .in(
        'changed_at',
        allAdjustments.map(adjustment => adjustment.created_at)
      )

    if (auditError) throw auditError

    return buildPayoutAdjustmentBreakdowns(
      allAdjustments.map(adjustment => ({
        ...adjustment,
        payouts: {
          id: adjustment.payout_id,
          profile_id: adjustment.profiles?.id ?? '',
          profiles: adjustment.profiles ?? null
        }
      })),
      (auditRows ?? []) as PayoutAnalyticsAdjustmentAudit[]
    ).map(
      breakdown =>
        ({
          adjusted_value: breakdown.adjustedAmount,
          adjustment_value: breakdown.adjustmentAmount,
          adjustments: breakdown.steps.map(step => ({
            adjustment_value: step.adjustmentAmount,
            created_at: step.createdAt,
            from_value: step.fromAmount,
            profile: step.profile ?? null,
            to_value: step.toAmount
          })),
          created_at: breakdown.createdAt,
          original_value: breakdown.originalAmount,
          payout_id: breakdown.payoutId,
          profile: breakdown.profile ?? null
        }) satisfies PayoutManualAdjustmentBreakdown
    )
  }

  async getDealMetricValuesForProfile(organizationId: number, dealId: number, profileId: string) {
    const { data, error } = await this.supabase.rpc('get_deal_related_metric_values', {
      _organization_id: organizationId,
      _deal_id: dealId,
      _profile_id: profileId
    })

    if (error) {
      throw error
    }

    return data ?? []
  }

  async getTvpAllocation(
    profileId: string,
    organizationId: number,
    compensationRuleVersionId: number
  ) {
    const { data: allocation, error: allocationError } = await this.supabase.rpc(
      'get_tvp_allocations',
      {
        _profile_ids: [profileId],
        _organization_id: organizationId,
        _compensation_rule_version_ids: [compensationRuleVersionId]
      }
    )

    if (allocationError) {
      console.error('Error fetching TVP allocation:', allocationError)
      return null
    }

    return allocation
  }

  async getLatestTvp(profileId: string, referenceDate: string, organizationId?: number) {
    let query = this.supabase
      .from('organizations_profiles_data')
      .select('*')
      .eq('profile_id', profileId)
      .lte('from_date', referenceDate)
      .order('from_date', { ascending: false })
      .limit(1)

    if (organizationId) {
      query = query.eq('organization_id', organizationId)
    }

    const { data, error } = await query.maybeSingle()

    if (error) {
      console.error('Error fetching TVP:', error)
      return null
    }

    return data
  }

  async getProfileOrganizationStartDate(profileId: string, organizationId: number) {
    const { data, error } = await this.supabase
      .from('profile_org_start_date')
      .select('start_date')
      .eq('profile_id', profileId)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching profile organization start date:', error)
      return null
    }

    return data?.start_date ?? null
  }

  async getActiveProfileDataAtDate(
    profileId: string,
    referenceDate: string,
    organizationId: number
  ): Promise<Pick<
    CurrentOrganizationProfileData,
    | 'type'
    | 'business_unit_id'
    | 'business_unit_name'
    | 'business_unit_cost_center_id'
    | 'business_unit_cost_center'
    | 'business_unit_department'
    | 'business_unit_legal_entity'
    | 'business_unit_location_country'
    | 'business_unit_location_city'
  > | null> {
    const { data, error } = await this.supabase
      .from('organizations_profiles_data')
      .select(
        [
          'type',
          'business_unit_id',
          'business_unit_name',
          'business_unit_cost_center_id',
          'business_unit_cost_center',
          'business_unit_department',
          'business_unit_legal_entity',
          'business_unit_location_country',
          'business_unit_location_city'
        ].join(', ')
      )
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .lte('from_date', referenceDate)
      .order('from_date', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      throw error
    }

    return data as Pick<
      CurrentOrganizationProfileData,
      | 'type'
      | 'business_unit_id'
      | 'business_unit_name'
      | 'business_unit_cost_center_id'
      | 'business_unit_cost_center'
      | 'business_unit_department'
      | 'business_unit_legal_entity'
      | 'business_unit_location_country'
      | 'business_unit_location_city'
    > | null
  }

  async getTvpAllocations(profileId: string, organizationId: number) {
    const { data: allocations, error: allocationsError } = await this.supabase.rpc(
      'get_tvp_allocations',
      {
        _profile_ids: [profileId],
        _organization_id: organizationId
      }
    )

    if (allocationsError) {
      console.error('Error fetching TVP allocations:', allocationsError)
      return []
    }

    return allocations || []
  }

  async getTvpDataForProfiles(profileIds: string[], referenceDate: string, organizationId: number) {
    const { data: tvpData, error: tvpError } = await this.supabase
      .from('organizations_profiles_data')
      .select('*')
      .eq('organization_id', organizationId)
      .in('profile_id', profileIds)
      .lte('from_date', referenceDate)
      .order('from_date', { ascending: false })

    if (tvpError) throw tvpError
    return tvpData || []
  }

  async getTvpAllocationsForProfiles(
    profileIds: string[],
    organizationId: number
  ): Promise<
    (Tables<'tvp_allocation'> & {
      compensation_rules: { plans: { organization_id: number } } | null
    })[]
  > {
    const { data: allocations, error: allocationsError } = await this.supabase
      .from('tvp_allocation')
      .select('*, compensation_rules(plans(organization_id))')
      .in('profile_id', profileIds)
      .eq('compensation_rules.plans.organization_id', organizationId)
      .is('compensation_rule_draft_id', null)

    if (allocationsError) throw allocationsError
    return (
      (allocations as (Tables<'tvp_allocation'> & {
        compensation_rules: { plans: { organization_id: number } } | null
      })[]) || []
    )
  }

  async getLatestCompRuleDraftOrVersion(
    compensationRuleId: number,
    referenceDate: string,
    isDraft: boolean
  ) {
    if (isDraft) {
      const { data: draft, error: draftError } = await this.supabase
        .from('compensation_rule_drafts')
        .select('id')
        .eq('compensation_rule_id', compensationRuleId)
        .limit(1)
        .single()

      if (draftError) throw draftError
      return {
        compensationRuleDraftId: draft.id,
        compensationRuleVersionId: null
      }
    } else {
      const activePeriodFilter = buildSingleDateRangeLiteral(referenceDate)
      const { data: version, error: versionError } = await this.supabase
        .from('compensation_rule_versions')
        .select('id')
        .eq('compensation_rule_id', compensationRuleId)
        .contains('active_period', activePeriodFilter)
        .order('active_period', { ascending: false })
        .limit(1)
        .single()

      if (versionError) throw versionError
      return {
        compensationRuleDraftId: null,
        compensationRuleVersionId: version.id
      }
    }
  }

  async upsertTvpAllocation(params: {
    profileId: string
    compensationRuleId: number
    percentage: number
    compensationRuleDraftId?: number | null
    compensationRuleVersionId?: number | null
  }): Promise<Tables<'tvp_allocation'>> {
    const conflictColumns = params.compensationRuleDraftId
      ? 'compensation_rule_draft_id, profile_id'
      : 'compensation_rule_version_id, profile_id'

    const { data, error } = await this.supabase
      .from('tvp_allocation')
      .upsert(
        {
          profile_id: params.profileId,
          compensation_rule_id: params.compensationRuleId,
          compensation_rule_draft_id: params.compensationRuleDraftId,
          compensation_rule_version_id: params.compensationRuleVersionId,
          percentage: params.percentage
        },
        { onConflict: conflictColumns }
      )
      .select()
      .single()

    if (error) throw error
    return data as Tables<'tvp_allocation'>
  }

  async getDraftTvpAllocations(
    compensationRuleId: number
  ): Promise<Pick<Tables<'tvp_allocation'>, 'percentage' | 'profile_id' | 'team_id'>[]> {
    const { data, error } = await this.supabase
      .from('tvp_allocation')
      .select('percentage, profile_id, team_id')
      .eq('compensation_rule_id', compensationRuleId)
      .not('compensation_rule_draft_id', 'is', null)

    if (error) throw error
    return data ?? []
  }

  async replaceVersionTvpAllocations({
    compensationRuleId,
    compensationRuleVersionId,
    allocations
  }: {
    compensationRuleId: number
    compensationRuleVersionId: number
    allocations: Pick<Tables<'tvp_allocation'>, 'percentage' | 'profile_id' | 'team_id'>[]
  }): Promise<void> {
    const { error } = await this.supabase.rpc('replace_version_tvp_allocations', {
      _compensation_rule_id: compensationRuleId,
      _compensation_rule_version_id: compensationRuleVersionId,
      _allocations: allocations as Json
    })

    if (error) throw error
  }

  async getTarget(targetId: number) {
    if (this.targetCache.has(targetId)) {
      return this.targetCache.get(targetId)!
    }

    const { data, error } = await this.supabase
      .from('targets')
      .select('*, metrics(*), teams_targets(*)')
      .eq('id', targetId)
      .single()

    if (error) {
      console.error('Error fetching target:', error)
      return null
    }

    this.targetCache.set(targetId, data)

    return data
  }

  async setTargetCreditSetupSelection({
    targetId,
    creditSetupId
  }: TargetCreditSetupSelectionParams): Promise<TargetWithCreditSetup> {
    const { data, error } = await this.supabase.rpc('set_target_credit_setup_selection', {
      p_target_id: targetId,
      // Null is the explicit direct-value selection. Do not omit this key.
      p_credit_setup_id: creditSetupId
    })

    if (error) throw error

    this.targetCache.delete(targetId)
    return data as TargetWithCreditSetup
  }

  async upsertTargetWithCreditSetupSelection({
    target,
    creditSetupId
  }: TargetUpsertWithCreditSetupSelectionParams): Promise<TargetWithCreditSetup> {
    const { data, error } = await this.supabase.rpc('upsert_target_with_credit_setup_selection', {
      p_target: target,
      p_credit_setup_id: creditSetupId
    })

    if (error) throw error

    if (target.id != null) this.targetCache.delete(target.id)
    return data as TargetWithCreditSetup
  }

  async upsertTeam(team: {
    id?: number
    name: string
    description?: string | null
    currency?: string | null
    organization_id: number
  }): Promise<Tables<'teams'>> {
    const { data, error } = await this.supabase
      .from('teams')
      .upsert(team, { onConflict: 'id' })
      .select()
      .single()

    if (error) {
      console.error('Error upserting team:', error)
      throw error
    }

    return {
      ...data,
      tags_array: (data as { tags_array?: number[] | null }).tags_array ?? null,
      teams_search_columns:
        (data as { teams_search_columns?: string | null }).teams_search_columns ?? null
    } as Tables<'teams'>
  }

  async upsertBusinessUnit(business_unit: {
    id?: number
    organization_id: number
    name: string
    cost_centers: {
      department_name: string
      legal_entity_name?: string | null
      location_country?: string | null
      location_city?: string | null
    }[]
  }): Promise<Tables<'business_units'>> {
    // Normalize the form payload before persisting. This keeps the SQL writes
    // deterministic and avoids creating duplicate reusable hierarchy rows from
    // whitespace-only differences.
    const normalizedCostCenters = business_unit.cost_centers.reduce<
      {
        department_name: string
        legal_entity_name: string | null
        location_country: string | null
        location_city: string | null
      }[]
    >((acc, costCenter) => {
      const normalized = {
        department_name: costCenter.department_name.trim(),
        legal_entity_name: costCenter.legal_entity_name?.trim() || null,
        location_country: costCenter.location_country?.trim() || null,
        location_city: costCenter.location_city?.trim() || null
      }

      if (normalized.department_name === '') {
        return acc
      }

      if (
        (normalized.location_country != null || normalized.location_city != null) &&
        normalized.legal_entity_name == null
      ) {
        throw new Error('Locations must belong to a legal entity when creating cost centers')
      }

      const duplicate = acc.some(
        existing =>
          existing.department_name === normalized.department_name &&
          existing.legal_entity_name === normalized.legal_entity_name &&
          existing.location_country === normalized.location_country &&
          existing.location_city === normalized.location_city
      )

      return duplicate ? acc : [...acc, normalized]
    }, [])

    if (normalizedCostCenters.length === 0) {
      throw new Error('At least one cost center is required')
    }

    // The business-unit row is the stable root. Child hierarchy records are
    // reconciled afterwards so the same RPC can handle both create and update.
    const payload = {
      organization_id: business_unit.organization_id,
      name: business_unit.name
    }

    let businessUnitRow: Tables<'business_units'>

    if (business_unit.id != null) {
      const { data, error } = await this.supabase
        .from('business_units')
        .update(payload)
        .eq('id', business_unit.id)
        .eq('organization_id', business_unit.organization_id)
        .select('*')
        .single()

      if (error) {
        console.error('Error updating business_unit:', error)
        throw error
      }

      businessUnitRow = data
    } else {
      const { data, error } = await this.supabase
        .from('business_units')
        .insert(payload)
        .select('*')
        .single()

      if (error) {
        console.error('Error creating business_unit:', error)
        throw error
      }

      businessUnitRow = data
    }

    const departmentNames = Array.from(
      new Set(normalizedCostCenters.map(costCenter => costCenter.department_name))
    )
    const legalEntityNames = Array.from(
      new Set(
        normalizedCostCenters
          .map(costCenter => costCenter.legal_entity_name)
          .filter((value): value is string => value != null)
      )
    )

    if (departmentNames.length > 0) {
      const { error: upsertDepartmentsError } = await this.supabase.from('departments').upsert(
        departmentNames.map(name => ({
          organization_id: business_unit.organization_id,
          name,
          archived_at: null
        })),
        { onConflict: 'organization_id,name' }
      )

      if (upsertDepartmentsError) {
        throw upsertDepartmentsError
      }
    }

    if (legalEntityNames.length > 0) {
      const { error: upsertLegalEntitiesError } = await this.supabase.from('legal_entities').upsert(
        legalEntityNames.map(name => ({
          organization_id: business_unit.organization_id,
          name,
          archived_at: null
        })),
        { onConflict: 'organization_id,name' }
      )

      if (upsertLegalEntitiesError) {
        throw upsertLegalEntitiesError
      }
    }

    const [
      { data: departments, error: departmentsError },
      { data: legalEntities, error: legalEntitiesError }
    ] = await Promise.all([
      this.supabase
        .from('departments')
        .select('*')
        .eq('organization_id', business_unit.organization_id)
        .in('name', departmentNames),
      legalEntityNames.length > 0
        ? this.supabase
            .from('legal_entities')
            .select('*')
            .eq('organization_id', business_unit.organization_id)
            .in('name', legalEntityNames)
        : Promise.resolve<{ data: Tables<'legal_entities'>[]; error: null }>({
            data: [],
            error: null
          })
    ])

    if (departmentsError) throw departmentsError
    if (legalEntitiesError) throw legalEntitiesError

    const departmentsByName = new Map<string, Tables<'departments'>>(
      (departments ?? []).map((row): readonly [string, Tables<'departments'>] => [row.name, row])
    )
    const legalEntitiesByName = new Map<string, Tables<'legal_entities'>>(
      (legalEntities ?? []).map((row): readonly [string, Tables<'legal_entities'>] => [
        row.name,
        row
      ])
    )

    const locationInputs = normalizedCostCenters
      .filter(costCenter => costCenter.location_country != null)
      .map(costCenter => ({
        organization_id: business_unit.organization_id,
        legal_entity_id: legalEntitiesByName.get(costCenter.legal_entity_name!)!.id,
        country: costCenter.location_country!,
        city: costCenter.location_city ?? '',
        archived_at: null
      }))

    if (locationInputs.length > 0) {
      const { error: upsertLocationsError } = await this.supabase
        .from('locations')
        .upsert(locationInputs, {
          onConflict: 'organization_id,legal_entity_id,country,city'
        })

      if (upsertLocationsError) throw upsertLocationsError
    }

    const { data: allLocations, error: locationsError } = await this.supabase
      .from('locations')
      .select('*')
      .eq('organization_id', business_unit.organization_id)

    if (locationsError) throw locationsError

    const locationsByKey = new Map<string, Tables<'locations'>>(
      (allLocations ?? []).map((location): readonly [string, Tables<'locations'>] => [
        `${location.legal_entity_id}:${location.country}:${location.city}`,
        location
      ])
    )

    const { data: existingCostCenters, error: existingCostCentersError } = await this.supabase
      .from('business_unit_cost_centers')
      .select('id, name')
      .eq('business_unit_id', businessUnitRow.id)
      .is('archived_at', null)

    if (existingCostCentersError) {
      console.error('Error fetching existing business unit cost centers:', existingCostCentersError)
      throw existingCostCentersError
    }

    const desiredCostCenterNames = normalizedCostCenters.map(costCenter =>
      buildBusinessUnitCostCenterName({
        departmentName: costCenter.department_name,
        legalEntityName: costCenter.legal_entity_name,
        locationCountry: costCenter.location_country,
        locationCity: costCenter.location_city
      })
    )

    const { error: upsertCostCentersError } = await this.supabase
      .from('business_unit_cost_centers')
      .upsert(
        normalizedCostCenters.map((costCenter, index) => {
          const legalEntity =
            costCenter.legal_entity_name != null
              ? (legalEntitiesByName.get(costCenter.legal_entity_name) ?? null)
              : null
          const location =
            costCenter.location_country != null && legalEntity != null
              ? (locationsByKey.get(
                  `${legalEntity.id}:${costCenter.location_country}:${
                    costCenter.location_city ?? ''
                  }`
                ) ?? null)
              : null

          return {
            business_unit_id: businessUnitRow.id,
            department_id: departmentsByName.get(costCenter.department_name)!.id,
            legal_entity_id: legalEntity?.id ?? null,
            location_id: location?.id ?? null,
            name: desiredCostCenterNames[index],
            archived_at: null
          }
        }),
        {
          onConflict: 'business_unit_id,name'
        }
      )

    if (upsertCostCentersError) {
      console.error('Error upserting business unit cost centers:', upsertCostCentersError)
      throw upsertCostCentersError
    }

    const staleCostCenterIds = (existingCostCenters ?? [])
      .filter(costCenter => !desiredCostCenterNames.includes(costCenter.name))
      .map(costCenter => costCenter.id)

    if (staleCostCenterIds.length > 0) {
      const { data: assignmentsUsingStaleCostCenters, error: assignmentsError } =
        await this.supabase
          .from('organizations_profiles')
          .select('business_unit_cost_center_id')
          .eq('organization_id', business_unit.organization_id)
          .eq('archived', false)
          .in('business_unit_cost_center_id', staleCostCenterIds)

      if (assignmentsError) {
        console.error('Error validating business unit cost center archival:', assignmentsError)
        throw assignmentsError
      }

      if ((assignmentsUsingStaleCostCenters ?? []).length > 0) {
        throw new Error(
          'Clear cost center assignments before removing cost centers from this business unit'
        )
      }

      const { error: archiveCostCentersError } = await this.supabase
        .from('business_unit_cost_centers')
        .update({ archived_at: new Date().toISOString() })
        .in('id', staleCostCenterIds)

      if (archiveCostCentersError) {
        console.error(
          'Error archiving removed business unit cost centers:',
          archiveCostCentersError
        )
        throw archiveCostCentersError
      }
    }

    return businessUnitRow
  }

  async removeBusinessUnit(
    businessUnitId: number,
    organizationId: number
  ): Promise<BusinessUnitRemovalResult> {
    const { data, error } = await this.supabase.rpc('remove_business_unit', {
      _business_unit_id: businessUnitId,
      _organization_id: organizationId
    })

    if (error) {
      console.error('Error removing business_unit:', error)
      throw error
    }

    const result = data?.[0]

    if (result == null || (result.action !== 'archived' && result.action !== 'deleted')) {
      throw new Error('Business unit removal did not return a valid action')
    }

    return result as BusinessUnitRemovalResult
  }

  async assignBusinessUnitToProfiles(params: {
    organizationId: number
    businessUnitId: number | null
    businessUnitCostCenterId: number | null
    effectiveFromDate: string
    effectiveToDate: string | null
    existingEffectiveFromDate: string | null
    extendPreviousAssignment: boolean
    bringForwardNextAssignment: boolean
    profileIds: string[]
  }): Promise<Tables<'organizations_profiles'>[]> {
    // Guard obvious hierarchy mistakes before we reach SQL. The migration still
    // validates everything again, but these errors become much easier to
    // present to the caller when they fail here with explicit messages.
    const profileIds = Array.from(new Set(params.profileIds))

    if (profileIds.length === 0) {
      return []
    }

    if (params.businessUnitId != null) {
      const { data: business_unit, error: businessUnitError } = await this.supabase
        .from('business_units')
        .select('id, organization_id, archived_at')
        .eq('id', params.businessUnitId)
        .maybeSingle()

      if (businessUnitError) {
        console.error('Error validating business unit assignment:', businessUnitError)
        throw businessUnitError
      }

      if (!business_unit || business_unit.organization_id !== params.organizationId) {
        throw new Error('Business unit does not belong to this organization')
      }

      if (business_unit.archived_at != null) {
        throw new Error('Archived business units cannot be assigned')
      }
    } else if (params.businessUnitCostCenterId != null) {
      throw new Error('A cost center cannot be assigned without a business unit')
    }

    if (params.businessUnitCostCenterId != null) {
      const { data: costCenter, error: costCenterError } = await this.supabase
        .from('business_unit_cost_centers')
        .select('id, business_unit_id, archived_at')
        .eq('id', params.businessUnitCostCenterId)
        .maybeSingle()

      if (costCenterError) {
        console.error('Error validating business unit cost center assignment:', costCenterError)
        throw costCenterError
      }

      if (!costCenter || costCenter.business_unit_id !== params.businessUnitId) {
        throw new Error('Cost center does not belong to the selected business unit')
      }

      if (costCenter.archived_at != null) {
        throw new Error('Archived cost centers cannot be assigned')
      }
    }

    // SQL owns the dated write semantics. The RPC passes both the desired start
    // date and the existing start date (when editing) so the migration can
    // reshape overlapping ranges coherently.
    const assignBusinessUnitArgs = {
      _organization_id: params.organizationId,
      // This RPC accepts null for unassignment, but generated DB function types
      // currently model these args as required numbers.
      _business_unit_id: params.businessUnitId,
      _business_unit_cost_center_id:
        params.businessUnitId == null ? null : params.businessUnitCostCenterId,
      _effective_from_date: params.effectiveFromDate,
      _effective_to_date: params.effectiveToDate ?? undefined,
      _existing_effective_from_date: params.existingEffectiveFromDate ?? undefined,
      _extend_previous_assignment: params.extendPreviousAssignment,
      _bring_forward_next_assignment: params.bringForwardNextAssignment,
      _profile_ids: profileIds
    } as DBFunctions['assign_business_unit_to_profiles']['Args']

    const { data, error } = await this.supabase.rpc(
      'assign_business_unit_to_profiles',
      assignBusinessUnitArgs
    )

    if (error) {
      console.error('Error assigning business_unit to profiles:', error)
      throw error
    }

    return data ?? []
  }

  async getProfileBusinessUnitsHistory(
    organizationId: number,
    profileId: string
  ): Promise<BusinessUnitTimelineHistoryRow[]> {
    const { data, error } = await this.supabase.rpc('get_profile_business_units_history', {
      _organization_id: organizationId,
      _profile_id: profileId
    })

    if (error) {
      console.error('Error getting business unit timeline history:', error)
      throw error
    }

    return (data ?? []) as BusinessUnitTimelineHistoryRow[]
  }

  async previewBusinessUnitTimeline(params: {
    organizationId: number
    profileId: string
    businessUnitId: number | null
    businessUnitCostCenterId: number | null
    effectiveFromDate: string
    effectiveToDate: string | null
    existingEffectiveFromDate: string | null
    extendPreviousAssignment: boolean
    bringForwardNextAssignment: boolean
  }): Promise<BusinessUnitTimelinePreviewResult> {
    const previewBusinessUnitArgs = {
      _organization_id: params.organizationId,
      _profile_id: params.profileId,
      // This RPC accepts null for unassignment, but generated DB function types
      // currently model these args as required numbers.
      _business_unit_id: params.businessUnitId,
      _business_unit_cost_center_id:
        params.businessUnitId == null ? null : params.businessUnitCostCenterId,
      _effective_from_date: params.effectiveFromDate,
      _effective_to_date: params.effectiveToDate ?? undefined,
      _existing_effective_from_date: params.existingEffectiveFromDate ?? undefined,
      _extend_previous_assignment: params.extendPreviousAssignment,
      _bring_forward_next_assignment: params.bringForwardNextAssignment
    } as unknown as DBFunctions['preview_business_unit_timeline']['Args']

    const { data, error } = await this.supabase.rpc(
      'preview_business_unit_timeline',
      previewBusinessUnitArgs
    )

    if (error) {
      console.error('Error previewing business unit timeline:', error)
      throw error
    }

    return (data ?? {
      history_rows: [],
      modifications: [],
      controls: {
        can_extend_previous: false,
        can_bring_forward_next: false,
        show_end_date: false
      }
    }) as BusinessUnitTimelinePreviewResult
  }

  async updateTeamsTags(teamId: number, tags: string[]) {
    const { error } = await this.supabase.rpc('update_teams_tags', {
      _team_id: teamId,
      _tags: tags
    })

    if (error) {
      console.error('Error updating team tags:', error)
      throw error
    }
  }

  async getMetricActiveCompensationRules(metricId: number) {
    const { data, error } = await this.supabase
      .from('compensation_rules')
      .select(
        ` *,
          compensation_rule_versions!inner (
            *,
            compensation_rule_version_metrics!inner (*)
          )
        `
      )
      .eq('compensation_rule_versions.compensation_rule_version_metrics.metric_id', metricId)

    if (error) throw new Error(error.message)

    const today = format(new Date(), 'yyyy-MM-dd')

    return (
      data
        ?.map(rule => ({
          ...rule,
          compensation_rule_versions: rule.compensation_rule_versions.filter(v =>
            isInRange(today, parseDateRange(v.active_period as string))
          )
        }))
        .filter(rule => rule.compensation_rule_versions.length > 0) ?? []
    )
  }

  async getMetricActiveTargets(metricId: number) {
    const { data, error } = await this.supabase
      .from('targets')
      .select('*')
      .eq('metric_id', metricId)
      .is('archived_at', null)

    if (error) throw new Error(error.message)
    return data ?? []
  }

  async getMetricActiveChallenges(metricId: number) {
    const { data, error } = await this.supabase
      .from('challenges')
      .select('*, is_active, is_scheduled')
      .eq('threshold_metric_id', metricId)
      .is('archived_at', null)
      .or('is_active.eq.true,is_scheduled.eq.true')

    if (error) throw new Error(error.message)
    return data ?? []
  }

  async getMetricActiveCreditSetups(metricId: number) {
    const { data, error } = await this.supabase
      .from('credit_setups')
      .select('*')
      .eq('source_metric_id', metricId)
      .is('archived_at', null)
      .order('created_at', { ascending: true })

    if (error) throw new Error(error.message)
    return data ?? []
  }

  async getMetricUsage(metricId: number): Promise<MetricUsage> {
    const [rules, targets, challenges, creditSetups] = await Promise.all([
      this.getMetricActiveCompensationRules(metricId),
      this.getMetricActiveTargets(metricId),
      this.getMetricActiveChallenges(metricId),
      this.getMetricActiveCreditSetups(metricId)
    ])
    return {
      compensationRules: rules,
      targets: targets as MetricUsage['targets'],
      challenges: challenges as MetricUsage['challenges'],
      creditSetups,
      isBeingUsed:
        rules.length > 0 || targets.length > 0 || challenges.length > 0 || creditSetups.length > 0
    }
  }

  async listCreditSetups({
    organizationId,
    sourceMetricId,
    includeArchived = false
  }: import('./index').CreditSetupListParams) {
    let query = this.supabase
      .from('credit_setups')
      .select('*, metrics!inner(organization_id)')
      .eq('metrics.organization_id', organizationId)
      .eq('source_metric_id', sourceMetricId)
      .order('name', { ascending: true })

    if (!includeArchived) {
      query = query.is('archived_at', null)
    }

    const { data, error } = await query
    if (error) throw error
    return (data ?? []).map(({ metrics: _metrics, ...setup }) => setup)
  }

  async listCreditSetupsByMetricIds({
    organizationId,
    sourceMetricIds,
    includeArchived = false
  }: import('./index').CreditSetupListByMetricIdsParams) {
    if (sourceMetricIds.length === 0) return []

    let query = this.supabase
      .from('credit_setups')
      .select('*, metrics!inner(organization_id)')
      .eq('metrics.organization_id', organizationId)
      .in('source_metric_id', sourceMetricIds)
      .order('created_at', { ascending: true })

    if (!includeArchived) {
      query = query.is('archived_at', null)
    }

    const { data, error } = await query
    if (error) throw error
    return (data ?? []).map(({ metrics: _metrics, ...setup }) => setup)
  }

  async getCreditSetup(setupId: number, organizationId: number, sourceMetricId: number) {
    const { data, error } = await this.supabase
      .from('credit_setups')
      .select('*, metrics!inner(organization_id)')
      .eq('id', setupId)
      .eq('metrics.organization_id', organizationId)
      .eq('source_metric_id', sourceMetricId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    const { metrics: _metrics, ...setup } = data
    return setup
  }

  async getCreditSetupUsage(
    organizationId: number,
    creditSetupId: number
  ): Promise<import('./index').CreditSetupUsage> {
    const { data, error } = await this.supabase.rpc('get_credit_setup_usage', {
      _organization_id: organizationId,
      _credit_setup_id: creditSetupId
    })

    if (error) throw error

    const usage = (data ?? {}) as {
      target_ids?: number[]
      compensation_rule_version_ids?: number[]
      dependency_count?: number
    }

    return {
      targetIds: usage.target_ids ?? [],
      compensationRuleVersionIds: usage.compensation_rule_version_ids ?? [],
      dependencyCount: usage.dependency_count ?? 0
    }
  }

  async createCreditSetup(setup: TablesInsert<'credit_setups'>) {
    const { data, error } = await this.supabase
      .from('credit_setups')
      .insert(setup)
      .select('*')
      .single()

    if (error) throw error
    return data
  }

  async updateCreditSetup({
    id,
    organizationId,
    sourceMetricId,
    update
  }: import('./index').CreditSetupUpdateParams) {
    const existing = await this.getCreditSetup(id, organizationId, sourceMetricId)
    if (!existing) throw new Error('Credit Setup was not found')

    const { data, error } = await this.supabase
      .from('credit_setups')
      .update(update)
      .eq('id', id)
      .eq('source_metric_id', sourceMetricId)
      .select('*')
      .single()

    if (error) throw error
    return data
  }

  async archiveCreditSetup(setupId: number, organizationId: number, sourceMetricId: number) {
    const existing = await this.getCreditSetup(setupId, organizationId, sourceMetricId)
    if (!existing) {
      throw new Error('Credit Setup was not found')
    }
    if (existing.archived_at) return existing

    return this.updateCreditSetup({
      id: setupId,
      organizationId,
      sourceMetricId,
      update: { archived_at: new Date().toISOString() }
    })
  }

  async listCreditSetupRules({
    organizationId,
    creditSetupId,
    includeArchived = false
  }: import('./index').CreditSetupRuleListParams) {
    let query = this.supabase
      .from('credit_setup_rules')
      .select('*, credit_setups!inner(metrics!inner(organization_id))')
      .eq('credit_setups.metrics.organization_id', organizationId)
      .eq('credit_setup_id', creditSetupId)
      .order('name', { ascending: true })
      .order('id', { ascending: true })

    if (!includeArchived) query = query.is('archived_at', null)

    const rules: Tables<'credit_setup_rules'>[] = []
    for (let from = 0; ; from += 1000) {
      const { data, error } = await query.range(from, from + 999)
      if (error) throw error
      rules.push(...(data ?? []).map(({ credit_setups: _setup, ...rule }) => rule))
      if ((data ?? []).length < 1000) return rules
    }
  }

  async listCreditSetupRulesPage({
    organizationId,
    creditSetupId,
    includeArchived = false,
    search,
    offset,
    limit
  }: import('./index').CreditSetupRulePageParams) {
    const { data, error } = await this.supabase.rpc('get_credit_setup_rules_page', {
      _organization_id: organizationId,
      _credit_setup_id: creditSetupId,
      _include_archived: includeArchived,
      _search: search,
      _offset: offset,
      _limit: limit
    })

    if (error) throw error
    const page = data ?? []
    return {
      items: page.slice(0, limit),
      nextOffset: page.length > limit ? offset + limit : null
    }
  }

  async getCreditSetupRule(ruleId: number, organizationId: number, creditSetupId: number) {
    const { data, error } = await this.supabase
      .from('credit_setup_rules')
      .select('*, credit_setups!inner(metrics!inner(organization_id))')
      .eq('id', ruleId)
      .eq('credit_setups.metrics.organization_id', organizationId)
      .eq('credit_setup_id', creditSetupId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    const { credit_setups: _setup, ...rule } = data
    return rule
  }

  async createCreditSetupRule(rule: TablesInsert<'credit_setup_rules'>) {
    const { data, error } = await this.supabase
      .from('credit_setup_rules')
      .insert(rule)
      .select('*')
      .single()

    if (error) throw error
    return data
  }

  async updateCreditSetupRule({
    id,
    organizationId,
    creditSetupId,
    update
  }: import('./index').CreditSetupRuleUpdateParams) {
    const existing = await this.getCreditSetupRule(id, organizationId, creditSetupId)
    if (!existing) throw new Error('Credit Setup Rule was not found')

    const { data, error } = await this.supabase
      .from('credit_setup_rules')
      .update(update)
      .eq('id', id)
      .eq('credit_setup_id', creditSetupId)
      .select('*')
      .single()

    if (error) throw error
    return data
  }

  async archiveCreditSetupRule(ruleId: number, organizationId: number, creditSetupId: number) {
    const existing = await this.getCreditSetupRule(ruleId, organizationId, creditSetupId)
    if (!existing) throw new Error('Credit Setup Rule was not found')
    if (existing.archived_at) return existing

    return this.updateCreditSetupRule({
      id: ruleId,
      organizationId,
      creditSetupId,
      update: { archived_at: new Date().toISOString() }
    })
  }

  async getCreditSetupRuleUsage(params: import('./index').CreditSetupRuleUsageParams) {
    return this.listCreditSetupRuleAssignments(params)
  }

  async listCreditSetupRuleAssignments({
    organizationId,
    creditSetupId,
    creditSetupRuleId
  }: import('./index').CreditSetupRuleAssignmentListParams) {
    const assignments: Awaited<ReturnType<DolfinRpc['listCreditSetupRuleAssignments']>> = []
    for (let from = 0; ; from += 1000) {
      const { data, error } = await this.supabase
        .rpc('get_credit_setup_rule_assignments', {
          _organization_id: organizationId,
          _credit_setup_id: creditSetupId,
          ...(creditSetupRuleId === undefined ? {} : { _credit_setup_rule_id: creditSetupRuleId })
        })
        .order('id')
        .range(from, from + 999)
      if (error) throw error
      assignments.push(...(data ?? []))
      if ((data ?? []).length < 1000) return assignments
    }
  }

  async listCreditSetupRuleAssignmentsForRules({
    organizationId,
    creditSetupRuleIds
  }: import('./index').CreditSetupRuleAssignmentBatchParams) {
    if (creditSetupRuleIds.length === 0) return []

    const { data, error } = await this.supabase.rpc('get_credit_setup_rule_assignments_for_rules', {
      _organization_id: organizationId,
      _credit_setup_rule_ids: creditSetupRuleIds
    })

    if (error) throw error
    return data ?? []
  }

  async getCreditSetupRuleAssignment(
    assignmentId: number,
    organizationId: number,
    creditSetupId: number
  ) {
    const { data, error } = await this.supabase.rpc('get_credit_setup_rule_assignments', {
      _assignment_id: assignmentId,
      _credit_setup_id: creditSetupId,
      _organization_id: organizationId
    })

    if (error) throw error
    return data?.[0] ?? null
  }

  async createCreditSetupRuleAssignment(
    params: import('./index').CreditSetupRuleAssignmentReplaceParams
  ) {
    const assignments = await this.replaceCreditSetupRuleAssignment(params)
    const assignment = assignments[0]
    if (!assignment) throw new Error('Credit Setup Rule assignment was not created')
    return assignment
  }

  async endCreditSetupRuleAssignment({
    assignmentId,
    organizationId,
    creditSetupId,
    creditSetupRuleId,
    effectiveTo
  }: import('./index').CreditSetupRuleAssignmentUpdateParams) {
    const { data, error } = await this.supabase.rpc('end_credit_setup_rule_assignment', {
      _assignment_id: assignmentId,
      _organization_id: organizationId,
      _credit_setup_id: creditSetupId,
      _credit_setup_rule_id: creditSetupRuleId,
      _effective_to: effectiveTo
    })

    if (error) throw error
    const assignment = data?.[0]
    if (!assignment) throw new Error('Credit Setup Rule assignment was not found')
    return assignment
  }

  async updateCreditSetupRuleAssignment({
    assignmentId,
    organizationId,
    creditSetupId,
    creditSetupRuleId,
    effectiveFrom,
    effectiveTo
  }: import('./index').CreditSetupRuleAssignmentEditParams) {
    const { data, error } = await this.supabase.rpc('update_credit_setup_rule_assignment', {
      _assignment_id: assignmentId,
      _organization_id: organizationId,
      _credit_setup_id: creditSetupId,
      _credit_setup_rule_id: creditSetupRuleId,
      _effective_from: effectiveFrom,
      _effective_to: effectiveTo ?? undefined
    })

    if (error) throw error
    const assignment = data?.[0]
    if (!assignment) throw new Error('Credit Setup Rule assignment was not found')
    return assignment
  }

  async replaceCreditSetupRuleAssignment({
    organizationId,
    creditSetupId,
    creditSetupRuleId,
    profileId,
    teamId,
    effectiveFrom,
    effectiveTo,
    existingAssignmentId
  }: import('./index').CreditSetupRuleAssignmentReplaceParams) {
    const { data, error } = await this.supabase.rpc('replace_credit_setup_rule_assignment', {
      _organization_id: organizationId,
      _credit_setup_id: creditSetupId,
      _credit_setup_rule_id: creditSetupRuleId,
      _effective_from: effectiveFrom,
      _profile_id: profileId ?? undefined,
      _team_id: teamId ?? undefined,
      _effective_to: effectiveTo ?? undefined,
      _existing_assignment_id: existingAssignmentId ?? undefined
    })

    if (error) throw error
    return data ?? []
  }

  async getDailyMetrics({
    organizationId,
    metricIds,
    teamIds = [],
    startDate,
    endDate
  }: {
    organizationId: number
    metricIds: number[]
    teamIds?: number[]
    startDate: string
    endDate: string
  }): Promise<DBFunctionReturns<'get_daily_metrics'>> {
    const { data, error } = await this.supabase.rpc('get_daily_metrics', {
      p_organization_id: organizationId,
      p_metric_ids: metricIds,
      ...(teamIds.length > 0 ? { p_team_ids: teamIds } : {}),
      p_start_date: startDate,
      p_end_date: endDate
    })

    if (error) throw error

    return data ?? []
  }

  async getTargetActiveCompensationRules(targetId: number) {
    const { data, error } = await this.supabase
      .from('compensation_rules')
      .select(
        ` *,
          compensation_rule_versions!inner (
            *,
            compensation_rule_version_targets!inner (*)
          )
        `
      )
      .eq('compensation_rule_versions.compensation_rule_version_targets.target_id', targetId)

    if (error) throw new Error(error.message)

    const today = format(new Date(), 'yyyy-MM-dd')

    return (
      data
        ?.map(rule => ({
          ...rule,
          compensation_rule_versions: rule.compensation_rule_versions.filter(v =>
            isInRange(today, parseDateRange(v.active_period as string))
          )
        }))
        .filter(rule => rule.compensation_rule_versions.length > 0) ?? []
    )
  }

  async getTargetUsage(targetId: number) {
    const rules = await this.getTargetActiveCompensationRules(targetId)
    return {
      compensationRules: rules,
      isBeingUsed: rules.length > 0
    }
  }

  async getTeamActivePlans(teamId: number) {
    const today = format(new Date(), 'yyyy-MM-dd')

    const { data, error } = await this.supabase
      .from('plans')
      .select(
        `
      *,
      teams_plans!inner (*),
      compensation_rules:compensation_rules!inner (
        plan_id,
        compensation_rule_versions!inner (
          active_period
        )
      )
    `
      )
      .eq('teams_plans.team_id', teamId)

    if (error) throw new Error(error.message)
    if (!data) return []

    return data
      .map(plan => {
        const activeCount =
          plan.compensation_rules?.filter(rule =>
            rule.compensation_rule_versions.some(v =>
              isInRange(today, parseDateRange(v.active_period as string))
            )
          ).length ?? 0

        return {
          ...plan,
          active_compensation_rules_count: activeCount
        }
      })
      .filter(p => p.active_compensation_rules_count > 0)
  }

  async getTeamActiveTargets(teamId: number) {
    const { data, error } = await this.supabase
      .from('targets')
      .select('*, teams_targets!inner( * )')
      .eq('teams_targets.team_id', teamId)
      .is('archived_at', null)

    if (error) throw new Error(error.message)
    return data ?? []
  }

  async getTeamActiveChallenges(teamId: number) {
    const { data, error } = await this.supabase
      .from('challenges')
      .select(
        `
        id,
        behavior,
        organization_id,
        archived_at,
        created_at,
        goal_amount,
        period,
        threshold_metric_id,
        title,
        top_n_value,
        winning_criteria,
        challenge_team_ids,
        is_active,
        is_scheduled,
        participants_count,
        teams_challenges!inner(team_id)
        `
      )
      .eq('teams_challenges.team_id', teamId)
      .is('archived_at', null)
      .or('is_active.eq.true,is_scheduled.eq.true')

    if (error) throw new Error(error.message)
    if (!data) return []

    return data.map(
      ({ teams_challenges: _teams_challenges, ...challenge }) => challenge
    ) as Tables<'challenges'>[]
  }

  async getTeamHasMetricValues(teamId: number) {
    const { data, error } = await this.supabase
      .from('metric_values')
      .select('*')
      .eq('team_id', teamId)
      .limit(1)

    if (error) throw new Error(error.message)

    return !!(data?.[0] ?? false)
  }

  async getTeamUsage(teamId: number) {
    const [team, plans, targets, challenges, hasMetricValues] = await Promise.all([
      this.supabase.from('teams').select('organization_id').eq('id', teamId).maybeSingle(),
      this.getTeamActivePlans(teamId),
      this.getTeamActiveTargets(teamId),
      this.getTeamActiveChallenges(teamId),
      this.getTeamHasMetricValues(teamId)
    ])

    if (team.error) throw new Error(team.error.message)
    if (!team.data) throw new Error(`Team ${teamId} not found`)

    return {
      organization_id: team.data.organization_id,
      plans,
      targets,
      challenges,
      hasMetricValues,
      isBeingUsed:
        plans.length > 0 || targets.length > 0 || challenges.length > 0 || hasMetricValues
    }
  }

  /**
   * Checks for any related rows that would block team deletion (FK RESTRICT).
   * Use this for delete flow; getTeamUsage only considers "active" targets/challenges.
   */
  async getTeamDeletionBlockers(teamId: number) {
    const [hasPlans, hasTargets, hasChallenges, hasMetricValues] = await Promise.all([
      this.getTeamHasPlans(teamId),
      this.getTeamHasTargets(teamId),
      this.getTeamHasChallenges(teamId),
      this.getTeamHasMetricValues(teamId)
    ])

    return {
      hasPlans,
      hasTargets,
      hasChallenges,
      hasMetricValues
    }
  }

  async unlinkTeamFromPlan({
    organizationId,
    teamId,
    planId,
    effectiveOn
  }: UnlinkTeamFromPlanArgs) {
    const { error } = await this.supabase.rpc('unlink_team_from_plan_with_history', {
      _organization_id: organizationId,
      _team_id: teamId,
      _plan_id: planId,
      _effective_on: effectiveOn
    })

    if (error) throw new Error(error.message)
  }

  async unlinkTeamFromTarget(organizationId: number, teamId: number, targetId: number) {
    if (!organizationId) {
      throw new Error('organizationId is required')
    }

    const { data, error } = await this.supabase
      .from('teams_targets')
      .delete()
      .select('team_id, target_id, teams!inner(organization_id), targets!inner(organization_id)')
      .eq('team_id', teamId)
      .eq('target_id', targetId)
      .eq('teams.organization_id', organizationId)
      .eq('targets.organization_id', organizationId)

    if (error) throw new Error(error.message)
    if (!data || data.length === 0) {
      throw new Error('No target-team link found for the provided organization')
    }

    this.targetCache.delete(targetId)
  }

  private async getTeamHasPlans(teamId: number) {
    const { data, error } = await this.supabase
      .from('teams_plans_history')
      .select('id')
      .eq('team_id', teamId)
      .limit(1)

    if (error) throw new Error(error.message)
    return !!(data?.[0] ?? false)
  }

  private async getTeamHasTargets(teamId: number) {
    const { data, error } = await this.supabase
      .from('teams_targets')
      .select('*')
      .eq('team_id', teamId)
      .limit(1)

    if (error) throw new Error(error.message)
    return !!(data?.[0] ?? false)
  }

  private async getTeamHasChallenges(teamId: number) {
    const { data, error } = await this.supabase
      .from('teams_challenges')
      .select('*')
      .eq('team_id', teamId)
      .limit(1)

    if (error) throw new Error(error.message)
    return !!(data?.[0] ?? false)
  }

  async setDraftTvpBased(draftId: number, isTvpBased: boolean): Promise<void> {
    const { error } = await this.supabase
      .from('compensation_rule_drafts')
      .update({ is_tvp_based: isTvpBased })
      .eq('id', draftId)

    if (error) throw error
  }

  async setVersionTvpBased(versionId: number, isTvpBased: boolean): Promise<void> {
    const { error } = await this.supabase
      .from('compensation_rule_versions')
      .update({ is_tvp_based: isTvpBased })
      .eq('id', versionId)

    if (error) throw error
  }

  async getPendingProcessingCountForMetric(
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    teamIds?: number[]
  ): Promise<number | undefined> {
    const { data, error } = await this.supabase.rpc('get_pending_processing_count_for_metric', {
      _metric_id: metricId,
      _profile_id: profileId,
      _period_start: periodStart,
      _period_end: periodEnd,
      _organization_id: organizationId,
      _team_ids: teamIds ?? undefined
    })

    if (error) {
      console.error('Error getting pending processing count:', error)
      return undefined
    }

    return (data as number) ?? 0
  }

  async getOrganizationPendingProcessingCountForMetric(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number
  ): Promise<number | undefined> {
    const { data, error } = await this.supabase.rpc(
      'get_organization_pending_processing_count_for_metric',
      {
        _metric_id: metricId,
        _period_start: periodStart,
        _period_end: periodEnd,
        _organization_id: organizationId
      }
    )

    if (error) {
      console.error('Error getting organization pending processing count:', error)
      return undefined
    }

    return (data as number) ?? 0
  }

  async getMetricSourceRecords(
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    teamIds?: number[],
    targetCurrency?: string
  ) {
    const { data, error } = await this.supabase.rpc(
      'get_metric_source_records_with_inclusion_status',
      {
        _metric_id: metricId,
        _profile_id: profileId,
        _period_start: periodStart,
        _period_end: periodEnd,
        _organization_id: organizationId,
        _team_ids: teamIds ?? undefined,
        _target_currency: targetCurrency ?? undefined
      }
    )

    if (error) {
      console.error('Error getting metric source records:', error)
      return []
    }

    return (data as unknown as MetricSourceRecord[]) ?? []
  }

  async getTargetMetricSourceRecords(
    targetId: number,
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    targetCurrency?: string
  ) {
    const { data, error } = await this.supabase.rpc('get_target_metric_source_records', {
      _target_id: targetId,
      _metric_id: metricId,
      _profile_id: profileId,
      _period_start: periodStart,
      _period_end: periodEnd,
      _organization_id: organizationId,
      _target_currency: targetCurrency ?? undefined
    })

    if (error) throw error

    return (data as unknown as MetricSourceRecord[]) ?? []
  }

  async getTargetMetricSourceRecordsPage(
    targetId: number,
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    params: import('./index').TargetContributingSourceRecordsPageParams,
    targetCurrency?: string
  ): Promise<import('./index').MetricSourceRecordsPage> {
    const { data, error } = await this.supabase.rpc('get_target_metric_source_records_page', {
      _target_id: targetId,
      _metric_id: metricId,
      _profile_id: profileId,
      _period_start: periodStart,
      _period_end: periodEnd,
      _organization_id: organizationId,
      _included: params.included,
      _search: params.search?.trim() || undefined,
      _offset: params.offset,
      _limit: params.limit,
      _target_currency: targetCurrency ?? undefined
    })

    if (error) throw error

    const rows = (data ?? []) as Array<MetricSourceRecord & { total_count: number | null }>
    return {
      records: rows.map(({ total_count: _totalCount, ...record }) => record),
      count: Number(rows[0]?.total_count ?? 0)
    }
  }

  async getOrganizationMetricSourceRecords(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    targetCurrency?: string
  ) {
    const { data, error } = await this.supabase.rpc('get_organization_metric_source_records', {
      _metric_id: metricId,
      _period_start: periodStart,
      _period_end: periodEnd,
      _organization_id: organizationId,
      _target_currency: targetCurrency ?? undefined
    })

    if (error) {
      console.error('Error getting organization metric source records:', error)
      return []
    }

    return (data as unknown as MetricSourceRecord[]) ?? []
  }

  async getOrganizationMetricSourceRecordsPage(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    profileId: string | undefined,
    params: import('./index').TargetContributingSourceRecordsPageParams,
    targetCurrency?: string
  ): Promise<import('./index').MetricSourceRecordsPage> {
    const { data, error } = await this.supabase.rpc('get_organization_metric_source_records_page', {
      _metric_id: metricId,
      _period_start: periodStart,
      _period_end: periodEnd,
      _organization_id: organizationId,
      _profile_id: profileId ?? undefined,
      _scope: params.scope,
      _included: params.included,
      _search: params.search?.trim() || undefined,
      _offset: params.offset,
      _limit: params.limit,
      _target_currency: targetCurrency ?? undefined
    })

    if (error) {
      console.error('Error getting paginated organization metric source records:', error)
      return { records: [], count: 0 }
    }

    const rows = (data ?? []) as Array<MetricSourceRecord & { total_count: number | null }>
    return {
      records: rows.map(({ total_count: _totalCount, ...record }) => record),
      count: Number(rows[0]?.total_count ?? 0)
    }
  }

  async getMetricTargetScope(
    targetId: number,
    profileId: string,
    organizationId: number,
    periodStart: string,
    periodEnd: string
  ): Promise<import('./index').MetricTargetScopeAccess | null> {
    const { data, error } = await this.supabase.rpc('get_metric_target_scope', {
      _target_id: targetId,
      _profile_id: profileId,
      _organization_id: organizationId,
      _period_start: periodStart,
      _period_end: periodEnd
    })

    if (error) {
      const rpcError = new Error(error.message)
      Object.assign(rpcError, error)
      throw rpcError
    }

    return (data as import('./index').MetricTargetScopeAccess[] | null)?.[0] ?? null
  }

  async getCreditSetupRuleMetricSourceRecordsPage(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    profileId: string | undefined,
    params: import('./index').TargetContributingSourceRecordsPageParams,
    targetCurrency?: string
  ): Promise<import('./index').MetricSourceRecordsPage> {
    const { data, error } = await this.supabase.rpc(
      'get_credit_setup_rule_metric_source_records_page',
      {
        _metric_id: metricId,
        _period_start: periodStart,
        _period_end: periodEnd,
        _organization_id: organizationId,
        _profile_id: profileId ?? undefined,
        _scope: params.scope,
        _included: params.included,
        _search: params.search?.trim() || undefined,
        _offset: params.offset,
        _limit: params.limit,
        _target_currency: targetCurrency ?? undefined
      }
    )

    if (error) throw error

    const rows = (data ?? []) as Array<MetricSourceRecord & { total_count: number | null }>
    return {
      records: rows.map(({ total_count: _totalCount, ...record }) => record),
      count: Number(rows[0]?.total_count ?? 0)
    }
  }

  async findMatchingDeals(
    organizationId: number,
    dealLinkField: string,
    dealLinkValue: string,
    referenceDate: string
  ): Promise<DBFunctionReturns<'find_matching_deals'>> {
    const { data, error } = await this.supabase.rpc('find_matching_deals', {
      p_organization_id: organizationId,
      p_deal_link_field: dealLinkField,
      p_deal_link_value: dealLinkValue,
      p_reference_date: referenceDate
    })

    if (error) {
      console.error('Error finding matching deals:', error)
      return []
    }

    return data ?? []
  }

  async getActiveCurrencies(organizationId: number): Promise<string[]> {
    const { data, error } = await this.supabase.rpc('get_active_currencies', {
      _organization_id: organizationId
    })

    if (error) {
      console.error('Error getting active currencies:', error)
      return []
    }

    return data ?? []
  }

  async saveMetricValueCurrencies(conversions: MetricValueCurrencySnapshot[]): Promise<number> {
    if (conversions.length === 0) return 0

    const { data, error } = await this.supabase.rpc('save_metric_value_currencies', {
      _conversions: conversions as Json
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  }

  async startMetricValueCurrencyBackfill(
    organizationId: number,
    currency: string
  ): Promise<MetricValueCurrencyBackfillProgress> {
    const { data, error } = await this.supabase.rpc('start_metric_value_currency_backfill', {
      _organization_id: organizationId,
      _currency: currency
    })
    if (error) throw new Error(error.message)
    return data
  }

  async reconcileMetricValueCurrencyDimensionsChunk({
    organizationId,
    currency,
    afterMetricValueId,
    throughMetricValueId,
    limit
  }: {
    organizationId: number
    currency: string
    afterMetricValueId: number
    throughMetricValueId: number
    limit: number
  }): Promise<MetricValueCurrencyReconciliationChunk> {
    const { data, error } = await this.supabase.rpc(
      'reconcile_metric_value_currency_dimensions_chunk',
      {
        _organization_id: organizationId,
        _currency: currency,
        _after_metric_value_id: afterMetricValueId,
        _through_metric_value_id: throughMetricValueId,
        _limit: limit
      }
    )
    if (error) throw new Error(error.message)
    return (
      data[0] ?? {
        processed_count: 0,
        reconciled_count: 0,
        next_metric_value_id: afterMetricValueId
      }
    )
  }

  async getMetricValuesForCurrencyBackfill({
    organizationId,
    currency,
    afterMetricValueId,
    throughMetricValueId,
    limit
  }: {
    organizationId: number
    currency: string
    afterMetricValueId: number
    throughMetricValueId: number
    limit: number
  }): Promise<MetricValueCurrencyBackfillPage> {
    const { data, error } = await this.supabase.rpc('get_metric_values_for_currency_backfill', {
      _organization_id: organizationId,
      _currency: currency,
      _after_metric_value_id: afterMetricValueId,
      _through_metric_value_id: throughMetricValueId,
      _limit: limit
    })
    if (error) throw new Error(error.message)
    return data
  }

  async updateMetricValueCurrencyBackfillProgress(
    organizationId: number,
    currency: string,
    progress: MetricValueCurrencyProgressUpdate
  ): Promise<MetricValueCurrencyBackfillProgress> {
    const { data, error } = await this.supabase.rpc(
      'update_metric_value_currency_backfill_progress',
      {
        _organization_id: organizationId,
        _currency: currency,
        _progress: progress as Json
      }
    )
    if (error) throw new Error(error.message)
    return data
  }

  async verifyMetricValueCurrencyBackfill(
    organizationId: number,
    currency: string
  ): Promise<MetricValueCurrencyBackfillProgress> {
    const { data, error } = await this.supabase.rpc('verify_metric_value_currency_backfill', {
      _organization_id: organizationId,
      _currency: currency
    })
    if (error) throw new Error(error.message)
    return data
  }

  async getTargetsMissingAmounts(organizationId: number) {
    const { data, error } = await this.supabase.rpc('get_targets_with_missing_amounts', {
      _organization_id: organizationId
    })
    if (error) throw error
    return data ?? []
  }

  async getDataIssuesSummary({
    organizationId,
    tableName,
    periodStart,
    periodEnd
  }: {
    organizationId: number
    tableName: string
    periodStart?: string
    periodEnd?: string
  }) {
    const { data, error } = await this.supabase.rpc('get_data_issues_summary', {
      _organization_id: organizationId,
      _table_name: tableName,
      _period_start: periodStart,
      _period_end: periodEnd
    })
    if (error) throw error
    return data
  }

  async getStatementPeriodCutDays(
    organizationId: number,
    currentPeriodDate: string
  ): Promise<number[]> {
    const { data, error } = await this.supabase
      .from('teams_statement_configs')
      .select('period, statement_config_id, statement_configs!inner(cut_day)')
      .eq('statement_configs.organization_id', organizationId)
      .contains('period', `[${currentPeriodDate},${currentPeriodDate}]`)
    if (error) throw error

    return (data ?? [])
      .map(row => {
        const config = row.statement_configs as unknown as { cut_day: number } | null
        return config?.cut_day
      })
      .filter((day): day is number => day != null)
  }

  async getReportedIssuesCount(organizationId: number): Promise<number> {
    // Open issues linked to payouts in this org
    const { count: payoutIssues, error: payoutError } = await this.supabase
      .from('issues')
      .select('*, payouts!inner(organization_id)', {
        head: true,
        count: 'exact'
      })
      .eq('status', 'open')
      .eq('payouts.organization_id', organizationId)
    if (payoutError) throw payoutError

    // Open issues linked to statements only (no payout) in this org
    const { count: statementOnlyIssues, error: statementError } = await this.supabase
      .from('issues')
      .select('*, statements!inner(organization_id)', {
        head: true,
        count: 'exact'
      })
      .eq('status', 'open')
      .is('payout_id', null)
      .eq('statements.organization_id', organizationId)
    if (statementError) throw statementError

    return (payoutIssues ?? 0) + (statementOnlyIssues ?? 0)
  }

  async getUnreviewedDiscrepanciesCount(organizationId: number): Promise<number> {
    const { count, error } = await this.supabase
      .from('payout_discrepancies')
      .select('*', { head: true, count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('reviewed', false)
    if (error) throw error
    return count ?? 0
  }

  async getUnreviewedDiscrepanciesCountForStatement(statementId: number): Promise<number> {
    const { data, error } = await this.supabase.rpc(
      'get_unreviewed_discrepancies_count_for_statement',
      { _statement_id: statementId }
    )
    if (error) throw error
    return data ?? 0
  }

  private readonly payoutDiscrepancySelect = [
    '*',
    'payouts!payout_discrepancies_payout_id_fkey(*, payout_categories(label), compensation_rules(id, source), profiles!payouts_profile_id_fkey(id, name, email, img_url), deals!payouts_deal_id_fkey(id, deal_id, name, split_record_type, split_anchor_id, split_anchor:deals!split_anchor_id(id, deal_id, name)), deal_line_items!payouts_deal_line_item_id_fkey(id, line_item_id, product_name, deals(id, deal_id, name, split_record_type, split_anchor_id, split_anchor:deals!split_anchor_id(id, deal_id, name))), invoices!public_payouts_invoice_id_fkey(id, invoice_number, deal_id, deals(id, deal_id, name, split_record_type, split_anchor_id, split_anchor:deals!split_anchor_id(id, deal_id, name))), line_items!payouts_line_item_id_fkey(id, invoices(id, invoice_number, deals(id, deal_id, name, split_record_type, split_anchor_id, split_anchor:deals!split_anchor_id(id, deal_id, name)))), activities!public_payouts_activity_id_fkey(id, activity_id, type, deal_id, deals(id, deal_id, name, split_record_type, split_anchor_id, split_anchor:deals!split_anchor_id(id, deal_id, name))), kpis!public_payouts_kpi_id_fkey(id, kpis_table_id, kpis_tables(label)), statements!payouts_statement_id_fkey(period_start, period_end, cut_period, status), payout_installments(id, installment_number, phase_id, statement_id, amount, statements(period_start, period_end, cut_period, status)))',
    'existing_statement:statements!payout_discrepancies_existing_statement_id_fkey(period_start, period_end, cut_period, status)',
    'calculated_statement:statements!payout_discrepancies_calculated_statement_id_fkey(period_start, period_end, cut_period, status)',
    'reviewer:profiles!payout_discrepancies_reviewed_by_fkey(id, name, email, img_url)'
  ].join(', ')

  private readonly updatesOnHoldReviewSelect = [
    'id, organization_id, payout_id, discrepancy_type, calculated_value, existing_value, existing_original_value, calculated_node_output, calculated_entities_ids, existing_currency, protected_reason, protected_status, execution_id, created_at',
    'calculation_execution:executions!payout_discrepancies_execution_id_fkey(compensation_rule_version_id)',
    'payouts!payout_discrepancies_payout_id_fkey(id, status, currency, original_value, description, compensation, edited, exchange_rate, compensation_rule_id, profile_id, period_start, period_end, entities_ids, deal_id, invoice_id, activity_id, kpi_id, line_item_id, custom_record_id, statement_id, execution:executions!payouts_execution_id_fkey(compensation_rule_version_id), profiles!payouts_profile_id_fkey(id, name, email, img_url), compensation_rules(id, label, source, compensation_rule_versions(id, active_period, created_at)), deals!payouts_deal_id_fkey(crm_provider), invoices!public_payouts_invoice_id_fkey(payroll_provider, deals!invoices_deal_id_fkey(crm_provider)), activities!public_payouts_activity_id_fkey(deals!activities_deal_id_fkey(crm_provider)), line_items!payouts_line_item_id_fkey(invoices!line_items_invoice_id_fkey(payroll_provider, deals!invoices_deal_id_fkey(crm_provider))), statements!payouts_statement_id_fkey(id, period_start, period_end, status), payout_installments(statement_id, status))',
    'existing_statement:statements!payout_discrepancies_existing_statement_id_fkey(id, period_start, period_end, status)',
    'calculated_statement:statements!payout_discrepancies_calculated_statement_id_fkey(id, period_start, period_end, status)'
  ].join(', ')

  private readonly updatesOnHoldScaffoldSelect = [
    'id, payout_id, discrepancy_type, protected_reason, protected_status, created_at',
    'payouts!payout_discrepancies_payout_id_fkey(id, status, profiles!payouts_profile_id_fkey(id, name), compensation_rules(source), deals!payouts_deal_id_fkey(crm_provider), invoices!public_payouts_invoice_id_fkey(payroll_provider, deals!invoices_deal_id_fkey(crm_provider)), activities!public_payouts_activity_id_fkey(deals!activities_deal_id_fkey(crm_provider)), line_items!payouts_line_item_id_fkey(invoices!line_items_invoice_id_fkey(payroll_provider, deals!invoices_deal_id_fkey(crm_provider))), statements!payouts_statement_id_fkey(id, period_start, period_end, status), payout_installments(statement_id, status))',
    'existing_statement:statements!payout_discrepancies_existing_statement_id_fkey(id, period_start, period_end, status)',
    'calculated_statement:statements!payout_discrepancies_calculated_statement_id_fkey(id, period_start, period_end, status)'
  ].join(', ')

  async getUnreviewedDiscrepancies(organizationId: number): Promise<PayoutDiscrepancyWithPayout[]> {
    const { data, error } = await this.supabase
      .from('payout_discrepancies')
      .select(this.payoutDiscrepancySelect)
      .eq('organization_id', organizationId)
      .eq('reviewed', false)
    if (error) throw error
    return (data ?? []) as unknown as PayoutDiscrepancyWithPayout[]
  }

  async getUpdatesOnHoldScaffoldRows(organizationId: number): Promise<UpdateOnHoldScaffoldRow[]> {
    const pageSize = 100
    const rows: UpdateOnHoldScaffoldRow[] = []
    let cursor: number | null = null

    do {
      let query = this.supabase
        .from('payout_discrepancies')
        .select(this.updatesOnHoldScaffoldSelect)
        .eq('organization_id', organizationId)
        .eq('reviewed', false)
        .order('id', { ascending: true })
        .limit(pageSize)
      if (cursor != null) query = query.gt('id', cursor)

      const { data, error } = await query
      if (error) throw error

      const page = (data ?? []) as unknown as UpdateOnHoldScaffoldRow[]
      rows.push(...page)
      cursor = page.at(-1)?.id ?? null
      if (page.length < pageSize) break
    } while (cursor != null)

    return rows
  }

  async getUpdatesOnHoldReviewRows(
    organizationId: number,
    updateIds?: number[]
  ): Promise<UpdateOnHoldReviewRow[]> {
    if (updateIds?.length === 0) return []

    if (updateIds) {
      const { data, error } = await this.supabase
        .from('payout_discrepancies')
        .select(this.updatesOnHoldReviewSelect)
        .eq('organization_id', organizationId)
        .eq('reviewed', false)
        .in('id', updateIds)
        .order('id', { ascending: true })
        .limit(updateIds.length)
      if (error) throw error
      return (data ?? []) as unknown as UpdateOnHoldReviewRow[]
    }

    const pageSize = 100
    const rows: UpdateOnHoldReviewRow[] = []
    let cursor: number | null = null

    do {
      let query = this.supabase
        .from('payout_discrepancies')
        .select(this.updatesOnHoldReviewSelect)
        .eq('organization_id', organizationId)
        .eq('reviewed', false)
        .order('id', { ascending: true })
        .limit(pageSize)
      if (cursor != null) query = query.gt('id', cursor)

      const { data, error } = await query
      if (error) throw error

      const page = (data ?? []) as unknown as UpdateOnHoldReviewRow[]
      rows.push(...page)
      cursor = page.at(-1)?.id ?? null
      if (page.length < pageSize) break
    } while (cursor != null)

    return rows
  }

  async previewUpdatesOnHoldResolution(
    organizationId: number,
    decisions: UpdateOnHoldDecisionInput[]
  ): Promise<UpdateOnHoldPreview> {
    const { data, error } = await this.supabase.rpc('preview_updates_on_hold_resolution', {
      _organization_id: organizationId,
      _decisions: decisions.map(({ updateId, decision }) => ({ update_id: updateId, decision }))
    })
    if (error) throw error
    const preview = (data ?? {}) as {
      blockers?: unknown
      fingerprint: string
      lines?: Record<string, unknown>[]
      summary?: Record<string, unknown>
    }
    return {
      ...preview,
      blockers: normalizeUpdatesOnHoldBlockers(preview.blockers),
      lines: preview.lines ?? [],
      summary: preview.summary ?? {}
    }
  }

  async resolveUpdatesOnHoldResolution(
    organizationId: number,
    decisions: UpdateOnHoldDecisionInput[],
    fingerprint: string
  ): Promise<UpdateOnHoldCommittedResult> {
    const { data, error } = await this.supabase.rpc('resolve_updates_on_hold_resolution', {
      _organization_id: organizationId,
      _decisions: decisions.map(({ updateId, decision }) => ({ update_id: updateId, decision })),
      _fingerprint: fingerprint
    })
    if (error) throw error
    const result = (data ?? {}) as unknown as UpdateOnHoldCommittedResult
    return {
      ...result,
      batchId: result.batchId ?? ((data ?? {}) as { batch_id?: string | null }).batch_id ?? null,
      resolvedBy:
        result.resolvedBy ?? ((data ?? {}) as { resolved_by?: string | null }).resolved_by ?? null,
      resolvedByName:
        result.resolvedByName ??
        ((data ?? {}) as { resolved_by_name?: string | null }).resolved_by_name ??
        null,
      resolvedAt:
        result.resolvedAt ?? ((data ?? {}) as { resolved_at?: string | null }).resolved_at ?? null,
      blockers: result.blockers ?? [],
      lines: result.lines ?? [],
      summary: result.summary ?? {}
    }
  }

  async getAppliedDiscrepancies(organizationId: number): Promise<PayoutDiscrepancyWithPayout[]> {
    const { data, error } = await this.supabase
      .from('payout_discrepancies')
      .select(this.payoutDiscrepancySelect)
      .eq('organization_id', organizationId)
      .eq('reviewed', true)
    if (error) throw error

    const rows = (data ?? []) as unknown as PayoutDiscrepancyWithPayout[]
    const reviewerIds = Array.from(
      new Set(rows.map(row => row.reviewed_by).filter((id): id is string => id != null))
    )
    if (reviewerIds.length === 0) return rows

    const { data: reviewers, error: reviewerError } = await this.supabase
      .from('profiles')
      .select('id, name, email, img_url')
      .in('id', reviewerIds)
    if (reviewerError) throw reviewerError

    const reviewerById = new Map((reviewers ?? []).map(reviewer => [reviewer.id, reviewer]))
    return rows.map(row => ({
      ...row,
      reviewer: row.reviewed_by ? (reviewerById.get(row.reviewed_by) ?? null) : null
    }))
  }

  async getPayoutDiscrepancyRecalculationReasons(
    organizationId: number,
    discrepancyIds: number[]
  ): Promise<PayoutDiscrepancyRecalculationReason[]> {
    if (discrepancyIds.length === 0) return []

    const { data, error } = await this.supabase.rpc(
      'get_payout_discrepancy_recalculation_reasons',
      {
        _organization_id: organizationId,
        _discrepancy_ids: discrepancyIds
      }
    )

    if (error) throw error

    return (data ?? []) as PayoutDiscrepancyRecalculationReason[]
  }

  async getRecordRecalculationHistory({
    organizationId,
    source,
    recordId
  }: {
    organizationId: number
    source: RecordInvestigationSource
    recordId: number
  }): Promise<RecordRecalculationHistoryEntry[]> {
    const idColumn = DolfinRecord.getSourceRecordIdColumn(source)
    if (!idColumn) {
      return []
    }

    type HistoryRow = Pick<
      Tables<'records_to_be_processed_history'>,
      'id' | 'records_to_be_processed_id' | 'created_at' | 'recalculation_reason' | 'source'
    > &
      Record<typeof idColumn, number | null>

    const { data, error } = await this.supabase
      .from('records_to_be_processed_history')
      .select(
        `id, records_to_be_processed_id, created_at, recalculation_reason, source, ${idColumn}`
      )
      .eq('organization_id', organizationId)
      .eq('source', source)
      .eq(idColumn, recordId)
      .order('created_at', { ascending: false })

    if (error) throw error

    return ((data ?? []) as unknown as HistoryRow[]).flatMap(row => {
      const normalizedRecordId = row[idColumn]
      if (normalizedRecordId == null) {
        return []
      }

      return [
        {
          id: row.id,
          records_to_be_processed_id: row.records_to_be_processed_id,
          created_at: row.created_at,
          recalculation_reason: row.recalculation_reason,
          source,
          record_id: normalizedRecordId
        }
      ]
    })
  }

  async getMeaningfulAuditChanges({
    tableName,
    idColumn,
    organizationId,
    recordIds,
    beforeTimestamp
  }: {
    tableName: MeaningfulAuditChangeTableName
    idColumn: string
    organizationId: number
    recordIds: number[]
    beforeTimestamp: string
  }): Promise<MeaningfulAuditChange[]> {
    if (recordIds.length === 0) return []

    type AuditRow = {
      id: number
      changed_at: string
      old_data: Record<string, unknown> | null
      new_data: Record<string, unknown> | null
    } & Record<string, unknown>

    const { data, error } = await this.supabase
      .from(tableName)
      .select(`id, ${idColumn}, changed_at, old_data, new_data`)
      .eq('organization_id', organizationId)
      .in(idColumn, recordIds)
      .lte('changed_at', beforeTimestamp)
      .order('changed_at', { ascending: false })

    if (error) throw error

    const matches = findMeaningfulRecordAuditChanges({
      auditRows: (data ?? []) as unknown as AuditRow[],
      getRecordId: auditRow => Number(auditRow[idColumn]),
      ignoredFields: undefined
    })
    const profileIds = matches.flatMap(
      match =>
        collectRecordAuditReferenceIds({
          oldData: match.auditRow.old_data ?? {},
          newData: match.auditRow.new_data ?? {},
          rows: match.rows
        }).profileIds
    )
    const profileReferences =
      profileIds.length > 0
        ? (
            await loadRecordAuditReferenceLookup({
              supabase: this.supabase,
              organizationId,
              references: {
                dealIds: [],
                invoiceIds: [],
                profileIds,
                statementIds: []
              }
            })
          ).profileDetails
        : undefined

    return matches.map(match => ({
      table_name: tableName,
      record_id: match.recordId,
      audit_row: {
        id: match.auditRow.id,
        changed_at: match.auditRow.changed_at,
        old_data: match.auditRow.old_data,
        new_data: match.auditRow.new_data
      },
      rows: match.rows,
      ...(profileReferences && Object.keys(profileReferences).length > 0
        ? { profileReferences }
        : {})
    }))
  }

  async getPayoutRuleScopedMetricContributions(
    payoutId: number,
    sourceIds: number[]
  ): Promise<PayoutRuleScopedMetricContribution[]> {
    if (sourceIds.length === 0) return []

    const context = await loadPayoutRuleMetricsContext(this.supabase, payoutId)
    if (!context || context.ruleMetricIds.length === 0) return []

    const sourceColumn = getRecordForeignKeyField(context.payoutSource)

    type MetricValueRow = Pick<
      Tables<'metric_values'>,
      | 'metric_id'
      | 'accrual_date'
      | 'value'
      | 'deal_id'
      | 'invoice_id'
      | 'activity_id'
      | 'kpi_id'
      | 'line_item_id'
      | 'deal_line_item_id'
      | 'custom_record_id'
    >

    const { data: metricValues, error: metricValuesError } = await this.supabase
      .from('metric_values')
      .select(
        'metric_id, accrual_date, value, deal_id, invoice_id, activity_id, kpi_id, line_item_id, deal_line_item_id, custom_record_id'
      )
      .in('metric_id', context.ruleMetricIds)
      .in(sourceColumn, sourceIds)
      .gte('accrual_date', context.periodStart)
      .lte('accrual_date', context.periodEnd)

    if (metricValuesError) throw metricValuesError

    const metricValuesBySourceAndMetric = ((metricValues ?? []) as MetricValueRow[]).reduce(
      (map, row) => {
        const sourceId = row[sourceColumn]

        if (sourceId == null) {
          return map
        }

        const key = `${sourceId}:${row.metric_id}`
        const existing = map.get(key)

        if (
          !existing ||
          (existing.accrual_date == null && row.accrual_date != null) ||
          (existing.accrual_date != null &&
            row.accrual_date != null &&
            row.accrual_date < existing.accrual_date)
        ) {
          map.set(key, row)
        }

        return map
      },
      new Map<string, MetricValueRow>()
    )

    return sourceIds.flatMap(sourceId =>
      context.ruleMetricIds.map(metricId => {
        const row = metricValuesBySourceAndMetric.get(`${sourceId}:${metricId}`)

        return {
          source_id: sourceId,
          metric_id: metricId,
          metric_label: context.metricsById.get(metricId)?.label ?? `Metric #${metricId}`,
          accrual_date: row?.accrual_date ?? null,
          metric_value: row?.value ?? null
        } satisfies PayoutRuleScopedMetricContribution
      })
    )
  }

  async getPayoutRuleMetricFieldReferences(
    payoutId: number
  ): Promise<PayoutRuleMetricFieldReference[]> {
    const context = await loadPayoutRuleMetricsContext(this.supabase, payoutId)

    if (!context || context.ruleMetricIds.length === 0) {
      return []
    }

    return context.ruleMetricIds.flatMap(metricId => {
      const metric = context.metricsById.get(metricId)

      if (!metric) return []

      return extractFormulaSourceFieldKeys({
        source: context.payoutSource,
        formula: metric.formula
      }).map(fieldKey => ({
        field_key: fieldKey,
        metric_label: metric.label
      }))
    })
  }

  async getDiscrepanciesForPayout(payoutId: number): Promise<PayoutDiscrepancyWithStatements[]> {
    const selectParts = [
      '*',
      'existing_statement:statements!payout_discrepancies_existing_statement_id_fkey(period_start, period_end, cut_period, status)',
      'calculated_statement:statements!payout_discrepancies_calculated_statement_id_fkey(period_start, period_end, cut_period, status)',
      'adjustment_payouts:payouts!payouts_payout_discrepancy_id_fkey(value, currency, statement_id, statements!payouts_statement_id_fkey(period_start, period_end, cut_period, status))'
    ]
    const { data, error } = await this.supabase
      .from('payout_discrepancies')
      .select(selectParts.join(', '))
      .eq('payout_id', payoutId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })

    if (error) throw error

    const discrepancies = (data ?? []) as unknown as PayoutDiscrepancyWithStatements[]

    return discrepancies.filter(
      discrepancy => !discrepancy.reviewed || discrepancy.resolution_type === 'applied'
    )
  }

  async getDiscrepancyIndicators(
    organizationId: number
  ): Promise<
    Pick<
      Tables<'payout_discrepancies'>,
      'payout_id' | 'reviewed' | 'resolution_type' | 'discrepancy_type'
    >[]
  > {
    type DiscrepancyIndicator = Pick<
      Tables<'payout_discrepancies'>,
      'payout_id' | 'reviewed' | 'resolution_type' | 'discrepancy_type'
    >
    const indicators: DiscrepancyIndicator[] = []

    for (let from = 0; ; from += PAYOUT_DISCREPANCY_INDICATORS_PAGE_SIZE) {
      const { data, error } = await this.supabase
        .from('payout_discrepancies')
        .select('payout_id, reviewed, resolution_type, discrepancy_type')
        .eq('organization_id', organizationId)
        .not('payout_id', 'is', null)
        .order('id', { ascending: true })
        .range(from, from + PAYOUT_DISCREPANCY_INDICATORS_PAGE_SIZE - 1)
      if (error) throw error

      const page = (data ?? []) as DiscrepancyIndicator[]
      indicators.push(...page)
      if (page.length < PAYOUT_DISCREPANCY_INDICATORS_PAGE_SIZE) break
    }

    return indicators
  }

  async applyValueDiscrepancy(
    discrepancyId: number,
    adjustmentPayoutData: ValueDiscrepancyAdjustmentInput
  ): Promise<void> {
    const { error } = await this.supabase.rpc('apply_value_discrepancy_transactional', {
      _discrepancy_id: discrepancyId,
      _adjustment_payout_data: adjustmentPayoutData
    })
    if (error) throw error
  }

  async applyStatementDiscrepancy(
    discrepancyId: number,
    payoutId: number,
    newStatementId: number
  ): Promise<void> {
    const { error } = await this.supabase.rpc('apply_statement_discrepancy_transactional', {
      _discrepancy_id: discrepancyId,
      _payout_id: payoutId,
      _new_statement_id: newStatementId
    })
    if (error) throw error
  }

  async resolvePayoutDiscrepancy(
    discrepancyId: number,
    action: 'apply' | 'discard' | 'next_statement'
  ): Promise<Record<string, unknown>> {
    const { data, error } = await this.supabase.rpc('resolve_payout_discrepancy_transactional', {
      _discrepancy_id: discrepancyId,
      _action: action
    })
    if (error) throw error
    return (data ?? {}) as Record<string, unknown>
  }

  async getNextStatementForPayoutDiscrepancy(discrepancyId: number) {
    const { data, error } = await this.supabase.rpc('get_next_statement_for_payout_discrepancy', {
      _discrepancy_id: discrepancyId
    })
    if (error) throw error
    return data?.[0] ?? null
  }

  async revertStatementDiscrepancy(
    discrepancyId: number,
    payoutId: number,
    targetStatementId: number
  ): Promise<StatementDiscrepancyRevertResult> {
    const { data, error } = await this.supabase.rpc('revert_statement_discrepancy_transactional', {
      _discrepancy_id: discrepancyId,
      _payout_id: payoutId,
      _target_statement_id: targetStatementId
    })
    if (error) throw error
    return {
      targetStatementReopened: data ?? false
    }
  }

  async markDiscrepancyAsReviewed(discrepancyId: number): Promise<void> {
    const { data, error } = await this.supabase
      .from('payout_discrepancies')
      .update({ reviewed: true, resolution_type: 'dismissed' })
      .eq('id', discrepancyId)
      .eq('reviewed', false)
      .select('id')
    if (error) throw error
    if (!data?.length) {
      throw new Error(`Payout discrepancy ${discrepancyId} was already reviewed or resolved.`)
    }
  }

  async resetPayoutValidation(
    payoutId: number,
    discrepancyId: number,
    newValue: number
  ): Promise<void> {
    const { error } = await this.supabase.rpc('reset_payout_validation_transactional', {
      _payout_id: payoutId,
      _discrepancy_id: discrepancyId,
      _new_value: newValue
    })
    if (error) throw error
  }
  async revokePayoutValidations(payoutId: number): Promise<void> {
    const { error } = await this.supabase.rpc('revoke_payout_validations', {
      _payout_id: payoutId
    })
    if (error) throw error
  }

  async getStatementIdForAccrualDate(
    organizationId: number,
    profileId: string,
    accrualDate: string
  ): Promise<number | null> {
    const { data, error } = await this.supabase.rpc('get_statement_id_for_accrual_date', {
      _organization_id: organizationId,
      _profile_id: profileId,
      _accrual_date: accrualDate
    })
    if (error) throw error
    return data ?? null
  }

  async getNotificationStatementReferences(
    statementIds: number[]
  ): Promise<NotificationStatementReference[]> {
    if (statementIds.length === 0) return []

    const { data, error } = await this.supabase
      .from('statements')
      .select(
        'id, period_start, period_end, status, profile_id, organization_id, profiles!statements_profile_id_fkey(id, name, email, img_url)'
      )
      .in('id', statementIds)

    if (error) throw error

    return (data ?? []) as NotificationStatementReference[]
  }

  async getStatementPeriodReferences(statementIds: number[]): Promise<StatementPeriodReference[]> {
    if (statementIds.length === 0) return []

    const { data, error } = await this.supabase
      .from('statements')
      .select('id, period_start')
      .in('id', statementIds)

    if (error) throw error

    return (data ?? []) as StatementPeriodReference[]
  }

  async getStatementReviewFlowRows(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Promise<StatementReviewFlowRow[]> {
    const pageSize = 1000
    const rows: StatementReviewFlowRow[] = []
    let start = 0

    while (true) {
      const { data, error } = await this.supabase
        .from('statements')
        .select(
          'id, profile_id, period_start, status, has_pending_payouts, payouts(id), profiles!statements_profile_id_fkey!inner(organizations_profiles!inner())'
        )
        .eq('organization_id', organizationId)
        .eq('profiles.organizations_profiles.organization_id', organizationId)
        .eq('profiles.organizations_profiles.archived', false)
        .neq('profiles.organizations_profiles.get_profile_status_field', 'inactive')
        .neq('profiles.organizations_profiles.get_profile_status_field', 'terminated')
        .gte('period_start', startDate)
        .lte('period_start', endDate)
        .order('id', { ascending: true })
        .range(start, start + pageSize - 1)

      if (error) throw error

      const page = (data ?? []) as StatementReviewFlowRow[]
      rows.push(...page)

      if (page.length < pageSize) break
      start += pageSize
    }

    return rows
  }

  async getOrganizationValidationProcessConfig(
    organizationId: number
  ): Promise<ValidationProcessOrganizationConfig> {
    const { data: organization, error: organizationError } = await this.supabase
      .from('organizations')
      .select('id, payout_validation_flow, statement_approval_flow')
      .eq('id', organizationId)
      .single()

    if (organizationError) throw organizationError

    return {
      organizationId: organization.id,
      payoutValidationFlow: Boolean(organization.payout_validation_flow),
      statementApprovalFlow: Boolean(organization.statement_approval_flow)
    }
  }

  async getValidationStepEntities(organizationId: number): Promise<ValidationProcessStepEntity[]> {
    const { data: validationSteps, error: validationStepsError } = await this.supabase
      .from('validation_steps')
      .select('entity')
      .eq('organization_id', organizationId)
      .in('entity', ['compensations_validation', 'statements_approval'])

    if (validationStepsError) throw validationStepsError

    return (validationSteps ?? []).map(step => step.entity as ValidationProcessStepEntity)
  }

  async getValidationProcessProfilesByIds(profileIds: string[]) {
    const uniqueProfileIds = Array.from(new Set(profileIds)).filter(Boolean)
    if (uniqueProfileIds.length === 0) {
      return []
    }

    const { data: profiles, error } = await this.supabase
      .from('profiles')
      .select('id, name, email')
      .in('id', uniqueProfileIds)

    if (error) throw error

    return profiles ?? []
  }

  async countCompletedValidationPayouts(params: GetValidationProcessPayoutParams): Promise<number> {
    const { count, error } = await this.buildValidationPayoutQuery(params, 'id', {
      head: true,
      count: 'exact'
    }).in('status', ['validated', 'paid'])

    if (error) throw error
    return count ?? 0
  }

  async countPendingValidationPayouts(params: GetValidationProcessPayoutParams): Promise<number> {
    const { count, error } = await this.buildValidationPayoutQuery(params, 'id', {
      head: true,
      count: 'exact'
    })
      .eq('status', 'achieved')
      .is('pending_validation', true)

    if (error) throw error
    return count ?? 0
  }

  async listPendingValidationPayoutReviewerRows(
    params: GetValidationProcessPayoutParams & { limit: number }
  ): Promise<ValidationProcessReviewerRow[]> {
    const { data, error } = await this.buildValidationPayoutQuery(params, 'id, next_validators')
      .eq('status', 'achieved')
      .is('pending_validation', true)
      .order('effective_date', { ascending: true })
      .limit(params.limit)

    if (error) throw error

    // This path intentionally returns only item IDs plus next-reviewer IDs. The
    // domain layer aggregates "people to chase" counts from these compact rows
    // instead of loading full payout payloads for every pending item.
    return ((data ?? []) as unknown as ValidationProcessPendingReviewerRow[]).map(row => ({
      id: row.id,
      nextReviewerIds: parseCommaSeparatedProfileIds(row.next_validators)
    }))
  }

  async listPendingValidationPayoutSamples(
    params: GetValidationProcessPayoutParams & { limit?: number }
  ): Promise<ValidationProcessRawPayoutSample[]> {
    let query = this.buildValidationPayoutQuery(
      params,
      'id, profile_id, value, currency, effective_date, period_start, period_end, next_validators, status, profiles!payouts_profile_id_fkey(id, name, email)'
    )
      .eq('status', 'achieved')
      .is('pending_validation', true)
      .order('effective_date', { ascending: true })

    if (params.limit != null) {
      query = query.limit(params.limit)
    }

    const { data, error } = await query

    if (error) throw error

    return ((data ?? []) as unknown as ValidationProcessPendingPayoutRow[]).map(row =>
      this.mapValidationPayoutRow(row)
    )
  }

  async listCompletedValidationPayoutSamples(
    params: GetValidationProcessPayoutParams & { limit?: number }
  ): Promise<ValidationProcessRawPayoutSample[]> {
    let query = this.buildValidationPayoutQuery(
      params,
      'id, profile_id, value, currency, effective_date, period_start, period_end, next_validators, status, profiles!payouts_profile_id_fkey(id, name, email)'
    )
      .in('status', ['validated', 'paid'])
      .order('effective_date', { ascending: false })

    if (params.limit != null) {
      query = query.limit(params.limit)
    }

    const { data, error } = await query

    if (error) throw error

    return ((data ?? []) as unknown as ValidationProcessPendingPayoutRow[]).map(row =>
      this.mapValidationPayoutRow(row)
    )
  }

  async countCompletedValidationStatements(
    params: GetValidationProcessStatementParams
  ): Promise<number> {
    const { count, error } = await this.buildValidationStatementQuery(params, 'id', {
      head: true,
      count: 'exact'
    }).in('status', ['approved', 'paid'])

    if (error) throw error
    return count ?? 0
  }

  async countPendingValidationStatements(
    params: GetValidationProcessStatementParams
  ): Promise<number> {
    const { count, error } = await this.buildValidationStatementQuery(params, 'id', {
      head: true,
      count: 'exact'
    })
      .eq('status', 'open')
      .is('pending_approval', true)

    if (error) throw error
    return count ?? 0
  }

  async countOpenNotPendingValidationStatements(
    params: GetValidationProcessStatementParams
  ): Promise<number> {
    const { count, error } = await this.buildValidationStatementQuery(params, 'id', {
      head: true,
      count: 'exact'
    })
      .eq('status', 'open')
      .eq('pending_approval', false)

    if (error) throw error
    return count ?? 0
  }

  async listPendingValidationStatementReviewerRows(
    params: GetValidationProcessStatementParams & { limit: number }
  ): Promise<ValidationProcessReviewerRow[]> {
    const { data, error } = await this.buildValidationStatementQuery(params, 'id, next_approvers')
      .eq('status', 'open')
      .is('pending_approval', true)
      .order('period_start', { ascending: true })
      .limit(params.limit)

    if (error) throw error

    // Same pattern as payouts: fetch just enough metadata to answer "who are the
    // next approvers?" and let the domain turn those per-item approver lists into
    // reviewer counts and human-readable summaries.
    return ((data ?? []) as unknown as ValidationProcessPendingReviewerRow[]).map(row => ({
      id: row.id,
      nextReviewerIds: parseCommaSeparatedProfileIds(row.next_approvers)
    }))
  }

  async listPendingValidationStatementSamples(
    params: GetValidationProcessStatementParams & { limit?: number }
  ): Promise<ValidationProcessRawStatementSample[]> {
    let query = this.buildValidationStatementQuery(
      params,
      'id, profile_id, period_start, period_end, status, next_approvers, profiles!statements_profile_id_fkey(id, name, email)'
    )
      .eq('status', 'open')
      .is('pending_approval', true)
      .order('period_start', { ascending: true })

    if (params.limit != null) {
      query = query.limit(params.limit)
    }

    const { data, error } = await query

    if (error) throw error

    return ((data ?? []) as unknown as ValidationProcessPendingStatementRow[]).map(row =>
      this.mapValidationStatementRow(row)
    )
  }

  async listOpenNotPendingValidationStatementSamples(
    params: GetValidationProcessStatementParams & { limit?: number }
  ): Promise<ValidationProcessRawStatementSample[]> {
    let query = this.buildValidationStatementQuery(
      params,
      'id, profile_id, period_start, period_end, status, next_approvers, profiles!statements_profile_id_fkey(id, name, email)'
    )
      .eq('status', 'open')
      .eq('pending_approval', false)
      .order('period_start', { ascending: true })

    if (params.limit != null) {
      query = query.limit(params.limit)
    }

    const { data, error } = await query

    if (error) throw error

    return ((data ?? []) as unknown as ValidationProcessPendingStatementRow[]).map(row =>
      this.mapValidationStatementRow(row)
    )
  }

  async listCompletedValidationStatementSamples(
    params: GetValidationProcessStatementParams & { limit?: number }
  ): Promise<ValidationProcessRawStatementSample[]> {
    let query = this.buildValidationStatementQuery(
      params,
      'id, profile_id, period_start, period_end, status, next_approvers, profiles!statements_profile_id_fkey(id, name, email)'
    )
      .in('status', ['approved', 'paid'])
      .order('period_start', { ascending: false })

    if (params.limit != null) {
      query = query.limit(params.limit)
    }

    const { data, error } = await query

    if (error) throw error

    return ((data ?? []) as unknown as ValidationProcessPendingStatementRow[]).map(row =>
      this.mapValidationStatementRow(row)
    )
  }

  private buildValidationPayoutQuery(
    params: GetValidationProcessPayoutParams,
    columns = '*',
    options?: { head?: boolean; count?: 'exact' }
  ) {
    let query = this.supabase
      .from('payouts')
      .select(columns, options)
      .eq('organization_id', params.organizationId)
      .in('status', ['achieved', 'validated', 'paid'])
      .is('is_withheld', false)

    if (params.payoutMonthStarts?.length) {
      query = query.overlaps('payment_dates', params.payoutMonthStarts)
    }

    return query
  }

  private buildValidationStatementQuery(
    params: GetValidationProcessStatementParams,
    columns = '*',
    options?: { head?: boolean; count?: 'exact' }
  ) {
    const columnsWithProfileFilters = `${columns}, validation_profiles:profiles!statements_profile_id_fkey!inner(organizations_profiles!inner(organization_id, archived, get_profile_status_field), teams_profiles!inner(role, teams!inner(organization_id)))`
    let query = this.supabase
      .from('statements')
      .select(columnsWithProfileFilters, options)
      .eq('organization_id', params.organizationId)
      .in('status', ['open', 'approved', 'paid'])
      .eq('validation_profiles.organizations_profiles.organization_id', params.organizationId)
      .eq('validation_profiles.teams_profiles.teams.organization_id', params.organizationId)
      .eq('validation_profiles.teams_profiles.role', 'member')
      .eq('validation_profiles.organizations_profiles.archived', false)
      .neq('validation_profiles.organizations_profiles.get_profile_status_field', 'inactive')
      .neq('validation_profiles.organizations_profiles.get_profile_status_field', 'terminated')

    if (params.statementPeriodStart) {
      query = query.gte('period_start', params.statementPeriodStart)
    }
    if (params.statementPeriodEnd) {
      query = query.lt('period_start', params.statementPeriodEnd)
    }

    return query
  }

  private mapValidationPayoutRow(
    row: ValidationProcessPendingPayoutRow
  ): ValidationProcessRawPayoutSample {
    return {
      id: row.id,
      profileId: row.profile_id,
      recipientName: row.profiles?.name ?? null,
      recipientEmail: row.profiles?.email ?? null,
      value: row.value ?? null,
      currency: row.currency ?? null,
      effectiveDate: row.effective_date ?? null,
      periodStart: row.period_start ?? null,
      periodEnd: row.period_end ?? null,
      status: row.status,
      nextReviewerIds: parseCommaSeparatedProfileIds(row.next_validators)
    }
  }

  private mapValidationStatementRow(
    row: ValidationProcessPendingStatementRow
  ): ValidationProcessRawStatementSample {
    return {
      id: row.id,
      profileId: row.profile_id,
      recipientName: row.profiles?.name ?? null,
      recipientEmail: row.profiles?.email ?? null,
      periodStart: row.period_start,
      periodEnd: row.period_end,
      status: row.status,
      nextReviewerIds: parseCommaSeparatedProfileIds(row.next_approvers)
    }
  }

  async getPendingPayoutValidationsCount(
    organizationId: number,
    monthStarts: string[],
    validatorUserId?: string
  ): Promise<number> {
    let query = this.supabase
      .from('payouts')
      .select('payment_dates, profiles!payouts_profile_id_fkey!inner(teams_profiles(role))', {
        head: true,
        count: 'exact'
      })
      .eq('organization_id', organizationId)
      .eq('profiles.teams_profiles.role', 'member')
      .is('pending_validation', true)
      .overlaps('payment_dates', monthStarts)

    if (validatorUserId != null) {
      query = query.ilike('next_validators', `%${validatorUserId}%`)
    }

    const { count, error } = await query
    if (error) throw error
    return count ?? 0
  }

  async getPayoutAnalytics(params: PayoutAnalyticsParams): Promise<PayoutAnalytics> {
    const filteredProfileIds = await this.getDashboardFilteredProfileIds(
      params.organizationId,
      params.teamId,
      params.tagIds
    )

    if (filteredProfileIds?.length === 0) {
      return {
        generatedPayouts: buildGeneratedPayoutsSummary([]),
        manualCompensationAndDeductions: buildManualCompensationSummary([], []),
        payoutAdjustments: buildPayoutAdjustmentsSummary([], []),
        payoutsToBePaid: buildPayoutsToBePaidSummary([], [])
      }
    }

    const statementMonthStarts = buildStatementMonthStarts(params.startDate, params.endDate)

    const [generatedPayoutRows, payablePayoutRows, adjustmentRows, manualPayoutRows] =
      await Promise.all([
        this.getDashboardGeneratedPayoutRows(params, filteredProfileIds),
        this.getDashboardPayablePayoutRows(params, filteredProfileIds),
        this.getDashboardAdjustmentRows(params, filteredProfileIds),
        this.getDashboardManualPayoutRows(params, filteredProfileIds)
      ])

    const adjustmentComments = adjustmentRows.length
      ? await this.getDashboardAdjustmentComments(adjustmentRows, params.organizationId)
      : new Map<number, string>()
    const enrichedAdjustmentRows = adjustmentRows.map(adjustment => ({
      ...adjustment,
      comment: adjustmentComments.get(adjustment.id) ?? null
    }))
    const adjustmentAuditRows =
      enrichedAdjustmentRows.length > 0
        ? await this.getDashboardAdjustmentAuditRows(enrichedAdjustmentRows, params.organizationId)
        : []
    const manualAuditRows = manualPayoutRows.length
      ? await this.getDashboardManualAuditRows(manualPayoutRows, params.organizationId)
      : []

    return {
      generatedPayouts: buildGeneratedPayoutsSummary(generatedPayoutRows),
      manualCompensationAndDeductions: buildManualCompensationSummary(
        manualPayoutRows,
        manualAuditRows
      ),
      payoutAdjustments: buildPayoutAdjustmentsSummary(enrichedAdjustmentRows, adjustmentAuditRows),
      payoutsToBePaid: buildPayoutsToBePaidSummary(payablePayoutRows, statementMonthStarts)
    }
  }

  async getGeneratedPayoutsAnalytics(
    params: PayoutAnalyticsParams
  ): Promise<GeneratedPayoutsAnalytics> {
    const filteredProfileIds = await this.getDashboardFilteredProfileIds(
      params.organizationId,
      params.teamId,
      params.tagIds
    )

    if (filteredProfileIds?.length === 0) {
      return buildGeneratedPayoutsSummary([])
    }

    const generatedPayoutsSummaryArgs: Database['public']['Functions']['get_dashboard_generated_payouts_summary']['Args'] =
      {
        _end_date: params.endDate,
        _organization_id: params.organizationId,
        _start_date: params.startDate
      }

    if (params.planId != null) {
      generatedPayoutsSummaryArgs._plan_id = params.planId
    }
    if (filteredProfileIds != null) {
      generatedPayoutsSummaryArgs._profile_ids = filteredProfileIds
    }

    const { data, error } = await this.supabase.rpc(
      'get_dashboard_generated_payouts_summary',
      generatedPayoutsSummaryArgs
    )

    if (error) throw error

    const totals = data?.[0]
    return buildGeneratedPayoutsSummaryFromStatusTotals({
      accruedAmount: totals?.accrued_amount ?? 0,
      expiredAmount: totals?.expired_amount ?? 0,
      lockedAmount: totals?.locked_amount ?? 0,
      totalAmount: totals?.total_amount ?? 0
    })
  }

  async getPayablePayoutsAnalytics(
    params: PayoutAnalyticsParams
  ): Promise<PayablePayoutsAnalytics> {
    const filteredProfileIds = await this.getDashboardFilteredProfileIds(
      params.organizationId,
      params.teamId,
      params.tagIds
    )

    if (filteredProfileIds?.length === 0) {
      return buildPayoutsToBePaidSummary([], [])
    }

    const payablePayoutsSummaryArgs: Database['public']['Functions']['get_dashboard_payable_payouts_summary']['Args'] =
      {
        _end_date: params.endDate,
        _organization_id: params.organizationId,
        _start_date: params.startDate
      }

    if (params.planId != null) {
      payablePayoutsSummaryArgs._plan_id = params.planId
    }
    if (filteredProfileIds != null) {
      payablePayoutsSummaryArgs._profile_ids = filteredProfileIds
    }

    const { data, error } = await this.supabase.rpc(
      'get_dashboard_payable_payouts_summary',
      payablePayoutsSummaryArgs
    )

    if (error) throw error

    return buildPayoutsToBePaidSummaryFromMonthTotals(
      (data ?? []).map(row => ({
        paidAmount: row.paid_amount ?? 0,
        pendingAmount: row.pending_amount ?? 0,
        periodStart: row.period_start
      }))
    )
  }

  async getManualPayoutAnalytics(params: PayoutAnalyticsParams): Promise<ManualPayoutsAnalytics> {
    const filteredProfileIds = await this.getDashboardFilteredProfileIds(
      params.organizationId,
      params.teamId,
      params.tagIds
    )

    if (filteredProfileIds?.length === 0) {
      return buildManualCompensationSummary([])
    }

    const rows = await this.getDashboardManualPayoutRows(params, filteredProfileIds)
    const auditRows = rows.length
      ? await this.getDashboardManualAuditRows(rows, params.organizationId)
      : []
    return buildManualCompensationSummary(rows, auditRows)
  }

  async getPayoutAdjustmentAnalytics(
    params: PayoutAnalyticsParams
  ): Promise<PayoutAdjustmentsAnalytics> {
    const filteredProfileIds = await this.getDashboardFilteredProfileIds(
      params.organizationId,
      params.teamId,
      params.tagIds
    )

    if (filteredProfileIds?.length === 0) {
      return buildPayoutAdjustmentsSummary([], [])
    }

    const rows = await this.getDashboardAdjustmentRows(params, filteredProfileIds)
    const comments = rows.length
      ? await this.getDashboardAdjustmentComments(rows, params.organizationId)
      : new Map<number, string>()
    const enrichedRows = rows.map(adjustment => ({
      ...adjustment,
      comment: comments.get(adjustment.id) ?? null
    }))
    const auditRows = enrichedRows.length
      ? await this.getDashboardAdjustmentAuditRows(enrichedRows, params.organizationId)
      : []

    return buildPayoutAdjustmentsSummary(enrichedRows, auditRows)
  }

  async getPendingStatementApprovalsCount(
    organizationId: number,
    periodStart: string,
    periodEnd: string,
    approverUserId?: string
  ): Promise<number> {
    let query = this.supabase
      .from('statements')
      .select(
        '*, organizations!inner(id), profiles!statements_profile_id_fkey!inner(teams_profiles(role))',
        { head: true, count: 'exact' }
      )
      .eq('organization_id', organizationId)
      .eq('profiles.teams_profiles.role', 'member')
      .is('organizations.statement_approval_flow', true)
      .eq('pending_approval', true)
      .gte('period_start', periodStart)
      .lt('period_start', periodEnd)

    if (approverUserId != null) {
      query = query.ilike('next_approvers', `%${approverUserId}%`)
    }

    const { count, error } = await query
    if (error) throw error
    return count ?? 0
  }

  async ensureStatementsForPeriodRange(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Promise<number> {
    const { data, error } = await this.supabase.rpc('ensure_statements_for_period_range', {
      _organization_id: organizationId,
      _start_date: startDate,
      _end_date: endDate
    })

    if (error) throw error

    return data ?? 0
  }

  async getStatementApproval(
    statementId: number,
    actorId: string
  ): Promise<Tables<'statement_approvals'> | null> {
    const { data, error } = await this.supabase
      .from('statement_approvals')
      .select('*')
      .eq('statement_id', statementId)
      .eq('profile_id', actorId)
      .maybeSingle()
    if (error) throw error
    return data
  }

  async insertStatementApproval(params: {
    statementId: number
    actorId: string
  }): Promise<Tables<'statement_approvals'>> {
    const { data, error } = await this.supabase
      .from('statement_approvals')
      .insert({ statement_id: params.statementId, profile_id: params.actorId })
      .select('*')
      .single()
    if (error) throw error
    return data
  }

  async createStatementApprovalNotification(params: {
    organizationId: number
    statementId: number
    actorId: string
  }): Promise<void> {
    const { error } = await this.supabase.from('notifications').insert({
      type: 'statement_approved',
      entity_type: 'statement',
      entity_id: params.statementId,
      organization_id: params.organizationId,
      profile_id: params.actorId
    })
    if (error) throw error
  }

  async insertStatementIssue(params: {
    organizationId: number
    statementId: number
    actorId: string
  }): Promise<Tables<'issues'>> {
    const { data, error } = await this.supabase
      .from('issues')
      .insert({
        organization_id: params.organizationId,
        statement_id: params.statementId,
        created_by: params.actorId,
        status: 'open'
      })
      .select('*')
      .single()
    if (error) throw error
    return data
  }

  async createStatementIssueNotification(params: {
    organizationId: number
    issueId: number
    actorId: string
  }): Promise<void> {
    const { error } = await this.supabase.from('notifications').insert({
      type: 'statement_flagged',
      entity_type: 'issue',
      entity_id: params.issueId,
      organization_id: params.organizationId,
      profile_id: params.actorId
    })
    if (error) throw error
  }

  async changeStatementPaymentStatus(
    params: ChangeStatementPaymentStatusParams
  ): Promise<Pick<Tables<'statements'>, 'id' | 'status'> | null> {
    const { organizationId, statementId, expectedStatus, status } = params
    if (
      !(
        (expectedStatus !== 'paid' && status === 'paid') ||
        (expectedStatus === 'paid' && (status === 'open' || status === 'approved'))
      )
    ) {
      throw new Error('Unsupported statement payment transition')
    }
    const { data, error } = await this.supabase
      .from('statements')
      .update({ status })
      .eq('organization_id', organizationId)
      .eq('id', statementId)
      .eq('status', expectedStatus)
      .select('id,status')
      .maybeSingle()
    if (error) throw error
    return data
  }

  async createStatementPaidNotifications(params: {
    organizationId: number
    actorId: string
    statementIds: number[]
  }): Promise<void> {
    if (params.statementIds.length === 0) return
    const { error } = await this.supabase.from('notifications').insert(
      params.statementIds.map(statementId => ({
        type: 'statement_paid' as const,
        entity_type: 'statement' as const,
        entity_id: statementId,
        organization_id: params.organizationId,
        profile_id: params.actorId,
        payload: {}
      }))
    )
    if (error) throw error
  }

  async startStatementsReview(
    organizationId: number,
    statementIds: number[]
  ): Promise<StartStatementsReviewResult> {
    const { data, error } = await this.supabase
      .rpc('start_statements_review', {
        _organization_id: organizationId,
        _statement_ids: statementIds
      })
      .single()

    if (error) throw error

    return {
      updatedStatementIds: data?.updated_statement_ids ?? [],
      skippedStatementIds: data?.skipped_statement_ids ?? []
    }
  }

  async getStatementResetEligibility(statementIds: number[]): Promise<StatementResetEligibility[]> {
    if (statementIds.length === 0) return []

    const { data, error } = await this.supabase.rpc('get_statement_reset_eligibility', {
      _statement_ids: statementIds
    })

    if (error) throw error
    return (data ?? []).map(row => ({
      statementId: row.statement_id,
      canReset: row.can_reset,
      reason: row.reason as StatementResetEligibility['reason']
    }))
  }

  async resetStatementToPendingApproval(statementId: number): Promise<void> {
    const { error } = await this.supabase.rpc('reset_statement_to_pending_approval', {
      _statement_id: statementId
    })

    if (error) throw error
  }

  async getUsersWithoutTeamCount(organizationId: number): Promise<number> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select(
        'id, teams_profiles(organization_id, team_id), organizations_profiles!inner(organization_id, role, archived, get_profile_status_field)'
      )
      .eq('organizations_profiles.organization_id', organizationId)
      .neq('organizations_profiles.role', 'admin')
      .neq('organizations_profiles.role', 'supervisor')
      .neq('organizations_profiles.archived', true)
      .neq('organizations_profiles.get_profile_status_field', 'terminated')
      .neq('organizations_profiles.get_profile_status_field', 'inactive')

    if (error) throw error

    return ((data ?? []) as Array<Record<string, unknown>>).filter(
      row => this.getScopedMemberTeams(row).length === 0
    ).length
  }

  private getScopedMemberTeams(row: Record<string, unknown>) {
    const organizationsProfiles = Array.isArray(row.organizations_profiles)
      ? (row.organizations_profiles as Array<{ organization_id?: number | null }>)
      : []
    const orgId = organizationsProfiles[0]?.organization_id
    const teamsProfiles = Array.isArray(row.teams_profiles)
      ? (row.teams_profiles as Array<Record<string, unknown>>)
      : []

    if (typeof orgId !== 'number') {
      return []
    }

    return teamsProfiles.filter(team => Number(team.organization_id) === orgId)
  }

  private async getDashboardFilteredProfileIds(
    organizationId: number,
    teamId?: number | null,
    tagIds?: number[] | null
  ): Promise<string[] | null> {
    const normalizedTagIds = tagIds?.filter(tagId => Number.isFinite(tagId)) ?? []

    if (teamId == null && normalizedTagIds.length === 0) {
      return null
    }

    let teamIds: number[] | null = null

    if (normalizedTagIds.length > 0) {
      let teamTagsQuery = this.supabase
        .from('teams_tags')
        .select('team_id, tag_id')
        .in('tag_id', normalizedTagIds)

      if (teamId != null) {
        teamTagsQuery = teamTagsQuery.eq('team_id', teamId)
      }

      const teamsTags = await this.getAllDashboardRows<{ team_id: number; tag_id: number }>(
        teamTagsQuery.order('id', { ascending: true })
      )

      const selectedTagIds = new Set(normalizedTagIds)
      const teamTagIds = new Map<number, Set<number>>()

      for (const row of teamsTags) {
        if (!teamTagIds.has(row.team_id)) {
          teamTagIds.set(row.team_id, new Set())
        }
        teamTagIds.get(row.team_id)?.add(row.tag_id)
      }

      teamIds = Array.from(teamTagIds)
        .filter(([, tagIds]) => tagIds.size === selectedTagIds.size)
        .map(([id]) => id)

      if (teamIds.length === 0) {
        return []
      }
    }

    let teamsProfilesQuery = this.supabase
      .from('teams_profiles')
      .select('profile_id')
      .eq('organization_id', organizationId)
      .eq('role', 'member')

    if (teamIds != null) {
      teamsProfilesQuery = teamsProfilesQuery.in('team_id', teamIds)
    } else if (teamId != null) {
      teamsProfilesQuery = teamsProfilesQuery.eq('team_id', teamId)
    }

    const teamsProfiles = await this.getAllDashboardRows<{ profile_id: string }>(
      teamsProfilesQuery
        .order('profile_id', { ascending: true })
        .order('team_id', { ascending: true })
    )

    return Array.from(new Set(teamsProfiles.map(row => row.profile_id).filter(Boolean)))
  }

  private async getAllDashboardRows<T>(query: {
    range: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>
  }): Promise<T[]> {
    const rows: T[] = []
    let start = 0

    while (true) {
      const end = start + DASHBOARD_KPI_PAGE_SIZE - 1
      const { data, error } = await query.range(start, end)

      if (error) throw error

      const page = data ?? []
      if (page.length === 0) break

      rows.push(...page)
      start += DASHBOARD_KPI_PAGE_SIZE

      if (page.length < DASHBOARD_KPI_PAGE_SIZE) break
    }

    return rows
  }

  private async getDashboardGeneratedPayoutRows(
    params: PayoutAnalyticsParams,
    filteredProfileIds: string[] | null
  ): Promise<PayoutAnalyticsPayout[]> {
    let query = this.supabase
      .from('payouts')
      .select(DASHBOARD_PAYOUT_SUMMARY_SELECT)
      .eq('organization_id', params.organizationId)
      .eq('ignore', false)
      .gte('effective_date', params.startDate)
      .lte('effective_date', params.endDate)

    if (filteredProfileIds != null) {
      query = query.in('profile_id', filteredProfileIds)
    }

    if (params.planId != null) {
      query = query.eq('plan_id', params.planId)
    }

    query = query.order('effective_date', { ascending: true }).order('id', { ascending: true })

    return (await this.getAllDashboardRows(query)) as PayoutAnalyticsPayout[]
  }

  private async getDashboardPayablePayoutRows(
    params: PayoutAnalyticsParams,
    filteredProfileIds: string[] | null
  ): Promise<PayoutAnalyticsPayout[]> {
    const statementMonthStarts = buildStatementMonthStarts(params.startDate, params.endDate)

    if (statementMonthStarts.length === 0) {
      return []
    }

    // Keep both relationship paths in the database query. Passing every
    // matching statement or payout ID through PostgREST can exceed its URL
    // limit for organizations with a long compensation history.
    const [directPayouts, installmentPayouts] = await Promise.all([
      this.getAllDashboardRows(
        this.buildDashboardPayablePayoutQuery(
          params,
          filteredProfileIds,
          DASHBOARD_PAYABLE_PAYOUT_SELECT
        )
          .in('statements.period_start', statementMonthStarts)
          .order('effective_date', { ascending: true })
          .order('id', { ascending: true })
      ),
      this.getAllDashboardRows(
        this.buildDashboardPayablePayoutQuery(
          params,
          filteredProfileIds,
          DASHBOARD_PAYABLE_INSTALLMENT_PAYOUT_SELECT
        )
          .in('payout_installments.statements.period_start', statementMonthStarts)
          .order('effective_date', { ascending: true })
          .order('id', { ascending: true })
      )
    ])

    const payoutsById = new Map<number, PayoutAnalyticsPayout>()

    ;[...directPayouts, ...installmentPayouts].forEach(payout => {
      const row = payout as unknown as PayoutAnalyticsPayout
      if (!payoutsById.has(row.id)) {
        payoutsById.set(row.id, row)
      }
    })

    return Array.from(payoutsById.values())
  }

  private buildDashboardPayablePayoutQuery(
    params: PayoutAnalyticsParams,
    filteredProfileIds: string[] | null,
    select: string
  ) {
    let query = this.supabase
      .from('payouts')
      .select(select)
      .eq('organization_id', params.organizationId)
      .eq('ignore', false)
      .in('status', ['achieved', 'validated', 'paid'])
      .eq('profiles.organizations_profiles.organization_id', params.organizationId)
      .eq('profiles.teams_profiles.teams.organization_id', params.organizationId)
      .eq('profiles.teams_profiles.role', 'member')
      .eq('profiles.organizations_profiles.archived', false)
      .neq('profiles.organizations_profiles.get_profile_status_field', 'inactive')
      .neq('profiles.organizations_profiles.get_profile_status_field', 'terminated')

    if (filteredProfileIds != null) {
      query = query.in('profile_id', filteredProfileIds)
    }

    if (params.planId != null) {
      query = query.eq('plan_id', params.planId)
    }

    return query
  }

  private async getDashboardManualPayoutRows(
    params: PayoutAnalyticsParams,
    filteredProfileIds: string[] | null
  ): Promise<PayoutAnalyticsPayout[]> {
    let query = this.supabase
      .from('payouts')
      .select(DASHBOARD_MANUAL_PAYOUT_SELECT)
      .eq('organization_id', params.organizationId)
      .eq('ignore', false)
      .is('compensation_rule_id', null)
      .is('target_id', null)
      .is('payout_discrepancy_id', null)
      .gte('effective_date', params.startDate)
      .lte('effective_date', params.endDate)

    if (filteredProfileIds != null) {
      query = query.in('profile_id', filteredProfileIds)
    }

    if (params.planId != null) {
      query = query.eq('plan_id', params.planId)
    }

    query = query.order('effective_date', { ascending: true }).order('id', { ascending: true })

    return (await this.getAllDashboardRows(query)) as PayoutAnalyticsPayout[]
  }

  private async getDashboardAdjustmentRows(
    params: PayoutAnalyticsParams,
    filteredProfileIds: string[] | null
  ): Promise<PayoutAnalyticsAdjustment[]> {
    const nextDay = format(addDays(parseISO(params.endDate), 1), 'yyyy-MM-dd')

    let query = this.supabase
      .from('payout_adjustments')
      .select(
        'id, payout_id, profile_id, amount, created_at, profiles!public_payout_adjustments_profile_id_fkey(id, name, email, img_url), payouts!inner(id, profile_id, organization_id, plan_id, ignore, statement_id, profiles!payouts_profile_id_fkey(id, name, email, img_url), statements!payouts_statement_id_fkey(period_start, status))'
      )
      .gte('created_at', params.startDate)
      .lt('created_at', nextDay)
      .eq('payouts.organization_id', params.organizationId)
      .eq('payouts.ignore', false)

    if (filteredProfileIds != null) {
      query = query.in('payouts.profile_id', filteredProfileIds)
    }

    if (params.planId != null) {
      query = query.eq('payouts.plan_id', params.planId)
    }

    query = query.order('created_at', { ascending: true }).order('id', { ascending: true })

    return (await this.getAllDashboardRows(query)) as unknown as PayoutAnalyticsAdjustment[]
  }

  private async getDashboardManualAuditRows(
    payouts: PayoutAnalyticsPayout[],
    organizationId: number
  ): Promise<PayoutAnalyticsAdjustmentAudit[]> {
    const payoutIds = Array.from(new Set(payouts.map(payout => String(payout.id))))
    if (payoutIds.length === 0) return []

    const auditRows = await Promise.all(
      Array.from(batched(payoutIds, PROFILE_IDS_IN_FILTER_BATCH_SIZE)).map(async payoutIdBatch => {
        const query = this.supabase
          .from('payouts_audit_log')
          .select(
            'operation_type, new_data, old_data, changed_at, changed_by, changed_by_profile:profiles!payouts_audit_log_changed_by_fkey(id, name, email, img_url)'
          )
          .eq('operation_type', 'INSERT')
          .eq('organization_id', organizationId)
          .in('new_data->>id', payoutIdBatch)
          .order('changed_at', { ascending: true })
          .order('id', { ascending: true })

        return (await this.getAllDashboardRows(query)) as PayoutAnalyticsAdjustmentAudit[]
      })
    )

    return auditRows.flat()
  }

  private async getDashboardAdjustmentComments(
    adjustments: PayoutAnalyticsAdjustment[],
    organizationId: number
  ): Promise<Map<number, string>> {
    const adjustmentIds = adjustments.map(adjustment => adjustment.id)
    const commentsByAdjustmentId = new Map<number, string>()
    if (adjustmentIds.length === 0) return commentsByAdjustmentId

    const query = this.supabase
      .from('comments')
      .select('content, notifications!inner(entity_id, entity_type, organization_id)')
      .eq('notifications.organization_id', organizationId)
      .eq('notifications.entity_type', 'adjustment')
      .in('notifications.entity_id', adjustmentIds)
      .order('id', { ascending: true })

    const rows = await this.getAllDashboardRows<{
      content: string
      notifications:
        | { entity_id: number | string; entity_type: string; organization_id: number }
        | { entity_id: number | string; entity_type: string; organization_id: number }[]
        | null
    }>(
      query as unknown as {
        range: (
          from: number,
          to: number
        ) => PromiseLike<{
          data:
            | {
                content: string
                notifications:
                  | { entity_id: number | string; entity_type: string; organization_id: number }
                  | { entity_id: number | string; entity_type: string; organization_id: number }[]
                  | null
              }[]
            | null
          error: unknown
        }>
      }
    )

    rows.forEach(row => {
      const notification = Array.isArray(row.notifications)
        ? row.notifications[0]
        : row.notifications
      const adjustmentId = Number(notification?.entity_id)
      if (Number.isFinite(adjustmentId) && !commentsByAdjustmentId.has(adjustmentId)) {
        commentsByAdjustmentId.set(adjustmentId, row.content)
      }
    })

    return commentsByAdjustmentId
  }

  private async getDashboardAdjustmentAuditRows(
    adjustments: PayoutAnalyticsAdjustment[],
    organizationId: number
  ): Promise<PayoutAnalyticsAdjustmentAudit[]> {
    const payoutIds = Array.from(
      new Set(adjustments.map(adjustment => String(adjustment.payout_id)))
    )
    const changedAtValues = Array.from(
      new Set(adjustments.map(adjustment => adjustment.created_at))
    )

    if (payoutIds.length === 0 || changedAtValues.length === 0) {
      return []
    }

    const query = this.supabase
      .from('payouts_audit_log')
      .select(
        'operation_type, old_data, new_data, changed_at, changed_by, changed_by_profile:profiles!payouts_audit_log_changed_by_fkey(id, name, email, img_url)'
      )
      .eq('operation_type', 'UPDATE')
      .eq('organization_id', organizationId)
      .in('old_data->>id', payoutIds)
      .in('changed_at', changedAtValues)
      .order('changed_at', { ascending: true })
      .order('id', { ascending: true })

    return this.getAllDashboardRows<PayoutAnalyticsAdjustmentAudit>(query)
  }

  async listOrganizationTeamsSetup(organizationId: number): Promise<TeamSetupSummaryRow[]> {
    const { data, error } = await this.supabase
      .from('teams')
      .select(
        'id, name, description, currency, archived_at, teams_profiles(role, full_view_access), teams_plans(plan_id), teams_targets(target_id)'
      )
      .eq('organization_id', organizationId)
      .order('name', { ascending: true })

    if (error) {
      throw error
    }

    return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
      const teamsProfiles = Array.isArray(row.teams_profiles)
        ? (row.teams_profiles as Array<Record<string, unknown>>)
        : []
      const teamsPlans = Array.isArray(row.teams_plans)
        ? (row.teams_plans as Array<Record<string, unknown>>)
        : []
      const teamsTargets = Array.isArray(row.teams_targets)
        ? (row.teams_targets as Array<Record<string, unknown>>)
        : []

      return {
        team_id: Number(row.id),
        team_name: String(row.name ?? ''),
        description: typeof row.description === 'string' ? row.description : null,
        currency: typeof row.currency === 'string' ? row.currency : null,
        archived_at: typeof row.archived_at === 'string' ? row.archived_at : null,
        members_count: teamsProfiles.filter(member => member.role === 'member').length,
        managers_count: teamsProfiles.filter(member => member.role === 'manager').length,
        full_view_users_count: teamsProfiles.filter(member => member.full_view_access === true)
          .length,
        linked_plans_count: new Set(teamsPlans.map(plan => Number(plan.plan_id))).size,
        linked_targets_count: new Set(teamsTargets.map(target => Number(target.target_id))).size
      } satisfies TeamSetupSummaryRow
    })
  }

  async listOrganizationTeamsWithoutMembersSetup(
    organizationId: number
  ): Promise<TeamSetupSummaryRow[]> {
    const rows = await this.listOrganizationTeamsSetup(organizationId)
    return rows.filter(row => row.members_count === 0 && row.archived_at == null)
  }

  async listTeamSetupMembers(
    organizationId: number,
    teamId: number
  ): Promise<TeamSetupMemberRow[]> {
    const { data, error } = await this.supabase
      .from('teams_profiles')
      .select(
        'profile_id, team_id, role, full_view_access, profiles!inner(id, name, email, img_url)'
      )
      .eq('organization_id', organizationId)
      .eq('team_id', teamId)
      .order('created_at', { ascending: true })

    if (error) {
      throw error
    }

    return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
      const profile = row.profiles as {
        id?: string
        name?: string | null
        email?: string | null
        img_url?: string | null
      } | null
      return {
        profile_id: String(profile?.id ?? row.profile_id),
        name: typeof profile?.name === 'string' ? profile.name : null,
        email: typeof profile?.email === 'string' ? profile.email : null,
        img_url: typeof profile?.img_url === 'string' ? profile.img_url : null,
        team_id: Number(row.team_id),
        role: row.role as Enums<'team_role'>,
        full_view_access: row.full_view_access === true
      } satisfies TeamSetupMemberRow
    })
  }

  async listTeamSetupPlans(
    organizationId: number,
    teamId: number
  ): Promise<TeamSetupPlanSummaryRow[]> {
    const today = format(new Date(), 'yyyy-MM-dd')
    const { data, error } = await this.supabase
      .from('teams_plans_history')
      .select('team_id, plan_id, plans!inner(label, archived, organization_id)')
      .eq('team_id', teamId)
      .eq('plans.organization_id', organizationId)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)
      .order('plan_id', { ascending: true })

    if (error) {
      throw error
    }

    return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
      const plan = row.plans as { label?: string; archived?: boolean } | null
      return {
        team_id: Number(row.team_id),
        plan_id: Number(row.plan_id),
        label: String(plan?.label ?? ''),
        archived: plan?.archived === true
      } satisfies TeamSetupPlanSummaryRow
    })
  }

  async listTeamSetupTargets(
    organizationId: number,
    teamId: number
  ): Promise<TeamSetupTargetSummaryRow[]> {
    const { data, error } = await this.supabase
      .from('teams_targets')
      .select('team_id, target_id, targets!inner(label, archived_at, organization_id)')
      .eq('team_id', teamId)
      .eq('targets.organization_id', organizationId)
      .order('target_id', { ascending: true })

    if (error) {
      throw error
    }

    return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
      const target = row.targets as { label?: string; archived_at?: string | null } | null
      return {
        team_id: Number(row.team_id),
        target_id: Number(row.target_id),
        label: String(target?.label ?? ''),
        archived_at: typeof target?.archived_at === 'string' ? target.archived_at : null
      } satisfies TeamSetupTargetSummaryRow
    })
  }

  async listTeamMembershipHistorySetup(
    organizationId: number,
    teamId: number
  ): Promise<TeamMembershipHistorySetupRow[]> {
    const { data, error } = await this.supabase
      .from('teams_profiles_events')
      .select('event_date, status, profile_id, team_id, profiles(name, email), teams(name)')
      .eq('organization_id', organizationId)
      .eq('team_id', teamId)
      .order('event_date', { ascending: true })

    if (error) {
      throw error
    }

    return this.mapTeamMembershipHistoryRows(
      organizationId,
      (data ?? []) as Array<Record<string, unknown>>
    )
  }

  async listMemberMembershipHistorySetup(
    organizationId: number,
    profileId: string
  ): Promise<TeamMembershipHistorySetupRow[]> {
    const { data, error } = await this.supabase
      .from('teams_profiles_events')
      .select('event_date, status, profile_id, team_id, profiles(name, email), teams(name)')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .order('event_date', { ascending: true })

    if (error) {
      throw error
    }

    return this.mapTeamMembershipHistoryRows(
      organizationId,
      (data ?? []) as Array<Record<string, unknown>>
    )
  }

  async listProfileCurrentTeamMembershipsSetup(
    organizationId: number,
    profileId: string
  ): Promise<CurrentTeamMembershipSetupRow[]> {
    const { data, error } = await this.supabase
      .from('teams_profiles')
      .select('profile_id, team_id, role, full_view_access, profiles(name, email), teams(name)')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .order('team_id', { ascending: true })

    if (error) {
      throw error
    }

    return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
      const profile = row.profiles as { name?: string | null; email?: string | null } | null
      const team = row.teams as { name?: string | null } | null

      return {
        profile_id: String(row.profile_id),
        profile_name: typeof profile?.name === 'string' ? profile.name : null,
        profile_email: typeof profile?.email === 'string' ? profile.email : null,
        team_id: Number(row.team_id),
        team_name: typeof team?.name === 'string' ? team.name : null,
        role: row.role as Enums<'team_role'>,
        full_view_access: row.full_view_access === true
      } satisfies CurrentTeamMembershipSetupRow
    })
  }

  async saveUserEditTeamMembershipRangeSetup(
    args: SaveUserEditTeamMembershipRangeSetupParams
  ): Promise<void> {
    if (args.existingTeamId != null && args.existingEffectiveFromDate != null) {
      const { error: deleteExistingActiveError } = await this.supabase
        .from('teams_profiles_events')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('team_id', args.existingTeamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'active')
        .eq('event_date', args.existingEffectiveFromDate)

      if (deleteExistingActiveError) {
        throw deleteExistingActiveError
      }

      if (args.existingEffectiveToDate != null) {
        const { error: deleteExistingRemovedError } = await this.supabase
          .from('teams_profiles_events')
          .delete()
          .eq('organization_id', args.organizationId)
          .eq('team_id', args.existingTeamId)
          .eq('profile_id', args.profileId)
          .eq('status', 'removed')
          .eq('event_date', args.existingEffectiveToDate)

        if (deleteExistingRemovedError) {
          throw deleteExistingRemovedError
        }
      }
    }

    const { error: deleteDuplicateActiveError } = await this.supabase
      .from('teams_profiles_events')
      .delete()
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('status', 'active')
      .eq('event_date', args.effectiveFromDate)

    if (deleteDuplicateActiveError) {
      throw deleteDuplicateActiveError
    }

    if (args.role === 'member') {
      await this.closeOverlappingMemberMembershipsForUserEditRange(args)
    }

    const { error: insertActiveError } = await this.supabase.from('teams_profiles_events').insert({
      organization_id: args.organizationId,
      team_id: args.teamId,
      profile_id: args.profileId,
      status: 'active',
      event_date: args.effectiveFromDate
    })

    if (insertActiveError) {
      throw insertActiveError
    }

    if (args.effectiveToDate != null) {
      const { error: deleteDuplicateRemovedError } = await this.supabase
        .from('teams_profiles_events')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('team_id', args.teamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'removed')
        .eq('event_date', args.effectiveToDate)

      if (deleteDuplicateRemovedError) {
        throw deleteDuplicateRemovedError
      }

      const { error: insertRemovedError } = await this.supabase
        .from('teams_profiles_events')
        .insert({
          organization_id: args.organizationId,
          team_id: args.teamId,
          profile_id: args.profileId,
          status: 'removed',
          event_date: args.effectiveToDate
        })

      if (insertRemovedError) {
        throw insertRemovedError
      }
    }

    const today = format(new Date(), 'yyyy-MM-dd')
    const membershipIsCurrentlyEffective =
      args.effectiveFromDate <= today &&
      (args.effectiveToDate == null || args.effectiveToDate >= today)

    if (!membershipIsCurrentlyEffective) {
      if (args.role === 'member') {
        await this.deleteCurrentMemberSnapshotForUserEditRange({
          organizationId: args.organizationId,
          teamId: args.teamId,
          profileId: args.profileId,
          targetRemovedEventDate: args.effectiveToDate ?? null,
          targetRemovedEventAlreadyExists: args.effectiveToDate != null
        })
        await this.syncFutureMemberSnapshotForUserEditRange(args)
      }

      return
    }

    if (args.role === 'member') {
      const { error: deleteOtherMemberSnapshotsError } = await this.supabase
        .from('teams_profiles')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('profile_id', args.profileId)
        .eq('role', 'member')
        .neq('team_id', args.teamId)

      if (deleteOtherMemberSnapshotsError) {
        throw deleteOtherMemberSnapshotsError
      }
    }

    const { error: upsertMembershipError } = await this.supabase.from('teams_profiles').upsert(
      {
        organization_id: args.organizationId,
        team_id: args.teamId,
        profile_id: args.profileId,
        role: args.role,
        full_view_access: args.role === 'manager' ? true : args.fullViewAccess
      },
      {
        onConflict: 'profile_id,team_id'
      }
    )

    if (upsertMembershipError) {
      throw upsertMembershipError
    }
  }

  private async syncFutureMemberSnapshotForUserEditRange(
    args: SaveUserEditTeamMembershipRangeSetupParams
  ): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd')
    const { data, error } = await this.supabase
      .from('teams_profiles_events')
      .select('team_id, event_date, status')
      .eq('organization_id', args.organizationId)
      .eq('profile_id', args.profileId)
      .gt('event_date', today)
      .order('event_date', { ascending: true })

    if (error) {
      throw error
    }

    const latestFutureActive = (
      (data ?? []) as Array<{
        team_id: number | null
        event_date: string | null
        status: Enums<'team_profile_status'> | null
      }>
    )
      .filter(row => row.status === 'active' && row.team_id != null && row.event_date != null)
      .at(-1)

    if (latestFutureActive?.team_id == null) {
      return
    }

    const latestTeamId = latestFutureActive.team_id
    const fullViewAccess =
      latestTeamId === args.teamId && latestFutureActive.event_date === args.effectiveFromDate
        ? args.fullViewAccess
        : await this.getExistingMemberFullViewAccess({
            organizationId: args.organizationId,
            teamId: latestTeamId,
            profileId: args.profileId
          })

    const { error: deleteOtherMemberSnapshotsError } = await this.supabase
      .from('teams_profiles')
      .delete()
      .eq('organization_id', args.organizationId)
      .eq('profile_id', args.profileId)
      .eq('role', 'member')
      .neq('team_id', latestTeamId)

    if (deleteOtherMemberSnapshotsError) {
      throw deleteOtherMemberSnapshotsError
    }

    const { error: upsertMembershipError } = await this.supabase.from('teams_profiles').upsert(
      {
        organization_id: args.organizationId,
        team_id: latestTeamId,
        profile_id: args.profileId,
        role: 'member',
        full_view_access: fullViewAccess
      },
      {
        onConflict: 'profile_id,team_id'
      }
    )

    if (upsertMembershipError) {
      throw upsertMembershipError
    }
  }

  private async getExistingMemberFullViewAccess(args: {
    organizationId: number
    teamId: number
    profileId: string
  }): Promise<boolean> {
    const { data, error } = await this.supabase
      .from('teams_profiles')
      .select('full_view_access')
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('role', 'member')
      .maybeSingle()

    if (error) {
      throw error
    }

    return data?.full_view_access === true
  }

  private async closeOverlappingMemberMembershipsForUserEditRange(
    args: SaveUserEditTeamMembershipRangeSetupParams
  ): Promise<void> {
    const targetRemovedEventDate = subtractIsoDateDay(args.effectiveFromDate)
    const previousTargetRemovedEventDate =
      args.existingEffectiveFromDate == null
        ? null
        : subtractIsoDateDay(args.existingEffectiveFromDate)
    const { data, error } = await this.supabase
      .from('teams_profiles_events')
      .select('event_date, status, team_id')
      .eq('organization_id', args.organizationId)
      .eq('profile_id', args.profileId)
      .order('event_date', { ascending: true })

    if (error) {
      throw error
    }

    const rowsByTeamId = new Map<
      number,
      Array<{ event_date: string; status: Enums<'team_profile_status'>; team_id: number }>
    >()

    for (const row of (data ?? []) as Array<{
      event_date?: string | null
      status?: Enums<'team_profile_status'> | null
      team_id?: number | null
    }>) {
      if (
        row.team_id == null ||
        row.team_id === args.teamId ||
        row.event_date == null ||
        row.status == null
      ) {
        continue
      }

      const teamRows = rowsByTeamId.get(row.team_id) ?? []
      teamRows.push({
        event_date: row.event_date,
        status: row.status,
        team_id: row.team_id
      })
      rowsByTeamId.set(row.team_id, teamRows)
    }

    for (const [teamId, teamRows] of rowsByTeamId.entries()) {
      const resumeEventDate =
        args.effectiveToDate == null ? null : addIsoDateDay(args.effectiveToDate)
      const coveredRanges = teamRows
        .filter(
          row =>
            row.status === 'active' &&
            row.event_date >= args.effectiveFromDate &&
            (args.effectiveToDate == null || row.event_date <= args.effectiveToDate)
        )
        .map(activeRow => ({
          activeRow,
          nextRow: teamRows.find(row => row.event_date > activeRow.event_date) ?? null
        }))

      for (const { activeRow, nextRow } of coveredRanges) {
        const { error: deleteCoveredActiveError } = await this.supabase
          .from('teams_profiles_events')
          .delete()
          .eq('organization_id', args.organizationId)
          .eq('team_id', teamId)
          .eq('profile_id', args.profileId)
          .eq('status', 'active')
          .eq('event_date', activeRow.event_date)

        if (deleteCoveredActiveError) {
          throw deleteCoveredActiveError
        }

        const rangeContinuesAfterTarget =
          args.effectiveToDate != null &&
          resumeEventDate != null &&
          (nextRow == null ||
            nextRow.status !== 'removed' ||
            nextRow.event_date > args.effectiveToDate)

        if (nextRow?.status === 'removed' && !rangeContinuesAfterTarget) {
          const { error: deleteCoveredRemovedError } = await this.supabase
            .from('teams_profiles_events')
            .delete()
            .eq('organization_id', args.organizationId)
            .eq('team_id', teamId)
            .eq('profile_id', args.profileId)
            .eq('status', 'removed')
            .eq('event_date', nextRow.event_date)

          if (deleteCoveredRemovedError) {
            throw deleteCoveredRemovedError
          }
        }

        if (!rangeContinuesAfterTarget || resumeEventDate == null) {
          continue
        }

        if (nextRow?.status === 'removed' && nextRow.event_date === resumeEventDate) {
          continue
        }

        const { error: deleteDuplicateResumeActiveError } = await this.supabase
          .from('teams_profiles_events')
          .delete()
          .eq('organization_id', args.organizationId)
          .eq('team_id', teamId)
          .eq('profile_id', args.profileId)
          .eq('status', 'active')
          .eq('event_date', resumeEventDate)

        if (deleteDuplicateResumeActiveError) {
          throw deleteDuplicateResumeActiveError
        }

        const { error: insertResumeActiveError } = await this.supabase
          .from('teams_profiles_events')
          .insert({
            organization_id: args.organizationId,
            team_id: teamId,
            profile_id: args.profileId,
            status: 'active',
            event_date: resumeEventDate
          })

        if (insertResumeActiveError) {
          throw insertResumeActiveError
        }
      }

      const activeRowsBeforeTarget = teamRows.filter(
        row => row.status === 'active' && row.event_date < args.effectiveFromDate
      )
      const latestActiveRow = activeRowsBeforeTarget.at(-1)

      if (latestActiveRow == null || targetRemovedEventDate < latestActiveRow.event_date) {
        continue
      }

      const nextRow = teamRows.find(row => row.event_date > latestActiveRow.event_date) ?? null
      const shouldCloseOpenRange = nextRow == null
      const shouldCloseOverlappingRange =
        nextRow != null && nextRow.event_date >= args.effectiveFromDate
      const shouldShiftExistingClose =
        previousTargetRemovedEventDate != null &&
        nextRow?.status === 'removed' &&
        nextRow.event_date === previousTargetRemovedEventDate &&
        nextRow.event_date >= args.effectiveFromDate

      if (!shouldCloseOpenRange && !shouldCloseOverlappingRange && !shouldShiftExistingClose) {
        continue
      }

      if (
        previousTargetRemovedEventDate != null &&
        previousTargetRemovedEventDate !== targetRemovedEventDate
      ) {
        const { error: deletePreviousRemovedError } = await this.supabase
          .from('teams_profiles_events')
          .delete()
          .eq('organization_id', args.organizationId)
          .eq('team_id', teamId)
          .eq('profile_id', args.profileId)
          .eq('status', 'removed')
          .eq('event_date', previousTargetRemovedEventDate)

        if (deletePreviousRemovedError) {
          throw deletePreviousRemovedError
        }
      }

      const { error: deleteDuplicateRemovedError } = await this.supabase
        .from('teams_profiles_events')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('team_id', teamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'removed')
        .eq('event_date', targetRemovedEventDate)

      if (deleteDuplicateRemovedError) {
        throw deleteDuplicateRemovedError
      }

      const { error: insertRemovedError } = await this.supabase
        .from('teams_profiles_events')
        .insert({
          organization_id: args.organizationId,
          team_id: teamId,
          profile_id: args.profileId,
          status: 'removed',
          event_date: targetRemovedEventDate
        })

      if (insertRemovedError) {
        throw insertRemovedError
      }

      const shouldDeleteCoveredRemoved =
        nextRow?.status === 'removed' &&
        nextRow.event_date >= args.effectiveFromDate &&
        (args.effectiveToDate == null || nextRow.event_date <= args.effectiveToDate)

      if (shouldDeleteCoveredRemoved) {
        const { error: deleteCoveredRemovedError } = await this.supabase
          .from('teams_profiles_events')
          .delete()
          .eq('organization_id', args.organizationId)
          .eq('team_id', teamId)
          .eq('profile_id', args.profileId)
          .eq('status', 'removed')
          .eq('event_date', nextRow.event_date)

        if (deleteCoveredRemovedError) {
          throw deleteCoveredRemovedError
        }
      }

      const rangeContinuesAfterTarget =
        args.effectiveToDate != null &&
        resumeEventDate != null &&
        (nextRow == null ||
          nextRow.status !== 'removed' ||
          nextRow.event_date > args.effectiveToDate)

      if (!rangeContinuesAfterTarget || resumeEventDate == null) {
        continue
      }

      if (nextRow?.status === 'removed' && nextRow.event_date === resumeEventDate) {
        continue
      }

      const { error: deleteDuplicateResumeActiveError } = await this.supabase
        .from('teams_profiles_events')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('team_id', teamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'active')
        .eq('event_date', resumeEventDate)

      if (deleteDuplicateResumeActiveError) {
        throw deleteDuplicateResumeActiveError
      }

      const { error: insertResumeActiveError } = await this.supabase
        .from('teams_profiles_events')
        .insert({
          organization_id: args.organizationId,
          team_id: teamId,
          profile_id: args.profileId,
          status: 'active',
          event_date: resumeEventDate
        })

      if (insertResumeActiveError) {
        throw insertResumeActiveError
      }
    }
  }

  private async deleteCurrentMemberSnapshotForUserEditRange(args: {
    organizationId: number
    teamId: number
    profileId: string
    targetRemovedEventDate: string | null
    targetRemovedEventAlreadyExists: boolean
  }): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd')
    const triggerRemovedEventDate = subtractIsoDateDay(today)
    const { data: existingMembership, error: existingMembershipError } = await this.supabase
      .from('teams_profiles')
      .select('profile_id')
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('role', 'member')
      .maybeSingle()

    if (existingMembershipError) {
      throw existingMembershipError
    }

    if (!existingMembership) {
      return
    }

    const { error: deleteExistingTriggerEventError } = await this.supabase
      .from('teams_profiles_events')
      .delete()
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('status', 'removed')
      .eq('event_date', triggerRemovedEventDate)

    if (deleteExistingTriggerEventError) {
      throw deleteExistingTriggerEventError
    }

    const { error: deleteMembershipError } = await this.supabase
      .from('teams_profiles')
      .delete()
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('role', 'member')

    if (deleteMembershipError) {
      throw deleteMembershipError
    }

    if (args.targetRemovedEventDate == null) {
      const { error: deleteTriggerEventError } = await this.supabase
        .from('teams_profiles_events')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('team_id', args.teamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'removed')
        .eq('event_date', triggerRemovedEventDate)

      if (deleteTriggerEventError) {
        throw deleteTriggerEventError
      }

      return
    }

    await this.reconcileTeamsProfileEventDate({
      organizationId: args.organizationId,
      teamId: args.teamId,
      profileId: args.profileId,
      status: 'removed',
      triggerEventDate: triggerRemovedEventDate,
      targetEventDate: args.targetRemovedEventDate,
      targetAlreadyExists: args.targetRemovedEventAlreadyExists
    })
  }

  private async mapTeamMembershipHistoryRows(
    organizationId: number,
    rows: Array<Record<string, unknown>>
  ): Promise<TeamMembershipHistorySetupRow[]> {
    const membershipSnapshots = new Map<
      string,
      Pick<Tables<'teams_profiles'>, 'role' | 'full_view_access'>
    >()

    const uniquePairs = Array.from(
      new Set(rows.map(row => `${Number(row.team_id)}:${String(row.profile_id)}`))
    )

    if (uniquePairs.length > 0) {
      const { data: currentMemberships, error } = await this.supabase
        .from('teams_profiles')
        .select('team_id, profile_id, role, full_view_access')
        .eq('organization_id', organizationId)
        .or(
          uniquePairs
            .map(pair => {
              const [teamId, profileId] = pair.split(':')
              return `and(team_id.eq.${teamId},profile_id.eq.${profileId})`
            })
            .join(',')
        )

      if (error) {
        throw error
      }

      for (const membership of currentMemberships ?? []) {
        membershipSnapshots.set(`${membership.team_id}:${membership.profile_id}`, {
          role: membership.role,
          full_view_access: membership.full_view_access
        })
      }
    }

    return rows.map((row, index) => {
      const profile = row.profiles as { name?: string | null; email?: string | null } | null
      const team = row.teams as { name?: string | null } | null
      const currentMembership =
        membershipSnapshots.get(`${Number(row.team_id)}:${String(row.profile_id)}`) ?? null
      const nextEntry = rows
        .slice(index + 1)
        .find(
          candidate =>
            Number(candidate.team_id) === Number(row.team_id) &&
            String(candidate.profile_id) === String(row.profile_id)
        )

      return {
        profile_id: String(row.profile_id),
        profile_name: typeof profile?.name === 'string' ? profile.name : null,
        profile_email: typeof profile?.email === 'string' ? profile.email : null,
        team_id: Number(row.team_id),
        team_name: typeof team?.name === 'string' ? team.name : null,
        event_date: String(row.event_date),
        status: row.status as Enums<'team_profile_status'>,
        role: currentMembership?.role ?? null,
        full_view_access: currentMembership?.full_view_access ?? null,
        effective_from: String(row.event_date),
        effective_to:
          nextEntry && typeof nextEntry.event_date === 'string' ? nextEntry.event_date : null
      }
    })
  }

  async setTeamArchivedState(
    organizationId: number,
    teamId: number,
    archived: boolean
  ): Promise<Tables<'teams'> | null> {
    const { data, error } = await this.supabase
      .from('teams')
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .eq('organization_id', organizationId)
      .eq('id', teamId)
      .select('*')
      .maybeSingle()

    if (error) {
      throw error
    }

    return data as Tables<'teams'> | null
  }

  async deleteTeamSetup(organizationId: number, teamId: number): Promise<void> {
    const { error } = await this.supabase
      .from('teams')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', teamId)

    if (error) {
      throw error
    }
  }

  async upsertTeamMembershipRoleAccess(args: {
    organizationId: number
    teamId: number
    profileId: string
    role?: Enums<'team_role'>
    fullViewAccess?: boolean
  }): Promise<Tables<'teams_profiles'> | null> {
    const payload: TablesInsert<'teams_profiles'> = {
      organization_id: args.organizationId,
      team_id: args.teamId,
      profile_id: args.profileId
    }

    if (args.role != null) {
      payload.role = args.role
    }
    if (args.fullViewAccess != null) {
      payload.full_view_access = args.fullViewAccess
    }

    const { data, error } = await this.supabase
      .from('teams_profiles')
      .upsert(payload, {
        onConflict: 'organization_id,profile_id,team_id'
      })
      .select('*')
      .maybeSingle()

    if (error) {
      throw error
    }

    return data
  }

  async moveProfilesToTeams(args: {
    organizationId: number
    moves: Array<{
      profileId: string
      teamId: number
      date: string
      role: Enums<'team_role'>
      fullViewAccess: boolean
    }>
  }): Promise<void> {
    const profileIds = Array.from(new Set(args.moves.map(move => move.profileId)))
    const currentMembershipsByProfile = new Map<string, number>()

    if (profileIds.length > 0) {
      const { data: currentMemberships, error: currentMembershipsError } = await this.supabase
        .from('teams_profiles')
        .select('profile_id, team_id')
        .eq('organization_id', args.organizationId)
        .eq('role', 'member')
        .in('profile_id', profileIds)

      if (currentMembershipsError) {
        throw currentMembershipsError
      }

      for (const membership of currentMemberships ?? []) {
        currentMembershipsByProfile.set(membership.profile_id, membership.team_id)
      }
    }

    const { error } = await this.supabase.rpc('move_profiles_to_teams', {
      org_id: args.organizationId,
      _moves: args.moves as unknown as Json
    })

    if (error) {
      throw error
    }

    const today = new Date().toISOString().slice(0, 10)
    const triggerRemovedEventDate = subtractIsoDateDay(today)

    for (const move of args.moves) {
      if (move.date <= today) {
        continue
      }

      const targetActiveEventDate = move.date
      const targetRemovedEventDate = subtractIsoDateDay(move.date)
      const oldTeamId = currentMembershipsByProfile.get(move.profileId) ?? null

      const { data: existingActiveAtTargetDate, error: existingActiveAtTargetDateError } =
        await this.supabase
          .from('teams_profiles_events')
          .select('profile_id')
          .eq('organization_id', args.organizationId)
          .eq('team_id', move.teamId)
          .eq('profile_id', move.profileId)
          .eq('status', 'active')
          .eq('event_date', targetActiveEventDate)
          .maybeSingle()

      if (existingActiveAtTargetDateError) {
        throw existingActiveAtTargetDateError
      }

      await this.reconcileTeamsProfileEventDate({
        organizationId: args.organizationId,
        teamId: move.teamId,
        profileId: move.profileId,
        status: 'active',
        triggerEventDate: today,
        targetEventDate: targetActiveEventDate,
        targetAlreadyExists: !!existingActiveAtTargetDate
      })

      if (oldTeamId == null || oldTeamId === move.teamId) {
        continue
      }

      const { data: existingRemovedAtTargetDate, error: existingRemovedAtTargetDateError } =
        await this.supabase
          .from('teams_profiles_events')
          .select('profile_id')
          .eq('organization_id', args.organizationId)
          .eq('team_id', oldTeamId)
          .eq('profile_id', move.profileId)
          .eq('status', 'removed')
          .eq('event_date', targetRemovedEventDate)
          .maybeSingle()

      if (existingRemovedAtTargetDateError) {
        throw existingRemovedAtTargetDateError
      }

      await this.reconcileTeamsProfileEventDate({
        organizationId: args.organizationId,
        teamId: oldTeamId,
        profileId: move.profileId,
        status: 'removed',
        triggerEventDate: triggerRemovedEventDate,
        targetEventDate: targetRemovedEventDate,
        targetAlreadyExists: !!existingRemovedAtTargetDate
      })
    }
  }

  async reconcileTeamMembershipMoveEventDates(args: {
    organizationId: number
    profileId: string
    newTeamId: number
    oldTeamId: number | null
    effectiveDate: string
  }): Promise<void> {
    const today = new Date().toISOString().slice(0, 10)
    if (args.effectiveDate <= today) {
      return
    }

    const triggerActiveEventDate = today
    const targetActiveEventDate = args.effectiveDate
    const { data: existingActiveAtTargetDate, error: existingActiveAtTargetDateError } =
      await this.supabase
        .from('teams_profiles_events')
        .select('profile_id')
        .eq('organization_id', args.organizationId)
        .eq('team_id', args.newTeamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'active')
        .eq('event_date', targetActiveEventDate)
        .maybeSingle()

    if (existingActiveAtTargetDateError) {
      throw existingActiveAtTargetDateError
    }

    await this.reconcileTeamsProfileEventDate({
      organizationId: args.organizationId,
      teamId: args.newTeamId,
      profileId: args.profileId,
      status: 'active',
      triggerEventDate: triggerActiveEventDate,
      targetEventDate: targetActiveEventDate,
      targetAlreadyExists: !!existingActiveAtTargetDate
    })

    if (args.oldTeamId == null || args.oldTeamId === args.newTeamId) {
      return
    }

    const triggerRemovedEventDate = subtractIsoDateDay(today)
    const targetRemovedEventDate = subtractIsoDateDay(args.effectiveDate)

    const { data: existingRemovedAtTargetDate, error: existingRemovedAtTargetDateError } =
      await this.supabase
        .from('teams_profiles_events')
        .select('profile_id')
        .eq('organization_id', args.organizationId)
        .eq('team_id', args.oldTeamId)
        .eq('profile_id', args.profileId)
        .eq('status', 'removed')
        .eq('event_date', targetRemovedEventDate)
        .maybeSingle()

    if (existingRemovedAtTargetDateError) {
      throw existingRemovedAtTargetDateError
    }

    await this.reconcileTeamsProfileEventDate({
      organizationId: args.organizationId,
      teamId: args.oldTeamId,
      profileId: args.profileId,
      status: 'removed',
      triggerEventDate: triggerRemovedEventDate,
      targetEventDate: targetRemovedEventDate,
      targetAlreadyExists: !!existingRemovedAtTargetDate
    })
  }

  async removeProfileFromTeamSetup(args: {
    organizationId: number
    teamId: number
    profileId: string
    effectiveDate: string
  }): Promise<boolean> {
    const targetRemovedEventDate = subtractIsoDateDay(args.effectiveDate)

    const { data: existingMembership, error: existingMembershipError } = await this.supabase
      .from('teams_profiles')
      .select('profile_id, team_id, role')
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('role', 'member')
      .maybeSingle()

    if (existingMembershipError) {
      throw existingMembershipError
    }

    const { data: existingRemovedAtTargetDate, error: existingRemovedError } = await this.supabase
      .from('teams_profiles_events')
      .select('profile_id')
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('status', 'removed')
      .eq('event_date', targetRemovedEventDate)
      .maybeSingle()

    if (existingRemovedError) {
      throw existingRemovedError
    }

    if (!existingMembership) {
      return !!existingRemovedAtTargetDate
    }

    const triggerRemovedEventDate = subtractIsoDateDay(new Date().toISOString().slice(0, 10))

    const { error: deleteMembershipError } = await this.supabase
      .from('teams_profiles')
      .delete()
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('role', 'member')

    if (deleteMembershipError) {
      throw deleteMembershipError
    }

    await this.reconcileTeamsProfileEventDate({
      organizationId: args.organizationId,
      teamId: args.teamId,
      profileId: args.profileId,
      status: 'removed',
      triggerEventDate: triggerRemovedEventDate,
      targetEventDate: targetRemovedEventDate,
      targetAlreadyExists: !!existingRemovedAtTargetDate
    })

    return true
  }

  async getOrganizationSetupFlags(organizationId: number): Promise<OrganizationSetupFlags | null> {
    const { data, error } = await this.supabase
      .from('organizations')
      .select(
        'revenue_table, revenue_field, payout_validation_flow, statement_approval_flow, use_fixed_exchange_rates, use_invoices_exchange_rates, check_data_issues'
      )
      .eq('id', organizationId)
      .maybeSingle()

    if (error) throw error
    return data as OrganizationSetupFlags | null
  }

  async updateOrganizationSetupFlags(
    organizationId: number,
    flags: Partial<OrganizationSetupFlags>
  ): Promise<OrganizationSetupFlags> {
    const { data, error } = await this.supabase
      .from('organizations')
      .update(flags)
      .eq('id', organizationId)
      .select(
        'revenue_table, revenue_field, payout_validation_flow, statement_approval_flow, use_fixed_exchange_rates, use_invoices_exchange_rates, check_data_issues'
      )
      .single()

    if (error) throw error
    return data as OrganizationSetupFlags
  }

  async getOrganizationMemberCountsByRole(organizationId: number) {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .select('role')
      .eq('organization_id', organizationId)
      .eq('archived', false)

    if (error) throw error

    const byRole = new Map<Tables<'organizations_profiles'>['role'], number>()
    for (const row of data ?? []) {
      byRole.set(row.role, (byRole.get(row.role) ?? 0) + 1)
    }

    return Array.from(byRole.entries()).map(([role, count]) => ({ role, count }))
  }

  async getPendingInvitationCount(organizationId: number): Promise<number> {
    const { count, error } = await this.supabase
      .from('invitations')
      .select('*', { head: true, count: 'exact' })
      .eq('organization_id', organizationId)
      .in('status', ['created', 'sent', 'pending'])

    if (error) throw error
    return count ?? 0
  }

  async getTeamCount(organizationId: number): Promise<number> {
    const { count, error } = await this.supabase
      .from('teams')
      .select('*', { head: true, count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('archived', false)

    if (error) throw error
    return count ?? 0
  }

  async getPlanCount(organizationId: number): Promise<number> {
    const { count, error } = await this.supabase
      .from('plans')
      .select('*', { head: true, count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('template', false)
      .eq('archived', false)

    if (error) throw error
    return count ?? 0
  }

  async getTargetCount(organizationId: number): Promise<number> {
    const { count, error } = await this.supabase
      .from('targets')
      .select('*', { head: true, count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('archived', false)

    if (error) throw error
    return count ?? 0
  }

  async getBusinessUnitCount(organizationId: number): Promise<number> {
    const { count, error } = await this.supabase
      .from('business_units')
      .select('*', { head: true, count: 'exact' })
      .eq('organization_id', organizationId)
      .is('archived_at', null)

    if (error) throw error
    return count ?? 0
  }

  async getMultiTeamUsersCount(organizationId: number): Promise<number> {
    const { data, error } = await this.supabase
      .from('teams_profiles')
      .select('profile_id')
      .eq('organization_id', organizationId)

    if (error) throw error

    const byProfile = new Map<string, number>()
    for (const row of data ?? []) {
      byProfile.set(row.profile_id, (byProfile.get(row.profile_id) ?? 0) + 1)
    }

    let count = 0
    for (const teamCount of byProfile.values()) {
      if (teamCount > 1) {
        count += 1
      }
    }
    return count
  }

  async getTeamsWithoutMembersCount(organizationId: number): Promise<number> {
    const { data: teams, error: teamsError } = await this.supabase
      .from('teams')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('archived', false)

    if (teamsError) throw teamsError
    if (!teams || teams.length === 0) return 0

    const teamIds = teams.map(team => team.id)
    const { data: members, error: membersError } = await this.supabase
      .from('teams_profiles')
      .select('team_id')
      .in('team_id', teamIds)

    if (membersError) throw membersError

    const withMembers = new Set((members ?? []).map(row => row.team_id))
    return teamIds.filter(teamId => !withMembers.has(teamId)).length
  }

  async getPlansWithoutLinkedTeamsCount(organizationId: number): Promise<number> {
    const today = format(new Date(), 'yyyy-MM-dd')
    const { data: plans, error: plansError } = await this.supabase
      .from('plans')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('template', false)
      .eq('archived', false)

    if (plansError) throw plansError
    if (!plans || plans.length === 0) return 0

    const planIds = plans.map(plan => plan.id)
    const { data: links, error: linksError } = await this.supabase
      .from('teams_plans_history')
      .select('plan_id')
      .in('plan_id', planIds)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)

    if (linksError) throw linksError

    const linkedPlans = new Set((links ?? []).map(row => row.plan_id))
    return planIds.filter(planId => !linkedPlans.has(planId)).length
  }

  async getTargetsWithoutLinkedTeamsCount(organizationId: number): Promise<number> {
    const { data: targets, error: targetsError } = await this.supabase
      .from('targets')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('archived', false)

    if (targetsError) throw targetsError
    if (!targets || targets.length === 0) return 0

    const targetIds = targets.map(target => target.id)
    const { data: links, error: linksError } = await this.supabase
      .from('teams_targets')
      .select('target_id')
      .in('target_id', targetIds)

    if (linksError) throw linksError

    const linkedTargets = new Set((links ?? []).map(row => row.target_id))
    return targetIds.filter(targetId => !linkedTargets.has(targetId)).length
  }

  private mapMemberSetupRows(rows: Array<Record<string, unknown>>): OrganizationMemberSetupRow[] {
    return rows.map(row => ({
      profile_id: String(row.id),
      name: typeof row.name === 'string' ? row.name : null,
      email: typeof row.email === 'string' ? row.email : null,
      role: ((row.organizations_profiles as Array<{ role?: Enums<'org_role'> }> | null)?.[0]
        ?.role ?? 'member') as Enums<'org_role'>,
      archived:
        ((row.organizations_profiles as Array<{ archived?: boolean }> | null)?.[0]?.archived ??
          false) === true,
      teams: this.getScopedMemberTeams(row).map(team => ({
        team_id: Number(team.team_id),
        team_name:
          typeof (team.teams as { name?: string | null } | null)?.name === 'string'
            ? ((team.teams as { name?: string | null }).name ?? null)
            : null,
        team_role: ((team.role as Enums<'team_role'> | null | undefined) ??
          null) as Enums<'team_role'> | null
      }))
    }))
  }

  async listOrganizationMembersSetup(
    organizationId: number
  ): Promise<OrganizationMemberSetupRow[]> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select(
        'id, name, email, organizations_profiles!inner(organization_id, role, archived), teams_profiles(organization_id, team_id, role, teams(name))'
      )
      .eq('organizations_profiles.organization_id', organizationId)

    if (error) {
      throw error
    }

    return this.mapMemberSetupRows((data ?? []) as Array<Record<string, unknown>>)
  }

  async listOrganizationInvitationsSetup(
    organizationId: number
  ): Promise<OrganizationInvitationSetupRow[]> {
    const { data, error } = await this.supabase
      .from('invitations')
      .select(
        'invitation_id, email, name, organization_id, role, status, team_id, team_role, start_date, created_at'
      )
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return (data ?? []) as OrganizationInvitationSetupRow[]
  }

  async listUsersWithoutTeamsSetup(organizationId: number): Promise<OrganizationMemberSetupRow[]> {
    const rows = await this.listOrganizationMembersSetup(organizationId)
    return rows.filter(row => row.teams.length === 0 && row.archived !== true)
  }

  async listMultiTeamMembersSetup(organizationId: number): Promise<OrganizationMemberSetupRow[]> {
    const rows = await this.listOrganizationMembersSetup(organizationId)
    return rows.filter(row => row.teams.length > 1 && row.archived !== true)
  }

  async updateOrganizationMemberRole(args: {
    organizationId: number
    profileId: string
    role: Enums<'org_role'>
  }): Promise<Tables<'organizations_profiles'> | null> {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .update({ role: args.role })
      .eq('organization_id', args.organizationId)
      .eq('profile_id', args.profileId)
      .select('*')
      .maybeSingle()

    if (error) {
      throw error
    }

    return data
  }

  async setOrganizationMemberArchived(args: {
    organizationId: number
    profileId: string
    archived: boolean
  }): Promise<Tables<'organizations_profiles'> | null> {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .update({ archived: args.archived })
      .eq('organization_id', args.organizationId)
      .eq('profile_id', args.profileId)
      .select('*')
      .maybeSingle()

    if (error) {
      throw error
    }

    return data
  }

  async isOrganizationAdmin(profileId: string, organizationId: number): Promise<boolean> {
    const { data, error } = await this.supabase
      .from('organizations_profiles')
      .select('role')
      .eq('profile_id', profileId)
      .eq('organization_id', organizationId)
      .eq('archived', false)
      .maybeSingle()

    if (error) {
      throw error
    }

    if (data?.role !== 'admin') {
      return false
    }

    const { data: profileStatus, error: profileStatusError } = await this.supabase.rpc(
      'get_profile_status',
      {
        p_organization_id: organizationId,
        p_profile_id: profileId
      }
    )

    if (profileStatusError) {
      throw profileStatusError
    }

    return profileStatus !== 'terminated' && profileStatus !== 'inactive'
  }

  async getCompensationRuleAuthoringReferences(args: {
    organizationId: number
    planId: number
    compensationRuleId?: number
    source?: Enums<'plan_source'>
    kpisTableId?: number
    customRecordTableId?: number
    fieldNames?: string[]
    metricIds?: number[]
    targetIds?: number[]
  }): Promise<CompensationRuleAuthoringReferences> {
    const { data: plan, error: planError } = await this.supabase
      .from('plans')
      .select('id, label, organization_id, source')
      .eq('id', args.planId)
      .eq('organization_id', args.organizationId)
      .eq('template', false)
      .maybeSingle()

    if (planError) throw planError
    if (!plan) {
      return { plan: null, rule: null, fields: [], metrics: [], targets: [] }
    }

    const { data: rule, error: ruleError } =
      args.compensationRuleId == null
        ? { data: null, error: null }
        : await this.supabase
            .from('compensation_rules')
            .select(
              'id, plan_id, label, description, diagram, source, type, outcome_period, user_field, accrual_date_field, kpis_table_id, custom_record_table_id, compensation_rule_drafts(diagram, is_tvp_based)'
            )
            .eq('id', args.compensationRuleId)
            .eq('plan_id', args.planId)
            .eq('archived', false)
            .maybeSingle()

    if (ruleError) throw ruleError

    const kpisTableId = rule?.kpis_table_id ?? args.kpisTableId
    const customRecordTableId = rule?.custom_record_table_id ?? args.customRecordTableId
    const source = rule?.source ?? args.source
    if (!source) {
      return { plan, rule, fields: [], metrics: [], targets: [] }
    }
    if (
      (source === 'kpi' && kpisTableId == null) ||
      (source === 'custom_record' && customRecordTableId == null)
    ) {
      return { plan, rule, fields: [], metrics: [], targets: [] }
    }
    const requestedFieldNames = new Set(args.fieldNames ?? [])
    const fieldsResult =
      requestedFieldNames.size === 0
        ? { data: [], error: null }
        : await this.supabase.rpc('get_all_columns_labels', {
            org_id: args.organizationId,
            _source: source,
            _kpis_table_id: kpisTableId,
            _custom_record_table_id: customRecordTableId
          })

    if (fieldsResult.error) throw fieldsResult.error

    const rawFieldDefinitions = (Array.isArray(fieldsResult.data) ? fieldsResult.data : []).flatMap(
      field => {
        if (!field || typeof field !== 'object' || Array.isArray(field)) return []
        const name = 'name' in field ? field.name : undefined
        const label = 'label' in field ? field.label : undefined
        const type = 'type' in field ? field.type : undefined
        return typeof name === 'string' ? [{ name, label, type }] : []
      }
    )
    const fieldNameByAlias = new Map<string, string | null>()
    const addFieldAlias = (alias: string | undefined, canonicalName: string) => {
      if (!alias) return
      const previous = fieldNameByAlias.get(alias)
      if (previous === undefined) {
        fieldNameByAlias.set(alias, canonicalName)
      } else if (previous !== canonicalName) {
        fieldNameByAlias.set(alias, null)
      }
    }
    rawFieldDefinitions.forEach(field => {
      const canonicalName = canonicalizeRecordFilterField(source, field.name)
      addFieldAlias(field.name, canonicalName)
      addFieldAlias(canonicalName, canonicalName)
      addFieldAlias(typeof field.label === 'string' ? field.label : undefined, canonicalName)
      const labelParts = typeof field.label === 'string' ? field.label.split(' -> ') : []
      addFieldAlias(labelParts.at(-1), canonicalName)
      addFieldAlias(field.name.startsWith('$') ? field.name.slice(1) : undefined, canonicalName)
    })
    const resolveRequestedFieldName = (fieldName: string) =>
      fieldNameByAlias.get(fieldName) ?? canonicalizeRecordFilterField(source, fieldName)
    const canonicalRequestedFieldNames = new Set<string>()
    const requestedFieldNamesByCanonicalName = new Map<string, string[]>()
    for (const requestedFieldName of requestedFieldNames) {
      const canonicalName = resolveRequestedFieldName(requestedFieldName)
      if (fieldNameByAlias.get(requestedFieldName) === null) continue
      canonicalRequestedFieldNames.add(canonicalName)
      const resolvedAlias = fieldNameByAlias.get(requestedFieldName)
      const outputName = resolvedAlias !== undefined ? canonicalName : requestedFieldName
      const outputNames = requestedFieldNamesByCanonicalName.get(canonicalName) ?? []
      if (!outputNames.includes(outputName)) outputNames.push(outputName)
      requestedFieldNamesByCanonicalName.set(canonicalName, outputNames)
    }
    const metricIds = Array.from(new Set(args.metricIds ?? []))
    const targetIds = Array.from(new Set(args.targetIds ?? []))
    const metrics: CompensationRuleAuthoringReferences['metrics'] = []
    let targets: CompensationRuleAuthoringReferences['targets'] = []

    const metricSources = getCompensationRuleAuthoringMetricSources(source)
    for (const metricIdsChunk of batched(metricIds, PROFILE_IDS_IN_FILTER_BATCH_SIZE)) {
      let metricQuery = this.supabase
        .from('metrics')
        .select('id, label, type, source, kpis_table_id, custom_record_table_id, organization_id')
        .eq('organization_id', args.organizationId)
        .is('archived_at', null)
        .in('id', metricIdsChunk)
      metricQuery =
        metricSources.length === 1
          ? metricQuery.eq('source', metricSources[0])
          : metricQuery.in('source', metricSources)
      if (source === 'kpi' && kpisTableId != null) {
        metricQuery = metricQuery.eq('kpis_table_id', kpisTableId)
      }
      if (source === 'custom_record' && customRecordTableId != null) {
        metricQuery = metricQuery.eq('custom_record_table_id', customRecordTableId)
      }

      const { data, error } = await metricQuery

      if (error) throw error
      metrics.push(...(data ?? []))
    }

    if (targetIds.length > 0) {
      const today = new Date().toISOString().slice(0, 10)
      const { data: planTeams, error: planTeamsError } = await this.supabase
        .from('teams_plans_history')
        .select('team_id, teams!inner(organization_id)')
        .eq('plan_id', args.planId)
        .eq('teams.organization_id', args.organizationId)
        .lte('effective_from', today)
        .or(`effective_to.is.null,effective_to.gte.${today}`)

      if (planTeamsError) throw planTeamsError

      const teamIds = Array.from(new Set((planTeams ?? []).map(link => link.team_id)))
      for (const targetIdsChunk of batched(targetIds, PROFILE_IDS_IN_FILTER_BATCH_SIZE)) {
        if (teamIds.length === 0) break
        const { data: teamTargets, error: teamTargetsError } = await this.supabase
          .from('teams_targets')
          .select(
            'target_id, hide_target, targets!inner(id, label, metric_id, type, period, organization_id, archived_at)'
          )
          .in('team_id', teamIds)
          .in('target_id', targetIdsChunk)
          .eq('hide_target', false)
          .eq('targets.organization_id', args.organizationId)
          .is('targets.archived_at', null)
        if (teamTargetsError) throw teamTargetsError
        targets.push(
          ...(teamTargets ?? [])
            .map(teamTarget => teamTarget.targets)
            .filter((target): target is NonNullable<typeof target> => target != null)
        )
      }
      targets = Array.from(new Map(targets.map(target => [target.id, target])).values())
    }

    const fieldsByName = new Map<
      string,
      { name: string; label?: string; type?: string; organization_id?: number }
    >()
    const addRequestedField = (
      fieldName: string,
      definition: { label?: string; type?: string }
    ) => {
      const canonicalName = canonicalizeRecordFilterField(source, fieldName)
      if (!canonicalRequestedFieldNames.has(canonicalName)) return

      const outputNames = requestedFieldNamesByCanonicalName.get(canonicalName) ?? [canonicalName]
      outputNames.forEach(outputName => {
        fieldsByName.set(outputName, {
          name: outputName,
          ...definition,
          organization_id: args.organizationId
        })
      })
    }

    rawFieldDefinitions.forEach(field => {
      const canonicalName = canonicalizeRecordFilterField(source, field.name)
      if (!canonicalRequestedFieldNames.has(canonicalName)) return

      addRequestedField(canonicalName, {
        ...(typeof field.label === 'string' ? { label: field.label } : {}),
        ...(typeof field.type === 'string' ? { type: field.type } : {})
      })
    })

    for (const [name, definition] of Object.entries(getSourceColumns(source))) {
      if (!canonicalRequestedFieldNames.has(canonicalizeRecordFilterField(source, name))) {
        continue
      }
      addRequestedField(name, { type: definition.type })
    }
    const fields = [...fieldsByName.values()].sort((left, right) =>
      left.name.localeCompare(right.name)
    )

    return {
      plan,
      rule,
      fields,
      metrics: [...metrics].sort((left, right) => left.id - right.id),
      targets: [...targets].sort((left, right) => left.id - right.id)
    }
  }
  async searchCompensationRuleAuthoringReferences(args: {
    organizationId: number
    planId: number
    source?: Enums<'plan_source'>
    kpisTableId?: number
    customRecordTableId?: number
    query?: string
    limit?: number
  }): Promise<CompensationRuleAuthoringReferenceSearchResult> {
    const search = args.query?.trim() ?? ''

    const { data: plan, error: planError } = await this.supabase
      .from('plans')
      .select('id')
      .eq('id', args.planId)
      .eq('organization_id', args.organizationId)
      .eq('template', false)
      .maybeSingle()

    if (planError) throw planError
    if (!plan || !args.source) return { fields: [], metrics: [], targets: [] }
    const source = args.source
    const pattern = search ? `%${search.replace(/[\\%_]/g, '\\$&')}%` : null
    const limit = Math.min(Math.max(args.limit ?? 20, 1), 50)
    const metricSources = getCompensationRuleAuthoringMetricSources(source)
    let metricsQuery = this.supabase
      .from('metrics')
      .select('id, label, description, source, type, kpis_table_id, custom_record_table_id')
      .eq('organization_id', args.organizationId)
      .is('archived_at', null)
    if (pattern) {
      metricsQuery = metricsQuery.ilike('label', pattern)
    }
    metricsQuery =
      metricSources.length === 1
        ? metricsQuery.eq('source', metricSources[0])
        : metricsQuery.in('source', metricSources)
    if (source === 'kpi' && args.kpisTableId != null) {
      metricsQuery = metricsQuery.eq('kpis_table_id', args.kpisTableId)
    }
    if (source === 'custom_record' && args.customRecordTableId != null) {
      metricsQuery = metricsQuery.eq('custom_record_table_id', args.customRecordTableId)
    }
    metricsQuery = metricsQuery.limit(limit)

    const today = new Date().toISOString().slice(0, 10)
    const targetReferenceMatch = search.match(
      /^target_(\d+)(?:_(?:month|quarter|half_year|year)(?:_full_period)?)?$/
    )
    const targetReferenceId = targetReferenceMatch ? Number(targetReferenceMatch[1]) : null
    const { data: planTeams, error: planTeamsError } = await this.supabase
      .from('teams_plans_history')
      .select('team_id, teams!inner(organization_id)')
      .eq('plan_id', args.planId)
      .eq('teams.organization_id', args.organizationId)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)

    if (planTeamsError) throw planTeamsError

    const teamIds = Array.from(new Set((planTeams ?? []).map(link => link.team_id)))
    const targetQuery = () =>
      this.supabase
        .from('teams_targets')
        .select(
          'target_id, hide_target, targets!inner(id, label, metric_id, type, period, organization_id, archived_at)'
        )
        .in('team_id', teamIds)
        .eq('targets.organization_id', args.organizationId)
        .is('targets.archived_at', null)
        .eq('hide_target', false)
    const targetsQuery =
      teamIds.length === 0
        ? Promise.resolve({ data: [], error: null })
        : targetReferenceId != null
          ? targetQuery().eq('target_id', targetReferenceId).limit(limit)
          : pattern
            ? targetQuery().ilike('targets.label', pattern).limit(limit)
            : targetQuery().limit(limit)

    const [fieldsResult, metricsResult, targetsResult] = await Promise.all([
      this.supabase.rpc('get_all_columns_labels', {
        org_id: args.organizationId,
        _source: source,
        ...(args.kpisTableId != null ? { _kpis_table_id: args.kpisTableId } : {}),
        ...(args.customRecordTableId != null
          ? { _custom_record_table_id: args.customRecordTableId }
          : {})
      }),
      metricsQuery,
      targetsQuery
    ])

    if (fieldsResult.error) throw fieldsResult.error
    if (metricsResult.error) throw metricsResult.error
    if (targetsResult.error) throw targetsResult.error

    const normalizedSearch = search.toLocaleLowerCase()
    const fields = new Map<string, { name: string; label?: string; type?: string }>()
    for (const field of Array.isArray(fieldsResult.data) ? fieldsResult.data : []) {
      if (!field || typeof field !== 'object' || Array.isArray(field)) continue
      const name = 'name' in field && typeof field.name === 'string' ? field.name : null
      if (!name) continue
      const label = 'label' in field && typeof field.label === 'string' ? field.label : undefined
      if (
        !name.toLocaleLowerCase().includes(normalizedSearch) &&
        !label?.toLocaleLowerCase().includes(normalizedSearch)
      ) {
        continue
      }
      const type = 'type' in field && typeof field.type === 'string' ? field.type : undefined
      fields.set(name, { name, ...(label ? { label } : {}), ...(type ? { type } : {}) })
      if (fields.size >= limit) break
    }
    for (const [name, definition] of Object.entries(getSourceColumns(source))) {
      const existing = fields.get(name)
      if (!existing) {
        if (fields.size >= limit || !name.toLocaleLowerCase().includes(normalizedSearch)) {
          continue
        }
      }
      fields.set(name, { ...existing, name, type: definition.type })
    }

    return {
      fields: [...fields.values()].sort((left, right) => left.name.localeCompare(right.name)),
      metrics: metricsResult.data ?? [],
      targets: Array.from(
        new Map(
          (targetsResult.data ?? [])
            .map(teamTarget => teamTarget.targets)
            .filter(target => target != null)
            .map(target => [target.id, target])
        ).values()
      )
    }
  }

  async startCompensationRuleAuthoringSession(
    args: CompensationRuleAuthoringSessionRpcParams
  ): Promise<Json> {
    const { data, error } = await this.supabase.rpc('start_compensation_rule_authoring_session', {
      _organization_id: args.organizationId,
      _plan_id: args.planId,
      _compensation_rule_id: args.compensationRuleId,
      _rule_metadata: args.ruleMetadata,
      _diagram: args.diagram,
      _context_fingerprint: args.contextFingerprint,
      _ttl_seconds: args.ttlSeconds ?? 86400
    })

    if (error) {
      const diagnostic = [
        error.code ? `code=${error.code}` : null,
        error.message ? `message=${error.message}` : null,
        error.details ? `details=${error.details}` : null,
        error.hint ? `hint=${error.hint}` : null
      ]
        .filter((value): value is string => value != null)
        .join('; ')
      throw new Error(
        `Compensation authoring session start failed${diagnostic ? `: ${diagnostic}` : '.'}`
      )
    }
    return data
  }

  async readCompensationRuleAuthoringSession(sessionId: string): Promise<Json> {
    const { data, error } = await this.supabase.rpc('read_compensation_rule_authoring_session', {
      _session_id: sessionId
    })

    if (error) throw error
    return data
  }

  async listCompensationRuleDraftHistory(args: {
    organizationId: number
    compensationRuleId: number
    limit?: number
  }): Promise<CompensationRuleDraftHistoryRow[]> {
    const { data, error } = await this.supabase
      .from('compensation_rule_draft_history')
      .select(
        'id, organization_id, compensation_rule_id, actor_id, before_state, after_state, before_hash, after_hash, operation_kind, created_at, history_sequence'
      )
      .eq('organization_id', args.organizationId)
      .eq('compensation_rule_id', args.compensationRuleId)
      .order('created_at', { ascending: false })
      .order('history_sequence', { ascending: false })
      .limit(args.limit ?? 50)

    if (error) throw error
    return (data ?? []) as unknown as CompensationRuleDraftHistoryRow[]
  }

  async recordCompensationRuleDraftHistory(args: {
    organizationId: number
    compensationRuleId: number
    event: CompensationRuleDraftHistoryEvent
    maxEntries?: number
  }): Promise<CompensationRuleDraftHistoryRecord | null> {
    const { data, error } = await this.supabase.rpc('record_compensation_rule_draft_history', {
      _organization_id: args.organizationId,
      _compensation_rule_id: args.compensationRuleId,
      _before_state: args.event.beforeState,
      _after_state: args.event.afterState,
      _before_hash: args.event.beforeHash,
      _after_hash: args.event.afterHash,
      _operation_kind: args.event.operationKind,
      _max_entries: args.maxEntries ?? 50
    })

    if (error) throw error

    const result = data as {
      status?: string
      history?: CompensationRuleDraftHistoryRecord
    } | null

    return result?.status === 'recorded' ? (result.history ?? null) : null
  }

  async readCompensationRuleAuthoringSessionForActor(
    actorId: string,
    sessionId: string
  ): Promise<Json> {
    const { data, error } = await this.supabase.rpc(
      'read_compensation_rule_authoring_session_for_actor',
      {
        _actor_id: actorId,
        _session_id: sessionId
      }
    )

    if (error) throw error
    return data
  }

  async updateCompensationRuleAuthoringSession(
    args: CompensationRuleAuthoringSessionUpdateRpcParams
  ): Promise<Json> {
    const { data, error } = await this.supabase.rpc('update_compensation_rule_authoring_session', {
      _session_id: args.sessionId,
      _expected_revision: args.expectedRevision,
      _diagram: args.diagram,
      _rule_metadata: args.ruleMetadata,
      _context_fingerprint: args.contextFingerprint,
      _model_metadata: args.modelMetadata,
      _referenced_fields: args.referencedFields,
      _referenced_metric_ids: args.referencedMetricIds,
      _referenced_target_ids: args.referencedTargetIds
    })

    if (error) throw error
    return data
  }

  async updateCompensationRuleAuthoringSessionForActor(
    args: CompensationRuleAuthoringSessionUpdateRpcParams & { actorId: string }
  ): Promise<Json> {
    const { data, error } = await this.supabase.rpc(
      'update_compensation_rule_authoring_session_for_actor',
      {
        _actor_id: args.actorId,
        _session_id: args.sessionId,
        _expected_revision: args.expectedRevision,
        _diagram: args.diagram,
        _rule_metadata: args.ruleMetadata,
        _context_fingerprint: args.contextFingerprint,
        _model_metadata: args.modelMetadata,
        _referenced_fields: args.referencedFields,
        _referenced_metric_ids: args.referencedMetricIds,
        _referenced_target_ids: args.referencedTargetIds
      }
    )

    if (error) throw error
    return data
  }

  async commitCompensationRuleAuthoringSession(
    args: CompensationRuleAuthoringCommitRpcParams
  ): Promise<Json> {
    const { data, error } = await this.supabase.rpc('commit_compensation_rule_authoring_session', {
      _session_id: args.sessionId,
      _expected_revision: args.expectedRevision,
      _operation_key: args.operationKey,
      _draft_hash: args.draftHash,
      _context_fingerprint: args.contextFingerprint
    })

    if (error) throw error
    return data
  }

  async commitCompensationRuleAuthoringSessionForActor(
    args: CompensationRuleAuthoringCommitRpcParams & { actorId: string }
  ): Promise<Json> {
    const { data, error } = await this.supabase.rpc(
      'commit_compensation_rule_authoring_session_for_actor',
      {
        _actor_id: args.actorId,
        _session_id: args.sessionId,
        _expected_revision: args.expectedRevision,
        _operation_key: args.operationKey,
        _draft_hash: args.draftHash,
        _context_fingerprint: args.contextFingerprint
      }
    )

    if (error) throw error
    return data
  }

  private async reconcileTeamsProfileEventDate(args: {
    organizationId: number
    teamId: number
    profileId: string
    status: Enums<'team_profile_status'>
    triggerEventDate: string
    targetEventDate: string
    targetAlreadyExists: boolean
  }) {
    if (args.triggerEventDate === args.targetEventDate) {
      return
    }

    if (args.targetAlreadyExists) {
      const { error } = await this.supabase
        .from('teams_profiles_events')
        .delete()
        .eq('organization_id', args.organizationId)
        .eq('team_id', args.teamId)
        .eq('profile_id', args.profileId)
        .eq('status', args.status)
        .eq('event_date', args.triggerEventDate)

      if (error) {
        throw error
      }

      return
    }

    const { error } = await this.supabase
      .from('teams_profiles_events')
      .update({ event_date: args.targetEventDate })
      .eq('organization_id', args.organizationId)
      .eq('team_id', args.teamId)
      .eq('profile_id', args.profileId)
      .eq('status', args.status)
      .eq('event_date', args.triggerEventDate)

    if (error) {
      throw error
    }
  }

  async getPlanForDuplication(
    planId: number,
    organizationId: number
  ): Promise<PlanDuplicationRecord | null> {
    const { data, error } = await this.supabase
      .from('plans')
      .select(
        `id, organization_id, label, description, currency, source,
         teams_plans(team_id),
         compensation_rules(
           id, label, description, source, kpis_table_id, outcome_period,
           accrual_date_field, type, user_field, clawback_application_method,
           clawback_date_field, clawback_deduction_method, clawback_diagram,
           created_at, updated_at, diagram, installments, plan_id, archived,
           deal_resolution_config, deal_resolution_strategy,
           long_term_balancing_clawback_method, long_term_balancing_installments,
           long_term_balancing_max_deduction_per_period, long_term_balancing_only_if_higher,
           long_term_balancing_period,
           compensation_rule_drafts(
             id, compensation_rule_id, diagram, clawback_diagram, is_tvp_based, created_at, updated_at
           ),
           compensation_rule_versions(
             id, compensation_rule_id, active_period, diagram, clawback_diagram,
             is_tvp_based, visible_if_scheduled, created_at, updated_at
           )
         )`
      )
      .eq('id', planId)
      .eq('organization_id', organizationId)
      .eq('template', false)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }

      throw error
    }

    return data as unknown as PlanDuplicationRecord
  }

  async listPlanLabels(organizationId: number): Promise<string[]> {
    const { data, error } = await this.supabase
      .from('plans')
      .select('label')
      .eq('organization_id', organizationId)
      .eq('template', false)

    if (error) throw error

    return (data ?? []).map(plan => plan.label)
  }

  async createPlan(plan: TablesInsert<'plans'>): Promise<Pick<Tables<'plans'>, 'id' | 'label'>> {
    const { data, error } = await this.supabase
      .from('plans')
      .insert(plan)
      .select('id, label')
      .single()

    if (error) throw error

    return data
  }

  async createCompensationRule(
    rule: TablesInsert<'compensation_rules'>
  ): Promise<Pick<Tables<'compensation_rules'>, 'id'>> {
    const { data, error } = await this.supabase
      .from('compensation_rules')
      .insert(rule)
      .select('id')
      .single()

    if (error) throw error

    return data
  }

  // ---- Chat history ----

  async createChatConversation(params: {
    organizationId: number
    profileId: string
    title?: string
    chatType?: string
    metadata?: Record<string, unknown>
  }) {
    const { data, error } = await this.supabase
      .from('chat_conversations')
      .insert({
        organization_id: params.organizationId,
        profile_id: params.profileId,
        title: params.title ?? null,
        chat_type: params.chatType ?? 'flipper',
        metadata: (params.metadata ?? {}) as Json
      })
      .select('id')
      .single()

    if (error) throw error
    return data as { id: string }
  }

  async getChatConversation(params: {
    conversationId: string
    organizationId: number
    profileId: string
  }) {
    const { data, error } = await this.supabase
      .from('chat_conversations')
      .select('id, organization_id, profile_id, title, chat_type, created_at, updated_at')
      .eq('id', params.conversationId)
      .eq('organization_id', params.organizationId)
      .eq('profile_id', params.profileId)
      .maybeSingle()

    if (error) throw error
    return data
  }

  async getChatConversations(params: {
    organizationId: number
    profileId: string
    chatType?: string
    limit?: number
    offset?: number
  }) {
    let query = this.supabase
      .from('chat_conversations')
      .select('id, title, chat_type, created_at, updated_at')
      .eq('organization_id', params.organizationId)
      .eq('profile_id', params.profileId)
      .order('updated_at', { ascending: false })
      .range(params.offset ?? 0, (params.offset ?? 0) + (params.limit ?? 50) - 1)

    if (params.chatType) {
      query = query.eq('chat_type', params.chatType)
    }

    const { data, error } = await query

    if (error) throw error
    return data ?? []
  }

  async getChatConversationPreviews(conversationIds: string[]) {
    if (conversationIds.length === 0) return {}

    const { data, error } = await this.supabase
      .from('chat_messages')
      .select('conversation_id, content')
      .in('conversation_id', conversationIds)
      .eq('role', 'user')
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })

    if (error) throw error

    const previews: Record<string, string | null> = {}

    for (const row of data ?? []) {
      if (row.conversation_id && previews[row.conversation_id] === undefined) {
        previews[row.conversation_id] = row.content ?? null
      }
    }

    return previews
  }

  async updateChatConversation(
    params: {
      conversationId: string
      organizationId: number
      profileId: string
    },
    updates: { title?: string; metadata?: Record<string, unknown> }
  ) {
    const payload: { title?: string; metadata?: Json; updated_at: string } = {
      updated_at: new Date().toISOString()
    }
    if (updates.title !== undefined) payload.title = updates.title
    if (updates.metadata !== undefined) payload.metadata = updates.metadata as Json
    const { error } = await this.supabase
      .from('chat_conversations')
      .update(payload)
      .eq('id', params.conversationId)
      .eq('organization_id', params.organizationId)
      .eq('profile_id', params.profileId)

    if (error) throw error
  }

  async deleteChatConversation(params: {
    conversationId: string
    organizationId: number
    profileId: string
  }) {
    const { error } = await this.supabase
      .from('chat_conversations')
      .delete()
      .eq('id', params.conversationId)
      .eq('organization_id', params.organizationId)
      .eq('profile_id', params.profileId)

    if (error) throw error
  }

  async getChatMessages(params: {
    conversationId: string
    organizationId: number
    profileId: string
  }) {
    const { data, error } = await this.supabase
      .from('chat_messages')
      .select(
        'id, role, content, parts, metadata, created_at, chat_conversations!inner(organization_id, profile_id)'
      )
      .eq('conversation_id', params.conversationId)
      .eq('chat_conversations.organization_id', params.organizationId)
      .eq('chat_conversations.profile_id', params.profileId)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })

    if (error) throw error
    return (data ?? []).map(({ chat_conversations: _chatConversations, ...message }) => message)
  }

  async saveChatMessages(
    messages: {
      conversation_id: string
      role: string
      content: string
      parts?: unknown
      metadata?: Record<string, unknown>
    }[]
  ) {
    const { data, error } = await this.supabase
      .from('chat_messages')
      .insert(
        messages.map(m => ({
          conversation_id: m.conversation_id,
          role: m.role,
          content: m.content,
          parts: (m.parts ?? []) as Json,
          metadata: (m.metadata ?? {}) as Json
        }))
      )
      .select('id')

    if (error) throw error
    return (data ?? []) as { id: string }[]
  }

  async saveChatEmbedding(params: {
    conversationId: string
    organizationId: number
    content: string
    embedding: number[]
    metadata?: Record<string, unknown>
  }) {
    const { data, error } = await this.supabase
      .from('chat_embeddings')
      .insert({
        conversation_id: params.conversationId,
        organization_id: params.organizationId,
        content: params.content,
        // The generated db types still model pgvector as `string`, but we want
        // to send the raw numeric array so Postgres stores a native vector.
        embedding: params.embedding as unknown as string,
        metadata: (params.metadata ?? {}) as Json
      })
      .select('id')
      .single()

    if (error) throw error

    return data
  }

  async createCompensationRuleDraft(
    draft: TablesInsert<'compensation_rule_drafts'>
  ): Promise<Pick<Tables<'compensation_rule_drafts'>, 'id'>> {
    const { data, error } = await this.supabase
      .from('compensation_rule_drafts')
      .insert(draft)
      .select('id')
      .single()

    if (error) throw error

    return data
  }

  async linkTeamToPlan({
    organizationId,
    teamId,
    planId,
    effectiveFrom
  }: LinkTeamToPlanArgs): Promise<void> {
    const { error } = await this.supabase.rpc('link_team_to_plan_with_history', {
      _organization_id: organizationId,
      _team_id: teamId,
      _plan_id: planId,
      _effective_from: effectiveFrom
    })

    if (error) throw error
  }

  async listPlanTeamHistory(organizationId: number, planId: number) {
    const { data, error } = await this.supabase.rpc('list_plan_team_history', {
      _organization_id: organizationId,
      _plan_id: planId
    })

    if (error) throw error
    return data ?? []
  }

  async upsertPlanTeamHistory({
    organizationId,
    historyId,
    teamId,
    planId,
    effectiveFrom,
    effectiveTo
  }: UpsertPlanTeamHistoryArgs) {
    const args: Database['public']['Functions']['upsert_plan_team_history']['Args'] = {
      _organization_id: organizationId,
      _team_id: teamId,
      _plan_id: planId,
      _effective_from: effectiveFrom
    }
    if (historyId != null) args._history_id = historyId
    if (effectiveTo != null) args._effective_to = effectiveTo

    const { data, error } = await this.supabase.rpc('upsert_plan_team_history', args)

    if (error) throw error
    return data
  }

  async deletePlanTeamHistory(organizationId: number, historyId: number): Promise<void> {
    const { error } = await this.supabase.rpc('delete_plan_team_history', {
      _organization_id: organizationId,
      _history_id: historyId
    })

    if (error) throw error
  }

  async replacePlanTeamHistory({
    organizationId,
    planId,
    assignments,
    removedHistoryIds
  }: ReplacePlanTeamHistoryArgs) {
    const { data, error } = await this.supabase.rpc('replace_plan_team_history', {
      _organization_id: organizationId,
      _plan_id: planId,
      _assignments: assignments.map(assignment => ({
        history_id: assignment.historyId ?? null,
        team_id: assignment.teamId,
        plan_id: assignment.planId,
        effective_from: assignment.effectiveFrom,
        effective_to: assignment.effectiveTo ?? null
      })),
      _removed_history_ids: removedHistoryIds
    })

    if (error) throw error
    return data ?? []
  }

  async linkTeamToTarget(
    organizationId: number,
    teamTarget: TablesInsert<'teams_targets'>
  ): Promise<void> {
    if (!organizationId) {
      throw new Error('organizationId is required')
    }

    const { team_id, target_id } = teamTarget
    if (team_id == null || target_id == null) {
      throw new Error('team_id and target_id are required')
    }

    const [{ data: teamRows, error: teamError }, { data: targetRows, error: targetError }] =
      await Promise.all([
        this.supabase
          .from('teams')
          .select('id')
          .eq('id', team_id)
          .eq('organization_id', organizationId)
          .limit(1),
        this.supabase
          .from('targets')
          .select('id')
          .eq('id', target_id)
          .eq('organization_id', organizationId)
          .limit(1)
      ])

    if (teamError) throw teamError
    if (targetError) throw targetError
    if (!teamRows?.length || !targetRows?.length) {
      throw new Error('No target-team ownership match for the provided organization')
    }

    const { data, error } = await this.supabase
      .from('teams_targets')
      .insert(teamTarget)
      .select('team_id, target_id, teams!inner(organization_id), targets!inner(organization_id)')
      .eq('teams.organization_id', organizationId)
      .eq('targets.organization_id', organizationId)

    if (error) {
      if (error.code === '23505') {
        return
      }

      throw error
    }

    if (!data || data.length === 0) {
      throw new Error('No target-team link inserted for the provided organization')
    }

    this.targetCache.delete(target_id)
  }

  async listPlanTeamLinksByPlanIds(organizationId: number, planIds: number[]) {
    if (planIds.length === 0) {
      return []
    }

    const today = format(new Date(), 'yyyy-MM-dd')
    const { data, error } = await this.supabase
      .from('teams_plans_history')
      .select('plan_id, team_id, plans!inner(organization_id), teams!inner(organization_id)')
      .in('plan_id', planIds)
      .eq('plans.organization_id', organizationId)
      .eq('teams.organization_id', organizationId)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)

    if (error) throw error

    return (data ?? []).map(
      row => ({ plan_id: row.plan_id, team_id: row.team_id }) satisfies CurrentPlanTeamLink
    )
  }

  async listPlanTeamLinksByOrganization(organizationId: number) {
    const today = format(new Date(), 'yyyy-MM-dd')
    const { data, error } = await this.supabase
      .from('teams_plans_history')
      .select('plan_id, team_id, plans!inner(organization_id), teams!inner(organization_id)')
      .eq('plans.organization_id', organizationId)
      .eq('teams.organization_id', organizationId)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)

    if (error) throw error

    return (data ?? []).map(
      row => ({ plan_id: row.plan_id, team_id: row.team_id }) satisfies CurrentPlanTeamLink
    )
  }

  async listPlanTeamLinksByTeamIds(organizationId: number, teamIds: number[]) {
    if (teamIds.length === 0) {
      return []
    }

    const today = format(new Date(), 'yyyy-MM-dd')
    const { data, error } = await this.supabase
      .from('teams_plans_history')
      .select('plan_id, team_id, plans!inner(organization_id), teams!inner(organization_id)')
      .in('team_id', teamIds)
      .eq('plans.organization_id', organizationId)
      .eq('teams.organization_id', organizationId)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)

    if (error) throw error

    return (data ?? []).map(
      row => ({ plan_id: row.plan_id, team_id: row.team_id }) satisfies CurrentPlanTeamLink
    )
  }

  async listTargetTeamLinksByTargetIds(organizationId: number, targetIds: number[]) {
    if (targetIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('teams_targets')
      .select('target_id, team_id, targets!inner(organization_id), teams!inner(organization_id)')
      .in('target_id', targetIds)
      .eq('targets.organization_id', organizationId)
      .eq('teams.organization_id', organizationId)

    if (error) throw error

    return (data ?? []).map(row => ({ target_id: row.target_id, team_id: row.team_id }))
  }

  async listTargetTeamLinksByOrganization(organizationId: number) {
    const { data, error } = await this.supabase
      .from('teams_targets')
      .select('target_id, team_id, targets!inner(organization_id), teams!inner(organization_id)')
      .eq('targets.organization_id', organizationId)
      .eq('teams.organization_id', organizationId)

    if (error) throw error

    return (data ?? []).map(row => ({ target_id: row.target_id, team_id: row.team_id }))
  }

  async listTargetTeamLinksByTeamIds(organizationId: number, teamIds: number[]) {
    if (teamIds.length === 0) {
      return []
    }

    const { data, error } = await this.supabase
      .from('teams_targets')
      .select('target_id, team_id, targets!inner(organization_id), teams!inner(organization_id)')
      .in('team_id', teamIds)
      .eq('targets.organization_id', organizationId)
      .eq('teams.organization_id', organizationId)

    if (error) throw error

    return (data ?? []).map(row => ({ target_id: row.target_id, team_id: row.team_id }))
  }

  async deleteCompensationRuleDraft(draftId: number, compensationRuleId: number): Promise<void> {
    const { error } = await this.supabase
      .from('compensation_rule_drafts')
      .delete()
      .eq('id', draftId)
      .eq('compensation_rule_id', compensationRuleId)

    if (error) throw error
  }

  async deleteCompensationRule(ruleId: number, planId: number): Promise<void> {
    const { error } = await this.supabase
      .from('compensation_rules')
      .delete()
      .eq('id', ruleId)
      .eq('plan_id', planId)

    if (error) throw error
  }

  async deletePlan(planId: number, organizationId: number): Promise<void> {
    const { error } = await this.supabase
      .from('plans')
      .delete()
      .eq('id', planId)
      .eq('organization_id', organizationId)

    if (error) throw error
  }

  async searchChatEmbeddings(params: {
    queryEmbedding: number[]
    organizationId: number
    profileId: string
    matchThreshold?: number
    matchCount?: number
  }) {
    const { data, error } = await this.supabase.rpc('match_chat_embeddings', {
      query_embedding: JSON.stringify(params.queryEmbedding),
      match_organization_id: params.organizationId,
      match_threshold: params.matchThreshold ?? 0.7,
      match_count: params.matchCount ?? 5
    })

    if (error) throw error
    const results = (data ?? []) as {
      id: string
      conversation_id: string
      content: string
      similarity: number
    }[]

    if (results.length === 0) {
      return []
    }

    const conversationIds = Array.from(new Set(results.map(result => result.conversation_id)))
    const { data: conversations, error: conversationsError } = await this.supabase
      .from('chat_conversations')
      .select('id')
      .in('id', conversationIds)
      .eq('organization_id', params.organizationId)
      .eq('profile_id', params.profileId)

    if (conversationsError) throw conversationsError

    const allowedConversationIds = new Set(
      (conversations ?? []).map(conversation => conversation.id)
    )

    return results.filter(result => allowedConversationIds.has(result.conversation_id))
  }
  async claimFlipperSlackEvent(params: {
    eventId: string
    organizationId: number | null
    profileId: string | null
    teamId: string
    channelId: string | null
    rootThreadTs: string | null
    eventTs: string | null
    eventType: string
    payload: Json
  }) {
    const { data, error } = await this.supabase
      .from('flipper_slack_events')
      .insert({
        event_id: params.eventId,
        organization_id: params.organizationId,
        profile_id: params.profileId,
        team_id: params.teamId,
        channel_id: params.channelId,
        root_thread_ts: params.rootThreadTs,
        event_ts: params.eventTs,
        event_type: params.eventType,
        payload: params.payload as Json
      })
      .select('event_id')
      .maybeSingle()

    if (!error && data) {
      return true
    }

    if (error?.code === '23505') {
      return false
    }

    if (error) {
      throw error
    }

    return false
  }

  async releaseFlipperSlackEvent(params: { eventId: string }) {
    const { error } = await this.supabase
      .from('flipper_slack_events')
      .delete()
      .eq('event_id', params.eventId)
      .eq('status', 'received')

    if (error) throw error
  }

  async getActiveFlipperSlackEvent(params: {
    organizationId: number
    teamId: string
    channelId: string
    rootThreadTs: string
  }) {
    const { data, error } = await this.supabase
      .from('flipper_slack_events')
      .select('event_id, status')
      .eq('organization_id', params.organizationId)
      .eq('team_id', params.teamId)
      .eq('channel_id', params.channelId)
      .eq('root_thread_ts', params.rootThreadTs)
      .in('status', ['queued', 'processing'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw error
    return data
  }

  async updateFlipperSlackEvent(params: {
    eventId: string
    status?: 'received' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'expired'
    responseTs?: string | null
    feedback?: 'helpful' | 'not_helpful' | null
    errorMessage?: string | null
  }) {
    const updates: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    }

    if (params.status !== undefined) updates.status = params.status
    if (params.responseTs !== undefined) updates.response_ts = params.responseTs
    if (params.feedback !== undefined) updates.feedback = params.feedback
    if (params.errorMessage !== undefined) updates.error_message = params.errorMessage

    const updateQuery = this.supabase
      .from('flipper_slack_events')
      .update(updates)
      .eq('event_id', params.eventId)
    const query = params.status === 'queued' ? updateQuery.eq('status', 'received') : updateQuery

    const { error } = await query
    if (error) throw error
  }

  async getFlipperSlackThread(params: {
    organizationId: number
    teamId: string
    channelId: string
    rootThreadTs: string
  }) {
    const { data, error } = await this.supabase
      .from('flipper_slack_threads')
      .select(
        'id, organization_id, profile_id, team_id, channel_id, root_thread_ts, conversation_id, title, latest_event_id'
      )
      .eq('organization_id', params.organizationId)
      .eq('team_id', params.teamId)
      .eq('channel_id', params.channelId)
      .eq('root_thread_ts', params.rootThreadTs)
      .maybeSingle()

    if (error) throw error
    return data
  }

  async createFlipperSlackThread(params: {
    organizationId: number
    profileId: string
    teamId: string
    channelId: string
    rootThreadTs: string
    title?: string
  }) {
    const existing = await this.getFlipperSlackThread(params)
    if (existing) {
      return {
        id: existing.id,
        conversationId: existing.conversation_id,
        profileId: existing.profile_id
      }
    }

    const conversation = await this.createChatConversation({
      organizationId: params.organizationId,
      profileId: params.profileId,
      chatType: 'flipper_slack',
      title: params.title,
      metadata: {
        source: 'slack',
        team_id: params.teamId,
        channel_id: params.channelId,
        root_thread_ts: params.rootThreadTs
      }
    })

    const { data, error } = await this.supabase
      .from('flipper_slack_threads')
      .insert({
        organization_id: params.organizationId,
        profile_id: params.profileId,
        team_id: params.teamId,
        channel_id: params.channelId,
        root_thread_ts: params.rootThreadTs,
        conversation_id: conversation.id,
        title: params.title ?? null
      })
      .select('id, conversation_id, profile_id')
      .maybeSingle()

    if (!error && data) {
      return {
        id: data.id,
        conversationId: data.conversation_id,
        profileId: data.profile_id
      }
    }

    if (error?.code === '23505') {
      const { error: cleanupError } = await this.supabase
        .from('chat_conversations')
        .delete()
        .eq('id', conversation.id)
        .eq('organization_id', params.organizationId)

      if (cleanupError) {
        Logger.error(cleanupError)
      }

      const concurrent = await this.getFlipperSlackThread(params)
      if (concurrent) {
        return {
          id: concurrent.id,
          conversationId: concurrent.conversation_id,
          profileId: concurrent.profile_id
        }
      }
    }

    if (error) throw error
    throw new Error('Failed to create Slack Flipper thread')
  }

  async updateFlipperSlackThread(params: {
    organizationId: number
    teamId: string
    channelId: string
    rootThreadTs: string
    title?: string
    latestEventId?: string | null
  }) {
    const updates: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    }
    if (params.title !== undefined) updates.title = params.title
    if (params.latestEventId !== undefined) updates.latest_event_id = params.latestEventId

    const { error } = await this.supabase
      .from('flipper_slack_threads')
      .update(updates)
      .eq('organization_id', params.organizationId)
      .eq('team_id', params.teamId)
      .eq('channel_id', params.channelId)
      .eq('root_thread_ts', params.rootThreadTs)

    if (error) throw error
  }

  async getActiveFlipperSlackSessionDelegation(params: {
    organizationId: number
    slackTeamId: string
    slackUserId: string
  }) {
    const { data, error } = await this.supabase
      .from('flipper_slack_session_delegations')
      .select(
        'id, organization_id, profile_id, slack_team_id, slack_user_id, encrypted_session, pending_revoke_encrypted_session, session_expires_at, version'
      )
      .eq('organization_id', params.organizationId)
      .eq('slack_team_id', params.slackTeamId)
      .eq('slack_user_id', params.slackUserId)
      .is('revoked_at', null)
      .maybeSingle()

    if (error) throw error
    return data
  }

  async claimFlipperSlackSessionDelegation(params: {
    organizationId: number
    profileId: string
    slackTeamId: string
    slackUserId: string
    encryptedSession: string
    sessionExpiresAt: string
  }) {
    const { data, error } = await this.supabase
      .rpc('claim_flipper_slack_session_delegation', {
        p_organization_id: params.organizationId,
        p_profile_id: params.profileId,
        p_slack_team_id: params.slackTeamId,
        p_slack_user_id: params.slackUserId,
        p_encrypted_session: params.encryptedSession,
        p_session_expires_at: params.sessionExpiresAt
      })
      .single()

    if (error) throw error
    return {
      accepted: data.accepted,
      delegation: {
        id: data.delegation_id,
        organization_id: data.organization_id,
        profile_id: data.profile_id,
        slack_team_id: data.slack_team_id,
        slack_user_id: data.slack_user_id,
        encrypted_session: data.encrypted_session,
        pending_revoke_encrypted_session: data.replaced_encrypted_session,
        session_expires_at: data.session_expires_at,
        version: data.version
      },
      replacedEncryptedSession: data.replaced_encrypted_session
    }
  }

  async clearFlipperSlackPendingSessionRevocation(params: {
    delegationId: string
    version: number
    encryptedSession: string
  }) {
    const { data, error } = await this.supabase.rpc(
      'clear_flipper_slack_pending_session_revocation',
      {
        p_id: params.delegationId,
        p_version: params.version,
        p_encrypted_session: params.encryptedSession
      }
    )

    if (error) throw error
    return data
  }

  async rotateFlipperSlackSessionDelegation(params: {
    delegationId: string
    version: number
    encryptedSession: string
    sessionExpiresAt: string
  }) {
    const { data, error } = await this.supabase.rpc('rotate_flipper_slack_session_delegation', {
      p_id: params.delegationId,
      p_version: params.version,
      p_encrypted_session: params.encryptedSession,
      p_session_expires_at: params.sessionExpiresAt
    })

    if (error) throw error
    return data
  }

  async revokeFlipperSlackSessionDelegation(params: {
    organizationId: number
    profileId: string
    slackTeamId: string
    slackUserId: string
  }) {
    const { error } = await this.supabase
      .from('flipper_slack_session_delegations')
      .update({ revoked_at: new Date().toISOString() })
      .eq('organization_id', params.organizationId)
      .eq('profile_id', params.profileId)
      .eq('slack_team_id', params.slackTeamId)
      .eq('slack_user_id', params.slackUserId)
      .is('revoked_at', null)

    if (error) throw error
  }
}
